import { createContext, useContext } from "react";

// EN, FR, DE, IT — the four office-react declares; German in the Swiss spelling office-react uses (ss, never ß), and the four Harish named (some of the crew
// work in Italian).
//
// ⛔ FR IS THE DEFAULT, not EN. The warehouse floor is French-speaking and Ludovic's own tools are
// in French; defaulting to English would mean every person who matters here changes it on first
// use. office-react defaults to EN because its users are the office.
//
// Deliberately a plain map rather than react-i18next: the string count is small, it needs no
// dependency decision, and swapping it later touches only this file and the `t()` call sites.
export const LOCALES = ["fr", "en", "de", "it"] as const;
export type Locale = (typeof LOCALES)[number];

export const LOCALE_NAMES: Record<Locale, string> = {
  fr: "Français", en: "English", de: "Deutsch", it: "Italiano",
};

type Dict = Record<string, string>;

const STRINGS: Record<Locale, Dict> = {
  fr: {
    "recv.boxesPh": "N° des cartons (s'il y en a plusieurs), séparés par des virgules",
    "job.dmToTick": "À cocher dans le Delivery Manager : {n}",
    "art.title": "Stock des articles",
    "art.other": "Autres",
    "art.refs": "références",
    "art.save": "Enregistrer le comptage ({n} référence(s))",
    "alloc.sync": "Synchroniser avec le planning",
    "alloc.result": "{q} livraisons lues · {c} créée(s) · {u} mise(s) à jour · {r} libérée(s) · {d} sortie(s) du stock",
    "alloc.unknown": "Non reconnu",
    "docs.title": "Documents & checklists",
    "docs.hint": "Checklists à imprimer pour chaque type de kit — y compris les kits sans bon de production en cours — et les documents de référence PDF.",
    "docs.refTitle": "Documents de référence (PDF)",
    "docs.refHint": "Les mêmes documents que ceux déjà imprimés et glissés dans les cartons / affichés en rayon.",
    "docs.open": "Ouvrir",
    "docs.selectAll": "Tout sélectionner",
    "docs.selectNone": "Aucun",
    "docs.print": "Imprimer la sélection ({n})",
    "docs.select": "Imprimer cette checklist",
    "alloc.schedule": "Contrôle automatique du lundi au vendredi à 9h00 et 15h00, et mise à jour à 8h35, de 10h35 à 14h35, à 16h35 et 17h35",
    "alloc.points": "Points à vérifier (dernier contrôle)",
    "alloc.point.partial": "{date} · {wh} · contrat #{contract} · {client} : livraison cochée partiellement ({n}/{total}) : kits sortis en entier, à vérifier",
    "alloc.point.notTicked": "⚠ livraison du {date} · {wh} · contrat #{contract} pas encore cochée par l'équipe : stock non déduit",
    "alloc.point.stillReserved": "❌ livraison du {date} · {wh} · contrat #{contract} toujours réservée (sortie non faite : {reason})",
    "alloc.point.reason.missing": "événement introuvable dans le planning",
    "alloc.point.reason.unapproved": "validation retirée",
    "alloc.point.reason.feed": "le planning n'a pas répondu",
    "alloc.point.postponed": "livraison du {date} · {wh} · contrat #{contract} reportée au {newDate} après la sortie du stock : kits à remettre en stock à la main si besoin",
    "alloc.point.short": "⚠ {wh} — {code} : stock insuffisant à la sortie ({before} pour {qty}) : comptage ou envoi à vérifier · contrat #{contract}",
    "alloc.point.shortArticle": "⚠ stock d'articles insuffisant : {code} {before} pour {qty} · contrat #{contract}",
    "alloc.lastRun": "Dernier contrôle",
    "alloc.auto": "automatique",
    "alloc.never": "Aucun contrôle enregistré pour l'instant.",
    "alloc.next": "Prochain",
    "alloc.failed": "échec",
    "crew.title": "Équipe",
    "crew.none": "Personne n'a travaillé ici ces 3 dernières semaines selon Myotaku.",
    "crew.lastWorked": "dernier jour",
    "crew.permanent": "Fixe",
    "crew.hint": "Les fixes sont proposés en premier au démarrage d'un montage.",
    "compo.title": "Composition",
    "compo.single": "1 pièce",
    "compo.edit": "Modifier",
    "compo.hint": "En combien de pièces ce meuble est-il stocké ? Une étiquette par pièce. Retenu pour ce produit.",
    "compo.save": "Enregistrer",
    "stock.palletPh": "ex. 12",
    "stock.rackPh": "ex. R3-2",
    "stock.recent": "Récents",
    "stock.upcoming": "À venir",
    "stock.src.return": "retour client",
    "stock.src.reception": "réception fournisseur",
    "stock.by.person": "à la main",
    "stock.by.ai": "par Claude",
    "stock.leaveSure": "Oui, sortir du stock",
    "com.cancelSure": "Oui, annuler",
    "com.done": "{n} meuble(s) réservé(s) pour {client} — référence {ref}.",
    "inv.title": "📋 Inventaire des kits",
    "inv.last": "Dernier inventaire le {when} par {who} — {n} écart(s)",
    "inv.counted": "compté",
    "inv.save": "Valider l'inventaire",
    "inv.hint": "Après un comptage physique : choisissez l'entrepôt, tapez le nombre compté pour chaque kit (laissez vide ce que vous n'avez pas compté), vérifiez les écarts puis « Valider l'inventaire ». Le stock de l'application est remplacé par vos chiffres et chaque correction est gardée dans l'historique.",
    "inv.history": "Historique du stock des kits",
    "inv.noHistory": "Aucun changement enregistré pour l'instant.",
    "inv.src.production": "production",
    "inv.src.transfer": "envoi",
    "inv.src.delivery": "livraison",
    "inv.src.inventory": "inventaire",
    "inv.src.receipt": "réception",
    "inv.src.reception_check": "vérification de réception",
    "inv.src.manual": "ajout manuel",
    "nav.carts": "Chariots",
    "nav.commercial": "Commercial",
    "carts.sub": "Un QR sur chaque chariot : chez le client on l'associe au retour, à l'entrepôt on le scanne pour savoir à qui il est.",
    "carts.number": "N° de chariot",
    "carts.numberPh": "ex. 42",
    "carts.open": "Ouvrir",
    "carts.back": "← Tous les chariots",
    "carts.missing": "Retours passés sans chariot scanné",
    "carts.missingHint": "Le chariot n'a sans doute pas été scanné chez le client : scanne-le à son arrivée et associe-le ici.",
    "carts.linked": "Chariots en circulation",
    "carts.linkedNone": "Aucun chariot associé pour l'instant.",
    "carts.grid": "Les {n} chariots",
    "carts.gridHint": "Touche un numéro pour ouvrir le chariot.",
    "carts.linkT": "Associer ce chariot au retour du client",
    "carts.search": "Chercher un client, un nom ou un n° d'événement",
    "carts.linkedBy": "Associé par {who} le {when}",
    "carts.arrivedAt": "arrivé le {when}",
    "carts.arrive": "Arrivé à l'entrepôt",
    "carts.toStock": "Entrer les meubles en stock",
    "carts.free": "Chariot vidé — le libérer",
    "carts.freeConfirm": "Confirmer : libérer le chariot",
    "carts.st.free": "Libre",
    "carts.st.on_the_road": "Chez le client ou en route",
    "carts.st.at_warehouse": "À l'entrepôt · à vider",
    "carts.st.lost": "Sans nouvelles depuis 14 jours",
    "com.s1": "Contrat",
    "com.s2": "Meubles du stock",
    "com.s3": "Vérifier et envoyer",
    "com.change": "Changer",
    "com.searchContract": "Nom du client ou n° de contrat",
    "com.reaches": "Livraisons du planning jusqu'au {d} (limite de la source Myotaku).",
    "com.noStock": "Aucun meuble disponible dans cet entrepôt.",
    "com.available": "dispo",
    "com.prepare": "Préparation à l'entrepôt",
    "com.prepareHint": "Proposé : le jour ouvré avant la livraison.",
    "com.note": "Note pour la logistique",
    "com.notePh": "Ex. retirer les pieds du canapé, livrer avec la table du même client",
    "com.send": "Réserver {n} meuble(s)",
    "com.sendHint": "Les meubles sont réservés tout de suite. L'événement « PRÉPARATION CLIENT » se crée encore à la main dans le planning ; notez son n° dans la réservation.",
    "com.reservations": "Réservations",
    "com.none": "Aucune réservation.",
    "com.st.sent": "envoyée",
    "com.st.prepared": "préparée",
    "com.st.cancelled": "annulée",
    "com.prepareShort": "prép.",
    "com.deliverShort": "livr.",
    "com.prepared": "Préparée — sortir du stock",
    "com.cancel": "Annuler",
    "com.eventPh": "N° événement",
    "com.saveEvent": "Noter",
    "nav.stock": "Stock",
    "stock.tab.intake": "Entrée",
    "stock.tab.inventory": "Inventaire",
    "stock.tab.returns": "Retours",
    "stock.tab.labelling": "Étiquetage",
    "ret.title": "Retours du planning",
    "ret.hint": "Articles attendus par retour et meubles déjà entrés en stock.",
    "ret.empty": "Les retours (court terme, long terme, compléments) des 2 dernières semaines et de la semaine à venir apparaissent ici.",
    "ret.entered": "entrés",
    "ret.noItems": "Pas de liste de meubles pour ce retour.",
    "rec.title": "Étiquetage des réceptions",
    "rec.hint": "Meubles commandés pour les clients (planning des livraisons). Un toucher sur « Étiqueter » = étiquette prête, sans autre étape.",
    "rec.itemsHint": "Étiquetez chaque meuble après montage et emballage, avant de le ranger dans le chariot du client.",
    "rec.labelOne": "Étiqueter",
    "rec.labelAll": "Étiqueter les {n} restants",
    "rec.labeling": "Étiquetage…",
    "rec.empty": "Aucune livraison avec des meubles à étiqueter.",
    "rec.labelled": "étiquetés",
    "rec.assembled": "monté",
    "rec.for": "Pour",
    "rec.delivery": "livraison",
    "rec.unit": "pièce",
    "stock.tagline": "Entrée en stock des meubles de retour client",
    "stock.pickWarehouse": "Choisissez un entrepôt en haut : chaque meuble entre dans un entrepôt précis.",
    "stock.s1": "Retour client",
    "stock.s1hint": "Choisissez le retour du planning d'où vient le meuble.",
    "stock.s2": "Meuble",
    "stock.s2hint": "Touchez le meuble que vous avez devant vous. Pas dans la liste ? Cherchez dans tout le catalogue.",
    "stock.s2hintNone": "Sans retour planifié : cherchez le meuble dans le catalogue.",
    "stock.chosen": "Meuble retenu",
    "stock.nothingChosen": "Aucun meuble choisi.",
    "stock.s3": "État du meuble",
    "stock.cond.new": "Neuf",
    "stock.cond.good": "Bon",
    "stock.cond.good.hint": "Prêt à relouer",
    "stock.cond.light_marks": "Moyen",
    "stock.cond.light_marks.hint": "Traces légères",
    "stock.cond.damaged": "Abîmé",
    "stock.cond.damaged.hint": "Réparation requise",
    "stock.damagePh": "Description des dégâts — ex. rayure 10 cm plateau, pied gauche fendu",
    "stock.s4": "Emballage et rangement",
    "stock.blanket": "Couverture Myotaku",
    "stock.film": "Film plastique",
    "stock.unwrapped": "Non emballé",
    "stock.pallet": "N° de palette",
    "stock.palletShort": "Palette",
    "stock.rack": "N° de rack",
    "stock.qty": "Meubles identiques",
    "stock.placeHint": "La palette et le rack sont repris pour le meuble suivant. Plusieurs meubles identiques : une fiche, un n° et une étiquette chacun.",
    "stock.save": "Enregistrer {n} meuble(s) et préparer les étiquettes",
    "stock.noReturns": "Aucun retour pour cet entrepôt cette semaine ni la suivante.",
    "stock.today": "Aujourd'hui",
    "stock.contract": "contrat",
    "stock.entered": "entré(s)",
    "stock.noReturn": "Sans retour planifié",
    "stock.noItems": "Ce retour n'a aucun meuble du catalogue : cherchez-le ci-dessous.",
    "stock.searchPh": "Nom, réf. Myotaku, réf. fournisseur…",
    "stock.search": "Chercher dans tout le catalogue",
    "stock.searchUnavailable": "La recherche dans le catalogue n'est pas encore disponible sur ce serveur Myotaku.",
    "stock.searchNone": "Aucun produit ne correspond.",
    "stock.filterPh": "N° de stock, nom, palette…",
    "stock.status.held": "En stock et réservés",
    "stock.status.in_stock": "En stock",
    "stock.status.reserved": "Réservé",
    "stock.status.left": "Sorti",
    "stock.pieces": "meubles",
    "stock.emptyInventory": "Aucun meuble ici pour l'instant.",
    "stock.days": "j",
    "stock.fromReturn": "retour",
    "stock.move": "Déplacer",
    "stock.reprint": "Réimprimer l'étiquette",
    "media.title": "Photos et vidéos",
    "media.add": "Ajouter une photo ou une vidéo",
    "media.uploading": "Envoi…",
    "media.empty": "Aucune photo pour l'instant.",
    "media.video": "Vidéo",
    "stock.leave": "Sortir du stock",
    "stock.ev.received": "entré",
    "stock.ev.identified": "identifié",
    "stock.ev.labelled": "étiqueté",
    "stock.ev.moved": "déplacé",
    "stock.ev.graded": "état changé",
    "stock.ev.reserved": "réservé",
    "stock.ev.released": "libéré",
    "stock.ev.left": "sorti",
    "stock.labelsReady": "{n} étiquette(s) prête(s)",
    "stock.print": "Imprimer",
    "stock.close": "Fermer",
    "stock.part": "Pièce",
    "hist.live": "En cours",
    "hist.past": "R\xe9el vs estim\xe9",
    "hist.none": "Aucun montage termin\xe9.",
    "hist.when": "Termin\xe9",
    "hist.estimate": "Pr\xe9vu",
    "hist.actual": "R\xe9el",
    "demand.title": "Kits demand\xe9s par mois",
    "demand.hint": "6 derniers mois, bons non annul\xe9s. Survolez une couleur pour le d\xe9tail.",
    "demand.chart": "Graphique",
    "demand.table": "Tableau",
    "demand.empty": "Aucun bon de production sur les 6 derniers mois.",
    "demand.kits": "kits",
    "demand.kit": "Kit",
    "demand.total": "Total",
    "demand.never": "Aucune demande sur 6 mois",
    "nav.supervisor": "Responsable",
    "sup.running": "Montages en cours",
    "sup.people": "Monteurs au travail",
    "sup.none": "Aucun montage en cours.",
    "sup.yield": "rendement",
    "sup.earned": "gagné",
    "sup.spent": "passé",
    "sup.alertYield": "rendement {p} % (seuil {l} %)",
    "sup.alertIdle": "rien validé depuis {m} (attendu : {l} au plus)",
    "nav.receiving": "R\u00e9ception",
    "recv.title": "R\u00e9ceptions fournisseur",
    "recv.new": "Nouveau bon de livraison",
    "recv.supplier": "Fournisseur",
    "recv.docRef": "N\u00b0 du bon",
    "recv.order": "Commande",
    "recv.lines": "Lignes",
    "recv.sku": "R\u00e9f\u00e9rence",
    "recv.received": "Re\u00e7u",
    "recv.ordered": "Command\u00e9",
    "recv.boxes": "Cartons",
    "recv.addLine": "Ajouter une ligne",
    "recv.save": "Enregistrer",
    "recv.reconcile": "Lire et v\u00e9rifier",
    "recv.complete": "complet",
    "recv.missing": "manquant",
    "recv.extra": "en trop",
    "recv.unmatched": "non rattach\u00e9",
    "recv.none": "Aucune r\u00e9ception.",
    "recv.help": "Photographiez le bon avant d'ouvrir, puis comparez-le \u00e0 la commande.",
    "ident.title": "Photo pour reconnaissance",
    "ident.hint": "Photographiez le meuble entier, avant de l'emballer. L'application le cherche d'abord parmi les articles du retour.",
    "ident.take": "📷 Prendre la photo",
    "ident.retake": "Reprendre",
    "ident.sending": "Envoi de la photo…",
    "ident.analyzing": "Reconnaissance : le type de meuble, puis la comparaison avec le retour (et le catalogue si besoin)…",
    "ident.pickThis": "C'est ce meuble",
    "ident.other": "Autres possibilités",
    "ident.searchCatalogue": "Chercher dans tout le catalogue",
    "ident.seen": "Vu sur la photo",
    "ident.sure": "Sûr",
    "ident.likely": "Probable",
    "ident.check": "À vérifier",
    "ident.noMatch": "Aucun article du retour ne correspond.",
    "ident.noMatchAny": "Aucun produit identique trouvé, ni dans le retour ni dans le catalogue. Cherchez à la main ou reprenez la photo (meuble entier, de face).",
    "ident.notInRetour": "Pas trouvé parmi les articles du retour : voici la meilleure correspondance dans le catalogue. Vérifiez le retour choisi.",
    "ident.failed": "La reconnaissance a échoué. Réessayez ou cherchez à la main.",
    "ident.fromReturn": "du retour",
    "ident.fromCatalogue": "catalogue",
    "sheet.open": "Voir la fiche article (références, conditions d'achat, historique)",
    "sheet.stockNow": "Stock actuel :",
    "sheet.kitsPossible": "soit {n} kit(s) possible(s)",
    "sheet.references": "Références",
    "sheet.refZ": "Réf. Myotaku (Z)",
    "sheet.refSystem": "Réf. système Myotaku",
    "sheet.systemName": "Nom dans le système",
    "sheet.supplier": "Fournisseur",
    "sheet.supplierRef": "Réf. fournisseur",
    "sheet.supplierDesignation": "Désignation fournisseur",
    "sheet.onNotes": "Réf. relevées sur les bulletins",
    "sheet.purchase": "Conditions d'achat",
    "sheet.price": "Prix unitaire",
    "sheet.noPrice": "non renseigné",
    "sheet.moq": "Minimum par article",
    "sheet.rules": "Conditions du fournisseur",
    "sheet.usualQty": "Quantités commandées habituellement",
    "sheet.toConfirm": "À confirmer",
    "sheet.remark": "Remarque",
    "sheet.notInCatalogue": "Aucune donnée fournisseur pour cet article dans le catalogue (liste de Charles Vila du 07.10.2026).",
    "sheet.reserved": "Conditions d'achat réservées aux responsables.",
    "sheet.notImported": "Les conditions d'achat (liste de Charles Vila du 07.10.2026) ne sont pas encore importées dans l'application.",
    "sheet.history": "Historique du stock",
    "sheet.noHistory": "Aucun mouvement enregistré.",
    "nav.access": "Accès",
    "access.title": "Accès par module",
    "access.hint": "Chaque personne utilise les modules cochés et ouvre l'application sur son module d'accueil. Un admin utilise tous les modules. L'application refuse toute action hors des modules d'une personne.",
    "access.person": "Personne",
    "access.home": "Accueil",
    "access.active": "Actif",
    "access.admin": "admin",
    "access.new": "Nouvelle personne",
    "access.name": "Nom",
    "access.password": "Mot de passe (6 caractères min.)",
    "access.homeFirst": "Le premier module coché devient son accueil (modifiable ensuite).",
    "access.create": "Créer l'accès",
    "access.noModule": "Votre compte n'a encore aucun module. Demandez à un admin de vous en donner dans « Accès ».",
    "unit.title": "Unité n° {n} / {q}",
    "unit.count": "{c} / {m} articles",
    "unit.inStock": "{n} en stock",
    "unit.validate": "Valider cette unité ({d} → {n} / {q})",
    "unit.tickAll": "Cochez tous les articles pour valider ({c}/{m})",
    "unit.correct": "Erreur ? Retirer 1 unité déjà validée ({d} → {p})",
    "unit.finishNow": "Terminer maintenant",
    "unit.markDone": "Marquer terminé",
    "unit.finishConfirm": "Terminer maintenant avec {d} / {q} unités faites ? Les articles déjà cochés de l'unité en cours seront remis en stock.",
    "unit.cancel": "Annuler le bon",
    "unit.cancelConfirm": "Annuler ce bon ({d} / {q} unités faites) ? Les kits déjà faits restent en stock ; les articles cochés de l'unité en cours sont remis en stock.",
    "unit.yes": "Oui",
    "unit.no": "Non",
    "artrec.title": "📷 Réception fournisseur — photo, capture d'écran ou PDF",
    "artrec.hint": "Envoyez tout ce qui montre ce qui est arrivé : photo du bulletin de livraison (une photo par page), capture d'écran (confirmation de commande, e-mail, WhatsApp, site du fournisseur), facture, PDF, photo des étiquettes des cartons… Chaque article reconnu entre dans le stock des articles ; le reste est à vérifier.",
    "artrec.send": "Envoyer la réception ({n} page(s))",
    "artrec.typeAny": "Type de document (optionnel)",
    "artrec.type.bulletin": "Bulletin de livraison",
    "artrec.type.capture": "Capture d'écran (commande, e-mail, WhatsApp, site)",
    "artrec.type.facture": "Facture / confirmation de commande",
    "artrec.type.etiquettes": "Photo des cartons / étiquettes",
    "artrec.type.autre": "Autre",
    "artrec.note": "Note (optionnel)",
    "artrec.noteShown": "Note :",
    "artrec.latest": "Dernières réceptions",
    "artrec.none": "Aucune réception photographiée pour l'instant.",
    "artrec.updated": "Stock mis à jour :",
    "artrec.total": "total",
    "artrec.toVerify": "À vérifier :",
    "artrec.clientReserved": "Réservé client (non entré en stock) :",
    "artrec.forClient": "pour",
    "artrec.summary": "Lu :",
    "artrec.st.verified": "Vérifiée et corrigée",
    "artrec.verifiedBy": "Vérifiée et corrigée le {at} par {by} :",
    "artrec.corrections": "Corrections de stock :",
    "artrec.check.title": "Vérification de cette réception",
    "artrec.check.hint": "Corriger le stock si nécessaire (article et quantité, plus ou moins), écrire ce qui a été vérifié, puis « Vérifié et corrigé ».",
    "artrec.check.pick": "Choisir l'article",
    "artrec.check.qty": "+/− quantité",
    "artrec.check.remove": "Retirer cette correction",
    "artrec.check.add": "Correction de stock",
    "artrec.check.note": "Note (obligatoire) : ce qui a été vérifié et corrigé",
    "artrec.check.button": "Vérifié et corrigé",
    "artrec.disputed.title": "Références fournisseur contestées",
    "artrec.disputed.hint": "Tant qu'une référence est contestée, la lecture des réceptions fait comme si elle n'existait pas : choisir le bon article, puis « Valider ».",
    "artrec.disputed.now": "actuellement {code}",
    "artrec.disputed.validate": "Valider",
    "artrec.cal.noCatalogue": "Le catalogue fournisseurs de Z Production n'est pas encore importé : une commande dont les références fournisseur ne sont connues que là n'est pas contrôlée ici.",
    "artrec.cal.held": "Messages Slack de ce contrôle suspendus : l'entrepôt photographie encore ses réceptions dans Z Production, qui prévient déjà l'équipe.",
    "artrec.cal.missing": "Arrivé selon le Delivery calendar mais pas encore photographié dans l'application :",
    "artrec.cal.missingAsk": "Merci de photographier ce bulletin ici.",
    "artrec.cal.last": "Dernier contrôle avec le Delivery calendar : {at}",
    "artrec.cal.error": "erreur",
    "artrec.cal.otherWarehouses": "Cette application ne voit pas encore vers quel entrepôt va chaque ligne d'une commande : une ligne d'une commande de Penthalaz livrée ailleurs est encore comptée ici.",
    "artrec.cal.st.ok": "✅ Contrôle Delivery calendar : conforme",
    "artrec.cal.st.ecart": "⚠️ Contrôle Delivery calendar : écart",
    "artrec.cal.st.absent_calendar": "ℹ️ Aucune commande correspondante dans le Delivery calendar",
    "artrec.cal.st.non_livree": "ℹ️ Commande trouvée dans le Delivery calendar mais pas encore marquée livrée",
    "artrec.cal.orders": "commande {ids}",
    "artrec.cal.diff": "{name} ({code}) : commande {ordered}, photo {photo}",
    "artrec.cal.extra": "{name} ({code}) : photo {photo}, absent de la commande",
    "artrec.cal.notDeliveredNote": "commande pas encore marquée livrée dans le Delivery calendar (statut {statuses})",
    "artrec.cal.absentNote": "aucune commande de Penthalaz avec ces articles depuis le {since}",
    "artrec.st.waiting": "En attente de traitement",
    "artrec.st.running": "Traitement en cours",
    "artrec.st.done": "Traitée — stock mis à jour",
    "artrec.st.verify": "À vérifier",
    "art.modeCount": "✏️ Corriger le total",
    "art.modeAdd": "➕ Ajouter",
    "art.addSave": "Ajouter au stock ({n} référence(s))",
    "art.search": "Chercher une référence, un article, un fournisseur ou une réf. fournisseur…",
    "jobs.onlyAssembly": "Montages seulement (liste de Ludovic)",
    "jobs.today": "Aujourd'hui",
    "jobs.upcoming": "À venir",
    "jobs.done": "Terminés",
    "jobs.archived": "Archivés",
    "jobs.noDelivery": "livraison à planifier",
    "stock.otherCat": "Autres",
    "stock.inStockN": "En stock",
    "stock.outN": "Sortis",
    "stock.avgAge": "Âge moyen",
    "stock.searchAll": "Rechercher n° stock, nom, réf., client…",
    "stock.allCats": "Toutes les catégories",
    "stock.allPallets": "Toutes les palettes",
    "stock.palletN": "Palette {p}",
    "stock.all": "Tous",
    "stock.allSources": "Toutes origines",
    "stock.srcReturns": "Retours clients",
    "stock.srcReceptions": "Réceptions",
    "stock.allWarehouses": "Tous entrepôts",
    "stock.oldestFirst": "Plus anciens d'abord",
    "stock.exportCsv": "Exporter CSV",
    "stock.csvDone": "Export prêt",
    "stock.emptyInvTitle": "Aucun meuble en stock ici",
    "stock.noResults": "Aucun résultat pour ces filtres.",
    "stock.forClient": "Pour",
    "stock.enteredOn": "entré le",
    "stock.openCatalogue": "Voir la fiche catalogue ↗",
    "stock.changeCond": "Changer l'état",
    "stock.backIn": "Remettre en stock",
    "stock.markOut": "Marquer comme sorti",
    "stock.delete": "Retirer",
    "stock.damageNotePh": "Défaut (obligatoire pour « Abîmé »)",
    "stock.reason": "Motif",
    "stock.out.redeliv": "Relivré à un client",
    "stock.out.repair": "Parti en réparation",
    "stock.out.sale": "Vendu",
    "stock.out.scrap": "Détruit / déchetterie",
    "stock.out.transfer": "Transfert entrepôt",
    "stock.deleteConfirm": "Supprimer définitivement cette fiche ? Les photos restent stockées.",
    "stock.deleteYes": "Oui, supprimer",
    "stock.ev.back": "remis en stock",
    "prod.capacitySaved": "Capacité enregistrée : {p} personne(s) × {h} h / jour",
    "prod.objective": "Objectif",
    "prod.timeRequired": "Temps total requis",
    "prod.capacityDay": "Capacité du jour",
    "prod.marginLeft": "Marge disponible",
    "prod.overrun": "Dépassement",
    "prod.assignTo": "Attribuer à un préparateur (optionnel)",
    "prod.assignPh": "Ex. Emerson — vide = n'importe qui",
    "prod.note": "Note (optionnel)",
    "prod.teamCapacity": "Capacité de l'équipe",
    "prod.people": "Personnes",
    "prod.hoursPerDay": "Heures / jour",
    "prod.doneShort": "faits",
    "prod.alertFailed": "Préparation arrêtée, mais l'alerte n'a pas pu être envoyée : prévenez le responsable directement.",
    "prod.resolve": "Marquer comme réglé",
    "prod.for": "Pour",
    "prod.takeOver": "Prendre le relais",
    "prod.changeAssign": "Changer l'attribution",
    "prod.assign": "Attribuer",
    "prod.split": "Diviser le bon",
    "prod.lockTitle": "En cours de préparation",
    "prod.lockBody": "par {o}{paused} — {d} / {q} faits. La checklist est réservée à {o}.",
    "prod.pausedTag": "(en pause)",
    "prod.takeOverAsk": "Prendre le relais sur {k} ?",
    "prod.takeOverYes": "Oui, je prends le relais",
    "prod.assignedAsk": "Ce bon est attribué à {n}. Le démarrer quand même ?",
    "prod.assignedYes": "Oui, je le démarre",
    "prod.assignName": "Nom du préparateur (vide = n'importe qui)",
    "prod.save": "Enregistrer",
    "prod.splitQty": "Nombre de kits à mettre dans le nouveau bon (1 à {n})",
    "prod.splitTo": "Attribuer le nouveau bon à (optionnel)",
    "prod.splitYes": "Diviser",
    "prod.reopen": "Rouvrir cette commande",
    "prod.reopenPartial": "Rouvrir cette commande ({d}/{q} fait)",
    "jobs.overdue": "en retard",
    "jobs.issue": "Problème signalé",
    "jobs.issues": "{n} problèmes signalés",
    "jobs.gone": "Retiré du planning",
    "team.title": "Monteurs sur ce montage",
    "team.nobody": "Personne pour l'instant",
    "team.add": "+ Monteur",
    "team.remove": "Retirer",
    "team.left": "a quitté le montage",
    "issue.howMany": "Combien sont touchés ?",
    "issue.ofN": "sur {n}",
    "issue.sentTo": "envoyé à {n} le {t}",
    "job.fromOrders": "Meubles des commandes fournisseur seulement (liste de Ludovic)",
    "job.otherList": "Ce montage se coche dans l'autre liste : celle-ci se lit seulement.",
    "job.toAssemble": "Meubles à monter",
    "job.toCheck": "Articles à contrôler (sans montage)",
    "job.toCheckHint": "Ouvre chaque emballage et vérifie l'article : casse, rayures, pièces ou accessoires manquants. Referme ou protège-le, puis coche ✓ la ligne (ou compte avec + si tu n'en as fait qu'une partie). S'il est abîmé : 📷 photo, puis « ⚠️ Problème » sur sa ligne.",
    "job.order": "commande",
    "job.arrived": "arrivé le {d}",
    "job.expected": "arrivée prévue le {d}",
    "job.noArrival": "sans date d'arrivée",
    "job.cartons": "📦 {n} cartons",
    "job.cartonsPer": "📦 {n} cartons par meuble · {t} au total",
    "job.ref": "réf.",
    "job.unreadable": "Commande citée mais illisible : {ids}",
    "job.noOrders": "Le commentaire du planning ne cite aucune commande fournisseur.",
    "arch.hint": "Un client est archivé automatiquement le lendemain de sa date de livraison du planning (livraison effectuée), sauf pendant un montage en cours. Archiver ou désarchiver à la main tient tant que les dates de montage et de livraison du planning ne changent pas.",
    "arch.q": "Rechercher un client ou un n°",
    "arch.none": "Aucun client archivé pour l'instant.",
    "arch.noHit": "Aucun client archivé ne correspond à la recherche.",
    "arch.un": "Désarchiver",
    "arch.ar": "Archiver",
    "arch.by": "par",
    "arch.boxOn": "📦 Client archivé",
    "arch.boxOff": "Archivage",
    "arch.pAuto": "Livraison effectuée le {d} : le client est rangé dans Archivés.",
    "arch.pMan": "Archivé à la main le {d}{w}.",
    "arch.pWill": "Archivé automatiquement le lendemain de la livraison ({d}).",
    "arch.pNone": "Pas de date de livraison dans le planning : archive-le à la main quand c'est fini (livraison annulée ou faite sans date).",
    "arch.pBack": "Désarchivé à la main : reste dans les listes tant que les dates du planning ne changent pas.",
    "arch.run": "Montage en cours : mets-le en pause ou termine-le avant d'archiver.",
    "note.title": "Réception des documents fournisseur",
    "note.hint": "Photographie chaque page du bon de livraison (ou d'un autre document fournisseur). L'application la fait lire et la croise avec le Delivery calendar : ce qui est reçu, manquant ou en trop pour chaque client.",
    "note.photo": "📷 Photographier une page",
    "note.files": "📁 Choisir des photos ou PDF",
    "note.drop": "…ou glisse ici les photos ou PDF depuis ton ordinateur",
    "note.page": "Page {n}",
    "note.del": "Retirer",
    "note.read": "Lire et croiser avec le Delivery calendar ({n} page(s))",
    "note.uploading": "Envoi des pages…",
    "note.reading": "Lecture puis croisement avec le Delivery calendar… (10 à 60 s par page, et environ 1 minute pour le croisement)",
    "note.failed": "La lecture n'a pas abouti :",
    "note.retry": "Relire",
    "note.rematch": "Recroiser avec le Delivery calendar",
    "note.type": "Fichier non pris en charge : photo (JPEG, PNG, HEIC) ou PDF.",
    "note.doc.delivery_note": "Bon de livraison",
    "note.doc.order_confirmation": "Confirmation de commande",
    "note.doc.invoice": "Facture",
    "note.doc.labels": "Étiquettes",
    "note.doc.other": "Document",
    "note.lines": "{n} ligne(s) lue(s)",
    "note.pages": "{n} page(s)",
    "note.readAt": "lu le {when}",
    "note.clients": "{n} client(s)",
    "note.ord": "commandé",
    "note.got": "reçu",
    "note.conf": "confirmé",
    "note.ok": "complet",
    "note.extra": "{n} en trop",
    "note.miss": "pas sur ce bon",
    "note.confSt": "pas encore une réception",
    "note.colli": "{n} colis",
    "note.cartons": "{n} cartons",
    "note.cw": "cartons incomplets : vérifier",
    "note.un": "Pas trouvé dans le Delivery calendar (pas de montage prévu pour ces articles)",
    "note.many": "Plusieurs clients possibles : à attribuer à la main",
    "note.unread": "Numéro illisible",
    "note.unclear": "À vérifier sur le papier",
    "note.open": "Ouvrir la fiche",
    "note.sec": "Réception (documents fournisseur lus)",
    "note.ocW": "Ce document est une confirmation de commande (ou une facture), pas un bon de livraison : il ne prouve pas que la marchandise est arrivée. Ne pas chercher le meuble tant qu'il n'est pas réceptionné.",
    "note.deliveryOn": "livraison {when}",
    "note.hist": "Derniers documents lus",
    "note.none": "Aucun document lu pour l'instant.",
    "note.byHand": "Saisir un bon à la main",
    "note.matched": "{n} rattachée(s)",
    "note.pillOk": "📄 reçu",
    "note.pillWarn": "⚠️ réception",
    "note.pillOc": "📝 commande confirmée",
    "tr.title": "Envois entre entrep\u00f4ts",
    "tr.from": "De",
    "tr.to": "Vers",
    "tr.send": "Envoyer",
    "tr.receive": "R\u00e9ceptionner",
    "tr.pending": "en attente",
    "tr.inTransit": "en route",
    "tr.received": "re\u00e7u",
    "tr.new": "Nouvel envoi",
    "tr.none": "Aucun envoi.",
    "tr.arrived": "Arriv\u00e9s",
    "tr.arrivalHint": "Comptez ce qui est arriv\u00e9 : seuls ces kits entrent dans le stock de ce site.",
    "tr.lossNote": "Ce qui est arriv\u00e9 aux manquants (obligatoire)",
    "tr.confirmArrival": "Confirmer l'arriv\u00e9e",
    "tr.receivedOf": "{n} sur {m} arriv\u00e9s",
    "tr.inTransitNote": "En route : la marchandise n'appartient \u00e0 aucun des deux sites.",
    "nav.production": "Production",
    "prod.progress": "Avancement du jour",
    "prod.target": "Objectif",
    "prod.done": "Faits",
    "prod.variance": "\u00c9cart",
    "prod.ahead": "en avance",
    "prod.behind": "en retard",
    "prod.byPreparer": "Suivi par pr\u00e9parateur",
    "prod.kitsMade": "kits faits",
    "prod.perKit": "min / kit",
    "prod.queue": "File de production",
    "prod.finishedToday": "Termin\u00e9s aujourd'hui",
    "prod.stock": "\u00c9tat du stock",
    "prod.onHand": "En stock",
    "prod.reserved": "R\u00e9serv\u00e9",
    "prod.available": "Disponible",
    "prod.newOrder": "Nouveau bon de production",
    "prod.kit": "Kit",
    "prod.qty": "Quantit\u00e9",
    "prod.raise": "Envoyer",
    "prod.noQueue": "Aucun bon en attente — la file est vide.",
    "prod.allocations": "Kits r\u00e9serv\u00e9s pour les prochaines livraisons",
    "prod.neededOn": "Pour le",
    "prod.unitMinutes": "min / kit",
    "prod.reservedNote": "Une r\u00e9servation ne diminue jamais le stock r\u00e9el.",
    "wh.all": "Tous",
    "job.assembly": "Montage",
    "job.delivery": "Livraison",
    "job.estimate": "Estimation",
    "job.assembled": "mont\u00e9s",
    "job.checked": "contrôlés",
    "job.inProgress": "En cours",
    "job.todo": "\u00c0 faire",
    "job.finished": "Termin\u00e9",
    "job.issueReported": "Probl\u00e8me signal\u00e9",
    "job.packing": "Emballage obligatoire",
    "job.packingBody": "Chaque meuble mont\u00e9 est emball\u00e9 dans une couverture MYOTAKU puis film\u00e9, avant d'aller dans le chariot du client.",
    "job.furniture": "Meubles \u00e0 monter",
    "job.pickOne": "Choisissez une livraison.",
    "start.title": "Qui monte ?",
    "start.help": "Touchez chaque monteur qui travaille avec vous. Un monteur d\u00e9j\u00e0 lanc\u00e9 sur un autre client en est retir\u00e9 automatiquement.",
    "start.extras": "Extras",
    "start.permanent": "Fixes",
    "start.addName": "Ajouter un pr\u00e9nom",
    "start.add": "Ajouter",
    "start.atLeastOne": "Choisis au moins un monteur",
    "issue.title": "Signaler un probl\u00e8me",
    "issue.kind": "Probl\u00e8me",
    "issue.item": "Article concern\u00e9",
    "issue.none": "Aucun en particulier",
    "issue.what": "Ce que vous voyez",
    "issue.by": "Signal\u00e9 par",
    "issue.blocks": "Le montage est bloqu\u00e9 (met le minuteur en pause)",
    "issue.send": "Signaler",
    "issue.damaged_furniture": "Meuble endommagé",
    "issue.missing_part": "Pièce ou quincaillerie manquante",
    "issue.missing_furniture": "Meuble manquant",
    "issue.wrong_model_or_colour": "Mauvais mod\u00e8le ou couleur",
    "issue.missing_instructions": "Notice de montage manquante",
    "issue.other": "Autre probl\u00e8me",
    "finish.title": "Terminer le travail",
    "finish.send": "Terminer",
    "finish.reopen": "Rouvrir",
    "finish.recapSent": "Récap envoyé sur Slack à",
    "finish.resend": "Envoyer le récap",
    "cancel": "Annuler",
    "prod.stop": "⛔ Arrêter",
    "prod.stopTitle": "Arrêter la préparation et prévenir le responsable",
    "prod.stopSend": "⛔ Arrêter et envoyer l'alerte",
    "prod.stopDetail": "Précisez (quel article, combien il en manque…)",
    "prod.stockShort": "Stock insuffisant",
    "prod.stoppedTitle": "Préparations arrêtées",
    "prod.stoppedBecause": "Arrêtée :",
    "prod.reason0": "Manque de matériel / d'article",
    "prod.reason1": "Plus de cartons",
    "prod.reason2": "Plus de stock",
    "prod.reason3": "Autre problème",
    "item.issue": "Probl\u00e8me",
    "item.blocked": "Bloqu\u00e9",
    "app.title": "Myotaku Entrepôt",
    "nav.preparation": "Montage",
    "nav.signOut": "Se déconnecter",
    "days.title": "Livraisons à venir",
    "days.none": "Aucune livraison ce jour-là.",
    "days.outsideWindow": "Hors de la fenêtre du flux",
    "event.pieces": "pièces",
    "event.lines": "lignes",
    "event.packs": "pièces de la maison",
    "event.awaitingSales": "En attente des ventes",
    "event.awaitingLogistics": "En attente de la logistique",
    "event.approved": "Validé",
    "run.start": "Démarrer la préparation",
    "run.resume": "Reprendre",
    "run.pause": "Mettre en pause",
    "run.running": "En cours",
    "run.paused": "En pause",
    "run.wall": "Temps écoulé",
    "run.worked": "Temps de travail",
    "run.minutes": "min",
    "run.noRun": "Pas encore commencé",
    "item.done": "Monté",
    "item.todo": "À monter",
    "item.noAssembly": "Sans montage",
    "item.estimate": "Estimation",
    "pack.standardKit": "Kit standard",
    "error.title": "Problème",
    "loading": "Chargement…",
    "lang.label": "Langue",
    "login.title": "Myotaku Entrepôt",
    "login.intro": "Connectez-vous pour continuer.",
    "login.email": "E-mail",
    "login.password": "Mot de passe",
    "login.submit": "Se connecter",
    "login.busy": "Connexion…",
    "login.noSignup": "Les comptes sont créés par un administrateur. Il n'y a pas d'inscription.",
    "login.failed": "La connexion a échoué.",
    "login.failedStatus": "La connexion a échoué ({status}).",
    "login.wrong": "Cet e-mail et ce mot de passe ne correspondent pas.",
    "login.noToken": "Connecté, mais aucun jeton n'a été émis. Prévenez un administrateur.",
    "login.sessionCheck": "Impossible de vérifier la session ({status}).",
    "error.request": "La demande a échoué ({status}).",
    "error.upload": "Le stockage a refusé l'envoi du fichier ({status}).",
    "status.intro": "Cette page vérifie seulement que toute la chaîne fonctionne.",
    "status.signedInAs": "Connecté en tant que",
    "status.frontEnd": "Navigateur",
    "status.server": "Serveur",
    "status.browser": "Navigateur",
    "status.mounted": "le code est chargé et React a démarré",
    "status.asking": "interrogation…",
    "status.unreachable": "INJOIGNABLE",
    "status.answeredAt": "Réponse du serveur à {when}",
    "docs.ref.checklists.title": "Checklists A4 (carton)",
    "docs.ref.checklists.desc": "Une affiche par kit à glisser dans chaque carton — même présentation que ci-dessous.",
    "docs.ref.shelf.title": "Étiquettes d'étagère",
    "docs.ref.shelf.desc": "Étiquettes couleur pour le rayon, 8 par page A4.",
    "docs.ref.inventory.title": "Inventaire général",
    "docs.ref.inventory.desc": "Toutes les références, à remplir lors d'un comptage physique.",
    "chk.title": "Liste de vérification du carton",
    "chk.phase1": "Phase 1 — Kit installé",
    "chk.phase2": "Phase 2 — Kit de réserve",
    "chk.phase1Short": "Kit installé",
    "chk.phase2Short": "Kit de réserve",
    "chk.complete": "Carton complet et vérifié",
    "chk.preparedBy": "Préparé par",
    "chk.checkedBy": "Vérifié par",
    "chk.date": "Date",
    "chk.signature": "Signature",
    "chk.article": "{n} article",
    "chk.articles": "{n} articles",
    "csv.col.stock_no": "stock_no",
    "csv.col.entrepot": "entrepot",
    "csv.col.categorie": "categorie",
    "csv.col.statut": "statut",
    "csv.col.etat": "etat",
    "csv.col.origine": "origine",
    "csv.col.nom": "nom",
    "csv.col.ref_myotaku": "ref_myotaku",
    "csv.col.ref_fournisseur": "ref_fournisseur",
    "csv.col.fournisseur": "fournisseur",
    "csv.col.commande_fournisseur": "commande_fournisseur",
    "csv.col.dimensions": "dimensions",
    "csv.col.emplacement": "emplacement",
    "csv.col.palette": "palette",
    "csv.col.rack": "rack",
    "csv.col.numero": "numero",
    "csv.col.jours_au_depot": "jours_au_depot",
    "csv.col.retour": "retour",
    "csv.col.client": "client",
    "csv.col.reserve_pour": "reserve_pour",
    "csv.col.livraison": "livraison",
    "csv.col.entree_le": "entree_le",
    "csv.col.degats": "degats",
    "csv.col.catalogue_url": "catalogue_url",
    "csv.in": "en stock",
    "csv.out": "sorti",
    "csv.reception": "réception fournisseur",
    "csv.return": "retour client",
    "csv.cond.new": "neuf",
    "csv.cond.good": "bon",
    "csv.cond.light_marks": "moyen",
    "csv.cond.damaged": "abime",
    "csv.pallet": "Palette {p}",
    "csv.rack": "Rack {r}",
    "csv.file": "stock-meubles",
    "csv.fileAll": "tous",
    "label.stockLabel": "STOCK MEUBLES",
    "label.piece": "Pièce",
    "label.unit": "Meuble",
    "label.withOthers": "avec {n} autre(s) pièce(s)",
    "label.refMyo": "Réf. Myotaku",
    "label.refSup": "Réf. fournisseur",
    "label.entry": "Entrée",
    "label.qrCaption": "Fiche catalogue",
    "label.qrCaptionBig": "Scannez : fiche catalogue Myotaku",
    "label.for": "Pour",
    "label.deliv": "livr.",
    "label.ret": "Retour",
    "label.sample": "Exemple",
    "label.cond.neuf": "Neuf",
    "label.cond.bon": "Bon",
    "label.cond.moyen": "Moyen",
    "label.cond.abime": "Abîmé",
    "label.fmt.a4": "A4 paysage · 1 étiquette par feuille (lisible de loin)",
    "label.fmt.a4x2": "A4 · 2 étiquettes par feuille (A5, à couper)",
    "label.fmt.ql62": "Brother QL · 62 × 100 mm",
    "label.fmt.dymo": "Dymo LabelWriter · 54 × 101 mm",
    "label.fmt.zebra": "Zebra / thermique · 100 × 150 mm",
    "label.format": "Format d'étiquette",
    "label.preview": "Aperçu de l'étiquette",
    "label.dlOne": "Télécharger l'étiquette",
    "label.dlAll": "Télécharger les {n} étiquettes",
    "label.file1": "etiquette-{id}.pdf",
    "label.fileN": "etiquettes-{wh}-{day}.pdf",
    "stock.todayLabels": "Étiquettes du jour",
    "rec.reprint": "Réimprimer",
    "art.perKit": "×{n}/kit",
    "label.printA4": "Ouvrez le PDF et imprimez en A4, échelle 100 %.",
    "label.printRoll": "Imprimer avec l'appli de l'imprimante (PDF).",
    "label.s5": "Étiquette",
    "label.count": "{n} étiquettes (une par pièce)",
    "label.previewFirst": "aperçu de la pièce 1",
    "cartLabel.cart": "Chariot",
    "cartLabel.atClient": "Chez le client : scanner et choisir le retour.",
    "cartLabel.atWarehouse": "À l'entrepôt : scanner pour voir le client.",
    "cartLabel.scanBoth": "Scanner au chargement chez le client et au déchargement.",
    "cartLabel.keep": "ne pas retirer",
    "cartLabel.fmt.zebra": "Zebra / thermique · 150 × 100 mm (paysage)",
    "cartLabel.fmt.ql62": "Brother QL · 62 × 100 mm",
    "cartLabel.fmt.a4x4": "A4 paysage · 4 étiquettes par feuille, à découper",
    "cartLabel.open": "🖨 Imprimer des étiquettes",
    "cartLabel.title": "Imprimer des étiquettes de chariots",
    "cartLabel.hint": "Le module prépare un PDF : ouvre-le et imprime-le sur l'imprimante d'étiquettes (taille réelle, sans mise à l'échelle). Fais d'abord une page test.",
    "cartLabel.format": "Format",
    "cartLabel.from": "Du chariot n°",
    "cartLabel.to": "au n°",
    "cartLabel.copies": "Étiquettes par chariot",
    "cartLabel.print1": "🖨 Imprimer {n} étiquette (PDF)",
    "cartLabel.printN": "🖨 Imprimer {n} étiquettes (PDF)",
    "cartLabel.busy": "Préparation du PDF…",
    "cartLabel.ok": "PDF prêt : ouvre-le pour l'imprimer.",
    "cartLabel.err": "Le PDF n'a pas pu être préparé : réessaie.",
    "cartLabel.range": "Indique des numéros de 1 à 999, du plus petit au plus grand (400 étiquettes au plus par PDF).",
    "cartLabel.reprint": "🖨 Réimprimer l'étiquette de ce chariot",
    "cartLabel.file1": "etiquette-{id}-{fmt}.pdf",
    "cartLabel.fileN": "etiquettes-chariots-{from}-{to}-{fmt}.pdf",
    "cartLabel.copiesN": "× {n}",
    "cartLabel.close": "Fermer",
    "note.notMatched": "Lu, mais pas encore croisé avec le Delivery calendar :",
    "carton.title": "Étiquettes cartons",
    "carton.sub": "en noir et blanc, même présentation que les étiquettes d'étagère.",
    "carton.printOn": "Imprimer sur :",
    "carton.fmt.a4": "Feuille A4 — 8 étiquettes 10 × 7 cm (imprimante normale)",
    "carton.fmt.ql102x70": "Étiqueteuse Brother QL — 102 × 70 mm (rouleau continu 102 mm, DK-22243)",
    "carton.fmt.ql102x152": "Étiqueteuse Brother QL — 102 × 152 mm (étiquettes prédécoupées DK-11241)",
    "carton.fmt.ql62x100": "Étiqueteuse Brother QL — 62 × 100 mm (étiquettes prédécoupées DK-11202)",
    "carton.helpQl": "Une étiquette par carton. Dans la fenêtre d'impression : choisissez l'imprimante « Brother QL-1100 », Marges « Aucune », et le même format de papier ({w} × {h} mm) dans « Plus de paramètres ».",
    "carton.helpQlPage": "Une étiquette par page. Dans la fenêtre d'impression : imprimante « Brother QL-1100 », Marges « Aucune », format {w} × {h} mm.",
    "carton.helpA4": "8 étiquettes identiques de 10 × 7 cm par feuille A4.",
    "carton.helpA4Page": "Feuille A4 : 8 étiquettes identiques de 10 × 7 cm par feuille.",
    "carton.editHint": "La quantité reçue et la référence fournisseur s'impriment sur l'étiquette — modifiables ci-dessous (laisser vide pour ne pas les imprimer).",
    "carton.ref": "Réf.",
    "carton.article": "Article",
    "carton.supRefCol": "Réf. fournisseur",
    "carton.qtyCol": "Qté reçue",
    "carton.qtyShort": "Qté",
    "carton.sheets": "Feuilles",
    "carton.labels": "Étiquettes",
    "carton.allRefs": "Toutes les références :",
    "carton.apply": "Appliquer",
    "carton.addRef": "Ajouter une réf. :",
    "carton.addPh": "ex. 9KUC",
    "carton.add": "Ajouter",
    "carton.unknown": "Référence « {ref} » inconnue.",
    "carton.noneRead": "Aucun article reconnu dans cette réception — ajoutez une référence ci-dessous.",
    "carton.print": "Imprimer",
    "carton.nLabel": "{n} étiquette {w} × {h} mm",
    "carton.nLabels": "{n} étiquettes {w} × {h} mm",
    "carton.nSheet": "{n} feuille · {labels} étiquettes",
    "carton.nSheets": "{n} feuilles · {labels} étiquettes",
    "carton.nLabelShort": "{n} étiquette",
    "carton.nLabelsShort": "{n} étiquettes",
    "carton.libTotal": "{refs} réf. · {what}",
    "carton.noneSelected": "Aucune étiquette sélectionnée",
    "carton.footer": "Réception du {date}",
    "carton.footerSupplier": "Réception du {date} · {supplier}",
    "carton.fileReception": "Etiquettes_cartons_{supplier}_{date}",
    "carton.fileLibrary": "Etiquettes_Myotaku",
    "carton.fileOne": "Etiquette_{code}",
    "carton.nothing": "Rien à imprimer.",
    "carton.blocked": "Impression impossible : le navigateur a bloqué la nouvelle fenêtre. Autorisez les fenêtres pour ce site puis réessayez.",
    "carton.qtyReceived": "Qté reçue",
    "carton.supRef": "Réf. fournisseur :",
    "carton.catLinen": "Kit linge",
    "carton.libTitle": "Base d'étiquettes",
    "carton.libSub": "Toutes les références Myotaku — 🖨️ sur la ligne = UNE seule étiquette (sur A4 : en haut à gauche de la feuille) ; pour en imprimer plusieurs, choisissez le nombre puis « Imprimer la sélection ». La réf. fournisseur et la quantité saisies sont mémorisées pour la prochaine fois.",
    "carton.searchPh": "Chercher : code, article, réf. fournisseur…",
    "carton.allKits": "Tous les kits",
    "carton.footerField": "Texte en bas de l'étiquette (optionnel) :",
    "carton.footerPh": "ex. Livraison Rotel du 05.10",
    "carton.noMatch": "Aucune référence ne correspond à la recherche.",
    "carton.reset": "Tout remettre à 0",
    "carton.printSelection": "Imprimer la sélection",
    "carton.printOne": "Imprimer cette étiquette",
    "carton.remembered": "mémorisé",
    "carton.saveFailed": "Mémorisation impossible : {reason}",
    "carton.printCartons": "Imprimer les étiquettes cartons",
    "cartx.today": "Aujourd'hui",
    "cartx.yesterday": "Hier",
    "cartx.tomorrow": "Demain",
    "cartx.waiting1": "{n} enregistrement en attente de réseau sur ce téléphone",
    "cartx.waitingN": "{n} enregistrements en attente de réseau sur ce téléphone",
    "cartx.queued": "Pas de réseau : enregistré sur ce téléphone, envoi automatique dès que le réseau revient.",
    "cartx.unknownNumber": "Numéro de chariot inconnu.",
    "cartx.missingNone": "Tous les retours passés de ces 3 derniers jours ont un chariot.",
    "cartx.notYet": "{n} pas encore scanné(s)",
    "cartx.cart": "Chariot",
    "cartx.lastClient": "Dernier client : {client} · libéré le {date}",
    "cartx.never": "Pas encore utilisé avec le module.",
    "cartx.linkedOk": "{cart} associé à {client}",
    "cartx.client": "Client",
    "cartx.event": "Événement",
    "cartx.byHand": "saisi à la main",
    "cartx.linkedBy": "Associé",
    "cartx.arrived": "Arrivé",
    "cartx.warehouse": "Entrepôt",
    "cartx.photo": "Photo du chariot chargé",
    "cartx.optional": "facultatif",
    "cartx.photoUp": "Envoi de la photo…",
    "cartx.photoErr": "Photo non envoyée : reprends-la ou continue sans.",
    "cartx.photoDel": "Retirer",
    "cartx.others": "Chariots de ce retour",
    "cartx.iOfN": "chariot {i} sur {n}",
    "cartx.items": "Meubles prévus au retour ({n})",
    "cartx.noItems": "Pas de liste de meubles pour ce retour (saisi à la main ou hors planning).",
    "cartx.arrivedOk": "{cart} arrivé à l'entrepôt",
    "cartx.toStock": "Entrée en stock de ce retour",
    "cartx.free": "Chariot vidé : libérer",
    "cartx.freeConfirm": "Confirmer : libérer {cart}",
    "cartx.freedOk": "{cart} est libre",
    "cartx.relink": "Changer de retour",
    "cartx.relinkWarn": "Ce chariot est associé à {client}. La nouvelle association remplacera l'ancienne.",
    "cartx.history": "Historique",
    "cartx.h.link": "Associé",
    "cartx.h.arrive": "Arrivé à l'entrepôt",
    "cartx.h.free": "Libéré",
    "cartx.h.stock": "Entrée en stock",
    "cartx.cartsOk1": "{n} chariot ✓",
    "cartx.cartsOkN": "{n} chariots ✓",
    "cartx.noHit": "Aucun retour ne correspond.",
    "cartx.noEv": "Aucun retour du planning pour cet entrepôt autour d'aujourd'hui.",
    "cartx.manual": "Le retour n'est pas dans la liste ?",
    "cartx.manEv": "N° d'événement du planning",
    "cartx.manErr": "Indique le n° d'événement (chiffres) et le client.",
    "cartx.pickFirst": "Choisis le retour du client.",
    "cartx.nBad": "Le numéro du chariot dépasse le nombre de chariots.",
    "cartx.unitNo": "Chariot n°",
    "cartx.of": "sur",
    "cartx.forClient": "pour ce client",
    "cartx.doLink": "Associer {cart}",
    "stock.stillNeeded": "À compléter :",
    "stock.mProduct": "meuble",
    "stock.mPhoto": "photo",
    "stock.mCond": "état",
    "stock.mDamage": "photo des dégâts",
    "stock.mWrap": "couverture + film",
    "stock.mPallet": "n° de palette",
    "stock.mRack": "n° de rack",
    "stock.damagePhotos": "Photos des dégâts",
    "stock.required": "obligatoire",
    "stock.optional": "facultatif",
    "stock.addPhoto": "+ Photo",
    "stock.damageNote": "Description des dégâts",
    "stock.wrapEach": "Meuble en {n} pièces : emballez chaque pièce séparément et collez une étiquette sur chacune ({n} étiquettes).",
    "stock.wrapPhoto": "Photo du meuble emballé",
    "stock.saved": "Entré en stock",
    "stock.savedN": "{n} meubles entrés en stock",
    "stock.next": "Meuble suivant",
    "stock.nextSame": "Meuble suivant du même retour",
    "stock.photosFailed": "{n} photo(s) non envoyée(s) : ajoutez-les depuis la fiche du meuble.",
    "ret.emptyTitle": "Aucun retour pour cet entrepôt",
    "ret.group.today": "Aujourd'hui",
    "ret.group.upcoming": "À venir",
    "ret.group.recent": "Récents",
    "ret.tapToEnter": "Touchez un meuble pour démarrer son entrée en stock.",
    "stock.nParts": "{n} pièces",
    "stock.outPill": "Sorti",
    "stock.catPhoto": "Photo catalogue",
    "stock.typeSize": "Type",
    "stock.composition": "Composition",
    "stock.itemNo": "Numéro",
    "stock.ofReturn": "du retour",
    "stock.ofIntake": "de cette entrée",
    "stock.place": "Rangement",
    "stock.day1": "{n} jour",
    "stock.daysN": "{n} jours",
    "stock.stIn": "Au dépôt depuis",
    "stock.stInSince": "entré le {date}",
    "stock.stBackSince": "revenu le {date}",
    "stock.stOut": "Sorti le",
    "stock.stOutAfter": "après {n} au dépôt",
    "stock.stTotal": "Total au dépôt",
    "stock.stTotalH": "toutes périodes confondues",
    "stock.stCycles": "Entrées · sorties",
    "stock.stCyclesV": "{i} · {o}",
    "stock.stLast": "Dernier mouvement",
    "stock.stPlace": "À cet emplacement",
    "stock.stPlaceSince": "depuis le {date}",
    "stock.stRef": "Même référence",
    "stock.stRefV": "{n} en stock",
    "stock.stRefH": "ici · {all} tous entrepôts · {out} sortis",
    "stock.stAvg": "Durée moyenne",
    "stock.stAvgH": "de cette référence au dépôt ({n} fiches)",
    "stock.stRet": "Retours clients",
    "stock.stRetH": "de cette référence enregistrés",
    "media.role.identification": "Identification",
    "media.role.damage": "Dégât",
    "media.role.wrapped": "Emballé",
    "stock.factReturn": "Retour",
    "stock.factWrapping": "Emballage",
    "stock.factAdded": "Entré le",
    "com.tab.new": "Nouvelle réservation",
    "com.tab.mine": "Mes réservations",
    "com.tab.all": "Toutes les réservations",
    "com.step1": "Contrat",
    "com.step2": "Meubles",
    "com.step3": "Envoyer",
    "com.whichClient": "Pour quel client ?",
    "com.continueWith": "Continuer avec « {q} »",
    "com.notFoundManual": "Pas trouvé dans les livraisons à venir : saisie à la main",
    "com.contractN": "contrat {n}",
    "com.delivOn": "livraison {date}",
    "com.delivToPlan": "livraison à planifier",
    "com.noUpcoming": "Aucun contrat avec une livraison à venir pour l'instant. Utilise « Contrat pas dans la liste ».",
    "com.notInList": "Contrat pas dans la liste ?",
    "com.clientPh": "Famille Dupont",
    "com.contractNo": "N° de contrat",
    "com.ifYouHave": "si tu l'as",
    "com.delivDate": "Date de livraison",
    "com.ifKnown": "si elle est connue",
    "com.continueClient": "Continuer avec ce client",
    "com.manClientReq": "Indique le client.",
    "com.manCidBad": "Le n° de contrat a 3 à 7 chiffres.",
    "com.manDelivPast": "La date de livraison est déjà passée.",
    "com.searchFurniture": "Chercher : canapé, table, nom…",
    "com.transferWarn": "Ce client est livré depuis {from} : un meuble de {other} demandera un transfert entre entrepôts.",
    "com.allCats": "Tout",
    "com.loadingStock": "Chargement du stock…",
    "com.noneFilter": "Aucun meuble disponible avec ce filtre",
    "com.noneAt": "Aucun meuble disponible à {wh}",
    "com.noneHint": "Les meubles en stock, non réservés et en bon état apparaissent ici.",
    "com.seePhotos": "Voir les photos de {name}",
    "com.defectPhotos": "photos des défauts",
    "com.nAvailable": "{n} dispo",
    "com.add": "Ajouter",
    "com.removeOne": "En retirer un",
    "com.addOne1": "En ajouter un",
    "com.selected1": "meuble sélectionné",
    "com.selectedN": "meubles sélectionnés",
    "com.continue": "Continuer",
    "com.addOne": "Ajoute au moins un meuble.",
    "com.nthTaken": "{n}e pris",
    "com.noDefectNoted": "aucun défaut noté",
    "com.addOthers": "Ajouter d'autres meubles",
    "com.prepareHintDay": "Proposé : le jour ouvré avant la livraison du {date}.",
    "com.prepareNoDeliv": "Pas de livraison au planning : choisis le jour de préparation.",
    "com.sending": "Réservation en cours…",
    "com.send1": "Réserver {n} meuble et prévenir la logistique",
    "com.sendN": "Réserver {n} meubles et prévenir la logistique",
    "com.justTaken": "Un meuble vient d'être réservé par quelqu'un d'autre : la liste est à jour, vérifie ta sélection.",
    "com.doneTitle": "Meubles réservés pour {client}",
    "com.doneText1": "{n} meuble est réservé à ton nom. Emerson et Ludo sont prévenus ; préparation prévue le {prep}.",
    "com.doneTextN": "{n} meubles sont réservés à ton nom. Emerson et Ludo sont prévenus ; préparation prévue le {prep}.",
    "com.reference": "Référence {ref}",
    "com.seeMine": "Voir mes réservations",
    "com.filter.sent": "À préparer",
    "com.filter.prepared": "Préparées",
    "com.filter.cancelled": "Annulées",
    "com.filter.all": "Toutes",
    "com.noneYet": "Aucune réservation pour l'instant",
    "com.noneYetHint": "Tes réservations apparaissent ici, avec leur avancement : envoyée, au planning, préparée.",
    "com.status.cancelled": "Annulée",
    "com.status.prepared": "Préparée",
    "com.status.planned": "Au planning · #{ev}",
    "com.status.sent": "Envoyée · logistique prévenue",
    "com.prepOn": "préparation {date}",
    "com.reservedAt": "réservé le {when}",
    "com.cancelWarn": "Les meubles redeviennent disponibles.",
    "com.cancelWarnPlanning": "Emerson et Ludo seront prévenus de retirer la préparation du planning.",
    "com.markPrepared": "Marquer préparée",
    "com.cancelResa": "Annuler la réservation",
    "job.backToList": "Liste",
    "job.timesheetNo": "N° d'événement à reporter dans le timesheet",
    "job.copyNo": "Copier",
    "job.copied": "#{n} copié",
    "job.emptyToday": "Aucun assemblage prévu aujourd'hui",
    "job.emptyTodayHint": "Les prochains montages sont dans l'onglet À venir.",
    "job.emptyUpcoming": "Aucun assemblage à venir",
    "job.emptyUpcomingHint": "Les événements « Assemblage meubles pour … » sont créés automatiquement dès que les meubles d'un client ont une date d'arrivée.",
    "job.emptyDone": "Aucun montage terminé",
    "job.emptyDoneHint": "Quand les monteurs appuient sur Terminer, le temps réel apparaît ici et dans Réel vs estimé.",
    "jobs.notArrivedOne": "⏳ {n} pas encore arrivé",
    "jobs.notArrivedMany": "⏳ {n} pas encore arrivés",
    "jobs.receivedPct": "📦 reçu {p} %",
    "jobs.receivedOfOne": "{g} meuble reçu sur {n}",
    "jobs.receivedOfMany": "{g} meubles reçus sur {n}",
    "jobs.rxWarn": "⚠️ réception",
    "jobs.rxOk": "📄 reçu",
    "jobs.rxConfirmed": "📝 commande confirmée",
    "jobs.progress": "Avancement {p} % : travail fait, pondéré par le temps estimé de chaque article",
    "timer.notStarted": "Pas encore démarré",
    "timer.ofEst": "{t} sur {e} estimées",
    "timer.teamTime": "{n} monteurs · {t} de travail cumulé",
    "timer.left": "reste {t} sur l'estimation",
    "timer.over": "dépassement de {t}",
    "timer.doneSum": "Terminé en {r} de travail pour {e} estimées",
    "arch.nOne": "{n} client archivé",
    "arch.nMany": "{n} clients archivés",
    "arch.more": "… et {n} de plus : affine la recherche.",
    "arch.auto": "livré le {d}",
    "arch.man": "archivé à la main le {d}",
    "start.added": "Ajoutés",
    "start.addTitle": "Ajouter des monteurs",
    "start.busyOn": "sur {c}",
    "start.goOne": "Démarrer avec {n} monteur",
    "start.goMany": "Démarrer avec {n} monteurs",
    "start.addGoOne": "Ajouter {n} monteur",
    "start.addGoMany": "Ajouter {n} monteurs",
    "start.moved": "{n} retiré de {c}",
    "start.noStaff": "Aucun monteur trouvé pour cet entrepôt pour l'instant : ajoute les prénoms ci-dessous.",
    "furn.one.wardrobes": "armoire",
    "furn.many.wardrobes": "armoires",
    "furn.one.beds": "lit",
    "furn.many.beds": "lits",
    "furn.one.bed-components": "élément de lit",
    "furn.many.bed-components": "éléments de lit",
    "furn.one.chest-drawers": "commode",
    "furn.many.chest-drawers": "commodes",
    "furn.one.sideboard": "buffet",
    "furn.many.sideboard": "buffets",
    "furn.one.bookcase-display-cabinet": "bibliothèque",
    "furn.many.bookcase-display-cabinet": "bibliothèques",
    "furn.one.tv-tables": "meuble TV",
    "furn.many.tv-tables": "meubles TV",
    "furn.one.desks": "bureau",
    "furn.many.desks": "bureaux",
    "furn.one.dining-tables": "table à manger",
    "furn.many.dining-tables": "tables à manger",
    "furn.one.high-table": "table haute",
    "furn.many.high-table": "tables hautes",
    "furn.one.console": "console",
    "furn.many.console": "consoles",
    "furn.one.coffee-tables": "table basse",
    "furn.many.coffee-tables": "tables basses",
    "furn.one.bedside-tables": "table de nuit",
    "furn.many.bedside-tables": "tables de nuit",
    "furn.one.shoe-coat-rack": "porte-manteau",
    "furn.many.shoe-coat-rack": "porte-manteaux",
    "furn.one.sofas": "canapé",
    "furn.many.sofas": "canapés",
    "furn.one.outdoor-furniture": "meuble d'extérieur",
    "furn.many.outdoor-furniture": "meubles d'extérieur",
    "furn.one.side-table": "table d'appoint",
    "furn.many.side-table": "tables d'appoint",
    "furn.one.desk-chair": "chaise de bureau",
    "furn.many.desk-chair": "chaises de bureau",
    "furn.one.chairs": "chaise",
    "furn.many.chairs": "chaises",
    "furn.one.high-chair": "chaise haute",
    "furn.many.high-chair": "chaises hautes",
    "furn.one.armchairs": "fauteuil",
    "furn.many.armchairs": "fauteuils",
    "furn.one.pouf-stool-bench": "pouf",
    "furn.many.pouf-stool-bench": "poufs",
    "furn.one.floor-lamp": "lampadaire",
    "furn.many.floor-lamp": "lampadaires",
    "furn.one.mirror": "miroir",
    "furn.many.mirror": "miroirs",
    "furn.one.table-lamp": "lampe à poser",
    "furn.many.table-lamp": "lampes à poser",
    "furn.one.ceiling-light": "plafonnier",
    "furn.many.ceiling-light": "plafonniers",
    "furn.one.wall-light": "applique",
    "furn.many.wall-light": "appliques",
    "furn.one.decorative-items": "objet décoratif",
    "furn.many.decorative-items": "objets décoratifs",
    "furn.one.decoration": "décoration",
    "furn.many.decoration": "décorations",
    "furn.one.cushion-and-throws": "coussin ou plaid",
    "furn.many.cushion-and-throws": "coussins et plaids",
    "furn.one.carpet": "tapis",
    "furn.many.carpet": "tapis",
    "furn.one.artificial-plant": "plante artificielle",
    "furn.many.artificial-plant": "plantes artificielles",
    "furn.one.electric-appliances": "appareil électroménager",
    "furn.many.electric-appliances": "appareils électroménagers",
    "furn.one.internet": "article internet",
    "furn.many.internet": "articles internet",
    "furn.one.hangers": "cintre",
    "furn.many.hangers": "cintres",
    "furn.one.kits": "kit",
    "furn.many.kits": "kits",
    "furn.one.others": "autre article",
    "furn.many.others": "autres articles",
    "job.notArrived": "pas encore arrivé",
    "job.naExpected": "prévu le {d}",
    "job.rxScanned": "✅ reçu (bon de livraison scanné)",
    "job.boxesUnknown": "références des cartons pas encore connues",
    "docs.kindDn": "🚚 Bon de livraison",
    "docs.kindOc": "📝 Confirmation de commande",
    "docs.kindInv": "🧾 Facture",
    "docs.kindOth": "📄 Document de commande",
    "docs.notRx": "pas une réception",
    "docs.arrOk": "✅ arrivée le {d}",
    "docs.arrNo": "⏳ pas encore reçue",
    "docs.ocWarn": "Confirmation de commande ≠ réception : cette commande n'est pas encore arrivée au dépôt. Inutile de chercher le meuble.",
    "docs.others": "Autres documents de la commande ({n})",
    "docs.none": "Aucun document dans cette commande pour l'instant.",
    "docs.exp": "Liens peut-être expirés : ouvre la commande dans le planning.",
    "docs.img": "photo",
    "docs.pdf": "PDF",
    "docs.noOrder": "Aucune commande fournisseur liée.",
    "sdocs.title": "Documents fournisseur",
    "sdocs.hint": "Documents déposés dans la commande fournisseur du planning (ceux du client en premier). Attention : une confirmation de commande ou une facture ne veut pas dire que le meuble est arrivé. Seul « arrivé le … » ou un bon de livraison le confirme.",
    "issue.placeholder": "Ex. : plateau rayé sur 20 cm, vis du pied manquantes, carton ouvert à l'arrivée…",
    "issue.needText": "Décris le problème en quelques mots.",
    "issue.needBy": "Indique qui signale le problème.",
    "issue.to": "À :",
    "issue.cc": "Copie :",
    "issue.unsales": "Commercial absent du planning : le message part à Ludo.",
    "issue.sendTo": "Envoyer à {n}",
    "mediaJob.title": "Photos et vidéos du montage",
    "mediaJob.hint": "Photographie ou filme le montage : tiroirs et portes ouverts et fermés, stabilité, fixations, puis l'emballage. Chaque meuble a aussi son bouton 📷.",
    "mediaJob.empty": "Aucune photo ni vidéo pour l'instant.",
    "mediaJob.photo": "📷 Prendre une photo",
    "mediaJob.video": "🎥 Filmer",
    "mediaJob.file": "📁 Choisir un fichier",
    "mediaJob.prep": "Préparation de la photo…",
    "mediaJob.photoType": "Format de photo non accepté : prends la photo avec l'appareil (JPEG).",
    "fin.time": "{r} de travail pour {e} estimées",
    "fin.leftOne": "{n} meuble pas encore coché comme monté",
    "fin.leftMany": "{n} meubles pas encore cochés comme montés",
    "fin.allOk": "Tous les meubles sont cochés comme montés",
    "fin.chkLeftOne": "{n} article sans montage pas encore contrôlé",
    "fin.chkLeftMany": "{n} articles sans montage pas encore contrôlés",
    "fin.chkAllOk": "Tous les articles sans montage sont contrôlés",
    "fin.probsNone": "Aucun problème signalé",
    "fin.probsOne": "{n} problème signalé, repris dans le récap",
    "fin.probsMany": "{n} problèmes signalés, repris dans le récap",
    "fin.media": "📷 {c} de contrôle",
    "fin.noMedia": "⚠️ Aucune photo ni vidéo : photographie ou filme le montage avant de terminer",
    "fin.photosOne": "{n} photo",
    "fin.photosMany": "{n} photos",
    "fin.videosOne": "{n} vidéo",
    "fin.videosMany": "{n} vidéos",
    "fin.and": "et",
    "fin.pack": "J'ai emballé chaque meuble avec les couvertures MYOTAKU et le film plastique",
    "fin.needPack": "Confirme l'emballage (couvertures MYOTAKU + film plastique) avant de terminer.",
    "fin.all": "Tout cocher comme monté",
    "fin.chkAll": "Tout cocher comme contrôlé",
    "fin.note": "Remarques pour le commercial (facultatif)",
    "fin.notePh": "Ex. : buffet emballé dans la couverture MYOTAKU, prêt au chariot client.",
    "fin.send": "Envoyer le récap à {n}",
    "fin.go": "Terminer et envoyer le récap",
    "fin.goNo": "Terminer sans envoyer",
    "fin.done": "Montage terminé",
    "fin.doneSent": "Montage terminé, récap envoyé à {n}",
    "recap.none": "Récap non envoyé (choix du monteur)",
    "mediaJob.itemAdd": "📷 Ajouter",
    "mediaJob.itemTitle": "Photos et vidéos du meuble",
    "mediaJob.close": "Fermer",
    "thx.title": "Merci {names} !",
    "thx.titleTeam": "Merci à toute l'équipe !",
    "thx.lead": "Le montage pour {c} est terminé.",
    "thx.msg0": "Grâce à votre travail soigné, le client recevra des meubles bien montés, contrôlés et protégés : vous participez directement à sa satisfaction.",
    "thx.msg1": "Beau travail ! Chaque meuble monté avec soin, c'est un client satisfait le jour de la livraison. Merci pour votre engagement.",
    "thx.msg2": "Votre précision et votre soin font la différence chez le client. Merci d'avoir contribué à sa satisfaction !",
    "thx.asmOne": "meuble monté",
    "thx.asmMany": "meubles montés",
    "thx.chkOne": "article contrôlé",
    "thx.chkMany": "articles contrôlés",
    "thx.time": "de travail",
    "thx.sent": "Le récap est parti à {n}.",
    "thx.close": "Fermer",
    "links.title": "Liens",
    "links.order": "Delivery calendar commande #{id}",
    "links.event": "Événement d'assemblage dans le planning",
    "links.contract": "Contrat #{id}",
    "rst.title": "Responsable",
    "rst.p": "Remet ce montage à zéro : compteurs montés et contrôlés, chrono, temps passé, monteurs et message de fin. Les problèmes signalés, photos et vidéos sont gardés.",
    "rst.b": "↺ Réinitialiser ce montage",
    "rst.ok": "Confirmer : tout remettre à zéro",
    "rst.no": "Annuler",
    "rst.run": "Le montage est en cours : il sera arrêté.",
    "rst.done": "Montage réinitialisé",
    "rst.undo": "↩️ Annuler la réinitialisation",
    "rst.undone": "Réinitialisation annulée",
    "rst.last": "Réinitialisé le {d}",
    "media.del": "Supprimer",
    "media.delOk": "Confirmer la suppression",
    "hist.kpiJobs": "Montages terminés",
    "hist.kpiActual": "Temps réel",
    "hist.ofEst": "sur {e} estimées",
    "hist.kpiGap": "Écart global",
    "hist.kpiOk": "Dans l'estimation",
    "hist.perJob": "Par montage",
    "hist.statsNote": "Temps réel = somme des temps de chaque monteur sur le montage. Estimation = temps du planning (montage + manutention).",
    "hist.colNo": "#",
    "hist.colClient": "Client",
    "hist.colWh": "Entrepôt",
    "hist.colFitters": "Monteurs",
    "hist.gap": "Écart",
    "hist.perFitter": "Par monteur",
    "hist.perFitterNote": "Temps de chaque monteur sur les montages terminés, comparé à sa part de l'estimation. Visible seulement par les administrateurs.",
    "hist.colJobs": "Montages",
    "hist.colVs": "Réel / estimé",
    "sup.note": "Productivité minute par minute : temps prévu des meubles validés, plus le meuble en cours jusqu'à son temps prévu, comparé au temps passé par les monteurs. Alerte après 30 min de montage si le rendement passe sous 70 %, ou si rien n'est validé depuis 1,5 × le temps prévu du plus grand meuble restant (30 min au moins). Slack à Ludo et à Emerson, une fois par montage et par alerte (contrôle toutes les heures, de 7 h à 18 h). Les monteurs ne voient pas cet onglet.",
    "sup.sentAt": "Slack envoyé à {t}",
    "sup.sentWait": "Slack au prochain contrôle (toutes les heures, de 7 h à 18 h)",
    "sup.open": "Ouvrir la fiche",
    "sup.lastAgo": "dernière validation : il y a {t}",
    "sup.never": "dernière validation : aucune validation",
    "sup.dayRate": "Rendement du jour",
    "sup.per": "Par monteur",
    "sup.perN": "Temps de montage, temps prévu des meubles validés et rendement (gagné ÷ passé). Un meuble monté à plusieurs est partagé entre eux.",
    "sup.perNone": "Aucun temps de montage sur cette période.",
    "sup.period.today": "Aujourd'hui",
    "sup.period.7d": "7 jours",
    "sup.period.30d": "30 jours",
    "sup.col.fitter": "Monteur",
    "sup.col.minutes": "Temps de montage",
    "sup.col.earned": "Temps gagné",
    "sup.col.rate": "Rendement",
    "sup.col.items": "Meubles validés",
    "sup.col.alerts": "Alertes",
    "sup.spkSpent": "temps passé (minutes-personne)",
    "sup.spkEarned": "temps gagné (meubles validés)",
    "sup.tl": "Journée minute par minute",
    "sup.tlN": "Une ligne par monteur : chaque bloc est un montage (une couleur par client), les traits blancs sont les meubles validés. Survole un bloc pour le détail.",
    "sup.noTl": "Aucun montage aujourd'hui.",
    "jobs.readAt": "Planning lu le {d} à {t}",
    "job.readOnly": "Accès en lecture seule : demande à Ludo l'accès Contributeur pour démarrer les chronos et signaler un problème.",
    "stock.readOnly": "Vous avez un accès en lecture seule : demandez à Ludo un accès Contributeur pour entrer des meubles.",
    "cartx.readOnly": "Ton accès est en lecture seule : demande à Ludo un accès qui permet de modifier, pour associer les chariots.",
    "access.readOnly": "lecture seule",
    "access.adminCol": "Admin",
    "access.setPassword": "Nouveau mot de passe",
    "access.savePassword": "Enregistrer",
    "access.cancel": "Annuler",
    "access.passwordChanged": "Mot de passe changé.",
    "access.ownLocked": "Impossible sur votre propre compte.",
    "nav.home": "Accueil",
    "home.choose": "Choisis ta section",
    "home.desc.stock": "Entrée des retours clients, étiquettes, inventaire",
    "home.desc.preparation": "Assemblages du jour, chrono, réception des commandes",
    "home.desc.carts": "Scanner le QR d'un chariot : à quel client il appartient",
    "home.desc.supervisor": "Montages en cours, rendement, réel vs estimé",
    "home.desc.commercial": "Réserver des meubles du stock pour un client",
    "home.desc.production": "Production des kits, cadence et stock d'articles",
    "home.desc.receiving": "Lire les bons de livraison des fournisseurs",
    "home.desc.access": "Qui utilise quels modules",
    "prod.status.queued": "En attente",
    "prod.status.running": "En cours",
    "prod.status.paused": "En pause",
    "prod.status.stopped": "Arrêté",
    "prod.status.finished": "Terminé",
    "prod.status.cancelled": "Annulé",
    "prod.meta.total": "{t} au total",
    "prod.meta.startedBy": "démarré par {n}",
    "prod.meta.finishedBy": "terminé par {n}",
    "prod.stoppedAt": "⛔ Arrêté à {t} par {n} —",
    "prod.time.running": "En cours depuis {a} (temps donné {g}) — {d}",
    "prod.time.paused": "⏸ En pause depuis {at} — temps réalisé {a} (temps donné {g}) — {d}",
    "prod.time.done": "Réalisé en {a} (temps donné {g}) — {d}",
    "prod.time.exact": "pile dans le temps donné",
    "prod.time.more": "+{d} de plus",
    "prod.time.less": "−{d} de moins",
    "prod.pauseBanner": "⏸ Préparation en pause",
    "prod.stopBanner": "⛔ Préparation arrêtée — le responsable a été prévenu",
    "prod.pauseSince": "depuis {t}{by} — le chrono est arrêté. Unités déjà faites : {d} / {q}.",
    "prod.pauseBy": " (par {n})",
    "prod.resume": "▶️ Reprendre la préparation",
    "prod.reportProblem": "⛔ Signaler un problème (matériel, cartons, stock)",
    "prod.bravoMany": "🎉 Bravo ! Les {q} unités demandées sont prêtes. Cliquez sur « Marquer terminé » pour finaliser ce bon.",
    "prod.bravoOne": "🎉 Bravo ! L'unité demandée est prête. Cliquez sur « Marquer terminé » pour finaliser ce bon.",
    "prod.newOrderHint": "Choisissez le kit, la quantité : le bon apparaît en direct sur le poste du préparateur.",
    "prod.chip.min": "{n} min / unité",
    "prod.chip.composed": "Composé de {n} sous-kits",
    "prod.chip.inStock": "{n} en stock",
    "prod.chip.dispo": "dispo {n} (réservé {r})",
    "prod.chip.short": "manque {n} (réservé {r})",
    "prod.contains": "Ce kit comprend",
    "prod.pickKit": "Sélectionnez un kit",
    "prod.shortage": "⚠ Stock article insuffisant pour {q} × {k} — il manque : {list}{more}.",
    "prod.shortageMore": " + {n} autre(s)",
    "prod.notePh": "Ex. priorité client X, à livrer avant 14h…",
    "prod.sendOrder": "Envoyer le bon de production",
    "prod.sending": "Envoi…",
    "prod.sendFailed": "Échec — bon NON envoyé, réessayer",
    "prod.qtyPreview": "{q} × {m} min =",
    "prod.chip.minShort": "{n} min",
    "prod.sync.ok": "Synchronisé",
    "prod.sync.off": "Non synchronisé",
    "prod.sync.failed": "⚠️ Échec de l'enregistrement",
    "prod.prep.none": "Aucune activité de préparation enregistrée aujourd'hui.",
    "prod.prep.who": "Préparateur",
    "prod.prep.kits": "Kits validés",
    "prod.prep.worked": "Temps travaillé",
    "prod.prep.given": "Temps donné",
    "prod.prep.gap": "Écart",
    "prod.prep.avg": "Moy. / kit",
    "prod.prep.pauses": "Pauses",
    "prod.prep.now": "En ce moment",
    "prod.prep.noName": "(sans nom)",
    "prod.prep.nowRunning": "{k} ×{q} — en cours ({d}/{q})",
    "prod.prep.nowPaused": "⏸ {k} ×{q} — en pause depuis {t}",
    "audit.title": "Contrôle de cohérence du stock de kits",
    "audit.checkedAt": "vérifié à {t}",
    "audit.checking": "Vérification en cours…",
    "audit.ok": "✓ Tout est cohérent : stocks, historique et sorties des livraisons concordent.",
    "audit.errs": "✗ {n} erreur(s)",
    "audit.warns": "⚠ {n} point(s) à vérifier",
    "audit.checks": "Vérifié en continu : chaque stock = son historique, chaque livraison sortie une seule fois, aucune livraison passée oubliée, contrôle automatique à jour.",
    "audit.contract": "contrat #{n}",
    "audit.untraced": "{warehouse} — {kit} : mouvement non tracé avant le {at}",
    "audit.untracedD": "Un changement de stock n'a pas été enregistré dans l'historique : refaire un comptage de ce kit.",
    "audit.mismatch": "{warehouse} — {kit} : stock {stock} ≠ historique {history}",
    "audit.mismatchD": "Le stock affiché ne correspond pas au dernier mouvement enregistré. Un changement a été fait sans historique, ou une écriture a échoué : refaire un comptage de ce kit.",
    "audit.no_history": "{warehouse} — {kit} : stock {stock} sans aucun historique",
    "audit.no_historyD": "Ce stock n'a jamais été saisi par l'application : vérifier par un comptage.",
    "audit.delivered_no_exit": "Livraison {label} marquée livrée sans sortie de stock pour : {kits}",
    "audit.delivered_no_exitD": "Ces kits ne sont pas sortis du stock : refaire un comptage de ces kits.",
    "audit.reserved_but_exited": "Livraison {label} : sortie déjà faite pour {kits} alors qu'elle est encore « réservée »",
    "audit.reserved_but_exitedD": "Vérifier par un comptage avant le prochain contrôle.",
    "audit.past_reserved": "Livraison {label} : date passée et kits toujours réservés (sortie du stock manquée)",
    "audit.past_reservedD": "Le prochain contrôle automatique doit la sortir du stock. Si ce message reste après 9h ou 15h, le contrôle ne fonctionne pas.",
    "audit.short_exit": "{warehouse} — {kit} : livraison du {at} sortie avec un stock insuffisant ({had} pour {need})",
    "audit.short_exitD": "Le stock était déjà trop bas : vérifier par un comptage ou un envoi manquant.",
    "audit.orphan_exit": "Sortie {kit} du {at} sans livraison correspondante",
    "audit.orphan_exitD": "La livraison a été retirée du suivi après la sortie : vérifier si le kit est vraiment parti.",
    "audit.no_run": "Aucun contrôle automatique enregistré",
    "audit.no_runD": "La tâche de 9h / 15h n'a encore jamais écrit son passage.",
    "audit.run_failed": "Dernier contrôle automatique en échec ({at})",
    "audit.run_failedD": "{message} — aucune sortie de stock n'a été faite à ce passage.",
    "audit.run_stale": "Pas de contrôle automatique depuis {hours} h",
    "audit.run_staleD": "Les sorties de stock du jour ne sont pas faites : la tâche automatique ne tourne plus.",
    "audit.no_client": "{n} livraison(s) réservée(s) sans nom de client dans le planning",
    "audit.no_clientD": "{contracts}",
    "alloc.incomplete": "⚠️ contrôle incomplet",
    "alloc.stale": "⚠️ plus de 26 h sans contrôle",
    "prod.stockTotalTab": "Total",
    "prod.stockTotal": "Total des 3 entrepôts",
    "prod.stockAt": "En stock à {w}",
    "prod.kitsOne": "{n} kit",
    "prod.kitsMany": "{n} kits",
    "prod.pill.allocated": "alloué {n}",
    "prod.pill.allocatedT": "Réservé pour des livraisons clients validées à venir",
    "prod.pill.toSend": "à envoyer {n}",
    "prod.pill.toSendT": "Manque à Dielsdorf / Berlin, à couvrir par un envoi depuis Penthalaz",
    "prod.pill.dispo": "dispo {n}",
    "prod.pill.short": "manque {n}",
    "prod.short.wh": "⚠ {w} — {k} : manque {n} (alloué {a}, en stock {s})",
    "prod.short.pz": "⚠ Penthalaz — {k} : manque {n} (alloué {a}{send}, en stock {s}{prod})",
    "prod.short.send": " + à envoyer {n}",
    "prod.short.inProd": ", déjà en préparation {n}",
    "prod.short.makeOrder": "Préparer un bon ×{n}",
    "prod.alloc.empty": "Aucune livraison validée avec des kits pour le moment.",
    "prod.alloc.past": " — livraison passée, en attente du statut « Livré »",
    "prod.alloc.artPz": "Article à l'unité — retiré du stock des articles le jour de la livraison",
    "prod.alloc.artElse": "Article à l'unité — pas de stock d'articles suivi à {w}",
    "prod.alloc.type.CT": "Court Terme",
    "prod.alloc.type.LT": "Long Terme",
    "prod.alloc.type.COMPL": "Complément",
    "prod.alloc.type.VENTE": "Vente",
    "prod.alloc.type.HS": "Home staging",
    "prod.alloc.type.MAINT": "Maintenance",
    "prod.alloc.type.AUTRE": "Autre",
    "prod.alloc.type.BTR": "BTR",
    "prod.alloc.type.EVT": "Contrat événement",
    "prod.short.makeTransfer": "Préparer un envoi ×{n}",
    "prod.send.title": "Envoi de kits vers un entrepôt",
    "prod.send.hint": "Choisissez l'entrepôt, saisissez les quantités envoyées depuis Penthalaz, puis « Envoyer ». Le stock de Penthalaz baisse aussitôt ; les kits sont en route jusqu'à ce que l'entrepôt de destination les réceptionne (onglet Réception).",
    "prod.send.to": "Vers {w}",
    "prod.send.qtyFor": "Quantité envoyée — {k}",
    "prod.send.notePh": "Ex. camion du 26.09, n° de bon de livraison…",
    "prod.send.total": "Total : {n} kit(s) de Penthalaz vers {w}.",
    "prod.send.none": "Saisissez au moins une quantité.",
    "prod.send.over": "Stock de Penthalaz insuffisant — {list}.",
    "prod.send.button": "Envoyer à {w} ({n} kit(s))",
    "prod.send.buttonEmpty": "Envoyer à {w}",
    "prod.send.ask": "Envoyer {n} kit(s) de Penthalaz vers {w} ?",
    "prod.send.yes": "Oui, envoyer",
    "prod.send.done": "✓ Envoi enregistré : {n} kit(s) vers {w}. Le stock de Penthalaz est mis à jour ; {w} les ajoute à la réception.",
    "prod.send.log": "Derniers envois",
    "prod.send.logEmpty": "Aucun envoi enregistré pour l'instant.",
    "prod.send.inTransit": "en route",
    "prod.view.resp": "Responsable",
    "prod.view.prep": "Préparateur",
    "prod.orders": "Bons de production",
    "prod.ordersHint": "Du plus récent au plus ancien. L'avancement est mis à jour par le préparateur en temps réel.",
    "prod.ordersEmpty": "Aucun bon envoyé pour le moment.",
    "prod.queueHint": "Les bons envoyés par le responsable arrivent ici, du plus ancien au plus récent.",
    "prod.youAre": "Vous êtes :",
    "prod.youArePh": "Votre prénom (obligatoire pour démarrer)",
    "prod.nameFirst": "✋ Indiquez d'abord votre prénom dans « Vous êtes ».",
    "prod.stockInPlace": "Stock déjà en place",
    "demand.ofMonth": "(total du mois {n})",
    "demand.segment": "{k} · {m} : {n} kit(s) (total du mois {total})",
    "prod.entry.label": "Saisie :",
    "prod.entry.add": "➕ Ajouter une quantité",
    "prod.entry.set": "✏️ Corriger le total",
    "prod.entry.addHelp": "Tapez seulement la quantité reçue (ex. 30) puis « Ajouter » ou Entrée : elle s'ajoute au stock actuel, sans calcul. Un nombre négatif (ex. -2) retire.",
    "prod.entry.setHelp": "Tapez le nouveau total compté : il remplace la quantité actuelle.",
    "prod.entry.now": "Quantité actuelle",
    "prod.entry.addPh": "+ qté",
    "prod.entry.addFor": "Quantité à ajouter — {k}",
    "prod.entry.totalFor": "Nouveau total — {k}",
    "prod.entry.addBtn": "Ajouter",
    "prod.entry.askAdd": "Êtes-vous sûr de vouloir ajouter {n} à la quantité de {k} ?",
    "prod.entry.askDown": "Êtes-vous sûr de vouloir diminuer de {n} la quantité de {k} ?",
    "prod.entry.yesAdd": "Oui, ajouter",
    "prod.entry.yesDown": "Oui, diminuer",
    "prod.entry.saved": "✅ Merci, la nouvelle quantité est enregistrée dans la base de stock.",
    "prod.entry.notSaved": "⚠️ La quantité n'a PAS pu être enregistrée (connexion ?). Réessayez.",
    "prod.entry.tapClose": "Touchez pour fermer",
    "prod.history.open": "Cliquer pour voir l'historique",
    "prod.history.title": "Historique — {k} ({w})",
    "prod.history.close": "Fermer",
    "prod.history.current": "Stock actuel : {n}",
    "prod.history.none": "Aucun mouvement enregistré.",
    "inv.src.correction": "correction",
    "inv.src.plus_minus": "bouton ±",
    "prod.delete": "Supprimer",
    "prod.deleteYes": "Confirmer la suppression",
    "prod.unitSummary": "Unité n°{n} en cours d'assemblage — {c} / {m} articles cochés",
    "inv.lastAt": "Dernier inventaire à {w} : {when} par {who} — {n} kit(s) compté(s), {d} écart(s) corrigé(s).",
    "inv.noneAt": "Aucun inventaire enregistré pour {w}.",
    "inv.kit": "Kit",
    "inv.app": "Appli",
    "inv.gap": "Écart",
    "inv.summary": "{n} kit(s) compté(s) — {d} écart(s) à corriger",
    "inv.start": "Tapez les quantités comptées pour commencer.",
    "inv.ask": "Valider l'inventaire de {w} ?",
    "inv.askDetail": "{n} kit(s) compté(s) — {d} correction(s)",
    "inv.yes": "Oui, valider l'inventaire",
    "inv.done": "Inventaire {w} : {n} kit(s) compté(s), {d} correction(s)",
    "inv.historyHint": "Chaque changement de quantité — saisie manuelle, boutons +/−, ou crédit automatique de chaque kit assemblé (dès que l'unité est validée) — est enregistré ici avec la date et l'heure.",
    "inv.countedCol": "Compté",
    "art.hint": "Quantité disponible en rayon pour chaque article, même référence que sur les étiquettes. Se décrémente automatiquement quand le préparateur coche un article dans une checklist ; à réception d'une livraison, mode « ➕ Ajouter » : tapez seulement la quantité reçue, l'application fait le calcul ; après un comptage, mode « ✏️ Corriger le total ». Cliquez sur un article pour ouvrir sa fiche.",
    "art.total": "{q} unités en stock sur {n} références",
    "art.noMatch": "Aucun article ne correspond à la recherche.",
    "art.linenShared": "Linge — articles communs à plusieurs tailles",
    "art.refCount": "{n} réf.",
    "art.sharedBy": "Référence commune à : {list}",
    "art.chip.sku": "Syst. {s}",
    "art.chip.ref": "Réf. fourn. {s}",
    "art.chip.confirm": "à confirmer",
    "art.chip.open": "Fiche ›",
    "sheet.hist.date": "Date",
    "sheet.hist.move": "Mouvement",
    "sheet.hist.stock": "Stock",
    "sheet.hist.loading": "Chargement de l'historique…",
    "sheet.src.manual": "Ajout manuel",
    "sheet.src.correction": "Correction du total (comptage)",
    "sheet.src.plus_minus": "Bouton + / −",
    "sheet.src.production": "Prélevé pour un kit (checklist)",
    "sheet.src.restock": "Remis en stock (unité retirée)",
    "sheet.src.receipt": "Réception fournisseur",
    "sheet.src.reception_check": "Correction à la vérification d'une réception",
    "sheet.src.delivery": "Livraison client (article à l'unité)",
    "sheet.src.deliveryTo": "Livraison client — {c}",
    "sheet.src.inventory": "Inventaire (comptage physique)",
    "sheet.src.transfer": "Envoi entre entrepôts",
    "sheet.det.order": "bon {k} ×{n}",
    "sheet.det.unticked": "(décoché)",
    "sheet.det.supplier": "fournisseur {s}",
    "sheet.det.receipt": "réception {n}",
    "sheet.det.contract": "contrat #{n}",
    "sheet.det.by": "par {w}",
    "sheet.det.short": "{n} manquant(s) en rayon",
    "inv.src.restock": "remis en stock",
    "artrec.supplier": "Fournisseur (optionnel)",
    "artrec.hintPaste": "Sur ordinateur, vous pouvez aussi coller une capture (Ctrl+V) ou glisser un fichier sur cette carte. Si la quantité reçue n'apparaît pas sur le document, écrivez-la dans la note.",
    "note.notAdded": "⚠ Non ajouté : {list}",
    "note.othersReady": " — les autres fichiers sont prêts.",
    "note.maxImages": "{f} (maximum {n} images)",
    "note.badFormat": "{f} — format non pris en charge",
    "artrec.photoN": "Photo {n}",
    "prod.printSheets": "Imprimer {n} fiches carton",
    "prod.printSheet": "Imprimer 1 fiche carton",
    "ident.phase.type": "Identification du type de meuble…",
    "ident.phase.retour": "Comparaison de la photo avec le retour…",
    "ident.phase.catalogue": "Recherche dans le catalogue Myotaku…",
    "ident.cancel": "Annuler",
    "ident.recognize": "Reconnaître le meuble",
    "ident.noAi": "La reconnaissance n'est pas disponible ici : choisissez le meuble à la main.",
    "ident.aiLimit": "Trop de demandes pour le moment. Réessayez dans un instant ou cherchez à la main.",
    "ident.hintGrid": "Photographiez le meuble entier avant de l'emballer, puis comparez avec la photo catalogue.",
    "stock.qtyAuto": "{q} attendus dans ce retour, {k} déjà entrés : quantité réglée automatiquement.",
    "stock.qtyOver": "Ce retour en attendait {q} et {k} sont déjà entrés : ce meuble est en plus.",
    "stock.qtyHint": "Même référence, même état et même palette : {n} fiches, {n} n° de stock et {n} étiquettes, une par meuble. Si l'un d'eux est dans un autre état, entrez-le à part.",
    "stock.qtyNums": "Numéros attribués : {list}.",
    "stock.unitOf": "Meuble {i}/{n}",
    "compo.dialogTitle": "Composition du meuble",
    "compo.dialogHint": "En combien de pièces ce meuble est-il stocké ? Ex. Hudson = canapé + méridienne (2 pièces). Chaque pièce reçoit sa propre étiquette « Pièce 1/2 », « Pièce 2/2 ». Le réglage est retenu pour ce produit.",
    "compo.sofa": "Canapé",
    "compo.chaise": "Méridienne",
    "compo.saved": "Composition enregistrée",
    "compo.toConfirm": "à confirmer",
    "compo.pieceOf": "Pièce {i}/{n}",
    "compo.cancel": "Annuler",
    "ident.yourPhoto": "Votre photo",
    "ident.catPhoto": "Photo catalogue",
    "ident.refMyo": "Réf. Myotaku",
    "ident.refSup": "Réf. fournisseur",
    "ident.generic": "article générique sans réf. fournisseur",
    "ident.supplier": "Fournisseur",
    "ident.type": "Type",
    "ident.size": "Dimensions",
    "ident.notInCat": "hors catalogue en ligne",
    "rec.upcoming": "Livraisons à venir",
    "rec.past": "Livraisons passées",
    "rec.toSchedule": "à planifier",
    "rec.runDone": "Montage terminé",
    "rec.runGoing": "Montage en cours",
    "rec.assembledN": "monté {k}/{n}",
    "rec.po": "Cde #{n}",
    "stock.orderHint": "Commande",
    "note.stop": "Arrêter",
    "note.stopped": "Lecture arrêtée",
    "cam.title": "Caméra du PC",
    "cam.hint": "Placez l'objet ou la page devant la caméra, puis prenez la photo. Vous pouvez en prendre plusieurs à la suite.",
    "cam.shot": "📸 Prendre la photo",
    "cam.file": "Choisir un fichier à la place",
    "cam.close": "Fermer",
    "cam.which": "Caméra",
    "cam.starting": "Démarrage de la caméra…",
    "cam.added": "Photo ajoutée ✓",
    "cam.e.denied": "Accès à la caméra refusé. Autorisez la caméra pour ce site (icône caméra ou cadenas dans la barre d'adresse), puis rouvrez. Sinon, choisissez un fichier.",
    "cam.e.none": "Aucune caméra trouvée sur ce PC : branchez la caméra USB puis rouvrez.",
    "cam.e.busy": "La caméra est déjà utilisée par une autre application (Teams, Caméra Windows…) : fermez-la puis rouvrez.",
    "cam.e.blocked": "Cette page n'a pas le droit d'utiliser la caméra dans ce navigateur. Utilisez « Choisir un fichier ».",
    "cam.e.other": "La caméra n'a pas pu démarrer. Choisis un fichier à la place.",
    "cam.tab": "📱 Utiliser la tablette (poste photo)",
    "cam.waitT": "Photo avec la tablette",
    "cam.wait": "Demande envoyée à la tablette : « {l} ». Sur la tablette, ouvrez « 📱 Poste photo » (en haut de l'écran) et appuyez sur « Prendre la photo ». Les photos arrivent ici tout de suite.",
    "cam.waitRec": "Demande envoyée à la tablette. Sur la tablette, ouvrez « 📱 Poste photo » : elle passe sur l'onglet Réception pour photographier les pages, puis « Lire ». Le résultat apparaîtra ici.",
    "cam.got0": "En attente de la tablette…",
    "cam.fin": "Terminé",
    "st.btn": "📱 Poste photo",
    "st.title": "Poste photo",
    "st.hint": "Cette tablette sert d'appareil photo au PC : chaque demande faite sur le PC apparaît ici. Gardez cette page ouverte.",
    "st.none": "Aucune demande pour l'instant. Sur le PC, cliquez sur 📷 Photo puis « Utiliser la tablette ».",
    "st.shot": "📸 Prendre la photo",
    "st.rec": "📄 Photographier le bon (onglet Réception)",
    "st.quit": "Quitter le poste photo",
    "st.gen": "Photos du montage",
    "st.recL": "Bon de livraison (Réception)",
    "st.agoNow": "à l'instant",
    "st.ago": "il y a {m} min",
    "st.up": "Envoi…",
    "sdocs.photo": "📷 Photo du bon de livraison ou des cartons",
    "sdocs.photosT": "Photos prises sur place",
    "st.docs": "Documents fournisseur",
    "com.noticeFailed": "Message Slack à la logistique non remis : {error}",
    "stock.supplierOrder": "Commande fournisseur",
    "cartons.unknownTitle": "Meubles sans références carton",
    "cartons.unknownHint": "Meubles composés livrés en plusieurs cartons dont aucun bon de livraison PDF de leurs commandes n'a encore donné les références. Photographier leur bon de livraison ici les enregistre.",
    "cartons.nCartons": "{n} cartons",
    "cartons.checked": "Vérifié le {when}",
    "cartons.allKnown": "Tous les meubles en plusieurs cartons ont leurs références.",
    "prod.devBanner1": "Cette application a été développée par {name}. Si vous rencontrez le moindre problème concernant l'utilisation de l'application, veuillez voir avec le développeur {name}.",
    "prod.devBanner2": "Merci de ne pas utiliser le groupe WhatsApp « MYOTAKU - Computeur & Mobile » pour signaler un problème.",
    "guide.title": "🎬 Vidéo pour les monteurs : ton rôle, étape par étape (6 min)",
    "guide.sub": "sans voix, avec des textes à l'écran",
    "guide.hide": "Masquer la vidéo",
    "guide.show": "Afficher la vidéo",
    "job.productPage": "Fiche produit",
    "maybe.titleOne": "{n} article commandé sous un nom proche du client : à vérifier",
    "maybe.titleMany": "{n} articles commandés sous un nom proche du client : à vérifier",
    "maybe.hint": "Trouvé dans le Delivery calendar, mais pas relié avec certitude à ce client (absent de sa liste client, ou nom d'un autre appartement). Vérifie avec le commercial avant de monter ou de livrer : ce n'est pas compté dans le temps de montage.",
    "stock.tlIn": "au dépôt",
    "stock.tlOut": "sorti",
    "maybe.unseenOne": "{n} article commandé pour ce client, peut-être déjà livré",
    "maybe.unseenMany": "{n} articles commandés pour ce client, peut-être déjà livrés",
    "maybe.unseenHint": "Arrivés avant le {date} : l'app ne voit pas les livraisons de ce client avant cette date. Vérifie avec le commercial s'ils ont déjà été livrés avant de les monter ou de les livrer : ils ne sont pas comptés dans le temps de montage.",
    "arch.t": "Clients archivés",
    "arch.done": "Client archivé : il est dans l'onglet Archivés.",
    "arch.undone": "{c} est de retour dans les listes.",
    "jobs.asm": "Montage",
    "jobs.deliv": "Livraison",
    "jobs.est": "Estimé",
    "jobs.progressT": "Avancement {p} % : travail fait, pondéré par le temps estimé de chaque article · trait : {t} % du temps estimé déjà passé",
    "job.salesRep": "Commercial",
    "job.noRep": "non trouvé",
    "job.noItems": "Pas de liste de meubles lue dans la commande : voir les liens Delivery calendar ci-dessous.",
    "issue.reported": "Problèmes signalés",
    "issue.report": "Signaler un problème",
    "issue.sendFailed": "Envoi Slack échoué : le message n'est pas parti.",
    "issue.sendPending": "Envoi Slack en attente",
    "issue.qOfN": "{q} sur {n}",
    "item.probOne": "⚠️ {n} problème signalé",
    "item.probMany": "⚠️ {n} problèmes signalés",
    "recap.failed": "Récap non envoyé",
    "recap.unclear": "Envoi du récap en cours",
    "maybe.ref": "référence",
    "st.sentOne": "{n} photo envoyée",
    "st.sentMany": "{n} photos envoyées",
    "cam.gotOne": "✓ {n} photo reçue de la tablette",
    "cam.gotMany": "✓ {n} photos reçues de la tablette",
    "cam.doneOne": "Terminer et lire ({n} page)",
    "cam.doneMany": "Terminer et lire ({n} pages)",
    "mediaJob.saved": "Enregistré",
    "mediaJob.tooBig": "Fichier trop lourd ({mb} Mo, maximum {max} Mo).",
    "mediaJob.videoBig": "Vidéo trop lourde ({mb} Mo, maximum {max} Mo) : filme plus court.",
    "mediaJob.videoType": "Format de vidéo non accepté : filme avec l'appareil photo (MP4).",
    "mediaJob.fileType": "Format de fichier non accepté : choisis une photo ou une vidéo.",
    "docs.unread": "Documents de cette commande illisibles pour l'instant : réessaie plus tard.",
    "com.readOnly": "Accès en lecture seule : demande à Ludo l'accès Contributeur pour réserver des meubles.",
    "prod.readOnly": "Accès en lecture seule : demande à Ludo l'accès Contributeur pour produire et réceptionner des articles.",
    "sup.kAlerts": "Alertes en cours",
    "sup.alT": "Alertes",
    "sup.noAl": "Aucune alerte en ce moment.",
    "sup.live": "Montages en cours",
    "sup.stRun": "en cours",
    "sup.stPau": "en pause",
    "sup.nm": "pas mesurable : aucun meuble dans la fiche",
    "sup.readOnly": "Accès en lecture seule : demande à Ludo l'accès Contributeur pour marquer les fixes de l'équipe.",
    "note.modeNow": "Lecture immédiate des photos et des PDF.",
    "note.readOne": "Lire et croiser avec le Delivery calendar ({n} page)",
    "note.readMany": "Lire et croiser avec le Delivery calendar ({n} pages)",
    "artrec.sendOne": "Envoyer la réception ({n} page)",
    "artrec.sendMany": "Envoyer la réception ({n} pages)",
    "note.pagesOne": "{n} page",
    "note.pagesMany": "{n} pages",
    "note.linesOne": "{n} ligne lue",
    "note.linesMany": "{n} lignes lues",
    "note.clientsOne": "{n} client",
    "note.clientsMany": "{n} clients",
    "note.colliOne": "{n} colis",
    "note.colliMany": "{n} colis",
    "note.asmOn": "montage {d}",
    "note.err.rate_limited": "Trop de lectures d'un coup ou limite d'usage atteinte : réessaie plus tard.",
    "note.err.unavailable": "La lecture n'est pas autorisée pour cette application pour le moment : réessaie plus tard.",
    "note.err.photo": "Photo refusée (format ou taille) : reprends-la.",
    "note.err.failed": "Lecture interrompue : réessaie.",
    "note.notMatchedRetry": "Lu, mais pas encore croisé avec le Delivery calendar : relance le croisement.",
    "brand.z": "Z",
    "brand.furniture": "Furniture",
    "jobs.titleMode": "montage",
    "issue.sending": "envoi en cours",
    "issue.notSent": "message non envoyé à {n}",
    "note.shortOne": "manque {n}",
    "note.shortMany": "manque {n}",
    "recv.unmatchedOne": "{n} non rattaché",
    "recv.unmatchedMany": "{n} non rattachés",
    "recv.completeOne": "{n} complet",
    "recv.completeMany": "{n} complets",
    "recv.missingOne": "{n} manquant",
    "recv.missingMany": "{n} manquants",
    "job.start": "Démarrer",
    "job.writeFailed": "Enregistrement impossible : vérifie la connexion puis réessaie.",
    "media.mb": "Mo",
    "recv.readOnly": "Accès en lecture seule à la Réception : demande à Ludo l'accès Contributeur.",
    "issue.sendingNow": "Envoi en cours…",
    "issue.netFailed": "Envoi impossible : vérifie la connexion puis réessaie.",
    "crew.pickWarehouse": "Choisissez un entrepôt en haut : les fixes se choisissent entrepôt par entrepôt.",
    "media.failVideo": "La vidéo n'a pas pu être enregistrée : vérifie la connexion puis réessaie.",
    "media.failPhoto": "La photo n'a pas pu être enregistrée : vérifie la connexion puis réessaie.",
    "error.loadFailed": "Lecture impossible : vérifie la connexion puis réessaie.",
  },
  en: {
    "recv.boxesPh": "Box numbers (if several), separated by commas",
    "job.dmToTick": "To tick in the Delivery Manager: {n}",
    "art.title": "Article stock",
    "art.other": "Other",
    "art.refs": "references",
    "art.save": "Save the count ({n} reference(s))",
    "alloc.sync": "Sync with the planning",
    "alloc.result": "{q} deliveries read · {c} created · {u} updated · {r} released · {d} out of stock",
    "alloc.unknown": "Not recognised",
    "docs.title": "Documents & checklists",
    "docs.hint": "Checklists to print for every kit type — including kits with no production order running — and the reference PDFs.",
    "docs.refTitle": "Reference documents (PDF)",
    "docs.refHint": "The same documents already printed and slipped into the boxes / shown on the shelves.",
    "docs.open": "Open",
    "docs.selectAll": "Select all",
    "docs.selectNone": "None",
    "docs.print": "Print the selection ({n})",
    "docs.select": "Print this checklist",
    "alloc.schedule": "Automatic check Monday to Friday at 9:00 and 15:00, and a refresh at 8:35, from 10:35 to 14:35, at 16:35 and 17:35",
    "alloc.points": "Points to check (last check)",
    "alloc.point.partial": "{date} · {wh} · contract #{contract} · {client}: delivery ticked partly ({n}/{total}): all kits taken out, to check",
    "alloc.point.notTicked": "⚠ delivery of {date} · {wh} · contract #{contract} not yet ticked by the team: stock not reduced",
    "alloc.point.stillReserved": "❌ delivery of {date} · {wh} · contract #{contract} still reserved (not taken out: {reason})",
    "alloc.point.reason.missing": "event not found in the planning",
    "alloc.point.reason.unapproved": "validation removed",
    "alloc.point.reason.feed": "the planning did not answer",
    "alloc.point.postponed": "delivery of {date} · {wh} · contract #{contract} moved to {newDate} after its kits left stock: put the kits back by hand if needed",
    "alloc.point.short": "⚠ {wh} — {code}: not enough stock when taken out ({before} for {qty}): count or shipment to check · contract #{contract}",
    "alloc.point.shortArticle": "⚠ not enough article stock: {code} {before} for {qty} · contract #{contract}",
    "alloc.lastRun": "Last check",
    "alloc.auto": "automatic",
    "alloc.never": "No check recorded yet.",
    "alloc.next": "Next",
    "alloc.failed": "failed",
    "crew.title": "Crew",
    "crew.none": "Nobody worked here in the last 3 weeks, according to Myotaku.",
    "crew.lastWorked": "last day",
    "crew.permanent": "Permanent",
    "crew.hint": "Permanent crew are offered first when a job starts.",
    "compo.title": "Parts",
    "compo.single": "1 piece",
    "compo.edit": "Change",
    "compo.hint": "How many parts is this stored in? One label per part. Kept for this product.",
    "compo.save": "Save",
    "stock.palletPh": "e.g. 12",
    "stock.rackPh": "e.g. R3-2",
    "stock.recent": "Recent",
    "stock.upcoming": "Coming",
    "stock.src.return": "client return",
    "stock.src.reception": "supplier delivery",
    "stock.by.person": "by hand",
    "stock.by.ai": "by Claude",
    "stock.leaveSure": "Yes, take it out",
    "com.cancelSure": "Yes, cancel",
    "com.done": "{n} piece(s) reserved for {client} — reference {ref}.",
    "inv.title": "📋 Kit count",
    "inv.last": "Last count {when} by {who} — {n} difference(s)",
    "inv.counted": "counted",
    "inv.save": "Confirm the count",
    "inv.hint": "After a physical count: choose the warehouse, type the number counted for each kit (leave blank what you did not count), check the differences, then “Confirm the count”. The app's stock is replaced by your figures and each correction is kept in the history.",
    "inv.history": "Kit stock history",
    "inv.noHistory": "No change recorded yet.",
    "inv.src.production": "production",
    "inv.src.transfer": "transfer",
    "inv.src.delivery": "delivery",
    "inv.src.inventory": "count",
    "inv.src.receipt": "receipt",
    "inv.src.reception_check": "reception check",
    "inv.src.manual": "added by hand",
    "nav.carts": "Carts",
    "nav.commercial": "Sales",
    "carts.sub": "A QR on every cart: at the client's it is linked to the return, at the warehouse a scan says whose it is.",
    "carts.number": "Cart no.",
    "carts.numberPh": "e.g. 42",
    "carts.open": "Open",
    "carts.back": "← All carts",
    "carts.missing": "Past returns with no cart scanned",
    "carts.missingHint": "The cart was probably not scanned at the client's: scan it when it arrives and link it here.",
    "carts.linked": "Carts in use",
    "carts.linkedNone": "No cart linked yet.",
    "carts.grid": "All {n} carts",
    "carts.gridHint": "Tap a number to open the cart.",
    "carts.linkT": "Link this cart to the client's return",
    "carts.search": "Search a client, a name or an event no.",
    "carts.linkedBy": "Linked by {who} on {when}",
    "carts.arrivedAt": "arrived {when}",
    "carts.arrive": "Arrived at the warehouse",
    "carts.toStock": "Take the furniture into stock",
    "carts.free": "Cart emptied — free it",
    "carts.freeConfirm": "Confirm: free the cart",
    "carts.st.free": "Free",
    "carts.st.on_the_road": "At the client's or on the road",
    "carts.st.at_warehouse": "At the warehouse · to empty",
    "carts.st.lost": "No news for 14 days",
    "com.s1": "Contract",
    "com.s2": "Furniture from stock",
    "com.s3": "Check and send",
    "com.change": "Change",
    "com.searchContract": "Client name or contract no.",
    "com.reaches": "Deliveries in the planning up to {d} (the Myotaku source's limit).",
    "com.noStock": "No furniture available in this warehouse.",
    "com.available": "available",
    "com.prepare": "Preparation at the warehouse",
    "com.prepareHint": "Suggested: the working day before delivery.",
    "com.note": "Note for logistics",
    "com.notePh": "E.g. take the sofa's legs off, deliver with the same client's table",
    "com.send": "Reserve {n} piece(s)",
    "com.sendHint": "The pieces are reserved at once. The “PRÉPARATION CLIENT” event is still created by hand in the planning; record its number on the reservation.",
    "com.reservations": "Reservations",
    "com.none": "No reservations.",
    "com.st.sent": "sent",
    "com.st.prepared": "prepared",
    "com.st.cancelled": "cancelled",
    "com.prepareShort": "prep.",
    "com.deliverShort": "deliv.",
    "com.prepared": "Prepared — out of stock",
    "com.cancel": "Cancel",
    "com.eventPh": "Event no.",
    "com.saveEvent": "Save",
    "nav.stock": "Stock",
    "stock.tab.intake": "Intake",
    "stock.tab.inventory": "Inventory",
    "stock.tab.returns": "Returns",
    "stock.tab.labelling": "Labelling",
    "ret.title": "Planning returns",
    "ret.hint": "Expected items per return and pieces already added to stock.",
    "ret.empty": "Returns (short term, long term, add-ons) from the last 2 weeks and the coming week appear here.",
    "ret.entered": "entered",
    "ret.noItems": "No furniture list for this return.",
    "rec.title": "Labelling received furniture",
    "rec.hint": "Furniture ordered for clients (delivery planning). One tap on “Label” = label ready, nothing else to do.",
    "rec.itemsHint": "Label each piece after assembly and packing, before putting it in the client's cart.",
    "rec.labelOne": "Label",
    "rec.labelAll": "Label the {n} left",
    "rec.labeling": "Labelling…",
    "rec.empty": "No delivery with furniture to label.",
    "rec.labelled": "labelled",
    "rec.assembled": "assembled",
    "rec.for": "For",
    "rec.delivery": "delivery",
    "rec.unit": "piece",
    "stock.tagline": "Client furniture coming back into stock",
    "stock.pickWarehouse": "Pick a warehouse above: every piece goes into one warehouse.",
    "stock.s1": "Client return",
    "stock.s1hint": "Pick the return from the planning that the piece came back on.",
    "stock.s2": "Piece",
    "stock.s2hint": "Tap the piece in front of you. Not on the list? Search the whole catalogue.",
    "stock.s2hintNone": "No planned return: search the catalogue for the piece.",
    "stock.chosen": "Chosen piece",
    "stock.nothingChosen": "No piece chosen yet.",
    "stock.s3": "Condition",
    "stock.cond.new": "New",
    "stock.cond.good": "Good",
    "stock.cond.good.hint": "Ready to rent again",
    "stock.cond.light_marks": "Fair",
    "stock.cond.light_marks.hint": "Light marks",
    "stock.cond.damaged": "Damaged",
    "stock.cond.damaged.hint": "Needs repair",
    "stock.damagePh": "What is damaged — e.g. 10 cm scratch on the top, left leg split",
    "stock.s4": "Wrapping and place",
    "stock.blanket": "Myotaku blanket",
    "stock.film": "Plastic film",
    "stock.unwrapped": "Not wrapped",
    "stock.pallet": "Pallet no.",
    "stock.palletShort": "Pallet",
    "stock.rack": "Rack no.",
    "stock.qty": "Identical pieces",
    "stock.placeHint": "Pallet and rack carry over to the next piece. Several identical pieces: one record, one number and one label each.",
    "stock.save": "Save {n} piece(s) and prepare the labels",
    "stock.noReturns": "No returns for this warehouse this week or next.",
    "stock.today": "Today",
    "stock.contract": "contract",
    "stock.entered": "in",
    "stock.noReturn": "No planned return",
    "stock.noItems": "This return lists no catalogue furniture: search for the piece below.",
    "stock.searchPh": "Name, Myotaku ref., supplier ref.…",
    "stock.search": "Search the whole catalogue",
    "stock.searchUnavailable": "Catalogue search is not available on this Myotaku server yet.",
    "stock.searchNone": "No product matches.",
    "stock.filterPh": "Stock no., name, pallet…",
    "stock.status.held": "In stock and reserved",
    "stock.status.in_stock": "In stock",
    "stock.status.reserved": "Reserved",
    "stock.status.left": "Left",
    "stock.pieces": "pieces",
    "stock.emptyInventory": "No pieces here yet.",
    "stock.days": "d",
    "stock.fromReturn": "return",
    "stock.move": "Move",
    "stock.reprint": "Reprint the label",
    "media.title": "Photos and videos",
    "media.add": "Add a photo or video",
    "media.uploading": "Uploading…",
    "media.empty": "No photos yet.",
    "media.video": "Video",
    "stock.leave": "Take out of stock",
    "stock.ev.received": "received",
    "stock.ev.identified": "identified",
    "stock.ev.labelled": "labelled",
    "stock.ev.moved": "moved",
    "stock.ev.graded": "condition changed",
    "stock.ev.reserved": "reserved",
    "stock.ev.released": "released",
    "stock.ev.left": "left",
    "stock.labelsReady": "{n} label(s) ready",
    "stock.print": "Print",
    "stock.close": "Close",
    "stock.part": "Part",
    "hist.live": "Running now",
    "hist.past": "Actual vs estimate",
    "hist.none": "No finished job yet.",
    "hist.when": "Finished",
    "hist.estimate": "Estimate",
    "hist.actual": "Actual",
    "demand.title": "Kits requested per month",
    "demand.hint": "Last 6 months, orders not cancelled. Hover a colour for the detail.",
    "demand.chart": "Chart",
    "demand.table": "Table",
    "demand.empty": "No production order in the last 6 months.",
    "demand.kits": "kits",
    "demand.kit": "Kit",
    "demand.total": "Total",
    "demand.never": "No demand in 6 months",
    "nav.supervisor": "Supervisor",
    "sup.running": "Jobs in progress",
    "sup.people": "Fitters at work",
    "sup.none": "No job in progress.",
    "sup.yield": "rate",
    "sup.earned": "earned",
    "sup.spent": "spent",
    "sup.alertYield": "rate {p} % (threshold {l} %)",
    "sup.alertIdle": "nothing validated for {m} (expected: {l} at most)",
    "nav.receiving": "Receiving",
    "recv.title": "Supplier receipts",
    "recv.new": "New delivery note",
    "recv.supplier": "Supplier",
    "recv.docRef": "Note no.",
    "recv.order": "Order",
    "recv.lines": "Lines",
    "recv.sku": "Reference",
    "recv.received": "Received",
    "recv.ordered": "Ordered",
    "recv.boxes": "Boxes",
    "recv.addLine": "Add a line",
    "recv.save": "Save",
    "recv.reconcile": "Read and check",
    "recv.complete": "complete",
    "recv.missing": "missing",
    "recv.extra": "extra",
    "recv.unmatched": "unmatched",
    "recv.none": "No receipts yet.",
    "recv.help": "Photograph the note before opening, then compare it with the order.",
    "ident.title": "Photo for recognition",
    "ident.hint": "Photograph the whole piece before wrapping it. The app looks among the return's items first.",
    "ident.take": "📷 Take the photo",
    "ident.retake": "Retake",
    "ident.sending": "Sending the photo…",
    "ident.analyzing": "Recognising: the furniture type, then the comparison with the return (and the catalogue if needed)…",
    "ident.pickThis": "This is the piece",
    "ident.other": "Other possibilities",
    "ident.searchCatalogue": "Search the whole catalogue",
    "ident.seen": "Seen in photo",
    "ident.sure": "Sure",
    "ident.likely": "Likely",
    "ident.check": "Check",
    "ident.noMatch": "No item from this return matches.",
    "ident.noMatchAny": "No identical product found, neither in the return nor in the catalogue. Search by hand or retake the photo (whole piece, from the front).",
    "ident.notInRetour": "Not found among the return's items: here is the best match in the catalogue. Check the return you chose.",
    "ident.failed": "Recognition failed. Try again or search manually.",
    "ident.fromReturn": "from the return",
    "ident.fromCatalogue": "catalogue",
    "sheet.open": "Open the article sheet (references, purchase terms, history)",
    "sheet.stockNow": "Current stock:",
    "sheet.kitsPossible": "that is {n} kit(s) possible",
    "sheet.references": "References",
    "sheet.refZ": "Myotaku ref. (Z)",
    "sheet.refSystem": "Myotaku system ref.",
    "sheet.systemName": "Name in the system",
    "sheet.supplier": "Supplier",
    "sheet.supplierRef": "Supplier ref.",
    "sheet.supplierDesignation": "Supplier designation",
    "sheet.onNotes": "Refs found on delivery notes",
    "sheet.purchase": "Purchase terms",
    "sheet.price": "Unit price",
    "sheet.noPrice": "not given",
    "sheet.moq": "Minimum per article",
    "sheet.rules": "Supplier's terms",
    "sheet.usualQty": "Usually ordered quantities",
    "sheet.toConfirm": "To confirm",
    "sheet.remark": "Remark",
    "sheet.notInCatalogue": "No supplier data for this article in the catalogue (Charles Vila's list of 07.10.2026).",
    "sheet.reserved": "Purchase terms are for managers only.",
    "sheet.notImported": "The purchase terms (Charles Vila's list of 07.10.2026) are not imported into the app yet.",
    "sheet.history": "Stock history",
    "sheet.noHistory": "No movement recorded.",
    "nav.access": "Access",
    "access.title": "Access by module",
    "access.hint": "Each person uses the ticked modules and opens the app on their home module. An admin uses every module. The app refuses any action outside a person's modules.",
    "access.person": "Person",
    "access.home": "Home",
    "access.active": "Active",
    "access.admin": "admin",
    "access.new": "New person",
    "access.name": "Name",
    "access.password": "Password (6 characters min.)",
    "access.homeFirst": "The first module ticked becomes the home (can be changed afterwards).",
    "access.create": "Create the access",
    "access.noModule": "Your account has no module yet. Ask an admin to give you some in “Access”.",
    "unit.title": "Unit {n} / {q}",
    "unit.count": "{c} / {m} items",
    "unit.inStock": "{n} in stock",
    "unit.validate": "Validate this unit ({d} → {n} / {q})",
    "unit.tickAll": "Tick every item to validate ({c}/{m})",
    "unit.correct": "Mistake? Remove 1 validated unit ({d} → {p})",
    "unit.finishNow": "Finish now",
    "unit.markDone": "Mark as done",
    "unit.finishConfirm": "Finish now with {d} / {q} units made? The items already ticked for the unit in progress go back into stock.",
    "unit.cancel": "Cancel the order",
    "unit.cancelConfirm": "Cancel this order ({d} / {q} units made)? Kits already made stay in stock; the items ticked for the unit in progress go back into stock.",
    "unit.yes": "Yes",
    "unit.no": "No",
    "artrec.title": "📷 Supplier delivery — photo, screenshot or PDF",
    "artrec.hint": "Send anything that shows what arrived: a photo of the delivery note (one photo per page), a screenshot (order confirmation, e-mail, WhatsApp, supplier site), an invoice, a PDF, a photo of the carton labels… Each article recognised goes into article stock; the rest is to be checked.",
    "artrec.send": "Send the delivery ({n} page(s))",
    "artrec.typeAny": "Document type (optional)",
    "artrec.type.bulletin": "Delivery note",
    "artrec.type.capture": "Screenshot (order, e-mail, WhatsApp, site)",
    "artrec.type.facture": "Invoice / order confirmation",
    "artrec.type.etiquettes": "Photo of cartons / labels",
    "artrec.type.autre": "Other",
    "artrec.note": "Note (optional)",
    "artrec.noteShown": "Note:",
    "artrec.latest": "Latest deliveries",
    "artrec.none": "No delivery photographed yet.",
    "artrec.updated": "Stock updated:",
    "artrec.total": "total",
    "artrec.toVerify": "To check:",
    "artrec.clientReserved": "Reserved for clients (not put into stock):",
    "artrec.forClient": "for",
    "artrec.summary": "Read:",
    "artrec.st.verified": "Checked and corrected",
    "artrec.verifiedBy": "Checked and corrected on {at} by {by}:",
    "artrec.corrections": "Stock corrections:",
    "artrec.check.title": "Check this reception",
    "artrec.check.hint": "Correct the stock if needed (article and quantity, plus or minus), write what was checked, then “Checked and corrected”.",
    "artrec.check.pick": "Pick the article",
    "artrec.check.qty": "+/− quantity",
    "artrec.check.remove": "Remove this correction",
    "artrec.check.add": "Stock correction",
    "artrec.check.note": "Note (required): what was checked and corrected",
    "artrec.check.button": "Checked and corrected",
    "artrec.disputed.title": "Disputed supplier references",
    "artrec.disputed.hint": "While a reference is disputed, receptions are read as if it did not exist: pick the right article, then “Validate”.",
    "artrec.disputed.now": "currently {code}",
    "artrec.disputed.validate": "Validate",
    "artrec.cal.noCatalogue": "The supplier catalogue of Z Production is not imported yet: an order whose supplier codes are known only there is not checked here.",
    "artrec.cal.held": "This check's Slack messages are on hold: the warehouse still photographs its receptions in Z Production, which already tells the team.",
    "artrec.cal.missing": "Arrived according to the Delivery calendar but not photographed in the app yet:",
    "artrec.cal.missingAsk": "Please photograph this delivery note here.",
    "artrec.cal.last": "Last check with the Delivery calendar: {at}",
    "artrec.cal.error": "error",
    "artrec.cal.otherWarehouses": "This app cannot see yet which warehouse each line of an order goes to: a line of a Penthalaz order delivered elsewhere is still counted here.",
    "artrec.cal.st.ok": "✅ Delivery calendar check: matches",
    "artrec.cal.st.ecart": "⚠️ Delivery calendar check: difference",
    "artrec.cal.st.absent_calendar": "ℹ️ No matching order in the Delivery calendar",
    "artrec.cal.st.non_livree": "ℹ️ Order found in the Delivery calendar but not marked delivered yet",
    "artrec.cal.orders": "order {ids}",
    "artrec.cal.diff": "{name} ({code}): ordered {ordered}, photo {photo}",
    "artrec.cal.extra": "{name} ({code}): photo {photo}, not in the order",
    "artrec.cal.notDeliveredNote": "order not marked delivered in the Delivery calendar yet (status {statuses})",
    "artrec.cal.absentNote": "no Penthalaz order with these articles since {since}",
    "artrec.st.waiting": "Waiting to be processed",
    "artrec.st.running": "Processing",
    "artrec.st.done": "Done — stock updated",
    "artrec.st.verify": "To check",
    "art.modeCount": "✏️ Correct the total",
    "art.modeAdd": "➕ Add",
    "art.addSave": "Add to stock ({n} reference(s))",
    "art.search": "Search a reference, an item, a supplier or a supplier ref.…",
    "jobs.onlyAssembly": "Assembly jobs only (Ludovic's list)",
    "jobs.today": "Today",
    "jobs.upcoming": "Upcoming",
    "jobs.done": "Finished",
    "jobs.archived": "Archived",
    "jobs.noDelivery": "delivery not planned yet",
    "stock.otherCat": "Other",
    "stock.inStockN": "In stock",
    "stock.outN": "Out",
    "stock.avgAge": "Average age",
    "stock.searchAll": "Search stock no., name, ref., client…",
    "stock.allCats": "All categories",
    "stock.allPallets": "All pallets",
    "stock.palletN": "Pallet {p}",
    "stock.all": "All",
    "stock.allSources": "All origins",
    "stock.srcReturns": "Client returns",
    "stock.srcReceptions": "Receptions",
    "stock.allWarehouses": "All warehouses",
    "stock.oldestFirst": "Oldest first",
    "stock.exportCsv": "Export CSV",
    "stock.csvDone": "Export ready",
    "stock.emptyInvTitle": "No furniture in stock here",
    "stock.noResults": "No results for these filters.",
    "stock.forClient": "For",
    "stock.enteredOn": "entered on",
    "stock.openCatalogue": "Open catalogue page ↗",
    "stock.changeCond": "Change condition",
    "stock.backIn": "Back in stock",
    "stock.markOut": "Mark as out",
    "stock.delete": "Remove",
    "stock.damageNotePh": "Defect (required for “Damaged”)",
    "stock.reason": "Reason",
    "stock.out.redeliv": "Delivered to a client",
    "stock.out.repair": "Sent for repair",
    "stock.out.sale": "Sold",
    "stock.out.scrap": "Scrapped",
    "stock.out.transfer": "Warehouse transfer",
    "stock.deleteConfirm": "Delete this record for good? The photos stay stored.",
    "stock.deleteYes": "Yes, delete",
    "stock.ev.back": "back in stock",
    "prod.capacitySaved": "Capacity saved: {p} person(s) × {h} h / day",
    "prod.objective": "Objective",
    "prod.timeRequired": "Total time required",
    "prod.capacityDay": "Day's capacity",
    "prod.marginLeft": "Margin available",
    "prod.overrun": "Overrun",
    "prod.assignTo": "Assign to a preparer (optional)",
    "prod.assignPh": "E.g. Emerson — empty = anyone",
    "prod.note": "Note (optional)",
    "prod.teamCapacity": "Team capacity",
    "prod.people": "People",
    "prod.hoursPerDay": "Hours / day",
    "prod.doneShort": "done",
    "prod.alertFailed": "Preparation stopped, but the alert could not be sent: tell the manager directly.",
    "prod.resolve": "Mark as resolved",
    "prod.for": "For",
    "prod.takeOver": "Take over",
    "prod.changeAssign": "Change assignment",
    "prod.assign": "Assign",
    "prod.split": "Split the order",
    "prod.lockTitle": "Being prepared",
    "prod.lockBody": "by {o}{paused} — {d} / {q} done. The checklist is reserved for {o}.",
    "prod.pausedTag": "(paused)",
    "prod.takeOverAsk": "Take over {k}?",
    "prod.takeOverYes": "Yes, I take over",
    "prod.assignedAsk": "This order is assigned to {n}. Start it anyway?",
    "prod.assignedYes": "Yes, start it",
    "prod.assignName": "Preparer's name (empty = anyone)",
    "prod.save": "Save",
    "prod.splitQty": "Kits to put in the new order (1 to {n})",
    "prod.splitTo": "Assign the new order to (optional)",
    "prod.splitYes": "Split",
    "prod.reopen": "Reopen this order",
    "prod.reopenPartial": "Reopen this order ({d}/{q} done)",
    "jobs.overdue": "overdue",
    "jobs.issue": "Issue reported",
    "jobs.issues": "{n} issues reported",
    "jobs.gone": "Removed from the planning",
    "team.title": "Fitters on this job",
    "team.nobody": "Nobody yet",
    "team.add": "+ Fitter",
    "team.remove": "Remove",
    "team.left": "left the job",
    "issue.howMany": "How many are affected?",
    "issue.ofN": "of {n}",
    "issue.sentTo": "sent to {n} on {t}",
    "job.fromOrders": "Furniture on the supplier orders only (Ludovic's list)",
    "job.otherList": "This job is ticked in the other list: this one is read-only.",
    "job.toAssemble": "Furniture to assemble",
    "job.toCheck": "Items to check (no assembly)",
    "job.toCheckHint": "Open each package and check the item: broken, scratched, missing parts or accessories. Close or protect it again, then tick ✓ the line (or count with + if you have only done part of it). If it is damaged: 📷 photo, then « ⚠️ Issue » on its line.",
    "job.order": "order",
    "job.arrived": "arrived {d}",
    "job.expected": "expected {d}",
    "job.noArrival": "no arrival date",
    "job.cartons": "📦 {n} boxes",
    "job.cartonsPer": "📦 {n} boxes per item · {t} in total",
    "job.ref": "ref.",
    "job.unreadable": "Order cited but not readable: {ids}",
    "job.noOrders": "The planning comment cites no supplier order.",
    "arch.hint": "A client is archived automatically the day after its delivery date in the planning (delivery done), except while an assembly is running. Archiving or unarchiving by hand holds as long as the assembly and delivery dates in the planning do not change.",
    "arch.q": "Search a client or a number",
    "arch.none": "No archived client yet.",
    "arch.noHit": "No archived client matches the search.",
    "arch.un": "Unarchive",
    "arch.ar": "Archive",
    "arch.by": "by",
    "arch.boxOn": "📦 Archived client",
    "arch.boxOff": "Archiving",
    "arch.pAuto": "Delivery done on {d}: the client is filed under Archived.",
    "arch.pMan": "Archived by hand on {d}{w}.",
    "arch.pWill": "Archived automatically the day after the delivery ({d}).",
    "arch.pNone": "No delivery date in the planning: archive it by hand once it is over (delivery cancelled or done without a date).",
    "arch.pBack": "Unarchived by hand: stays in the lists as long as the planning dates do not change.",
    "arch.run": "Assembly running: pause or finish it before archiving.",
    "note.title": "Receiving supplier documents",
    "note.hint": "Take a photo of each page of the delivery note (or another supplier document). The app has it read and checks it against the Delivery calendar: what was received, missing or extra for each client.",
    "note.photo": "📷 Photograph a page",
    "note.files": "📁 Choose photos or PDFs",
    "note.drop": "…or drop photos or PDFs from your computer here",
    "note.page": "Page {n}",
    "note.del": "Remove",
    "note.read": "Read and check against the Delivery calendar ({n} page(s))",
    "note.uploading": "Sending the pages…",
    "note.reading": "Reading, then checking against the Delivery calendar… (10 to 60 s per page, and about a minute for the check)",
    "note.failed": "The reading did not succeed:",
    "note.retry": "Read again",
    "note.rematch": "Check against the Delivery calendar again",
    "note.type": "File not supported: photo (JPEG, PNG, HEIC) or PDF.",
    "note.doc.delivery_note": "Delivery note",
    "note.doc.order_confirmation": "Order confirmation",
    "note.doc.invoice": "Invoice",
    "note.doc.labels": "Labels",
    "note.doc.other": "Document",
    "note.lines": "{n} line(s) read",
    "note.pages": "{n} page(s)",
    "note.readAt": "read on {when}",
    "note.clients": "{n} client(s)",
    "note.ord": "ordered",
    "note.got": "received",
    "note.conf": "confirmed",
    "note.ok": "complete",
    "note.extra": "{n} extra",
    "note.miss": "not on this note",
    "note.confSt": "not a receipt yet",
    "note.colli": "{n} packages",
    "note.cartons": "{n} boxes",
    "note.cw": "boxes incomplete: check",
    "note.un": "Not found in the Delivery calendar (no assembly planned for these items)",
    "note.many": "Several possible clients: assign by hand",
    "note.unread": "Unreadable number",
    "note.unclear": "Check on the paper",
    "note.open": "Open the job",
    "note.sec": "Receiving (supplier documents read)",
    "note.ocW": "This document is an order confirmation (or an invoice), not a delivery note: it does not prove the goods have arrived. Do not look for the furniture until it has been received.",
    "note.deliveryOn": "delivery {when}",
    "note.hist": "Latest documents read",
    "note.none": "No document read yet.",
    "note.byHand": "Enter a note by hand",
    "note.matched": "{n} placed",
    "note.pillOk": "📄 received",
    "note.pillWarn": "⚠️ receiving",
    "note.pillOc": "📝 order confirmed",
    "tr.title": "Transfers between sites",
    "tr.from": "From",
    "tr.to": "To",
    "tr.send": "Send",
    "tr.receive": "Receive",
    "tr.pending": "pending",
    "tr.inTransit": "in transit",
    "tr.received": "received",
    "tr.new": "New transfer",
    "tr.none": "No transfers.",
    "tr.arrived": "Arrived",
    "tr.arrivalHint": "Count what arrived: only these kits go into this site's stock.",
    "tr.lossNote": "What happened to the missing ones (required)",
    "tr.confirmArrival": "Confirm arrival",
    "tr.receivedOf": "{n} of {m} arrived",
    "tr.inTransitNote": "In transit: the goods belong to neither site.",
    "nav.production": "Production",
    "prod.progress": "Today's progress",
    "prod.target": "Target",
    "prod.done": "Done",
    "prod.variance": "Variance",
    "prod.ahead": "ahead",
    "prod.behind": "behind",
    "prod.byPreparer": "By preparer",
    "prod.kitsMade": "kits made",
    "prod.perKit": "min / kit",
    "prod.queue": "Production queue",
    "prod.finishedToday": "Finished today",
    "prod.stock": "Stock",
    "prod.onHand": "On hand",
    "prod.reserved": "Reserved",
    "prod.available": "Available",
    "prod.newOrder": "New production order",
    "prod.kit": "Kit",
    "prod.qty": "Quantity",
    "prod.raise": "Send",
    "prod.noQueue": "No order waiting — the queue is empty.",
    "prod.allocations": "Kits reserved for upcoming deliveries",
    "prod.neededOn": "Needed",
    "prod.unitMinutes": "min / kit",
    "prod.reservedNote": "A reservation never reduces real stock.",
    "wh.all": "All",
    "job.assembly": "Assembly",
    "job.delivery": "Delivery",
    "job.estimate": "Estimate",
    "job.assembled": "assembled",
    "job.checked": "checked",
    "job.inProgress": "In progress",
    "job.todo": "To do",
    "job.finished": "Finished",
    "job.issueReported": "Issue reported",
    "job.packing": "Packing required",
    "job.packingBody": "Every assembled item is wrapped in MYOTAKU moving blankets, then sealed with plastic stretch film, before it goes into the client's cart.",
    "job.furniture": "Furniture to assemble",
    "job.pickOne": "Pick a delivery.",
    "start.title": "Who is assembling?",
    "start.help": "Tap every fitter working together on this client. A fitter already running on another client is removed from it automatically.",
    "start.extras": "Extras",
    "start.permanent": "Permanent staff",
    "start.addName": "Add a first name",
    "start.add": "Add",
    "start.atLeastOne": "Choose at least one fitter",
    "issue.title": "Report an issue",
    "issue.kind": "Issue",
    "issue.item": "Item concerned",
    "issue.none": "None in particular",
    "issue.what": "What you see",
    "issue.by": "Reported by",
    "issue.blocks": "Assembly is blocked (pauses the timer)",
    "issue.send": "Report",
    "issue.damaged_furniture": "Damaged furniture",
    "issue.missing_part": "Missing part or hardware",
    "issue.missing_furniture": "Missing furniture",
    "issue.wrong_model_or_colour": "Wrong model or colour",
    "issue.missing_instructions": "Missing assembly instructions",
    "issue.other": "Other issue",
    "finish.title": "Finish the job",
    "finish.send": "Finish",
    "finish.reopen": "Reopen",
    "finish.recapSent": "Summary sent on Slack to",
    "finish.resend": "Send the summary",
    "cancel": "Cancel",
    "prod.stop": "⛔ Stop",
    "prod.stopTitle": "Stop the preparation and alert the manager",
    "prod.stopSend": "⛔ Stop and send the alert",
    "prod.stopDetail": "Details (which article, how many are missing…)",
    "prod.stockShort": "Not enough stock",
    "prod.stoppedTitle": "Stopped preparations",
    "prod.stoppedBecause": "Stopped:",
    "prod.reason0": "Missing material / article",
    "prod.reason1": "Out of boxes",
    "prod.reason2": "Out of stock",
    "prod.reason3": "Other problem",
    "item.issue": "Issue",
    "item.blocked": "Blocked",
    "app.title": "Myotaku Warehouse",
    "nav.preparation": "Assembly",
    "nav.signOut": "Sign out",
    "days.title": "Upcoming deliveries",
    "days.none": "No deliveries that day.",
    "days.outsideWindow": "Outside the feed's window",
    "event.pieces": "pieces",
    "event.lines": "lines",
    "event.packs": "rooms",
    "event.awaitingSales": "Awaiting sales",
    "event.awaitingLogistics": "Awaiting logistics",
    "event.approved": "Approved",
    "run.start": "Start preparation",
    "run.resume": "Resume",
    "run.pause": "Pause",
    "run.running": "Running",
    "run.paused": "Paused",
    "run.wall": "Elapsed",
    "run.worked": "Worked",
    "run.minutes": "min",
    "run.noRun": "Not started",
    "item.done": "Built",
    "item.todo": "To build",
    "item.noAssembly": "No assembly",
    "item.estimate": "Estimate",
    "pack.standardKit": "Standard kit",
    "error.title": "Problem",
    "loading": "Loading…",
    "lang.label": "Language",
    "login.title": "Myotaku Warehouse",
    "login.intro": "Sign in to continue.",
    "login.email": "Email",
    "login.password": "Password",
    "login.submit": "Sign in",
    "login.busy": "Signing in…",
    "login.noSignup": "Accounts are created by an administrator. There is no sign-up.",
    "login.failed": "Sign-in failed.",
    "login.failedStatus": "Sign-in failed ({status}).",
    "login.wrong": "That email and password do not match.",
    "login.noToken": "Signed in, but no token was issued. Tell an administrator.",
    "login.sessionCheck": "Could not check the session ({status}).",
    "error.request": "Request failed ({status}).",
    "error.upload": "The storage refused the upload ({status}).",
    "status.intro": "This page only proves the chain is whole.",
    "status.signedInAs": "Signed in as",
    "status.frontEnd": "Front end",
    "status.server": "Server",
    "status.browser": "Browser",
    "status.mounted": "the bundle loaded and React mounted",
    "status.asking": "asking…",
    "status.unreachable": "UNREACHABLE",
    "status.answeredAt": "Server answered at {when}",
    "docs.ref.checklists.title": "A4 checklists (carton)",
    "docs.ref.checklists.desc": "One sheet per kit to slip into each carton — same layout as below.",
    "docs.ref.shelf.title": "Shelf labels",
    "docs.ref.shelf.desc": "Colour labels for the shelves, 8 per A4 page.",
    "docs.ref.inventory.title": "Full inventory sheet",
    "docs.ref.inventory.desc": "Every reference, to fill in during a physical count.",
    "chk.title": "Packing checklist",
    "chk.phase1": "Phase 1 — Installed kit",
    "chk.phase2": "Phase 2 — Reserve kit",
    "chk.phase1Short": "Installed kit",
    "chk.phase2Short": "Reserve kit",
    "chk.complete": "Box complete and checked",
    "chk.preparedBy": "Prepared by",
    "chk.checkedBy": "Checked by",
    "chk.date": "Date",
    "chk.signature": "Signature",
    "chk.article": "{n} item",
    "chk.articles": "{n} items",
    "csv.col.stock_no": "stock_no",
    "csv.col.entrepot": "warehouse",
    "csv.col.categorie": "category",
    "csv.col.statut": "status",
    "csv.col.etat": "condition",
    "csv.col.origine": "origin",
    "csv.col.nom": "name",
    "csv.col.ref_myotaku": "myotaku_ref",
    "csv.col.ref_fournisseur": "supplier_ref",
    "csv.col.fournisseur": "supplier",
    "csv.col.commande_fournisseur": "supplier_order",
    "csv.col.dimensions": "dimensions",
    "csv.col.emplacement": "location",
    "csv.col.palette": "pallet",
    "csv.col.rack": "rack",
    "csv.col.numero": "number",
    "csv.col.jours_au_depot": "days_in_stock",
    "csv.col.retour": "return",
    "csv.col.client": "client",
    "csv.col.reserve_pour": "reserved_for",
    "csv.col.livraison": "delivery",
    "csv.col.entree_le": "received_on",
    "csv.col.degats": "damage",
    "csv.col.catalogue_url": "catalogue_url",
    "csv.in": "in stock",
    "csv.out": "out",
    "csv.reception": "supplier delivery",
    "csv.return": "client return",
    "csv.cond.new": "new",
    "csv.cond.good": "good",
    "csv.cond.light_marks": "fair",
    "csv.cond.damaged": "damaged",
    "csv.pallet": "Pallet {p}",
    "csv.rack": "Rack {r}",
    "csv.file": "furniture-stock",
    "csv.fileAll": "all",
    "label.stockLabel": "FURNITURE STOCK",
    "label.piece": "Piece",
    "label.unit": "Item",
    "label.withOthers": "with {n} other piece(s)",
    "label.refMyo": "Myotaku ref",
    "label.refSup": "Supplier ref",
    "label.entry": "In",
    "label.qrCaption": "Catalogue page",
    "label.qrCaptionBig": "Scan: Myotaku catalogue page",
    "label.for": "For",
    "label.deliv": "deliv.",
    "label.ret": "Return",
    "label.sample": "Example",
    "label.cond.neuf": "New",
    "label.cond.bon": "Good",
    "label.cond.moyen": "Fair",
    "label.cond.abime": "Damaged",
    "label.fmt.a4": "A4 landscape · 1 label per sheet (readable from afar)",
    "label.fmt.a4x2": "A4 · 2 labels per sheet (A5, cut in half)",
    "label.fmt.ql62": "Brother QL · 62 × 100 mm",
    "label.fmt.dymo": "Dymo LabelWriter · 54 × 101 mm",
    "label.fmt.zebra": "Zebra / thermal · 100 × 150 mm",
    "label.format": "Label format",
    "label.preview": "Label preview",
    "label.dlOne": "Download label",
    "label.dlAll": "Download the {n} labels",
    "label.file1": "label-{id}.pdf",
    "label.fileN": "labels-{wh}-{day}.pdf",
    "stock.todayLabels": "Today's labels",
    "rec.reprint": "Reprint",
    "art.perKit": "×{n}/kit",
    "label.printA4": "Open the PDF and print on A4, 100 % scale.",
    "label.printRoll": "Print it from the printer's app (PDF).",
    "label.s5": "Label",
    "label.count": "{n} labels (one per piece)",
    "label.previewFirst": "preview of piece 1",
    "cartLabel.cart": "Cart",
    "cartLabel.atClient": "At the client: scan and choose the return.",
    "cartLabel.atWarehouse": "At the warehouse: scan to see the client.",
    "cartLabel.scanBoth": "Scan when loading at the client and when unloading.",
    "cartLabel.keep": "do not remove",
    "cartLabel.fmt.zebra": "Zebra / thermal · 150 × 100 mm (landscape)",
    "cartLabel.fmt.ql62": "Brother QL · 62 × 100 mm",
    "cartLabel.fmt.a4x4": "A4 landscape · 4 labels per sheet, to cut out",
    "cartLabel.open": "🖨 Print labels",
    "cartLabel.title": "Print cart labels",
    "cartLabel.hint": "The module prepares a PDF: open it and print it on the label printer (actual size, no scaling). Print a test page first.",
    "cartLabel.format": "Format",
    "cartLabel.from": "From cart no.",
    "cartLabel.to": "to no.",
    "cartLabel.copies": "Labels per cart",
    "cartLabel.print1": "🖨 Print {n} label (PDF)",
    "cartLabel.printN": "🖨 Print {n} labels (PDF)",
    "cartLabel.busy": "Preparing the PDF…",
    "cartLabel.ok": "PDF ready: open it to print.",
    "cartLabel.err": "The PDF could not be prepared: try again.",
    "cartLabel.range": "Enter numbers from 1 to 999, smallest first (400 labels at most per PDF).",
    "cartLabel.reprint": "🖨 Reprint this cart's label",
    "cartLabel.file1": "label-{id}-{fmt}.pdf",
    "cartLabel.fileN": "cart-labels-{from}-{to}-{fmt}.pdf",
    "cartLabel.copiesN": "× {n}",
    "cartLabel.close": "Close",
    "note.notMatched": "Read, but not yet matched with the Delivery calendar:",
    "carton.title": "Carton labels",
    "carton.sub": "in black and white, same layout as the shelf labels.",
    "carton.printOn": "Print on:",
    "carton.fmt.a4": "A4 sheet — 8 labels 10 × 7 cm (normal printer)",
    "carton.fmt.ql102x70": "Brother QL label printer — 102 × 70 mm (102 mm continuous roll, DK-22243)",
    "carton.fmt.ql102x152": "Brother QL label printer — 102 × 152 mm (die-cut labels DK-11241)",
    "carton.fmt.ql62x100": "Brother QL label printer — 62 × 100 mm (die-cut labels DK-11202)",
    "carton.helpQl": "One label per carton. In the print window: choose the \"Brother QL-1100\" printer, Margins \"None\", and the same paper size ({w} × {h} mm) under \"More settings\".",
    "carton.helpQlPage": "One label per page. In the print window: printer \"Brother QL-1100\", Margins \"None\", size {w} × {h} mm.",
    "carton.helpA4": "8 identical 10 × 7 cm labels per A4 sheet.",
    "carton.helpA4Page": "A4 sheet: 8 identical 10 × 7 cm labels per sheet.",
    "carton.editHint": "The quantity received and the supplier reference print on the label — change them below (leave empty not to print them).",
    "carton.ref": "Ref.",
    "carton.article": "Article",
    "carton.supRefCol": "Supplier ref.",
    "carton.qtyCol": "Qty received",
    "carton.qtyShort": "Qty",
    "carton.sheets": "Sheets",
    "carton.labels": "Labels",
    "carton.allRefs": "All references:",
    "carton.apply": "Apply",
    "carton.addRef": "Add a ref.:",
    "carton.addPh": "e.g. 9KUC",
    "carton.add": "Add",
    "carton.unknown": "Unknown reference \"{ref}\".",
    "carton.noneRead": "No article recognised in this reception — add a reference below.",
    "carton.print": "Print",
    "carton.nLabel": "{n} label {w} × {h} mm",
    "carton.nLabels": "{n} labels {w} × {h} mm",
    "carton.nSheet": "{n} sheet · {labels} labels",
    "carton.nSheets": "{n} sheets · {labels} labels",
    "carton.nLabelShort": "{n} label",
    "carton.nLabelsShort": "{n} labels",
    "carton.libTotal": "{refs} ref. · {what}",
    "carton.noneSelected": "No label selected",
    "carton.footer": "Received on {date}",
    "carton.footerSupplier": "Received on {date} · {supplier}",
    "carton.fileReception": "Carton_labels_{supplier}_{date}",
    "carton.fileLibrary": "Myotaku_labels",
    "carton.fileOne": "Label_{code}",
    "carton.nothing": "Nothing to print.",
    "carton.blocked": "Printing is not possible: the browser blocked the new window. Allow pop-ups for this site and try again.",
    "carton.qtyReceived": "Qty received",
    "carton.supRef": "Supplier ref:",
    "carton.catLinen": "Linen kit",
    "carton.libTitle": "Label base",
    "carton.libSub": "Every Myotaku reference — 🖨️ on a row = ONE single label (on A4: top left of the sheet); to print several, choose the number then \"Print the selection\". The supplier ref. and quantity typed here are remembered for next time.",
    "carton.searchPh": "Search: code, article, supplier ref.…",
    "carton.allKits": "All kits",
    "carton.footerField": "Text at the bottom of the label (optional):",
    "carton.footerPh": "e.g. Rotel delivery of 05.10",
    "carton.noMatch": "No reference matches the search.",
    "carton.reset": "Reset all to 0",
    "carton.printSelection": "Print the selection",
    "carton.printOne": "Print this label",
    "carton.remembered": "remembered",
    "carton.saveFailed": "Could not remember it: {reason}",
    "carton.printCartons": "Print carton labels",
    "cartx.today": "Today",
    "cartx.yesterday": "Yesterday",
    "cartx.tomorrow": "Tomorrow",
    "cartx.waiting1": "{n} save waiting for network on this phone",
    "cartx.waitingN": "{n} saves waiting for network on this phone",
    "cartx.queued": "No network: saved on this phone, sent automatically when the network is back.",
    "cartx.unknownNumber": "Unknown cart number.",
    "cartx.missingNone": "Every past return of the last 3 days has a cart.",
    "cartx.notYet": "{n} not scanned yet",
    "cartx.cart": "Cart",
    "cartx.lastClient": "Last client: {client} · released on {date}",
    "cartx.never": "Not used with the module yet.",
    "cartx.linkedOk": "{cart} linked to {client}",
    "cartx.client": "Client",
    "cartx.event": "Event",
    "cartx.byHand": "typed by hand",
    "cartx.linkedBy": "Linked",
    "cartx.arrived": "Arrived",
    "cartx.warehouse": "Warehouse",
    "cartx.photo": "Photo of the loaded cart",
    "cartx.optional": "optional",
    "cartx.photoUp": "Uploading photo…",
    "cartx.photoErr": "Photo not sent: take it again or carry on without.",
    "cartx.photoDel": "Remove",
    "cartx.others": "Carts of this return",
    "cartx.iOfN": "cart {i} of {n}",
    "cartx.items": "Furniture expected on this return ({n})",
    "cartx.noItems": "No furniture list for this return (typed by hand or not in the planning).",
    "cartx.arrivedOk": "{cart} arrived at the warehouse",
    "cartx.toStock": "Stock intake for this return",
    "cartx.free": "Cart emptied: release",
    "cartx.freeConfirm": "Confirm: release {cart}",
    "cartx.freedOk": "{cart} is free",
    "cartx.relink": "Change return",
    "cartx.relinkWarn": "This cart is linked to {client}. The new link will replace it.",
    "cartx.history": "History",
    "cartx.h.link": "Linked",
    "cartx.h.arrive": "Arrived at the warehouse",
    "cartx.h.free": "Released",
    "cartx.h.stock": "Stock intake",
    "cartx.cartsOk1": "{n} cart ✓",
    "cartx.cartsOkN": "{n} carts ✓",
    "cartx.noHit": "No matching return.",
    "cartx.noEv": "No return in the planning for this warehouse around today.",
    "cartx.manual": "Return not in the list?",
    "cartx.manEv": "Planning event number",
    "cartx.manErr": "Enter the event number (digits) and the client.",
    "cartx.pickFirst": "Pick the client's return.",
    "cartx.nBad": "The cart number is higher than the number of carts.",
    "cartx.unitNo": "Cart no.",
    "cartx.of": "of",
    "cartx.forClient": "for this client",
    "cartx.doLink": "Link {cart}",
    "stock.stillNeeded": "Still needed:",
    "stock.mProduct": "piece",
    "stock.mPhoto": "photo",
    "stock.mCond": "condition",
    "stock.mDamage": "damage photo",
    "stock.mWrap": "blanket + film",
    "stock.mPallet": "pallet no.",
    "stock.mRack": "rack no.",
    "stock.damagePhotos": "Damage photos",
    "stock.required": "required",
    "stock.optional": "optional",
    "stock.addPhoto": "+ Photo",
    "stock.damageNote": "Damage description",
    "stock.wrapEach": "Furniture in {n} pieces: wrap each piece separately and stick a label on each ({n} labels).",
    "stock.wrapPhoto": "Photo of wrapped piece",
    "stock.saved": "Added to stock",
    "stock.savedN": "{n} pieces added to stock",
    "stock.next": "Next piece",
    "stock.nextSame": "Next piece from same return",
    "stock.photosFailed": "{n} photo(s) not sent: add them from the piece's detail.",
    "ret.emptyTitle": "No returns for this warehouse",
    "ret.group.today": "Today",
    "ret.group.upcoming": "Upcoming",
    "ret.group.recent": "Recent",
    "ret.tapToEnter": "Tap a piece to start adding it to stock.",
    "stock.nParts": "{n} pieces",
    "stock.outPill": "Out",
    "stock.catPhoto": "Catalogue photo",
    "stock.typeSize": "Type",
    "stock.composition": "Composition",
    "stock.itemNo": "Number",
    "stock.ofReturn": "of the return",
    "stock.ofIntake": "of this intake",
    "stock.place": "Storage",
    "stock.day1": "{n} day",
    "stock.daysN": "{n} days",
    "stock.stIn": "In stock for",
    "stock.stInSince": "added on {date}",
    "stock.stBackSince": "back on {date}",
    "stock.stOut": "Out on",
    "stock.stOutAfter": "after {n} in stock",
    "stock.stTotal": "Total in stock",
    "stock.stTotalH": "all periods together",
    "stock.stCycles": "In · out",
    "stock.stCyclesV": "{i} · {o}",
    "stock.stLast": "Last movement",
    "stock.stPlace": "At this location",
    "stock.stPlaceSince": "since {date}",
    "stock.stRef": "Same reference",
    "stock.stRefV": "{n} in stock",
    "stock.stRefH": "here · {all} all warehouses · {out} out",
    "stock.stAvg": "Average time",
    "stock.stAvgH": "in stock for this reference ({n} records)",
    "stock.stRet": "Client returns",
    "stock.stRetH": "of this reference recorded",
    "media.role.identification": "Identification",
    "media.role.damage": "Damage",
    "media.role.wrapped": "Wrapped",
    "stock.factReturn": "Return",
    "stock.factWrapping": "Wrapping",
    "stock.factAdded": "Added on",
    "com.tab.new": "New reservation",
    "com.tab.mine": "My reservations",
    "com.tab.all": "All reservations",
    "com.step1": "Contract",
    "com.step2": "Furniture",
    "com.step3": "Send",
    "com.whichClient": "For which client?",
    "com.continueWith": "Continue with \"{q}\"",
    "com.notFoundManual": "Not found in the upcoming deliveries: typed by hand",
    "com.contractN": "contract {n}",
    "com.delivOn": "delivery {date}",
    "com.delivToPlan": "delivery to be planned",
    "com.noUpcoming": "No contract with an upcoming delivery yet. Use \"Contract not in the list\".",
    "com.notInList": "Contract not in the list?",
    "com.clientPh": "Smith family",
    "com.contractNo": "Contract no.",
    "com.ifYouHave": "if you have it",
    "com.delivDate": "Delivery date",
    "com.ifKnown": "if known",
    "com.continueClient": "Continue with this client",
    "com.manClientReq": "Enter the client.",
    "com.manCidBad": "The contract no. has 3 to 7 digits.",
    "com.manDelivPast": "The delivery date has already passed.",
    "com.searchFurniture": "Search: sofa, table, name…",
    "com.transferWarn": "This client is delivered from {from}: a piece from {other} will need a transfer between warehouses.",
    "com.allCats": "All",
    "com.loadingStock": "Loading the stock…",
    "com.noneFilter": "No furniture available with this filter",
    "com.noneAt": "No furniture available at {wh}",
    "com.noneHint": "Pieces in stock, not reserved and in good condition appear here.",
    "com.seePhotos": "See the photos of {name}",
    "com.defectPhotos": "defect photos",
    "com.nAvailable": "{n} available",
    "com.add": "Add",
    "com.removeOne": "Remove one",
    "com.addOne1": "Add one",
    "com.selected1": "piece selected",
    "com.selectedN": "pieces selected",
    "com.continue": "Continue",
    "com.addOne": "Add at least one piece.",
    "com.nthTaken": "taken #{n}",
    "com.noDefectNoted": "no defect noted",
    "com.addOthers": "Add other pieces",
    "com.prepareHintDay": "Suggested: the working day before the delivery on {date}.",
    "com.prepareNoDeliv": "No delivery in the planning: choose the preparation day.",
    "com.sending": "Reserving…",
    "com.send1": "Reserve {n} piece and tell logistics",
    "com.sendN": "Reserve {n} pieces and tell logistics",
    "com.justTaken": "A piece has just been reserved by someone else: the list is up to date, check your selection.",
    "com.doneTitle": "Furniture reserved for {client}",
    "com.doneText1": "{n} piece is reserved in your name. Emerson and Ludo are notified; preparation planned on {prep}.",
    "com.doneTextN": "{n} pieces are reserved in your name. Emerson and Ludo are notified; preparation planned on {prep}.",
    "com.reference": "Reference {ref}",
    "com.seeMine": "See my reservations",
    "com.filter.sent": "To prepare",
    "com.filter.prepared": "Prepared",
    "com.filter.cancelled": "Cancelled",
    "com.filter.all": "All",
    "com.noneYet": "No reservation yet",
    "com.noneYetHint": "Your reservations appear here with their progress: sent, in the planning, prepared.",
    "com.status.cancelled": "Cancelled",
    "com.status.prepared": "Prepared",
    "com.status.planned": "In the planning · #{ev}",
    "com.status.sent": "Sent · logistics notified",
    "com.prepOn": "preparation {date}",
    "com.reservedAt": "reserved on {when}",
    "com.cancelWarn": "The pieces become available again.",
    "com.cancelWarnPlanning": "Emerson and Ludo will be told to remove the preparation from the planning.",
    "com.markPrepared": "Mark prepared",
    "com.cancelResa": "Cancel the reservation",
    "job.backToList": "List",
    "job.timesheetNo": "Event number for your timesheet",
    "job.copyNo": "Copy",
    "job.copied": "#{n} copied",
    "job.emptyToday": "No assembly planned today",
    "job.emptyTodayHint": "The next jobs are in the Upcoming tab.",
    "job.emptyUpcoming": "No upcoming assembly",
    "job.emptyUpcomingHint": "“Assemblage meubles pour …” events are created automatically as soon as a client's furniture has an arrival date.",
    "job.emptyDone": "No finished job yet",
    "job.emptyDoneHint": "When fitters press Finish, the actual time shows here and in Actual vs estimate.",
    "jobs.notArrivedOne": "⏳ {n} not arrived yet",
    "jobs.notArrivedMany": "⏳ {n} not arrived yet",
    "jobs.receivedPct": "📦 received {p}%",
    "jobs.receivedOfOne": "{g} of {n} items received",
    "jobs.receivedOfMany": "{g} of {n} items received",
    "jobs.rxWarn": "⚠️ receiving",
    "jobs.rxOk": "📄 received",
    "jobs.rxConfirmed": "📝 order confirmed",
    "jobs.progress": "Progress {p}%: work done, weighted by each item's estimated time",
    "timer.notStarted": "Not started yet",
    "timer.ofEst": "{t} of {e} estimated",
    "timer.teamTime": "{n} fitters · {t} of combined work",
    "timer.left": "{t} left on the estimate",
    "timer.over": "{t} over the estimate",
    "timer.doneSum": "Finished in {r} of work for {e} estimated",
    "arch.nOne": "{n} archived client",
    "arch.nMany": "{n} archived clients",
    "arch.more": "… and {n} more: refine the search.",
    "arch.auto": "delivered on {d}",
    "arch.man": "archived by hand on {d}",
    "start.added": "Added",
    "start.addTitle": "Add fitters",
    "start.busyOn": "on {c}",
    "start.goOne": "Start with {n} fitter",
    "start.goMany": "Start with {n} fitters",
    "start.addGoOne": "Add {n} fitter",
    "start.addGoMany": "Add {n} fitters",
    "start.moved": "{n} removed from {c}",
    "start.noStaff": "No fitter found for this warehouse yet: add first names below.",
    "furn.one.wardrobes": "wardrobe",
    "furn.many.wardrobes": "wardrobes",
    "furn.one.beds": "bed",
    "furn.many.beds": "beds",
    "furn.one.bed-components": "bed component",
    "furn.many.bed-components": "bed components",
    "furn.one.chest-drawers": "chest of drawers",
    "furn.many.chest-drawers": "chests of drawers",
    "furn.one.sideboard": "sideboard",
    "furn.many.sideboard": "sideboards",
    "furn.one.bookcase-display-cabinet": "bookcase",
    "furn.many.bookcase-display-cabinet": "bookcases",
    "furn.one.tv-tables": "TV unit",
    "furn.many.tv-tables": "TV units",
    "furn.one.desks": "desk",
    "furn.many.desks": "desks",
    "furn.one.dining-tables": "dining table",
    "furn.many.dining-tables": "dining tables",
    "furn.one.high-table": "high table",
    "furn.many.high-table": "high tables",
    "furn.one.console": "console",
    "furn.many.console": "consoles",
    "furn.one.coffee-tables": "coffee table",
    "furn.many.coffee-tables": "coffee tables",
    "furn.one.bedside-tables": "bedside table",
    "furn.many.bedside-tables": "bedside tables",
    "furn.one.shoe-coat-rack": "coat rack",
    "furn.many.shoe-coat-rack": "coat racks",
    "furn.one.sofas": "sofa",
    "furn.many.sofas": "sofas",
    "furn.one.outdoor-furniture": "outdoor furniture",
    "furn.many.outdoor-furniture": "outdoor furniture",
    "furn.one.side-table": "side table",
    "furn.many.side-table": "side tables",
    "furn.one.desk-chair": "desk chair",
    "furn.many.desk-chair": "desk chairs",
    "furn.one.chairs": "chair",
    "furn.many.chairs": "chairs",
    "furn.one.high-chair": "bar chair",
    "furn.many.high-chair": "bar chairs",
    "furn.one.armchairs": "armchair",
    "furn.many.armchairs": "armchairs",
    "furn.one.pouf-stool-bench": "pouf",
    "furn.many.pouf-stool-bench": "poufs",
    "furn.one.floor-lamp": "floor lamp",
    "furn.many.floor-lamp": "floor lamps",
    "furn.one.mirror": "mirror",
    "furn.many.mirror": "mirrors",
    "furn.one.table-lamp": "table lamp",
    "furn.many.table-lamp": "table lamps",
    "furn.one.ceiling-light": "ceiling light",
    "furn.many.ceiling-light": "ceiling lights",
    "furn.one.wall-light": "wall light",
    "furn.many.wall-light": "wall lights",
    "furn.one.decorative-items": "decorative item",
    "furn.many.decorative-items": "decorative items",
    "furn.one.decoration": "decoration",
    "furn.many.decoration": "decorations",
    "furn.one.cushion-and-throws": "cushion or throw",
    "furn.many.cushion-and-throws": "cushions and throws",
    "furn.one.carpet": "rug",
    "furn.many.carpet": "rugs",
    "furn.one.artificial-plant": "artificial plant",
    "furn.many.artificial-plant": "artificial plants",
    "furn.one.electric-appliances": "appliance",
    "furn.many.electric-appliances": "appliances",
    "furn.one.internet": "online item",
    "furn.many.internet": "online items",
    "furn.one.hangers": "hanger",
    "furn.many.hangers": "hangers",
    "furn.one.kits": "kit",
    "furn.many.kits": "kits",
    "furn.one.others": "other item",
    "furn.many.others": "other items",
    "job.notArrived": "not arrived yet",
    "job.naExpected": "expected {d}",
    "job.rxScanned": "✅ received (delivery note scanned)",
    "job.boxesUnknown": "box references not known yet",
    "docs.kindDn": "🚚 Delivery note",
    "docs.kindOc": "📝 Order confirmation",
    "docs.kindInv": "🧾 Invoice",
    "docs.kindOth": "📄 Order document",
    "docs.notRx": "not a receipt",
    "docs.arrOk": "✅ arrived on {d}",
    "docs.arrNo": "⏳ not received yet",
    "docs.ocWarn": "Order confirmation ≠ receipt: this order has not arrived at the warehouse yet. No need to look for the furniture.",
    "docs.others": "Other documents of the order ({n})",
    "docs.none": "No document in this order yet.",
    "docs.exp": "Links may have expired: open the order in the planning.",
    "docs.img": "photo",
    "docs.pdf": "PDF",
    "docs.noOrder": "No supplier order linked.",
    "sdocs.title": "Supplier documents",
    "sdocs.hint": "Documents stored in the supplier order in the planning (this client's first). Careful: an order confirmation or an invoice does not mean the furniture has arrived. Only “arrived on …” or a delivery note confirms it.",
    "issue.placeholder": "E.g. top scratched over 20 cm, leg screws missing, box open on arrival…",
    "issue.needText": "Describe the issue in a few words.",
    "issue.needBy": "Say who is reporting the issue.",
    "issue.to": "To:",
    "issue.cc": "Cc:",
    "issue.unsales": "No sales rep in the planning: the message goes to Ludo.",
    "issue.sendTo": "Send to {n}",
    "mediaJob.title": "Assembly photos and videos",
    "mediaJob.hint": "Photograph or film the assembly: drawers and doors opened and closed, stability, fixings, then the packing. Each item also has its own 📷 button.",
    "mediaJob.empty": "No photo or video yet.",
    "mediaJob.photo": "📷 Take a photo",
    "mediaJob.video": "🎥 Film",
    "mediaJob.file": "📁 Choose a file",
    "mediaJob.prep": "Preparing the photo…",
    "mediaJob.photoType": "Photo format not accepted: take the photo with the camera (JPEG).",
    "fin.time": "{r} of work for {e} estimated",
    "fin.leftOne": "{n} item not ticked as assembled yet",
    "fin.leftMany": "{n} items not ticked as assembled yet",
    "fin.allOk": "All items are ticked as assembled",
    "fin.chkLeftOne": "{n} item without assembly not checked yet",
    "fin.chkLeftMany": "{n} items without assembly not checked yet",
    "fin.chkAllOk": "All items without assembly are checked",
    "fin.probsNone": "No issue reported",
    "fin.probsOne": "{n} issue reported, included in the summary",
    "fin.probsMany": "{n} issues reported, included in the summary",
    "fin.media": "📷 {c} for checking",
    "fin.noMedia": "⚠️ No photo or video: photograph or film the assembly before finishing",
    "fin.photosOne": "{n} photo",
    "fin.photosMany": "{n} photos",
    "fin.videosOne": "{n} video",
    "fin.videosMany": "{n} videos",
    "fin.and": "and",
    "fin.pack": "I wrapped every item in MYOTAKU blankets and plastic film",
    "fin.needPack": "Confirm the packing (MYOTAKU blankets + plastic film) before finishing.",
    "fin.all": "Tick everything as assembled",
    "fin.chkAll": "Tick everything as checked",
    "fin.note": "Notes for the sales rep (optional)",
    "fin.notePh": "E.g. sideboard wrapped in the MYOTAKU blanket, ready in the client cart.",
    "fin.send": "Send the summary to {n}",
    "fin.go": "Finish and send the summary",
    "fin.goNo": "Finish without sending",
    "fin.done": "Job finished",
    "fin.doneSent": "Job finished, summary sent to {n}",
    "recap.none": "Summary not sent (fitter's choice)",
    "mediaJob.itemAdd": "📷 Add",
    "mediaJob.itemTitle": "Item photos and videos",
    "mediaJob.close": "Close",
    "thx.title": "Thank you {names}!",
    "thx.titleTeam": "Thank you, team!",
    "thx.lead": "The assembly for {c} is finished.",
    "thx.msg0": "Thanks to your careful work, the client will receive furniture that is well assembled, checked and protected: you contribute directly to their satisfaction.",
    "thx.msg1": "Great job! Every piece assembled with care means a happy client on delivery day. Thank you for your commitment.",
    "thx.msg2": "Your precision and care make the difference for the client. Thank you for contributing to their satisfaction!",
    "thx.asmOne": "item assembled",
    "thx.asmMany": "items assembled",
    "thx.chkOne": "item checked",
    "thx.chkMany": "items checked",
    "thx.time": "of work",
    "thx.sent": "The summary has been sent to {n}.",
    "thx.close": "Close",
    "links.title": "Links",
    "links.order": "Delivery calendar order #{id}",
    "links.event": "Assembly event in the planning",
    "links.contract": "Contract #{id}",
    "rst.title": "Manager",
    "rst.p": "Resets this assembly: assembled and checked counters, timer, time spent, fitters and the end-of-job summary message. Reported issues, photos and videos are kept.",
    "rst.b": "↺ Reset this assembly",
    "rst.ok": "Confirm: reset everything",
    "rst.no": "Cancel",
    "rst.run": "The assembly is running: it will be stopped.",
    "rst.done": "Assembly reset",
    "rst.undo": "↩️ Undo the reset",
    "rst.undone": "Reset undone",
    "rst.last": "Reset on {d}",
    "media.del": "Delete",
    "media.delOk": "Confirm delete",
    "hist.kpiJobs": "Jobs finished",
    "hist.kpiActual": "Actual time",
    "hist.ofEst": "of {e} estimated",
    "hist.kpiGap": "Overall gap",
    "hist.kpiOk": "Within estimate",
    "hist.perJob": "Per job",
    "hist.statsNote": "Actual time = sum of each fitter's time on the job. Estimate = planning time (assembly + handling).",
    "hist.colNo": "#",
    "hist.colClient": "Client",
    "hist.colWh": "Warehouse",
    "hist.colFitters": "Fitters",
    "hist.gap": "Gap",
    "hist.perFitter": "Per fitter",
    "hist.perFitterNote": "Each fitter's time on finished jobs, compared with their share of the estimate. Visible only to admins.",
    "hist.colJobs": "Jobs",
    "hist.colVs": "Actual / estimate",
    "sup.note": "Minute-by-minute productivity: planned time of the validated items, plus the item in progress up to its planned time, compared with the time spent by the fitters. Alert after 30 min of assembly if the rate drops below 70 %, or if nothing has been validated for 1.5 × the planned time of the largest remaining item (30 min minimum). Slack to Ludo and Emerson, once per job and per alert (checked every hour, 7 am to 6 pm). Fitters do not see this tab.",
    "sup.sentAt": "Slack sent at {t}",
    "sup.sentWait": "Slack at the next check (every hour, 7 am to 6 pm)",
    "sup.open": "Open the job",
    "sup.lastAgo": "last validation: {t} ago",
    "sup.never": "last validation: no validation",
    "sup.dayRate": "Today's rate",
    "sup.per": "Per fitter",
    "sup.perN": "Assembly time, planned time of the validated items and rate (earned ÷ spent). An item assembled by several fitters is shared between them.",
    "sup.perNone": "No assembly time in this period.",
    "sup.period.today": "Today",
    "sup.period.7d": "7 days",
    "sup.period.30d": "30 days",
    "sup.col.fitter": "Fitter",
    "sup.col.minutes": "Assembly time",
    "sup.col.earned": "Time earned",
    "sup.col.rate": "Rate",
    "sup.col.items": "Items validated",
    "sup.col.alerts": "Alerts",
    "sup.spkSpent": "time spent (person-minutes)",
    "sup.spkEarned": "time earned (validated items)",
    "sup.tl": "Minute-by-minute day",
    "sup.tlN": "One row per fitter: each block is a job (one colour per client), white marks are validated items. Hover a block for details.",
    "sup.noTl": "No assembly today.",
    "jobs.readAt": "Planning read on {d} at {t}",
    "job.readOnly": "Read-only access: ask Ludo for Contributor access to start timers and report issues.",
    "stock.readOnly": "You have read-only access: ask Ludo for Contributor access to add furniture.",
    "cartx.readOnly": "Your access is read-only: ask Ludo for edit access to link carts.",
    "access.readOnly": "read only",
    "access.adminCol": "Admin",
    "access.setPassword": "New password",
    "access.savePassword": "Save",
    "access.cancel": "Cancel",
    "access.passwordChanged": "Password changed.",
    "access.ownLocked": "Not possible on your own account.",
    "nav.home": "Home",
    "home.choose": "Choose your section",
    "home.desc.stock": "Client returns intake, labels, inventory",
    "home.desc.preparation": "Today's assemblies, timer, receiving the orders",
    "home.desc.carts": "Scan a cart: whose return is it",
    "home.desc.supervisor": "Jobs in progress, rate, actual vs estimate",
    "home.desc.commercial": "Reserve stock furniture for a client",
    "home.desc.production": "Kit production, rate and article stock",
    "home.desc.receiving": "Read the suppliers' delivery notes",
    "home.desc.access": "Who uses which modules",
    "prod.status.queued": "Waiting",
    "prod.status.running": "In progress",
    "prod.status.paused": "Paused",
    "prod.status.stopped": "Stopped",
    "prod.status.finished": "Finished",
    "prod.status.cancelled": "Cancelled",
    "prod.meta.total": "{t} in total",
    "prod.meta.startedBy": "started by {n}",
    "prod.meta.finishedBy": "finished by {n}",
    "prod.stoppedAt": "⛔ Stopped at {t} by {n} —",
    "prod.time.running": "In progress for {a} (time given {g}) — {d}",
    "prod.time.paused": "⏸ Paused since {at} — time done {a} (time given {g}) — {d}",
    "prod.time.done": "Done in {a} (time given {g}) — {d}",
    "prod.time.exact": "exactly on time",
    "prod.time.more": "+{d} more",
    "prod.time.less": "−{d} less",
    "prod.pauseBanner": "⏸ Preparation paused",
    "prod.stopBanner": "⛔ Preparation stopped — the manager has been told",
    "prod.pauseSince": "since {t}{by} — the timer is stopped. Units already done: {d} / {q}.",
    "prod.pauseBy": " (by {n})",
    "prod.resume": "▶️ Resume the preparation",
    "prod.reportProblem": "⛔ Report a problem (material, boxes, stock)",
    "prod.bravoMany": "🎉 Well done! The {q} units asked for are ready. Click “Mark as done” to close this order.",
    "prod.bravoOne": "🎉 Well done! The unit asked for is ready. Click “Mark as done” to close this order.",
    "prod.newOrderHint": "Choose the kit and the quantity: the order appears at once on the preparer's station.",
    "prod.chip.min": "{n} min / unit",
    "prod.chip.composed": "Made of {n} sub-kits",
    "prod.chip.inStock": "{n} in stock",
    "prod.chip.dispo": "available {n} (reserved {r})",
    "prod.chip.short": "short {n} (reserved {r})",
    "prod.contains": "This kit contains",
    "prod.pickKit": "Select a kit",
    "prod.shortage": "⚠ Not enough article stock for {q} × {k} — missing: {list}{more}.",
    "prod.shortageMore": " + {n} other(s)",
    "prod.notePh": "E.g. client X first, to deliver before 2 pm…",
    "prod.sendOrder": "Send the production order",
    "prod.sending": "Sending…",
    "prod.sendFailed": "Failed — order NOT sent, retry",
    "prod.qtyPreview": "{q} × {m} min =",
    "prod.chip.minShort": "{n} min",
    "prod.sync.ok": "In sync",
    "prod.sync.off": "Not in sync",
    "prod.sync.failed": "⚠️ Save failed",
    "prod.prep.none": "No preparation activity recorded today.",
    "prod.prep.who": "Preparer",
    "prod.prep.kits": "Kits validated",
    "prod.prep.worked": "Time worked",
    "prod.prep.given": "Time given",
    "prod.prep.gap": "Difference",
    "prod.prep.avg": "Avg. / kit",
    "prod.prep.pauses": "Pauses",
    "prod.prep.now": "Right now",
    "prod.prep.noName": "(no name)",
    "prod.prep.nowRunning": "{k} ×{q} — in progress ({d}/{q})",
    "prod.prep.nowPaused": "⏸ {k} ×{q} — paused since {t}",
    "audit.title": "Kit stock consistency check",
    "audit.checkedAt": "checked at {t}",
    "audit.checking": "Checking…",
    "audit.ok": "✓ Everything is consistent: stock, history and delivery exits agree.",
    "audit.errs": "✗ {n} error(s)",
    "audit.warns": "⚠ {n} point(s) to check",
    "audit.checks": "Checked continuously: each stock = its history, each delivery taken out once, no past delivery forgotten, the automatic check up to date.",
    "audit.contract": "contract #{n}",
    "audit.untraced": "{warehouse} — {kit}: untraced movement before {at}",
    "audit.untracedD": "A stock change was not recorded in the history: count this kit again.",
    "audit.mismatch": "{warehouse} — {kit}: stock {stock} ≠ history {history}",
    "audit.mismatchD": "The stock shown does not match the last recorded movement. A change was made without history, or a write failed: count this kit again.",
    "audit.no_history": "{warehouse} — {kit}: stock {stock} with no history at all",
    "audit.no_historyD": "This stock was never entered through the app: check it with a count.",
    "audit.delivered_no_exit": "Delivery {label} marked delivered with no stock exit for: {kits}",
    "audit.delivered_no_exitD": "These kits did not leave stock: count them again.",
    "audit.reserved_but_exited": "Delivery {label}: exit already made for {kits} while it is still \"reserved\"",
    "audit.reserved_but_exitedD": "Check with a count before the next check.",
    "audit.past_reserved": "Delivery {label}: date passed and kits still reserved (stock exit missed)",
    "audit.past_reservedD": "The next automatic check should take it out of stock. If this message is still here after 9 am or 3 pm, the check is not working.",
    "audit.short_exit": "{warehouse} — {kit}: delivery of {at} taken out on insufficient stock ({had} for {need})",
    "audit.short_exitD": "The stock was already too low: check with a count or a missing transfer.",
    "audit.orphan_exit": "{kit} exit of {at} with no matching delivery",
    "audit.orphan_exitD": "The delivery was removed from tracking after the exit: check whether the kit really left.",
    "audit.no_run": "No automatic check recorded",
    "audit.no_runD": "The 9 am / 3 pm task has never recorded a run yet.",
    "audit.run_failed": "Last automatic check failed ({at})",
    "audit.run_failedD": "{message} — no stock exit was made on that run.",
    "audit.run_stale": "No automatic check for {hours} h",
    "audit.run_staleD": "Today's stock exits are not made: the automatic task is not running.",
    "audit.no_client": "{n} reserved delivery(ies) with no client name in the planning",
    "audit.no_clientD": "{contracts}",
    "alloc.incomplete": "⚠️ check incomplete",
    "alloc.stale": "⚠️ more than 26 h without a check",
    "prod.stockTotalTab": "Total",
    "prod.stockTotal": "Total of the 3 warehouses",
    "prod.stockAt": "In stock at {w}",
    "prod.kitsOne": "{n} kit",
    "prod.kitsMany": "{n} kits",
    "prod.pill.allocated": "allocated {n}",
    "prod.pill.allocatedT": "Reserved for approved client deliveries to come",
    "prod.pill.toSend": "to send {n}",
    "prod.pill.toSendT": "Missing at Dielsdorf / Berlin, to cover with a transfer from Penthalaz",
    "prod.pill.dispo": "available {n}",
    "prod.pill.short": "short {n}",
    "prod.short.wh": "⚠ {w} — {k}: short {n} (allocated {a}, in stock {s})",
    "prod.short.pz": "⚠ Penthalaz — {k}: short {n} (allocated {a}{send}, in stock {s}{prod})",
    "prod.short.send": " + to send {n}",
    "prod.short.inProd": ", already being made {n}",
    "prod.short.makeOrder": "Prepare an order ×{n}",
    "prod.alloc.empty": "No approved delivery with kits for now.",
    "prod.alloc.past": " — past delivery, waiting for the “Delivered” status",
    "prod.alloc.artPz": "Single article — taken from article stock on the delivery day",
    "prod.alloc.artElse": "Single article — no article stock kept at {w}",
    "prod.alloc.type.CT": "Short term",
    "prod.alloc.type.LT": "Long term",
    "prod.alloc.type.COMPL": "Complement",
    "prod.alloc.type.VENTE": "Sale",
    "prod.alloc.type.HS": "Home staging",
    "prod.alloc.type.MAINT": "Maintenance",
    "prod.alloc.type.AUTRE": "Other",
    "prod.alloc.type.BTR": "BTR",
    "prod.alloc.type.EVT": "Contract as event",
    "prod.short.makeTransfer": "Prepare a transfer ×{n}",
    "prod.send.title": "Send kits to a warehouse",
    "prod.send.hint": "Choose the warehouse, enter the quantities sent from Penthalaz, then “Send”. Penthalaz's stock goes down at once; the kits are on the way until the destination receives them (Receiving tab).",
    "prod.send.to": "To {w}",
    "prod.send.qtyFor": "Quantity sent — {k}",
    "prod.send.notePh": "E.g. truck of 26.09, delivery note no.…",
    "prod.send.total": "Total: {n} kit(s) from Penthalaz to {w}.",
    "prod.send.none": "Enter at least one quantity.",
    "prod.send.over": "Not enough stock at Penthalaz — {list}.",
    "prod.send.button": "Send to {w} ({n} kit(s))",
    "prod.send.buttonEmpty": "Send to {w}",
    "prod.send.ask": "Send {n} kit(s) from Penthalaz to {w}?",
    "prod.send.yes": "Yes, send",
    "prod.send.done": "✓ Shipment recorded: {n} kit(s) to {w}. Penthalaz's stock is updated; {w} adds them on receipt.",
    "prod.send.log": "Last shipments",
    "prod.send.logEmpty": "No shipment recorded yet.",
    "prod.send.inTransit": "on the way",
    "prod.view.resp": "Manager",
    "prod.view.prep": "Preparer",
    "prod.orders": "Production orders",
    "prod.ordersHint": "Newest first. The progress is updated by the preparer as it happens.",
    "prod.ordersEmpty": "No order sent yet.",
    "prod.queueHint": "Orders sent by the manager arrive here, oldest first.",
    "prod.youAre": "You are:",
    "prod.youArePh": "Your first name (needed to start)",
    "prod.nameFirst": "✋ First enter your first name in “You are”.",
    "prod.stockInPlace": "Stock already in place",
    "demand.ofMonth": "(month total {n})",
    "demand.segment": "{k} · {m}: {n} kit(s) (month total {total})",
    "prod.entry.label": "Entry:",
    "prod.entry.add": "➕ Add a quantity",
    "prod.entry.set": "✏️ Correct the total",
    "prod.entry.addHelp": "Type only the quantity received (e.g. 30), then “Add” or Enter: it is added to the current stock, no sums. A negative number (e.g. -2) removes.",
    "prod.entry.setHelp": "Type the new counted total: it replaces the current quantity.",
    "prod.entry.now": "Current quantity",
    "prod.entry.addPh": "+ qty",
    "prod.entry.addFor": "Quantity to add — {k}",
    "prod.entry.totalFor": "New total — {k}",
    "prod.entry.addBtn": "Add",
    "prod.entry.askAdd": "Are you sure you want to add {n} to the quantity of {k}?",
    "prod.entry.askDown": "Are you sure you want to decrease the quantity of {k} by {n}?",
    "prod.entry.yesAdd": "Yes, add",
    "prod.entry.yesDown": "Yes, decrease",
    "prod.entry.saved": "✅ Thank you, the new quantity is saved in the stock.",
    "prod.entry.notSaved": "⚠️ The quantity could NOT be saved (connection?). Try again.",
    "prod.entry.tapClose": "Tap to close",
    "prod.history.open": "Click to see the history",
    "prod.history.title": "History — {k} ({w})",
    "prod.history.close": "Close",
    "prod.history.current": "Current stock: {n}",
    "prod.history.none": "No movement recorded.",
    "inv.src.correction": "correction",
    "inv.src.plus_minus": "± button",
    "prod.delete": "Delete",
    "prod.deleteYes": "Confirm the deletion",
    "prod.unitSummary": "Unit no. {n} being assembled — {c} / {m} articles ticked",
    "inv.lastAt": "Last count at {w}: {when} by {who} — {n} kit(s) counted, {d} difference(s) corrected.",
    "inv.noneAt": "No count recorded for {w}.",
    "inv.kit": "Kit",
    "inv.app": "App",
    "inv.gap": "Difference",
    "inv.summary": "{n} kit(s) counted — {d} difference(s) to correct",
    "inv.start": "Type the quantities counted to start.",
    "inv.ask": "Confirm the count of {w}?",
    "inv.askDetail": "{n} kit(s) counted — {d} correction(s)",
    "inv.yes": "Yes, confirm the count",
    "inv.done": "Count {w}: {n} kit(s) counted, {d} correction(s)",
    "inv.historyHint": "Every quantity change — typed by hand, the +/− buttons, or the automatic credit of each kit made (as soon as the unit is validated) — is recorded here with its date and time.",
    "inv.countedCol": "Counted",
    "art.hint": "Quantity on the shelf for each article, the same reference as on the labels. It goes down by itself when the preparer ticks an article in a checklist; when a delivery arrives, “➕ Add” mode: type only the quantity received, the app does the sum; after a count, “✏️ Correct the total” mode. Click an article to open its sheet.",
    "art.total": "{q} units in stock across {n} references",
    "art.noMatch": "No article matches the search.",
    "art.linenShared": "Linen — articles shared by several sizes",
    "art.refCount": "{n} ref.",
    "art.sharedBy": "Reference shared by: {list}",
    "art.chip.sku": "Syst. {s}",
    "art.chip.ref": "Supplier ref. {s}",
    "art.chip.confirm": "to confirm",
    "art.chip.open": "Sheet ›",
    "sheet.hist.date": "Date",
    "sheet.hist.move": "Movement",
    "sheet.hist.stock": "Stock",
    "sheet.hist.loading": "Loading the history…",
    "sheet.src.manual": "Added by hand",
    "sheet.src.correction": "Total corrected (count)",
    "sheet.src.plus_minus": "+ / − button",
    "sheet.src.production": "Taken for a kit (checklist)",
    "sheet.src.restock": "Put back in stock (unit removed)",
    "sheet.src.receipt": "Supplier delivery",
    "sheet.src.reception_check": "Correction while checking a reception",
    "sheet.src.delivery": "Client delivery (single article)",
    "sheet.src.deliveryTo": "Client delivery — {c}",
    "sheet.src.inventory": "Inventory (physical count)",
    "sheet.src.transfer": "Transfer between warehouses",
    "sheet.det.order": "order {k} ×{n}",
    "sheet.det.unticked": "(unticked)",
    "sheet.det.supplier": "supplier {s}",
    "sheet.det.receipt": "receipt {n}",
    "sheet.det.contract": "contract #{n}",
    "sheet.det.by": "by {w}",
    "sheet.det.short": "{n} missing on the shelf",
    "inv.src.restock": "put back in stock",
    "artrec.supplier": "Supplier (optional)",
    "artrec.hintPaste": "On a computer you can also paste a screenshot (Ctrl+V) or drop a file on this card. If the quantity received is not on the document, write it in the note.",
    "note.notAdded": "⚠ Not added: {list}",
    "note.othersReady": " — the other files are ready.",
    "note.maxImages": "{f} (maximum {n} images)",
    "note.badFormat": "{f} — format not supported",
    "artrec.photoN": "Photo {n}",
    "prod.printSheets": "Print {n} carton sheets",
    "prod.printSheet": "Print 1 carton sheet",
    "ident.phase.type": "Identifying the furniture type…",
    "ident.phase.retour": "Comparing the photo with the return…",
    "ident.phase.catalogue": "Searching the Myotaku catalogue…",
    "ident.cancel": "Cancel",
    "ident.recognize": "Recognise the piece",
    "ident.noAi": "Recognition isn't available here: pick the piece manually.",
    "ident.aiLimit": "Too many requests right now. Try again shortly or search manually.",
    "ident.hintGrid": "Photograph the whole piece before wrapping it, then compare with the catalogue photo.",
    "stock.qtyAuto": "{q} expected in this return, {k} already added: quantity set automatically.",
    "stock.qtyOver": "This return expected {q} and {k} are already in: this piece is extra.",
    "stock.qtyHint": "Same reference, same condition and same pallet: {n} records, {n} stock numbers and {n} labels, one per piece. If one of them is in another condition, add it separately.",
    "stock.qtyNums": "Numbers given: {list}.",
    "stock.unitOf": "Item {i}/{n}",
    "compo.dialogTitle": "Furniture composition",
    "compo.dialogHint": "How many pieces is this furniture stored in? E.g. Hudson = sofa + chaise longue (2 pieces). Each piece gets its own label « Piece 1/2 », « Piece 2/2 ». The setting is kept for this product.",
    "compo.sofa": "Sofa",
    "compo.chaise": "Chaise longue",
    "compo.saved": "Composition saved",
    "compo.toConfirm": "to confirm",
    "compo.pieceOf": "Piece {i}/{n}",
    "compo.cancel": "Cancel",
    "ident.yourPhoto": "Your photo",
    "ident.catPhoto": "Catalogue photo",
    "ident.refMyo": "Myotaku ref",
    "ident.refSup": "Supplier ref",
    "ident.generic": "generic item without supplier ref",
    "ident.supplier": "Supplier",
    "ident.type": "Type",
    "ident.size": "Size",
    "ident.notInCat": "not in online catalogue",
    "rec.upcoming": "Upcoming deliveries",
    "rec.past": "Past deliveries",
    "rec.toSchedule": "to schedule",
    "rec.runDone": "Assembly done",
    "rec.runGoing": "Assembly in progress",
    "rec.assembledN": "assembled {k}/{n}",
    "rec.po": "PO #{n}",
    "stock.orderHint": "Order",
    "note.stop": "Stop",
    "note.stopped": "Reading stopped",
    "cam.title": "PC camera",
    "cam.hint": "Put the item or the page in front of the camera, then take the photo. You can take several in a row.",
    "cam.shot": "📸 Take photo",
    "cam.file": "Choose a file instead",
    "cam.close": "Close",
    "cam.which": "Camera",
    "cam.starting": "Starting the camera…",
    "cam.added": "Photo added ✓",
    "cam.e.denied": "Camera access denied. Allow the camera for this site (camera or lock icon in the address bar), then reopen. Otherwise choose a file.",
    "cam.e.none": "No camera found on this PC: plug in the USB camera and reopen.",
    "cam.e.busy": "The camera is already used by another app (Teams, Windows Camera…): close it and reopen.",
    "cam.e.blocked": "This page is not allowed to use the camera in this browser. Use “Choose a file”.",
    "cam.e.other": "The camera could not start. Choose a file instead.",
    "cam.tab": "📱 Use the tablet (photo station)",
    "cam.waitT": "Photo with the tablet",
    "cam.wait": "Request sent to the tablet: “{l}”. On the tablet, open “📱 Photo station” (top of the screen) and tap “Take photo”. Photos arrive here right away.",
    "cam.waitRec": "Request sent to the tablet. On the tablet, open “📱 Photo station”: it switches to the Receiving tab to photograph the pages, then “Read”. The result will appear here.",
    "cam.got0": "Waiting for the tablet…",
    "cam.fin": "Done",
    "st.btn": "📱 Photo station",
    "st.title": "Photo station",
    "st.hint": "This tablet is the PC's camera: every request made on the PC shows up here. Keep this page open.",
    "st.none": "No request yet. On the PC, click 📷 Photo then “Use the tablet”.",
    "st.shot": "📸 Take photo",
    "st.rec": "📄 Photograph the delivery note (Receiving tab)",
    "st.quit": "Leave the photo station",
    "st.gen": "Assembly photos",
    "st.recL": "Delivery note (Receiving)",
    "st.agoNow": "just now",
    "st.ago": "{m} min ago",
    "st.up": "Uploading…",
    "sdocs.photo": "📷 Photo of the delivery note or boxes",
    "sdocs.photosT": "Photos taken on site",
    "st.docs": "Supplier documents",
    "com.noticeFailed": "Slack message to logistics not delivered: {error}",
    "stock.supplierOrder": "Supplier order",
    "cartons.unknownTitle": "Furniture without carton references",
    "cartons.unknownHint": "Composite furniture delivered in several cartons whose references no PDF delivery note of its orders has given yet. Photographing its delivery note here records them.",
    "cartons.nCartons": "{n} cartons",
    "cartons.checked": "Checked {when}",
    "cartons.allKnown": "Every piece of furniture in several cartons has its references.",
    "prod.devBanner1": "This application was developed by {name}. If you have any problem using the application, please see the developer, {name}.",
    "prod.devBanner2": "Please do not use the WhatsApp group « MYOTAKU - Computeur & Mobile » to report a problem.",
    "guide.title": "🎬 Fitter video: your role, step by step (6 min)",
    "guide.sub": "no voice, on-screen captions",
    "guide.hide": "Hide the video",
    "guide.show": "Show the video",
    "job.productPage": "Product page",
    "maybe.titleOne": "{n} item ordered under a similar name: to check",
    "maybe.titleMany": "{n} items ordered under a similar name: to check",
    "maybe.hint": "Found in the Delivery calendar but not linked to this client for sure (missing from the client list, or another apartment's name). Check with the sales rep before assembling or delivering: it is not counted in the assembly time.",
    "stock.tlIn": "in stock",
    "stock.tlOut": "out",
    "maybe.unseenOne": "{n} item ordered for this client that may already have been delivered",
    "maybe.unseenMany": "{n} items ordered for this client that may already have been delivered",
    "maybe.unseenHint": "They arrived before {date}, and the app cannot see this client's deliveries before that date. Check with the sales rep whether they were already delivered before assembling or delivering them: they are not counted in the assembly time.",
    "arch.t": "Archived clients",
    "arch.done": "Client archived: it is in the Archived tab.",
    "arch.undone": "{c} is back in the lists.",
    "jobs.asm": "Assembly",
    "jobs.deliv": "Delivery",
    "jobs.est": "Estimate",
    "jobs.progressT": "Progress {p}%: work done, weighted by each item's estimated time · marker: {t}% of the estimated time spent",
    "job.salesRep": "Sales rep",
    "job.noRep": "not found",
    "job.noItems": "No item list read from the order: see the Delivery calendar links below.",
    "issue.reported": "Reported issues",
    "issue.report": "Report an issue",
    "issue.sendFailed": "Slack send failed: the message did not go out.",
    "issue.sendPending": "Waiting to be sent on Slack",
    "issue.qOfN": "{q} of {n}",
    "item.probOne": "⚠️ {n} issue reported",
    "item.probMany": "⚠️ {n} issues reported",
    "recap.failed": "Summary not sent",
    "recap.unclear": "Summary being sent",
    "maybe.ref": "reference",
    "st.sentOne": "{n} photo sent",
    "st.sentMany": "{n} photos sent",
    "cam.gotOne": "✓ {n} photo received from the tablet",
    "cam.gotMany": "✓ {n} photos received from the tablet",
    "cam.doneOne": "Finish and read ({n} page)",
    "cam.doneMany": "Finish and read ({n} pages)",
    "mediaJob.saved": "Saved",
    "mediaJob.tooBig": "File too large ({mb} MB, {max} MB maximum).",
    "mediaJob.videoBig": "Video too large ({mb} MB, {max} MB maximum): film a shorter clip.",
    "mediaJob.videoType": "Video format not accepted: film with the camera (MP4).",
    "mediaJob.fileType": "File format not accepted: choose a photo or a video.",
    "docs.unread": "This order's documents could not be read just now: try again later.",
    "com.readOnly": "Read-only access: ask Ludo for Contributor access to reserve furniture.",
    "prod.readOnly": "Read-only access: ask Ludo for Contributor access to produce kits and receive articles.",
    "sup.kAlerts": "Current alerts",
    "sup.alT": "Alerts",
    "sup.noAl": "No alert right now.",
    "sup.live": "Jobs in progress",
    "sup.stRun": "running",
    "sup.stPau": "paused",
    "sup.nm": "not measurable: no item in the job",
    "sup.readOnly": "Read-only access: ask Ludo for Contributor access to mark the permanent crew.",
    "note.modeNow": "Photos and PDFs are read immediately.",
    "note.readOne": "Read and check against the Delivery calendar ({n} page)",
    "note.readMany": "Read and check against the Delivery calendar ({n} pages)",
    "artrec.sendOne": "Send the delivery ({n} page)",
    "artrec.sendMany": "Send the delivery ({n} pages)",
    "note.pagesOne": "{n} page",
    "note.pagesMany": "{n} pages",
    "note.linesOne": "{n} line read",
    "note.linesMany": "{n} lines read",
    "note.clientsOne": "{n} client",
    "note.clientsMany": "{n} clients",
    "note.colliOne": "{n} package",
    "note.colliMany": "{n} packages",
    "note.asmOn": "assembly {d}",
    "note.err.rate_limited": "Too many reads at once or usage limit reached: try again later.",
    "note.err.unavailable": "Reading is not allowed for this app at the moment: try again later.",
    "note.err.photo": "Photo rejected (format or size): take it again.",
    "note.err.failed": "Reading interrupted: try again.",
    "note.notMatchedRetry": "Read, but not yet checked against the Delivery calendar: check against it again.",
    "brand.z": "Z",
    "brand.furniture": "Furniture",
    "jobs.titleMode": "montage",
    "issue.sending": "sending",
    "issue.notSent": "message not sent to {n}",
    "note.shortOne": "{n} missing",
    "note.shortMany": "{n} missing",
    "recv.unmatchedOne": "{n} unmatched",
    "recv.unmatchedMany": "{n} unmatched",
    "recv.completeOne": "{n} complete",
    "recv.completeMany": "{n} complete",
    "recv.missingOne": "{n} missing",
    "recv.missingMany": "{n} missing",
    "job.start": "Start",
    "job.writeFailed": "Could not save: check the connection and try again.",
    "media.mb": "MB",
    "recv.readOnly": "Read-only access to Receiving: ask Ludo for Contributor access.",
    "issue.sendingNow": "Sending…",
    "issue.netFailed": "Could not send: check the connection and try again.",
    "crew.pickWarehouse": "Pick a warehouse above: permanent crew is chosen per warehouse.",
    "media.failVideo": "The video could not be saved: check the connection and try again.",
    "media.failPhoto": "The photo could not be saved: check the connection and try again.",
    "error.loadFailed": "Could not load: check the connection and try again.",
  },
  de: {
    "recv.boxesPh": "Karton-Nummern (falls mehrere), durch Kommas getrennt",
    "job.dmToTick": "Im Delivery Manager abzuhaken: {n}",
    "art.title": "Artikelbestand",
    "art.other": "Andere",
    "art.refs": "Referenzen",
    "art.save": "Zählung speichern ({n} Referenz(en))",
    "alloc.sync": "Mit der Planung abgleichen",
    "alloc.result": "{q} Lieferungen gelesen · {c} erstellt · {u} aktualisiert · {r} freigegeben · {d} ausgebucht",
    "alloc.unknown": "Nicht erkannt",
    "docs.title": "Dokumente & Checklisten",
    "docs.hint": "Checklisten zum Drucken für jeden Kit-Typ — auch ohne laufenden Produktionsauftrag — und die PDF-Referenzdokumente.",
    "docs.refTitle": "Referenzdokumente (PDF)",
    "docs.refHint": "Dieselben Dokumente, die bereits gedruckt in die Kartons gelegt / am Regal ausgehängt sind.",
    "docs.open": "Öffnen",
    "docs.selectAll": "Alle auswählen",
    "docs.selectNone": "Keine",
    "docs.print": "Auswahl drucken ({n})",
    "docs.select": "Diese Checkliste drucken",
    "alloc.schedule": "Automatische Prüfung Montag bis Freitag um 9:00 und 15:00 Uhr, und Aktualisierung um 8:35, von 10:35 bis 14:35, um 16:35 und 17:35 Uhr",
    "alloc.points": "Zu prüfende Punkte (letzte Prüfung)",
    "alloc.point.partial": "{date} · {wh} · Vertrag #{contract} · {client}: Lieferung teilweise abgehakt ({n}/{total}): alle Kits ausgebucht, zu prüfen",
    "alloc.point.notTicked": "⚠ Lieferung vom {date} · {wh} · Vertrag #{contract} noch nicht vom Team abgehakt: Bestand nicht abgezogen",
    "alloc.point.stillReserved": "❌ Lieferung vom {date} · {wh} · Vertrag #{contract} weiterhin reserviert (nicht ausgebucht: {reason})",
    "alloc.point.reason.missing": "Termin im Planning nicht gefunden",
    "alloc.point.reason.unapproved": "Freigabe entzogen",
    "alloc.point.reason.feed": "das Planning hat nicht geantwortet",
    "alloc.point.postponed": "Lieferung vom {date} · {wh} · Vertrag #{contract} auf den {newDate} verschoben, nachdem die Kits ausgebucht wurden: bei Bedarf von Hand zurückbuchen",
    "alloc.point.short": "⚠ {wh} — {code}: Bestand beim Ausbuchen ungenügend ({before} für {qty}): Zählung oder Versand prüfen · Vertrag #{contract}",
    "alloc.point.shortArticle": "⚠ Artikelbestand ungenügend: {code} {before} für {qty} · Vertrag #{contract}",
    "alloc.lastRun": "Letzte Prüfung",
    "alloc.auto": "automatisch",
    "alloc.never": "Noch keine Prüfung erfasst.",
    "alloc.next": "Nächste",
    "alloc.failed": "fehlgeschlagen",
    "crew.title": "Team",
    "crew.none": "Laut Myotaku hat hier in den letzten 3 Wochen niemand gearbeitet.",
    "crew.lastWorked": "letzter Tag",
    "crew.permanent": "Fest",
    "crew.hint": "Feste Mitarbeitende werden beim Start einer Montage zuerst angeboten.",
    "compo.title": "Teile",
    "compo.single": "1 Teil",
    "compo.edit": "Ändern",
    "compo.hint": "In wie vielen Teilen wird es gelagert? Ein Etikett pro Teil. Für dieses Produkt gespeichert.",
    "compo.save": "Speichern",
    "stock.palletPh": "z. B. 12",
    "stock.rackPh": "z. B. R3-2",
    "stock.recent": "Kürzlich",
    "stock.upcoming": "Kommend",
    "stock.src.return": "Kundenrückgabe",
    "stock.src.reception": "Lieferanteneingang",
    "stock.by.person": "von Hand",
    "stock.by.ai": "von Claude",
    "stock.leaveSure": "Ja, aus dem Lager nehmen",
    "com.cancelSure": "Ja, stornieren",
    "com.done": "{n} Möbel für {client} reserviert — Referenz {ref}.",
    "inv.title": "📋 Kit-Inventur",
    "inv.last": "Letzte Inventur {when} von {who} — {n} Abweichung(en)",
    "inv.counted": "gezählt",
    "inv.save": "Inventur bestätigen",
    "inv.hint": "Nach einer Zählung: Lager wählen, für jedes Kit die gezählte Zahl eingeben (leer lassen, was nicht gezählt wurde), Abweichungen prüfen, dann „Inventur bestätigen“. Der Bestand der App wird durch deine Zahlen ersetzt und jede Korrektur bleibt im Verlauf.",
    "inv.history": "Verlauf des Kit-Bestands",
    "inv.noHistory": "Noch keine Änderung erfasst.",
    "inv.src.production": "Produktion",
    "inv.src.transfer": "Transfer",
    "inv.src.delivery": "Lieferung",
    "inv.src.inventory": "Inventur",
    "inv.src.receipt": "Wareneingang",
    "inv.src.reception_check": "Prüfung Wareneingang",
    "inv.src.manual": "von Hand hinzugefügt",
    "nav.carts": "Wagen",
    "nav.commercial": "Verkauf",
    "carts.sub": "Ein QR auf jedem Wagen: beim Kunden mit der Rückgabe verknüpfen, im Lager scannen, um zu wissen, wem er gehört.",
    "carts.number": "Wagen-Nr.",
    "carts.numberPh": "z. B. 42",
    "carts.open": "Öffnen",
    "carts.back": "← Alle Wagen",
    "carts.missing": "Vergangene Rückgaben ohne gescannten Wagen",
    "carts.missingHint": "Der Wagen wurde wohl beim Kunden nicht gescannt: bei Ankunft scannen und hier verknüpfen.",
    "carts.linked": "Wagen im Umlauf",
    "carts.linkedNone": "Noch kein Wagen verknüpft.",
    "carts.grid": "Alle {n} Wagen",
    "carts.gridHint": "Eine Nummer antippen, um den Wagen zu öffnen.",
    "carts.linkT": "Diesen Wagen mit der Kundenrückgabe verknüpfen",
    "carts.search": "Kunde, Name oder Ereignis-Nr. suchen",
    "carts.linkedBy": "Verknüpft von {who} am {when}",
    "carts.arrivedAt": "angekommen {when}",
    "carts.arrive": "Im Lager angekommen",
    "carts.toStock": "Möbel ins Lager aufnehmen",
    "carts.free": "Wagen geleert — freigeben",
    "carts.freeConfirm": "Bestätigen: Wagen freigeben",
    "carts.st.free": "Frei",
    "carts.st.on_the_road": "Beim Kunden oder unterwegs",
    "carts.st.at_warehouse": "Im Lager · zu leeren",
    "carts.st.lost": "Seit 14 Tagen ohne Nachricht",
    "com.s1": "Vertrag",
    "com.s2": "Möbel aus dem Lager",
    "com.s3": "Prüfen und senden",
    "com.change": "Ändern",
    "com.searchContract": "Kundenname oder Vertragsnummer",
    "com.reaches": "Lieferungen der Planung bis {d} (Grenze der Myotaku-Quelle).",
    "com.noStock": "Keine verfügbaren Möbel in diesem Lager.",
    "com.available": "verfügbar",
    "com.prepare": "Vorbereitung im Lager",
    "com.prepareHint": "Vorschlag: der Werktag vor der Lieferung.",
    "com.note": "Notiz für die Logistik",
    "com.notePh": "Z. B. Sofabeine abnehmen, mit dem Tisch desselben Kunden liefern",
    "com.send": "{n} Möbel reservieren",
    "com.sendHint": "Die Möbel sind sofort reserviert. Das Ereignis „PRÉPARATION CLIENT“ wird noch von Hand in der Planung angelegt; seine Nr. in der Reservierung eintragen.",
    "com.reservations": "Reservierungen",
    "com.none": "Keine Reservierungen.",
    "com.st.sent": "gesendet",
    "com.st.prepared": "vorbereitet",
    "com.st.cancelled": "storniert",
    "com.prepareShort": "Vorb.",
    "com.deliverShort": "Lief.",
    "com.prepared": "Vorbereitet — aus dem Lager",
    "com.cancel": "Stornieren",
    "com.eventPh": "Ereignis-Nr.",
    "com.saveEvent": "Speichern",
    "nav.stock": "Lager",
    "stock.tab.intake": "Eingang",
    "stock.tab.inventory": "Bestand",
    "stock.tab.returns": "Retouren",
    "stock.tab.labelling": "Etikettierung",
    "ret.title": "Retouren der Planung",
    "ret.hint": "Erwartete Artikel pro Retoure und bereits eingelagerte Möbel.",
    "ret.empty": "Retouren (kurzfristig, langfristig, Ergänzungen) der letzten 2 Wochen und der kommenden Woche erscheinen hier.",
    "ret.entered": "eingelagert",
    "ret.noItems": "Keine Möbelliste für diese Retoure.",
    "rec.title": "Etikettierung der Wareneingänge",
    "rec.hint": "Für Kunden bestellte Möbel (Lieferplanung). Ein Tippen auf „Etikettieren“ = Etikett bereit, nichts weiter.",
    "rec.itemsHint": "Jedes Möbel nach Montage und Verpackung etikettieren, bevor es in den Kundenwagen kommt.",
    "rec.labelOne": "Etikettieren",
    "rec.labelAll": "Die {n} restlichen etikettieren",
    "rec.labeling": "Etikettierung…",
    "rec.empty": "Keine Lieferung mit zu etikettierenden Möbeln.",
    "rec.labelled": "etikettiert",
    "rec.assembled": "montiert",
    "rec.for": "Für",
    "rec.delivery": "Lieferung",
    "rec.unit": "Teil",
    "stock.tagline": "Kundenmöbel zurück ins Lager",
    "stock.pickWarehouse": "Oben ein Lager wählen: jedes Möbel kommt in ein bestimmtes Lager.",
    "stock.s1": "Kundenrückgabe",
    "stock.s1hint": "Die Rückgabe aus der Planung wählen, mit der das Möbel kam.",
    "stock.s2": "Möbel",
    "stock.s2hint": "Das Möbel antippen, das vor Ihnen steht. Nicht in der Liste? Im ganzen Katalog suchen.",
    "stock.s2hintNone": "Ohne geplante Rückgabe: das Möbel im Katalog suchen.",
    "stock.chosen": "Gewähltes Möbel",
    "stock.nothingChosen": "Noch kein Möbel gewählt.",
    "stock.s3": "Zustand",
    "stock.cond.new": "Neu",
    "stock.cond.good": "Gut",
    "stock.cond.good.hint": "Wieder vermietbar",
    "stock.cond.light_marks": "Mittel",
    "stock.cond.light_marks.hint": "Leichte Spuren",
    "stock.cond.damaged": "Beschädigt",
    "stock.cond.damaged.hint": "Reparatur nötig",
    "stock.damagePh": "Was beschädigt ist — z. B. 10 cm Kratzer auf der Platte, linkes Bein gespalten",
    "stock.s4": "Verpackung und Lagerplatz",
    "stock.blanket": "Myotaku-Decke",
    "stock.film": "Plastikfolie",
    "stock.unwrapped": "Nicht verpackt",
    "stock.pallet": "Paletten-Nr.",
    "stock.palletShort": "Palette",
    "stock.rack": "Regal-Nr.",
    "stock.qty": "Gleiche Möbel",
    "stock.placeHint": "Palette und Regal werden für das nächste Möbel übernommen. Mehrere gleiche Möbel: je ein Eintrag, eine Nummer und ein Etikett.",
    "stock.save": "{n} Möbel speichern und Etiketten vorbereiten",
    "stock.noReturns": "Keine Rückgaben für dieses Lager diese oder nächste Woche.",
    "stock.today": "Heute",
    "stock.contract": "Vertrag",
    "stock.entered": "erfasst",
    "stock.noReturn": "Ohne geplante Rückgabe",
    "stock.noItems": "Diese Rückgabe enthält keine Katalogmöbel: unten suchen.",
    "stock.searchPh": "Name, Myotaku-Ref., Lieferanten-Ref.…",
    "stock.search": "Im ganzen Katalog suchen",
    "stock.searchUnavailable": "Die Katalogsuche ist auf diesem Myotaku-Server noch nicht verfügbar.",
    "stock.searchNone": "Kein Produkt passt.",
    "stock.filterPh": "Lager-Nr., Name, Palette…",
    "stock.status.held": "Am Lager und reserviert",
    "stock.status.in_stock": "Am Lager",
    "stock.status.reserved": "Reserviert",
    "stock.status.left": "Ausgegangen",
    "stock.pieces": "Möbel",
    "stock.emptyInventory": "Hier noch keine Möbel.",
    "stock.days": "T",
    "stock.fromReturn": "Rückgabe",
    "stock.move": "Umlagern",
    "stock.reprint": "Etikett neu drucken",
    "media.title": "Fotos und Videos",
    "media.add": "Foto oder Video hinzufügen",
    "media.uploading": "Wird hochgeladen…",
    "media.empty": "Noch keine Fotos.",
    "media.video": "Video",
    "stock.leave": "Aus dem Lager nehmen",
    "stock.ev.received": "eingegangen",
    "stock.ev.identified": "identifiziert",
    "stock.ev.labelled": "etikettiert",
    "stock.ev.moved": "umgelagert",
    "stock.ev.graded": "Zustand geändert",
    "stock.ev.reserved": "reserviert",
    "stock.ev.released": "freigegeben",
    "stock.ev.left": "ausgegangen",
    "stock.labelsReady": "{n} Etikett(en) bereit",
    "stock.print": "Drucken",
    "stock.close": "Schliessen",
    "stock.part": "Teil",
    "hist.live": "Laufend",
    "hist.past": "Ist gegen Soll",
    "hist.none": "Noch keine abgeschlossene Montage.",
    "hist.when": "Beendet",
    "hist.estimate": "Geplant",
    "hist.actual": "Ist",
    "demand.title": "Angeforderte Kits pro Monat",
    "demand.hint": "Letzte 6 Monate, ohne stornierte Auftr\xe4ge. F\xfcr Details auf eine Farbe zeigen.",
    "demand.chart": "Diagramm",
    "demand.table": "Tabelle",
    "demand.empty": "Kein Produktionsauftrag in den letzten 6 Monaten.",
    "demand.kits": "Kits",
    "demand.kit": "Kit",
    "demand.total": "Total",
    "demand.never": "Keine Nachfrage in 6 Monaten",
    "nav.supervisor": "Leitung",
    "sup.running": "Laufende Montagen",
    "sup.people": "Monteure im Einsatz",
    "sup.none": "Keine Montage in Arbeit.",
    "sup.yield": "Leistung",
    "sup.earned": "erreicht",
    "sup.spent": "aufgewendet",
    "sup.alertYield": "Leistung {p} % (Schwelle {l} %)",
    "sup.alertIdle": "seit {m} nichts bestätigt (erwartet: höchstens {l})",
    "nav.receiving": "Wareneingang",
    "recv.title": "Lieferantenbelege",
    "recv.new": "Neuer Lieferschein",
    "recv.supplier": "Lieferant",
    "recv.docRef": "Beleg-Nr.",
    "recv.order": "Bestellung",
    "recv.lines": "Zeilen",
    "recv.sku": "Referenz",
    "recv.received": "Erhalten",
    "recv.ordered": "Bestellt",
    "recv.boxes": "Kartons",
    "recv.addLine": "Zeile hinzuf\u00fcgen",
    "recv.save": "Speichern",
    "recv.reconcile": "Lesen und pr\u00fcfen",
    "recv.complete": "vollst\u00e4ndig",
    "recv.missing": "fehlt",
    "recv.extra": "zu viel",
    "recv.unmatched": "nicht zugeordnet",
    "recv.none": "Noch keine Belege.",
    "recv.help": "Den Beleg vor dem \u00d6ffnen fotografieren und mit der Bestellung vergleichen.",
    "ident.title": "Foto zur Erkennung",
    "ident.hint": "Fotografieren Sie das ganze Möbel vor dem Verpacken. Die App sucht zuerst unter den Artikeln der Rücknahme.",
    "ident.take": "📷 Foto aufnehmen",
    "ident.retake": "Neu aufnehmen",
    "ident.sending": "Foto wird gesendet…",
    "ident.analyzing": "Erkennung: zuerst der Möbeltyp, dann der Vergleich mit der Rücknahme (und bei Bedarf dem Katalog)…",
    "ident.pickThis": "Das ist das Möbel",
    "ident.other": "Andere Möglichkeiten",
    "ident.searchCatalogue": "Im ganzen Katalog suchen",
    "ident.seen": "Auf dem Foto",
    "ident.sure": "Sicher",
    "ident.likely": "Wahrscheinlich",
    "ident.check": "Prüfen",
    "ident.noMatch": "Kein Artikel der Rücknahme passt.",
    "ident.noMatchAny": "Kein identisches Produkt gefunden, weder in der Rücknahme noch im Katalog. Von Hand suchen oder das Foto neu aufnehmen (ganzes Möbel, von vorne).",
    "ident.notInRetour": "Nicht unter den Artikeln der Rücknahme gefunden: hier die beste Übereinstimmung im Katalog. Prüfen Sie die gewählte Rücknahme.",
    "ident.failed": "Die Erkennung ist fehlgeschlagen. Erneut versuchen oder von Hand suchen.",
    "ident.fromReturn": "aus der Rücknahme",
    "ident.fromCatalogue": "Katalog",
    "sheet.open": "Artikelblatt öffnen (Referenzen, Einkaufsbedingungen, Verlauf)",
    "sheet.stockNow": "Aktueller Bestand:",
    "sheet.kitsPossible": "also {n} Kit(s) möglich",
    "sheet.references": "Referenzen",
    "sheet.refZ": "Myotaku-Ref. (Z)",
    "sheet.refSystem": "Myotaku-Systemref.",
    "sheet.systemName": "Name im System",
    "sheet.supplier": "Lieferant",
    "sheet.supplierRef": "Lieferantenref.",
    "sheet.supplierDesignation": "Bezeichnung des Lieferanten",
    "sheet.onNotes": "Auf Lieferscheinen gefundene Ref.",
    "sheet.purchase": "Einkaufsbedingungen",
    "sheet.price": "Stückpreis",
    "sheet.noPrice": "nicht angegeben",
    "sheet.moq": "Mindestmenge pro Artikel",
    "sheet.rules": "Bedingungen des Lieferanten",
    "sheet.usualQty": "Üblich bestellte Mengen",
    "sheet.toConfirm": "Zu bestätigen",
    "sheet.remark": "Bemerkung",
    "sheet.notInCatalogue": "Keine Lieferantendaten für diesen Artikel im Katalog (Liste von Charles Vila vom 07.10.2026).",
    "sheet.reserved": "Einkaufsbedingungen nur für Verantwortliche.",
    "sheet.notImported": "Die Einkaufsbedingungen (Liste von Charles Vila vom 07.10.2026) sind noch nicht in die App importiert.",
    "sheet.history": "Bestandsverlauf",
    "sheet.noHistory": "Keine Bewegung erfasst.",
    "nav.access": "Zugriff",
    "access.title": "Zugriff pro Modul",
    "access.hint": "Jede Person nutzt die angehakten Module und öffnet die App auf ihrem Startmodul. Ein Admin nutzt alle Module. Die App verweigert jede Aktion ausserhalb der Module einer Person.",
    "access.person": "Person",
    "access.home": "Start",
    "access.active": "Aktiv",
    "access.admin": "Admin",
    "access.new": "Neue Person",
    "access.name": "Name",
    "access.password": "Passwort (mind. 6 Zeichen)",
    "access.homeFirst": "Das erste angehakte Modul wird zum Start (später änderbar).",
    "access.create": "Zugang anlegen",
    "access.noModule": "Ihr Konto hat noch kein Modul. Bitten Sie einen Admin, Ihnen unter „Zugriff“ welche zu geben.",
    "unit.title": "Einheit {n} / {q}",
    "unit.count": "{c} / {m} Artikel",
    "unit.inStock": "{n} am Lager",
    "unit.validate": "Diese Einheit bestätigen ({d} → {n} / {q})",
    "unit.tickAll": "Alle Artikel abhaken, um zu bestätigen ({c}/{m})",
    "unit.correct": "Fehler? 1 bestätigte Einheit entfernen ({d} → {p})",
    "unit.finishNow": "Jetzt beenden",
    "unit.markDone": "Als erledigt markieren",
    "unit.finishConfirm": "Jetzt mit {d} / {q} gefertigten Einheiten beenden? Die bereits abgehakten Artikel der laufenden Einheit kommen zurück ins Lager.",
    "unit.cancel": "Auftrag stornieren",
    "unit.cancelConfirm": "Diesen Auftrag stornieren ({d} / {q} Einheiten gefertigt)? Bereits gefertigte Kits bleiben am Lager; die abgehakten Artikel der laufenden Einheit kommen zurück.",
    "unit.yes": "Ja",
    "unit.no": "Nein",
    "artrec.title": "📷 Wareneingang — Foto, Screenshot oder PDF",
    "artrec.hint": "Senden Sie alles, was zeigt, was angekommen ist: Foto des Lieferscheins (ein Foto pro Seite), Screenshot (Auftragsbestätigung, E-Mail, WhatsApp, Lieferantenseite), Rechnung, PDF, Foto der Kartonetiketten… Jeder erkannte Artikel kommt ins Artikellager; der Rest ist zu prüfen.",
    "artrec.send": "Wareneingang senden ({n} Seite(n))",
    "artrec.typeAny": "Dokumenttyp (optional)",
    "artrec.type.bulletin": "Lieferschein",
    "artrec.type.capture": "Screenshot (Bestellung, E-Mail, WhatsApp, Website)",
    "artrec.type.facture": "Rechnung / Auftragsbestätigung",
    "artrec.type.etiquettes": "Foto der Kartons / Etiketten",
    "artrec.type.autre": "Andere",
    "artrec.note": "Notiz (optional)",
    "artrec.noteShown": "Notiz:",
    "artrec.latest": "Letzte Wareneingänge",
    "artrec.none": "Noch kein Wareneingang fotografiert.",
    "artrec.updated": "Bestand aktualisiert:",
    "artrec.total": "gesamt",
    "artrec.toVerify": "Zu prüfen:",
    "artrec.clientReserved": "Für Kunden reserviert (nicht eingelagert):",
    "artrec.forClient": "für",
    "artrec.summary": "Gelesen:",
    "artrec.st.verified": "Geprüft und korrigiert",
    "artrec.verifiedBy": "Geprüft und korrigiert am {at} von {by}:",
    "artrec.corrections": "Bestandskorrekturen:",
    "artrec.check.title": "Diesen Wareneingang prüfen",
    "artrec.check.hint": "Bestand bei Bedarf korrigieren (Artikel und Menge, plus oder minus), notieren, was geprüft wurde, dann „Geprüft und korrigiert“.",
    "artrec.check.pick": "Artikel wählen",
    "artrec.check.qty": "+/− Menge",
    "artrec.check.remove": "Diese Korrektur entfernen",
    "artrec.check.add": "Bestandskorrektur",
    "artrec.check.note": "Notiz (Pflicht): was geprüft und korrigiert wurde",
    "artrec.check.button": "Geprüft und korrigiert",
    "artrec.disputed.title": "Umstrittene Lieferantenreferenzen",
    "artrec.disputed.hint": "Solange eine Referenz umstritten ist, wird sie beim Lesen der Wareneingänge ignoriert: den richtigen Artikel wählen, dann „Bestätigen“.",
    "artrec.disputed.now": "derzeit {code}",
    "artrec.disputed.validate": "Bestätigen",
    "artrec.cal.noCatalogue": "Der Lieferantenkatalog von Z Production ist noch nicht importiert: Eine Bestellung, deren Lieferantennummern nur dort bekannt sind, wird hier nicht geprüft.",
    "artrec.cal.held": "Die Slack-Nachrichten dieses Abgleichs sind angehalten: Das Lager fotografiert seine Wareneingänge noch in Z Production, das das Team bereits informiert.",
    "artrec.cal.missing": "Laut Delivery calendar angekommen, aber in der App noch nicht fotografiert:",
    "artrec.cal.missingAsk": "Bitte diesen Lieferschein hier fotografieren.",
    "artrec.cal.last": "Letzter Abgleich mit dem Delivery calendar: {at}",
    "artrec.cal.error": "Fehler",
    "artrec.cal.otherWarehouses": "Diese App sieht noch nicht, in welches Lager jede Zeile einer Bestellung geht: Eine Zeile einer Penthalaz-Bestellung, die anderswo geliefert wird, wird hier noch mitgezählt.",
    "artrec.cal.st.ok": "✅ Abgleich Delivery calendar: stimmt überein",
    "artrec.cal.st.ecart": "⚠️ Abgleich Delivery calendar: Abweichung",
    "artrec.cal.st.absent_calendar": "ℹ️ Keine passende Bestellung im Delivery calendar",
    "artrec.cal.st.non_livree": "ℹ️ Bestellung im Delivery calendar gefunden, aber noch nicht als geliefert markiert",
    "artrec.cal.orders": "Bestellung {ids}",
    "artrec.cal.diff": "{name} ({code}): bestellt {ordered}, Foto {photo}",
    "artrec.cal.extra": "{name} ({code}): Foto {photo}, nicht in der Bestellung",
    "artrec.cal.notDeliveredNote": "Bestellung im Delivery calendar noch nicht als geliefert markiert (Status {statuses})",
    "artrec.cal.absentNote": "keine Penthalaz-Bestellung mit diesen Artikeln seit {since}",
    "artrec.st.waiting": "Wartet auf Verarbeitung",
    "artrec.st.running": "In Bearbeitung",
    "artrec.st.done": "Erledigt — Bestand aktualisiert",
    "artrec.st.verify": "Zu prüfen",
    "art.modeCount": "✏️ Bestand korrigieren",
    "art.modeAdd": "➕ Hinzufügen",
    "art.addSave": "Zum Bestand hinzufügen ({n} Referenz(en))",
    "art.search": "Referenz, Artikel, Lieferant oder Lieferantenref. suchen…",
    "jobs.onlyAssembly": "Nur Montagen (Ludovics Liste)",
    "jobs.today": "Heute",
    "jobs.upcoming": "Anstehend",
    "jobs.done": "Erledigt",
    "jobs.archived": "Archiviert",
    "jobs.noDelivery": "Lieferung noch nicht geplant",
    "stock.otherCat": "Andere",
    "stock.inStockN": "Im Lager",
    "stock.outN": "Ausgelagert",
    "stock.avgAge": "Durchschnittsalter",
    "stock.searchAll": "Suche Lager-Nr., Name, Ref., Kunde…",
    "stock.allCats": "Alle Kategorien",
    "stock.allPallets": "Alle Paletten",
    "stock.palletN": "Palette {p}",
    "stock.all": "Alle",
    "stock.allSources": "Alle Herkünfte",
    "stock.srcReturns": "Kundenrückgaben",
    "stock.srcReceptions": "Wareneingänge",
    "stock.allWarehouses": "Alle Lager",
    "stock.oldestFirst": "Älteste zuerst",
    "stock.exportCsv": "CSV exportieren",
    "stock.csvDone": "Export bereit",
    "stock.emptyInvTitle": "Hier keine Möbel im Lager",
    "stock.noResults": "Keine Ergebnisse für diese Filter.",
    "stock.forClient": "Für",
    "stock.enteredOn": "eingelagert am",
    "stock.openCatalogue": "Katalogseite öffnen ↗",
    "stock.changeCond": "Zustand ändern",
    "stock.backIn": "Wieder einlagern",
    "stock.markOut": "Als ausgelagert markieren",
    "stock.delete": "Entfernen",
    "stock.damageNotePh": "Mangel (Pflicht bei „Beschädigt“)",
    "stock.reason": "Grund",
    "stock.out.redeliv": "An einen Kunden geliefert",
    "stock.out.repair": "In Reparatur",
    "stock.out.sale": "Verkauft",
    "stock.out.scrap": "Entsorgt",
    "stock.out.transfer": "Lagerumzug",
    "stock.deleteConfirm": "Diesen Eintrag endgültig löschen? Die Fotos bleiben gespeichert.",
    "stock.deleteYes": "Ja, löschen",
    "stock.ev.back": "wieder eingelagert",
    "prod.capacitySaved": "Kapazität gespeichert: {p} Person(en) × {h} h / Tag",
    "prod.objective": "Ziel",
    "prod.timeRequired": "Benötigte Gesamtzeit",
    "prod.capacityDay": "Tageskapazität",
    "prod.marginLeft": "Verfügbare Marge",
    "prod.overrun": "Überschreitung",
    "prod.assignTo": "Einem Vorbereiter zuweisen (optional)",
    "prod.assignPh": "Z. B. Emerson — leer = beliebig",
    "prod.note": "Notiz (optional)",
    "prod.teamCapacity": "Teamkapazität",
    "prod.people": "Personen",
    "prod.hoursPerDay": "Stunden / Tag",
    "prod.doneShort": "erledigt",
    "prod.alertFailed": "Vorbereitung gestoppt, aber die Meldung konnte nicht gesendet werden: den Verantwortlichen direkt informieren.",
    "prod.resolve": "Als erledigt markieren",
    "prod.for": "Für",
    "prod.takeOver": "Übernehmen",
    "prod.changeAssign": "Zuweisung ändern",
    "prod.assign": "Zuweisen",
    "prod.split": "Auftrag teilen",
    "prod.lockTitle": "In Vorbereitung",
    "prod.lockBody": "von {o}{paused} — {d} / {q} erledigt. Die Checkliste ist {o} vorbehalten.",
    "prod.pausedTag": "(pausiert)",
    "prod.takeOverAsk": "{k} übernehmen?",
    "prod.takeOverYes": "Ja, ich übernehme",
    "prod.assignedAsk": "Dieser Auftrag ist {n} zugewiesen. Trotzdem starten?",
    "prod.assignedYes": "Ja, starten",
    "prod.assignName": "Name des Vorbereiters (leer = beliebig)",
    "prod.save": "Speichern",
    "prod.splitQty": "Kits für den neuen Auftrag (1 bis {n})",
    "prod.splitTo": "Neuen Auftrag zuweisen an (optional)",
    "prod.splitYes": "Teilen",
    "prod.reopen": "Diesen Auftrag wieder öffnen",
    "prod.reopenPartial": "Diesen Auftrag wieder öffnen ({d}/{q} erledigt)",
    "jobs.overdue": "überfällig",
    "jobs.issue": "Problem gemeldet",
    "jobs.issues": "{n} Probleme gemeldet",
    "jobs.gone": "Aus der Planung entfernt",
    "team.title": "Monteure an diesem Auftrag",
    "team.nobody": "Noch niemand",
    "team.add": "+ Monteur",
    "team.remove": "Entfernen",
    "team.left": "hat den Auftrag verlassen",
    "issue.howMany": "Wie viele sind betroffen?",
    "issue.ofN": "von {n}",
    "issue.sentTo": "gesendet an {n} am {t}",
    "job.fromOrders": "Nur Möbel aus den Lieferantenbestellungen (Ludovics Liste)",
    "job.otherList": "Dieser Auftrag wird in der anderen Liste abgehakt: diese hier ist nur lesbar.",
    "job.toAssemble": "Zu montierende Möbel",
    "job.toCheck": "Zu prüfende Artikel (ohne Montage)",
    "job.toCheckHint": "Jede Verpackung öffnen und den Artikel prüfen: Bruch, Kratzer, fehlende Teile oder Zubehör. Wieder schliessen oder schützen, dann die Zeile mit ✓ abhaken (oder mit + zählen, wenn erst ein Teil erledigt ist). Ist er beschädigt: 📷 Foto, dann « ⚠️ Problem » auf seiner Zeile.",
    "job.order": "Bestellung",
    "job.arrived": "eingetroffen am {d}",
    "job.expected": "erwartet am {d}",
    "job.noArrival": "ohne Liefertermin",
    "job.cartons": "📦 {n} Kartons",
    "job.cartonsPer": "📦 {n} Kartons pro Möbel · {t} insgesamt",
    "job.ref": "Ref.",
    "job.unreadable": "Bestellung erwähnt, aber nicht lesbar: {ids}",
    "job.noOrders": "Der Planungskommentar nennt keine Lieferantenbestellung.",
    "arch.hint": "Ein Kunde wird am Tag nach seinem Liefertermin im Plan automatisch archiviert (Lieferung erfolgt), ausser während einer laufenden Montage. Von Hand archivieren oder zurückholen gilt, solange sich Montage- und Liefertermin im Plan nicht ändern.",
    "arch.q": "Kunde oder Nummer suchen",
    "arch.none": "Noch kein archivierter Kunde.",
    "arch.noHit": "Kein archivierter Kunde passt zur Suche.",
    "arch.un": "Zurückholen",
    "arch.ar": "Archivieren",
    "arch.by": "von",
    "arch.boxOn": "📦 Archivierter Kunde",
    "arch.boxOff": "Archivierung",
    "arch.pAuto": "Lieferung am {d} erfolgt: der Kunde liegt unter Archiviert.",
    "arch.pMan": "Von Hand archiviert am {d}{w}.",
    "arch.pWill": "Automatisch archiviert am Tag nach der Lieferung ({d}).",
    "arch.pNone": "Kein Liefertermin im Plan: von Hand archivieren, wenn es vorbei ist (Lieferung storniert oder ohne Datum erfolgt).",
    "arch.pBack": "Von Hand zurückgeholt: bleibt in den Listen, solange sich die Termine im Plan nicht ändern.",
    "arch.run": "Montage läuft: pausieren oder beenden, bevor archiviert wird.",
    "note.title": "Wareneingang: Lieferantendokumente",
    "note.hint": "Fotografiere jede Seite des Lieferscheins (oder eines anderen Lieferantendokuments). Die App lässt sie lesen und gleicht sie mit dem Delivery calendar ab: was für jeden Kunden angekommen ist, fehlt oder zu viel ist.",
    "note.photo": "📷 Seite fotografieren",
    "note.files": "📁 Fotos oder PDFs wählen",
    "note.drop": "…oder Fotos bzw. PDFs vom Computer hierher ziehen",
    "note.page": "Seite {n}",
    "note.del": "Entfernen",
    "note.read": "Lesen und mit dem Delivery calendar abgleichen ({n} Seite(n))",
    "note.uploading": "Seiten werden gesendet…",
    "note.reading": "Wird gelesen, dann mit dem Delivery calendar abgeglichen… (10 bis 60 s pro Seite, etwa 1 Minute für den Abgleich)",
    "note.failed": "Das Lesen ist fehlgeschlagen:",
    "note.retry": "Erneut lesen",
    "note.rematch": "Erneut mit dem Delivery calendar abgleichen",
    "note.type": "Datei nicht unterstützt: Foto (JPEG, PNG, HEIC) oder PDF.",
    "note.doc.delivery_note": "Lieferschein",
    "note.doc.order_confirmation": "Auftragsbestätigung",
    "note.doc.invoice": "Rechnung",
    "note.doc.labels": "Etiketten",
    "note.doc.other": "Dokument",
    "note.lines": "{n} Zeile(n) gelesen",
    "note.pages": "{n} Seite(n)",
    "note.readAt": "gelesen am {when}",
    "note.clients": "{n} Kunde(n)",
    "note.ord": "bestellt",
    "note.got": "erhalten",
    "note.conf": "bestätigt",
    "note.ok": "vollständig",
    "note.extra": "{n} zu viel",
    "note.miss": "nicht auf diesem Lieferschein",
    "note.confSt": "noch kein Wareneingang",
    "note.colli": "{n} Packstücke",
    "note.cartons": "{n} Kartons",
    "note.cw": "Kartons unvollständig: prüfen",
    "note.un": "Nicht im Delivery calendar gefunden (keine Montage für diese Artikel geplant)",
    "note.many": "Mehrere mögliche Kunden: von Hand zuordnen",
    "note.unread": "Unlesbare Nummer",
    "note.unclear": "Auf dem Papier prüfen",
    "note.open": "Auftrag öffnen",
    "note.sec": "Wareneingang (gelesene Lieferantendokumente)",
    "note.ocW": "Dieses Dokument ist eine Auftragsbestätigung (oder eine Rechnung), kein Lieferschein: Es beweist nicht, dass die Ware angekommen ist. Das Möbel erst suchen, wenn es eingegangen ist.",
    "note.deliveryOn": "Lieferung {when}",
    "note.hist": "Zuletzt gelesene Dokumente",
    "note.none": "Noch kein Dokument gelesen.",
    "note.byHand": "Lieferschein von Hand erfassen",
    "note.matched": "{n} zugeordnet",
    "note.pillOk": "📄 erhalten",
    "note.pillWarn": "⚠️ Wareneingang",
    "note.pillOc": "📝 Bestellung bestätigt",
    "tr.title": "Transfers zwischen Standorten",
    "tr.from": "Von",
    "tr.to": "Nach",
    "tr.send": "Senden",
    "tr.receive": "Annehmen",
    "tr.pending": "offen",
    "tr.inTransit": "unterwegs",
    "tr.received": "angekommen",
    "tr.new": "Neuer Transfer",
    "tr.none": "Keine Transfers.",
    "tr.arrived": "Angekommen",
    "tr.arrivalHint": "Z\u00e4hlen Sie, was angekommen ist: nur diese Kits kommen in den Bestand dieses Standorts.",
    "tr.lossNote": "Was mit den fehlenden passiert ist (Pflicht)",
    "tr.confirmArrival": "Ankunft best\u00e4tigen",
    "tr.receivedOf": "{n} von {m} angekommen",
    "tr.inTransitNote": "Unterwegs: die Ware geh\u00f6rt keinem der beiden Standorte.",
    "nav.production": "Produktion",
    "prod.progress": "Fortschritt heute",
    "prod.target": "Ziel",
    "prod.done": "Erledigt",
    "prod.variance": "Abweichung",
    "prod.ahead": "vor Plan",
    "prod.behind": "im R\u00fcckstand",
    "prod.byPreparer": "Nach Vorbereiter",
    "prod.kitsMade": "Kits gefertigt",
    "prod.perKit": "Min. / Kit",
    "prod.queue": "Produktionsliste",
    "prod.finishedToday": "Heute fertig",
    "prod.stock": "Bestand",
    "prod.onHand": "Vorhanden",
    "prod.reserved": "Reserviert",
    "prod.available": "Verf\u00fcgbar",
    "prod.newOrder": "Neuer Produktionsauftrag",
    "prod.kit": "Kit",
    "prod.qty": "Menge",
    "prod.raise": "Senden",
    "prod.noQueue": "Kein Auftrag wartet — die Warteschlange ist leer.",
    "prod.allocations": "F\u00fcr kommende Lieferungen reservierte Kits",
    "prod.neededOn": "Ben\u00f6tigt",
    "prod.unitMinutes": "Min. / Kit",
    "prod.reservedNote": "Eine Reservierung verringert nie den echten Bestand.",
    "wh.all": "Alle",
    "job.assembly": "Montage",
    "job.delivery": "Lieferung",
    "job.estimate": "Sch\u00e4tzung",
    "job.assembled": "montiert",
    "job.checked": "geprüft",
    "job.inProgress": "L\u00e4uft",
    "job.todo": "Offen",
    "job.finished": "Fertig",
    "job.issueReported": "Problem gemeldet",
    "job.packing": "Verpackung erforderlich",
    "job.packingBody": "Jedes montierte M\u00f6bel wird in MYOTAKU-Decken gewickelt und mit Stretchfolie versiegelt, bevor es in den Wagen des Kunden kommt.",
    "job.furniture": "Zu montierende M\u00f6bel",
    "job.pickOne": "W\u00e4hlen Sie eine Lieferung.",
    "start.title": "Wer montiert?",
    "start.help": "Tippen Sie jeden Monteur an, der mitarbeitet. Ein Monteur, der bereits bei einem anderen Kunden l\u00e4uft, wird dort automatisch entfernt.",
    "start.extras": "Aushilfen",
    "start.permanent": "Festangestellte",
    "start.addName": "Vornamen hinzuf\u00fcgen",
    "start.add": "Hinzuf\u00fcgen",
    "start.atLeastOne": "Mindestens einen Monteur wählen",
    "issue.title": "Problem melden",
    "issue.kind": "Problem",
    "issue.item": "Betroffener Artikel",
    "issue.none": "Keiner im Besonderen",
    "issue.what": "Was Sie sehen",
    "issue.by": "Gemeldet von",
    "issue.blocks": "Montage blockiert (pausiert die Zeit)",
    "issue.send": "Melden",
    "issue.damaged_furniture": "Beschädigtes Möbel",
    "issue.missing_part": "Fehlendes Teil oder Beschlag",
    "issue.missing_furniture": "Fehlendes M\u00f6bel",
    "issue.wrong_model_or_colour": "Falsches Modell oder Farbe",
    "issue.missing_instructions": "Fehlende Montageanleitung",
    "issue.other": "Anderes Problem",
    "finish.title": "Auftrag abschliessen",
    "finish.send": "Abschliessen",
    "finish.reopen": "Wieder \u00f6ffnen",
    "finish.recapSent": "Zusammenfassung auf Slack gesendet an",
    "finish.resend": "Zusammenfassung senden",
    "cancel": "Abbrechen",
    "prod.stop": "⛔ Anhalten",
    "prod.stopTitle": "Vorbereitung anhalten und den Verantwortlichen benachrichtigen",
    "prod.stopSend": "⛔ Anhalten und Alarm senden",
    "prod.stopDetail": "Genauer (welcher Artikel, wie viele fehlen…)",
    "prod.stockShort": "Bestand ungenügend",
    "prod.stoppedTitle": "Angehaltene Vorbereitungen",
    "prod.stoppedBecause": "Angehalten:",
    "prod.reason0": "Material / Artikel fehlt",
    "prod.reason1": "Keine Kartons mehr",
    "prod.reason2": "Kein Bestand mehr",
    "prod.reason3": "Anderes Problem",
    "item.issue": "Problem",
    "item.blocked": "Blockiert",
    "app.title": "Myotaku Lager",
    "nav.preparation": "Montage",
    "nav.signOut": "Abmelden",
    "days.title": "Kommende Lieferungen",
    "days.none": "Keine Lieferungen an diesem Tag.",
    "days.outsideWindow": "Ausserhalb des Feed-Zeitraums",
    "event.pieces": "Stück",
    "event.lines": "Zeilen",
    "event.packs": "Räume",
    "event.awaitingSales": "Wartet auf Verkauf",
    "event.awaitingLogistics": "Wartet auf Logistik",
    "event.approved": "Freigegeben",
    "run.start": "Vorbereitung starten",
    "run.resume": "Fortsetzen",
    "run.pause": "Pausieren",
    "run.running": "Läuft",
    "run.paused": "Pausiert",
    "run.wall": "Verstrichen",
    "run.worked": "Arbeitszeit",
    "run.minutes": "Min.",
    "run.noRun": "Noch nicht begonnen",
    "item.done": "Montiert",
    "item.todo": "Zu montieren",
    "item.noAssembly": "Keine Montage",
    "item.estimate": "Schätzung",
    "pack.standardKit": "Standard-Kit",
    "error.title": "Problem",
    "loading": "Wird geladen…",
    "lang.label": "Sprache",
    "login.title": "Myotaku Lager",
    "login.intro": "Melden Sie sich an, um fortzufahren.",
    "login.email": "E-Mail",
    "login.password": "Passwort",
    "login.submit": "Anmelden",
    "login.busy": "Anmeldung…",
    "login.noSignup": "Konten werden von einem Administrator angelegt. Eine Selbstregistrierung gibt es nicht.",
    "login.failed": "Anmeldung fehlgeschlagen.",
    "login.failedStatus": "Anmeldung fehlgeschlagen ({status}).",
    "login.wrong": "E-Mail und Passwort passen nicht zusammen.",
    "login.noToken": "Angemeldet, aber es wurde kein Token ausgestellt. Bitte einen Administrator informieren.",
    "login.sessionCheck": "Die Sitzung konnte nicht geprüft werden ({status}).",
    "error.request": "Anfrage fehlgeschlagen ({status}).",
    "error.upload": "Der Speicher hat den Upload abgelehnt ({status}).",
    "status.intro": "Diese Seite prüft nur, dass die ganze Kette funktioniert.",
    "status.signedInAs": "Angemeldet als",
    "status.frontEnd": "Browser",
    "status.server": "Server",
    "status.browser": "Browser",
    "status.mounted": "der Code ist geladen und React gestartet",
    "status.asking": "wird abgefragt…",
    "status.unreachable": "NICHT ERREICHBAR",
    "status.answeredAt": "Server hat um {when} geantwortet",
    "docs.ref.checklists.title": "A4-Checklisten (Karton)",
    "docs.ref.checklists.desc": "Ein Blatt pro Kit, das in jeden Karton gelegt wird – gleiches Layout wie unten.",
    "docs.ref.shelf.title": "Regaletiketten",
    "docs.ref.shelf.desc": "Farbige Etiketten für das Regal, 8 pro A4-Seite.",
    "docs.ref.inventory.title": "Gesamtinventur",
    "docs.ref.inventory.desc": "Alle Artikelnummern, bei einer körperlichen Zählung auszufüllen.",
    "chk.title": "Karton-Checkliste",
    "chk.phase1": "Phase 1 — Installiertes Kit",
    "chk.phase2": "Phase 2 — Reserve-Kit",
    "chk.phase1Short": "Installiertes Kit",
    "chk.phase2Short": "Reserve-Kit",
    "chk.complete": "Karton vollständig und geprüft",
    "chk.preparedBy": "Vorbereitet von",
    "chk.checkedBy": "Geprüft von",
    "chk.date": "Datum",
    "chk.signature": "Unterschrift",
    "chk.article": "{n} Artikel",
    "chk.articles": "{n} Artikel",
    "csv.col.stock_no": "stock_nr",
    "csv.col.entrepot": "lager",
    "csv.col.categorie": "kategorie",
    "csv.col.statut": "status",
    "csv.col.etat": "zustand",
    "csv.col.origine": "herkunft",
    "csv.col.nom": "name",
    "csv.col.ref_myotaku": "myotaku_ref",
    "csv.col.ref_fournisseur": "lieferanten_ref",
    "csv.col.fournisseur": "lieferant",
    "csv.col.commande_fournisseur": "lieferantenbestellung",
    "csv.col.dimensions": "abmessungen",
    "csv.col.emplacement": "lagerort",
    "csv.col.palette": "palette",
    "csv.col.rack": "regal",
    "csv.col.numero": "nummer",
    "csv.col.jours_au_depot": "tage_am_lager",
    "csv.col.retour": "retoure",
    "csv.col.client": "kunde",
    "csv.col.reserve_pour": "reserviert_fuer",
    "csv.col.livraison": "lieferung",
    "csv.col.entree_le": "eingang_am",
    "csv.col.degats": "schaden",
    "csv.col.catalogue_url": "katalog_url",
    "csv.in": "am Lager",
    "csv.out": "ausgelagert",
    "csv.reception": "Lieferanteneingang",
    "csv.return": "Kundenretoure",
    "csv.cond.new": "neu",
    "csv.cond.good": "gut",
    "csv.cond.light_marks": "mittel",
    "csv.cond.damaged": "beschädigt",
    "csv.pallet": "Palette {p}",
    "csv.rack": "Regal {r}",
    "csv.file": "moebel-lager",
    "csv.fileAll": "alle",
    "label.stockLabel": "MÖBELLAGER",
    "label.piece": "Teil",
    "label.unit": "Möbel",
    "label.withOthers": "mit {n} weiteren Teil(en)",
    "label.refMyo": "Myotaku-Ref.",
    "label.refSup": "Lieferanten-Ref.",
    "label.entry": "Eingang",
    "label.qrCaption": "Katalogseite",
    "label.qrCaptionBig": "Scannen: Myotaku-Katalogseite",
    "label.for": "Für",
    "label.deliv": "Lief.",
    "label.ret": "Retoure",
    "label.sample": "Beispiel",
    "label.cond.neuf": "Neu",
    "label.cond.bon": "Gut",
    "label.cond.moyen": "Mittel",
    "label.cond.abime": "Beschädigt",
    "label.fmt.a4": "A4 quer · 1 Etikett pro Blatt (aus der Ferne lesbar)",
    "label.fmt.a4x2": "A4 · 2 Etiketten pro Blatt (A5, zum Halbieren)",
    "label.fmt.ql62": "Brother QL · 62 × 100 mm",
    "label.fmt.dymo": "Dymo LabelWriter · 54 × 101 mm",
    "label.fmt.zebra": "Zebra / Thermodruck · 100 × 150 mm",
    "label.format": "Etikettenformat",
    "label.preview": "Etikettenvorschau",
    "label.dlOne": "Etikett herunterladen",
    "label.dlAll": "Die {n} Etiketten herunterladen",
    "label.file1": "etikett-{id}.pdf",
    "label.fileN": "etiketten-{wh}-{day}.pdf",
    "stock.todayLabels": "Etiketten von heute",
    "rec.reprint": "Erneut drucken",
    "art.perKit": "×{n}/Kit",
    "label.printA4": "Öffnen Sie das PDF und drucken Sie es auf A4 mit 100 % Skalierung.",
    "label.printRoll": "Mit der App des Druckers drucken (PDF).",
    "label.s5": "Etikett",
    "label.count": "{n} Etiketten (eins pro Teil)",
    "label.previewFirst": "Vorschau von Teil 1",
    "cartLabel.cart": "Wagen",
    "cartLabel.atClient": "Beim Kunden: scannen und die Retoure wählen.",
    "cartLabel.atWarehouse": "Im Lager: scannen, um den Kunden zu sehen.",
    "cartLabel.scanBoth": "Beim Beladen beim Kunden und beim Entladen scannen.",
    "cartLabel.keep": "nicht entfernen",
    "cartLabel.fmt.zebra": "Zebra / Thermodruck · 150 × 100 mm (quer)",
    "cartLabel.fmt.ql62": "Brother QL · 62 × 100 mm",
    "cartLabel.fmt.a4x4": "A4 quer · 4 Etiketten pro Blatt, zum Ausschneiden",
    "cartLabel.open": "🖨 Etiketten drucken",
    "cartLabel.title": "Wagen-Etiketten drucken",
    "cartLabel.hint": "Das Modul erstellt ein PDF: Öffnen Sie es und drucken Sie es auf dem Etikettendrucker (Originalgrösse, ohne Skalierung). Drucken Sie zuerst eine Testseite.",
    "cartLabel.format": "Format",
    "cartLabel.from": "Von Wagen Nr.",
    "cartLabel.to": "bis Nr.",
    "cartLabel.copies": "Etiketten pro Wagen",
    "cartLabel.print1": "🖨 {n} Etikett drucken (PDF)",
    "cartLabel.printN": "🖨 {n} Etiketten drucken (PDF)",
    "cartLabel.busy": "PDF wird vorbereitet…",
    "cartLabel.ok": "PDF bereit: Öffnen Sie es zum Drucken.",
    "cartLabel.err": "Das PDF konnte nicht erstellt werden: bitte erneut versuchen.",
    "cartLabel.range": "Geben Sie Nummern von 1 bis 999 ein, die kleinere zuerst (höchstens 400 Etiketten pro PDF).",
    "cartLabel.reprint": "🖨 Etikett dieses Wagens neu drucken",
    "cartLabel.file1": "etikett-{id}-{fmt}.pdf",
    "cartLabel.fileN": "wagen-etiketten-{from}-{to}-{fmt}.pdf",
    "cartLabel.copiesN": "× {n}",
    "cartLabel.close": "Schliessen",
    "note.notMatched": "Gelesen, aber noch nicht mit dem Delivery calendar abgeglichen:",
    "carton.title": "Karton-Etiketten",
    "carton.sub": "schwarz-weiss, gleiches Layout wie die Regaletiketten.",
    "carton.printOn": "Drucken auf:",
    "carton.fmt.a4": "A4-Blatt — 8 Etiketten 10 × 7 cm (normaler Drucker)",
    "carton.fmt.ql102x70": "Brother-QL-Etikettendrucker — 102 × 70 mm (Endlosrolle 102 mm, DK-22243)",
    "carton.fmt.ql102x152": "Brother-QL-Etikettendrucker — 102 × 152 mm (vorgestanzte Etiketten DK-11241)",
    "carton.fmt.ql62x100": "Brother-QL-Etikettendrucker — 62 × 100 mm (vorgestanzte Etiketten DK-11202)",
    "carton.helpQl": "Ein Etikett pro Karton. Im Druckfenster: Drucker „Brother QL-1100“ wählen, Ränder „Keine“ und dasselbe Papierformat ({w} × {h} mm) unter „Weitere Einstellungen“.",
    "carton.helpQlPage": "Ein Etikett pro Seite. Im Druckfenster: Drucker „Brother QL-1100“, Ränder „Keine“, Format {w} × {h} mm.",
    "carton.helpA4": "8 gleiche Etiketten 10 × 7 cm pro A4-Blatt.",
    "carton.helpA4Page": "A4-Blatt: 8 gleiche Etiketten 10 × 7 cm pro Blatt.",
    "carton.editHint": "Die erhaltene Menge und die Lieferanten-Referenz werden auf das Etikett gedruckt — unten änderbar (leer lassen, um sie nicht zu drucken).",
    "carton.ref": "Ref.",
    "carton.article": "Artikel",
    "carton.supRefCol": "Lieferanten-Ref.",
    "carton.qtyCol": "Erhaltene Menge",
    "carton.qtyShort": "Menge",
    "carton.sheets": "Blätter",
    "carton.labels": "Etiketten",
    "carton.allRefs": "Alle Referenzen:",
    "carton.apply": "Übernehmen",
    "carton.addRef": "Ref. hinzufügen:",
    "carton.addPh": "z. B. 9KUC",
    "carton.add": "Hinzufügen",
    "carton.unknown": "Unbekannte Referenz „{ref}“.",
    "carton.noneRead": "In diesem Wareneingang wurde kein Artikel erkannt — unten eine Referenz hinzufügen.",
    "carton.print": "Drucken",
    "carton.nLabel": "{n} Etikett {w} × {h} mm",
    "carton.nLabels": "{n} Etiketten {w} × {h} mm",
    "carton.nSheet": "{n} Blatt · {labels} Etiketten",
    "carton.nSheets": "{n} Blätter · {labels} Etiketten",
    "carton.nLabelShort": "{n} Etikett",
    "carton.nLabelsShort": "{n} Etiketten",
    "carton.libTotal": "{refs} Ref. · {what}",
    "carton.noneSelected": "Kein Etikett ausgewählt",
    "carton.footer": "Eingang vom {date}",
    "carton.footerSupplier": "Eingang vom {date} · {supplier}",
    "carton.fileReception": "Karton_Etiketten_{supplier}_{date}",
    "carton.fileLibrary": "Myotaku_Etiketten",
    "carton.fileOne": "Etikett_{code}",
    "carton.nothing": "Nichts zu drucken.",
    "carton.blocked": "Drucken nicht möglich: Der Browser hat das neue Fenster blockiert. Pop-ups für diese Seite erlauben und erneut versuchen.",
    "carton.qtyReceived": "Menge erhalten",
    "carton.supRef": "Lieferanten-Ref.:",
    "carton.catLinen": "Wäsche-Kit",
    "carton.libTitle": "Etiketten-Bibliothek",
    "carton.libSub": "Alle Myotaku-Referenzen — 🖨️ in der Zeile = EIN einzelnes Etikett (auf A4: oben links auf dem Blatt); für mehrere die Anzahl wählen und dann „Auswahl drucken“. Die eingegebene Lieferanten-Ref. und Menge werden für das nächste Mal gespeichert.",
    "carton.searchPh": "Suchen: Code, Artikel, Lieferanten-Ref.…",
    "carton.allKits": "Alle Kits",
    "carton.footerField": "Text unten auf dem Etikett (optional):",
    "carton.footerPh": "z. B. Rotel-Lieferung vom 05.10",
    "carton.noMatch": "Keine Referenz entspricht der Suche.",
    "carton.reset": "Alles auf 0 setzen",
    "carton.printSelection": "Auswahl drucken",
    "carton.printOne": "Dieses Etikett drucken",
    "carton.remembered": "gespeichert",
    "carton.saveFailed": "Speichern nicht möglich: {reason}",
    "carton.printCartons": "Karton-Etiketten drucken",
    "cartx.today": "Heute",
    "cartx.yesterday": "Gestern",
    "cartx.tomorrow": "Morgen",
    "cartx.waiting1": "{n} Speicherung wartet auf Netz auf diesem Telefon",
    "cartx.waitingN": "{n} Speicherungen warten auf Netz auf diesem Telefon",
    "cartx.queued": "Kein Netz: auf diesem Telefon gespeichert, wird automatisch gesendet, sobald das Netz zurück ist.",
    "cartx.unknownNumber": "Unbekannte Wagennummer.",
    "cartx.missingNone": "Jede vergangene Retoure der letzten 3 Tage hat einen Wagen.",
    "cartx.notYet": "{n} noch nicht gescannt",
    "cartx.cart": "Wagen",
    "cartx.lastClient": "Letzter Kunde: {client} · freigegeben am {date}",
    "cartx.never": "Noch nicht mit dem Modul verwendet.",
    "cartx.linkedOk": "{cart} mit {client} verknüpft",
    "cartx.client": "Kunde",
    "cartx.event": "Termin",
    "cartx.byHand": "von Hand eingegeben",
    "cartx.linkedBy": "Verknüpft",
    "cartx.arrived": "Angekommen",
    "cartx.warehouse": "Lager",
    "cartx.photo": "Foto des beladenen Wagens",
    "cartx.optional": "optional",
    "cartx.photoUp": "Foto wird hochgeladen…",
    "cartx.photoErr": "Foto nicht gesendet: neu aufnehmen oder ohne weitermachen.",
    "cartx.photoDel": "Entfernen",
    "cartx.others": "Wagen dieser Retoure",
    "cartx.iOfN": "Wagen {i} von {n}",
    "cartx.items": "Erwartete Möbel dieser Retoure ({n})",
    "cartx.noItems": "Keine Möbelliste für diese Retoure (von Hand eingegeben oder nicht in der Planung).",
    "cartx.arrivedOk": "{cart} im Lager angekommen",
    "cartx.toStock": "Einlagerung dieser Retoure",
    "cartx.free": "Wagen geleert: freigeben",
    "cartx.freeConfirm": "Bestätigen: {cart} freigeben",
    "cartx.freedOk": "{cart} ist frei",
    "cartx.relink": "Retoure ändern",
    "cartx.relinkWarn": "Dieser Wagen ist mit {client} verknüpft. Die neue Verknüpfung ersetzt die alte.",
    "cartx.history": "Verlauf",
    "cartx.h.link": "Verknüpft",
    "cartx.h.arrive": "Im Lager angekommen",
    "cartx.h.free": "Freigegeben",
    "cartx.h.stock": "Einlagerung",
    "cartx.cartsOk1": "{n} Wagen ✓",
    "cartx.cartsOkN": "{n} Wagen ✓",
    "cartx.noHit": "Keine passende Retoure.",
    "cartx.noEv": "Keine Retoure in der Planung für dieses Lager rund um heute.",
    "cartx.manual": "Retoure nicht in der Liste?",
    "cartx.manEv": "Terminnummer der Planung",
    "cartx.manErr": "Terminnummer (Ziffern) und Kunde angeben.",
    "cartx.pickFirst": "Die Retoure des Kunden wählen.",
    "cartx.nBad": "Die Wagennummer ist höher als die Anzahl der Wagen.",
    "cartx.unitNo": "Wagen Nr.",
    "cartx.of": "von",
    "cartx.forClient": "für diesen Kunden",
    "cartx.doLink": "{cart} verknüpfen",
    "stock.stillNeeded": "Noch nötig:",
    "stock.mProduct": "Möbel",
    "stock.mPhoto": "Foto",
    "stock.mCond": "Zustand",
    "stock.mDamage": "Schadensfoto",
    "stock.mWrap": "Decke + Folie",
    "stock.mPallet": "Paletten-Nr.",
    "stock.mRack": "Regal-Nr.",
    "stock.damagePhotos": "Schadensfotos",
    "stock.required": "erforderlich",
    "stock.optional": "optional",
    "stock.addPhoto": "+ Foto",
    "stock.damageNote": "Schadensbeschreibung",
    "stock.wrapEach": "Möbel in {n} Teilen: jedes Teil einzeln verpacken und auf jedes ein Etikett kleben ({n} Etiketten).",
    "stock.wrapPhoto": "Foto des verpackten Möbels",
    "stock.saved": "Ins Lager aufgenommen",
    "stock.savedN": "{n} Möbel ins Lager aufgenommen",
    "stock.next": "Nächstes Möbel",
    "stock.nextSame": "Nächstes Möbel derselben Retoure",
    "stock.photosFailed": "{n} Foto(s) nicht gesendet: über die Möbel-Detailansicht hinzufügen.",
    "ret.emptyTitle": "Keine Retouren für dieses Lager",
    "ret.group.today": "Heute",
    "ret.group.upcoming": "Demnächst",
    "ret.group.recent": "Kürzlich",
    "ret.tapToEnter": "Ein Möbel antippen, um es einzulagern.",
    "stock.nParts": "{n} Teile",
    "stock.outPill": "Ausgelagert",
    "stock.catPhoto": "Katalogfoto",
    "stock.typeSize": "Typ",
    "stock.composition": "Zusammensetzung",
    "stock.itemNo": "Nummer",
    "stock.ofReturn": "der Retoure",
    "stock.ofIntake": "dieser Einlagerung",
    "stock.place": "Lagerort",
    "stock.day1": "{n} Tag",
    "stock.daysN": "{n} Tage",
    "stock.stIn": "Am Lager seit",
    "stock.stInSince": "eingelagert am {date}",
    "stock.stBackSince": "zurück am {date}",
    "stock.stOut": "Ausgelagert am",
    "stock.stOutAfter": "nach {n} am Lager",
    "stock.stTotal": "Insgesamt am Lager",
    "stock.stTotalH": "alle Zeiträume zusammen",
    "stock.stCycles": "Ein · Aus",
    "stock.stCyclesV": "{i} · {o}",
    "stock.stLast": "Letzte Bewegung",
    "stock.stPlace": "An diesem Platz",
    "stock.stPlaceSince": "seit {date}",
    "stock.stRef": "Gleiche Referenz",
    "stock.stRefV": "{n} am Lager",
    "stock.stRefH": "hier · {all} alle Lager · {out} ausgelagert",
    "stock.stAvg": "Durchschnittliche Dauer",
    "stock.stAvgH": "dieser Referenz am Lager ({n} Einträge)",
    "stock.stRet": "Kundenretouren",
    "stock.stRetH": "dieser Referenz erfasst",
    "media.role.identification": "Identifikation",
    "media.role.damage": "Schaden",
    "media.role.wrapped": "Verpackt",
    "stock.factReturn": "Retoure",
    "stock.factWrapping": "Verpackung",
    "stock.factAdded": "Eingelagert am",
    "com.tab.new": "Neue Reservierung",
    "com.tab.mine": "Meine Reservierungen",
    "com.tab.all": "Alle Reservierungen",
    "com.step1": "Vertrag",
    "com.step2": "Möbel",
    "com.step3": "Senden",
    "com.whichClient": "Für welchen Kunden?",
    "com.continueWith": "Mit „{q}“ fortfahren",
    "com.notFoundManual": "Nicht in den kommenden Lieferungen gefunden: von Hand eingeben",
    "com.contractN": "Vertrag {n}",
    "com.delivOn": "Lieferung {date}",
    "com.delivToPlan": "Lieferung noch zu planen",
    "com.noUpcoming": "Noch kein Vertrag mit einer kommenden Lieferung. „Vertrag nicht in der Liste“ verwenden.",
    "com.notInList": "Vertrag nicht in der Liste?",
    "com.clientPh": "Familie Müller",
    "com.contractNo": "Vertragsnummer",
    "com.ifYouHave": "falls vorhanden",
    "com.delivDate": "Lieferdatum",
    "com.ifKnown": "falls bekannt",
    "com.continueClient": "Mit diesem Kunden fortfahren",
    "com.manClientReq": "Den Kunden angeben.",
    "com.manCidBad": "Die Vertragsnummer hat 3 bis 7 Ziffern.",
    "com.manDelivPast": "Das Lieferdatum ist bereits vorbei.",
    "com.searchFurniture": "Suchen: Sofa, Tisch, Name…",
    "com.transferWarn": "Dieser Kunde wird von {from} beliefert: ein Möbel aus {other} braucht einen Transfer zwischen Lagern.",
    "com.allCats": "Alle",
    "com.loadingStock": "Lagerbestand wird geladen…",
    "com.noneFilter": "Keine Möbel mit diesem Filter verfügbar",
    "com.noneAt": "Keine Möbel verfügbar in {wh}",
    "com.noneHint": "Hier erscheinen Möbel am Lager, nicht reserviert und in gutem Zustand.",
    "com.seePhotos": "Fotos von {name} ansehen",
    "com.defectPhotos": "Fotos der Mängel",
    "com.nAvailable": "{n} verfügbar",
    "com.add": "Hinzufügen",
    "com.removeOne": "Eins entfernen",
    "com.addOne1": "Eins hinzufügen",
    "com.selected1": "Möbel ausgewählt",
    "com.selectedN": "Möbel ausgewählt",
    "com.continue": "Weiter",
    "com.addOne": "Mindestens ein Möbel hinzufügen.",
    "com.nthTaken": "als {n}. genommen",
    "com.noDefectNoted": "kein Mangel vermerkt",
    "com.addOthers": "Weitere Möbel hinzufügen",
    "com.prepareHintDay": "Vorgeschlagen: der Werktag vor der Lieferung am {date}.",
    "com.prepareNoDeliv": "Keine Lieferung in der Planung: den Vorbereitungstag wählen.",
    "com.sending": "Wird reserviert…",
    "com.send1": "{n} Möbel reservieren und die Logistik informieren",
    "com.sendN": "{n} Möbel reservieren und die Logistik informieren",
    "com.justTaken": "Ein Möbel wurde gerade von jemand anderem reserviert: die Liste ist aktuell, Auswahl prüfen.",
    "com.doneTitle": "Möbel reserviert für {client}",
    "com.doneText1": "{n} Möbel ist auf deinen Namen reserviert. Emerson und Ludo sind informiert; Vorbereitung am {prep}.",
    "com.doneTextN": "{n} Möbel sind auf deinen Namen reserviert. Emerson und Ludo sind informiert; Vorbereitung am {prep}.",
    "com.reference": "Referenz {ref}",
    "com.seeMine": "Meine Reservierungen ansehen",
    "com.filter.sent": "Vorzubereiten",
    "com.filter.prepared": "Vorbereitet",
    "com.filter.cancelled": "Storniert",
    "com.filter.all": "Alle",
    "com.noneYet": "Noch keine Reservierung",
    "com.noneYetHint": "Deine Reservierungen erscheinen hier mit ihrem Stand: gesendet, in der Planung, vorbereitet.",
    "com.status.cancelled": "Storniert",
    "com.status.prepared": "Vorbereitet",
    "com.status.planned": "In der Planung · #{ev}",
    "com.status.sent": "Gesendet · Logistik informiert",
    "com.prepOn": "Vorbereitung {date}",
    "com.reservedAt": "reserviert am {when}",
    "com.cancelWarn": "Die Möbel werden wieder verfügbar.",
    "com.cancelWarnPlanning": "Emerson und Ludo werden informiert, die Vorbereitung aus der Planung zu entfernen.",
    "com.markPrepared": "Als vorbereitet markieren",
    "com.cancelResa": "Reservierung stornieren",
    "job.backToList": "Liste",
    "job.timesheetNo": "Terminnummer für deinen Stundenzettel",
    "job.copyNo": "Kopieren",
    "job.copied": "#{n} kopiert",
    "job.emptyToday": "Heute ist keine Montage geplant",
    "job.emptyTodayHint": "Die nächsten Aufträge stehen im Tab «Anstehend».",
    "job.emptyUpcoming": "Keine anstehende Montage",
    "job.emptyUpcomingHint": "Die Termine «Assemblage meubles pour …» werden automatisch angelegt, sobald die Möbel eines Kunden ein Ankunftsdatum haben.",
    "job.emptyDone": "Noch kein Auftrag abgeschlossen",
    "job.emptyDoneHint": "Sobald die Monteure auf «Abschliessen» tippen, erscheint die tatsächliche Zeit hier und unter «Ist gegen Soll».",
    "jobs.notArrivedOne": "⏳ {n} noch nicht angekommen",
    "jobs.notArrivedMany": "⏳ {n} noch nicht angekommen",
    "jobs.receivedPct": "📦 erhalten {p} %",
    "jobs.receivedOfOne": "{g} von {n} Möbeln erhalten",
    "jobs.receivedOfMany": "{g} von {n} Möbeln erhalten",
    "jobs.rxWarn": "⚠️ Wareneingang",
    "jobs.rxOk": "📄 erhalten",
    "jobs.rxConfirmed": "📝 Bestellung bestätigt",
    "jobs.progress": "Fortschritt {p} %: erledigte Arbeit, gewichtet nach der geschätzten Zeit jedes Artikels",
    "timer.notStarted": "Noch nicht gestartet",
    "timer.ofEst": "{t} von {e} geschätzt",
    "timer.teamTime": "{n} Monteure · {t} gemeinsame Arbeit",
    "timer.left": "noch {t} bis zur Schätzung",
    "timer.over": "{t} über der Schätzung",
    "timer.doneSum": "Abgeschlossen in {r} Arbeit bei {e} geschätzt",
    "arch.nOne": "{n} archivierter Kunde",
    "arch.nMany": "{n} archivierte Kunden",
    "arch.more": "… und {n} weitere: Suche verfeinern.",
    "arch.auto": "geliefert am {d}",
    "arch.man": "von Hand archiviert am {d}",
    "start.added": "Hinzugefügt",
    "start.addTitle": "Monteure hinzufügen",
    "start.busyOn": "bei {c}",
    "start.goOne": "Mit {n} Monteur starten",
    "start.goMany": "Mit {n} Monteuren starten",
    "start.addGoOne": "{n} Monteur hinzufügen",
    "start.addGoMany": "{n} Monteure hinzufügen",
    "start.moved": "{n} von {c} abgezogen",
    "start.noStaff": "Noch kein Monteur für dieses Lager gefunden: Vornamen unten hinzufügen.",
    "furn.one.wardrobes": "Kleiderschrank",
    "furn.many.wardrobes": "Kleiderschränke",
    "furn.one.beds": "Bett",
    "furn.many.beds": "Betten",
    "furn.one.bed-components": "Bettelement",
    "furn.many.bed-components": "Bettelemente",
    "furn.one.chest-drawers": "Kommode",
    "furn.many.chest-drawers": "Kommoden",
    "furn.one.sideboard": "Sideboard",
    "furn.many.sideboard": "Sideboards",
    "furn.one.bookcase-display-cabinet": "Bücherregal",
    "furn.many.bookcase-display-cabinet": "Bücherregale",
    "furn.one.tv-tables": "TV-Möbel",
    "furn.many.tv-tables": "TV-Möbel",
    "furn.one.desks": "Schreibtisch",
    "furn.many.desks": "Schreibtische",
    "furn.one.dining-tables": "Esstisch",
    "furn.many.dining-tables": "Esstische",
    "furn.one.high-table": "Stehtisch",
    "furn.many.high-table": "Stehtische",
    "furn.one.console": "Konsole",
    "furn.many.console": "Konsolen",
    "furn.one.coffee-tables": "Couchtisch",
    "furn.many.coffee-tables": "Couchtische",
    "furn.one.bedside-tables": "Nachttisch",
    "furn.many.bedside-tables": "Nachttische",
    "furn.one.shoe-coat-rack": "Garderobe",
    "furn.many.shoe-coat-rack": "Garderoben",
    "furn.one.sofas": "Sofa",
    "furn.many.sofas": "Sofas",
    "furn.one.outdoor-furniture": "Gartenmöbel",
    "furn.many.outdoor-furniture": "Gartenmöbel",
    "furn.one.side-table": "Beistelltisch",
    "furn.many.side-table": "Beistelltische",
    "furn.one.desk-chair": "Bürostuhl",
    "furn.many.desk-chair": "Bürostühle",
    "furn.one.chairs": "Stuhl",
    "furn.many.chairs": "Stühle",
    "furn.one.high-chair": "Barhocker",
    "furn.many.high-chair": "Barhocker",
    "furn.one.armchairs": "Sessel",
    "furn.many.armchairs": "Sessel",
    "furn.one.pouf-stool-bench": "Pouf",
    "furn.many.pouf-stool-bench": "Poufs",
    "furn.one.floor-lamp": "Stehlampe",
    "furn.many.floor-lamp": "Stehlampen",
    "furn.one.mirror": "Spiegel",
    "furn.many.mirror": "Spiegel",
    "furn.one.table-lamp": "Tischlampe",
    "furn.many.table-lamp": "Tischlampen",
    "furn.one.ceiling-light": "Deckenleuchte",
    "furn.many.ceiling-light": "Deckenleuchten",
    "furn.one.wall-light": "Wandleuchte",
    "furn.many.wall-light": "Wandleuchten",
    "furn.one.decorative-items": "Dekorationsobjekt",
    "furn.many.decorative-items": "Dekorationsobjekte",
    "furn.one.decoration": "Dekoration",
    "furn.many.decoration": "Dekorationen",
    "furn.one.cushion-and-throws": "Kissen oder Plaid",
    "furn.many.cushion-and-throws": "Kissen und Plaids",
    "furn.one.carpet": "Teppich",
    "furn.many.carpet": "Teppiche",
    "furn.one.artificial-plant": "Kunstpflanze",
    "furn.many.artificial-plant": "Kunstpflanzen",
    "furn.one.electric-appliances": "Elektrogerät",
    "furn.many.electric-appliances": "Elektrogeräte",
    "furn.one.internet": "Online-Artikel",
    "furn.many.internet": "Online-Artikel",
    "furn.one.hangers": "Kleiderbügel",
    "furn.many.hangers": "Kleiderbügel",
    "furn.one.kits": "Kit",
    "furn.many.kits": "Kits",
    "furn.one.others": "anderer Artikel",
    "furn.many.others": "andere Artikel",
    "job.notArrived": "noch nicht angekommen",
    "job.naExpected": "erwartet am {d}",
    "job.rxScanned": "✅ erhalten (Lieferschein gescannt)",
    "job.boxesUnknown": "Kartonreferenzen noch nicht bekannt",
    "docs.kindDn": "🚚 Lieferschein",
    "docs.kindOc": "📝 Auftragsbestätigung",
    "docs.kindInv": "🧾 Rechnung",
    "docs.kindOth": "📄 Auftragsdokument",
    "docs.notRx": "kein Wareneingang",
    "docs.arrOk": "✅ angekommen am {d}",
    "docs.arrNo": "⏳ noch nicht erhalten",
    "docs.ocWarn": "Auftragsbestätigung ≠ Wareneingang: Dieser Auftrag ist noch nicht im Lager angekommen. Das Möbel muss nicht gesucht werden.",
    "docs.others": "Weitere Dokumente des Auftrags ({n})",
    "docs.none": "Noch kein Dokument in diesem Auftrag.",
    "docs.exp": "Links möglicherweise abgelaufen: öffne die Bestellung in der Planung.",
    "docs.img": "Foto",
    "docs.pdf": "PDF",
    "docs.noOrder": "Kein Lieferantenauftrag verknüpft.",
    "sdocs.title": "Lieferantendokumente",
    "sdocs.hint": "Im Lieferantenauftrag der Planung abgelegte Dokumente (die des Kunden zuerst). Achtung: Eine Auftragsbestätigung oder eine Rechnung bedeutet nicht, dass das Möbel angekommen ist. Nur «angekommen am …» oder ein Lieferschein bestätigt es.",
    "issue.placeholder": "Z. B. Platte auf 20 cm zerkratzt, Fussschrauben fehlen, Karton bei Ankunft offen…",
    "issue.needText": "Beschreiben Sie das Problem in wenigen Worten.",
    "issue.needBy": "Geben Sie an, wer das Problem meldet.",
    "issue.to": "An:",
    "issue.cc": "Kopie:",
    "issue.unsales": "Kein Verkäufer in der Planung: Die Nachricht geht an Ludo.",
    "issue.sendTo": "An {n} senden",
    "mediaJob.title": "Fotos und Videos der Montage",
    "mediaJob.hint": "Fotografieren oder filmen Sie die Montage: Schubladen und Türen offen und geschlossen, Stabilität, Befestigungen, dann die Verpackung. Jedes Möbel hat auch seine eigene 📷-Schaltfläche.",
    "mediaJob.empty": "Noch kein Foto und kein Video.",
    "mediaJob.photo": "📷 Foto aufnehmen",
    "mediaJob.video": "🎥 Filmen",
    "mediaJob.file": "📁 Datei wählen",
    "mediaJob.prep": "Foto wird vorbereitet…",
    "mediaJob.photoType": "Fotoformat nicht akzeptiert: nimm das Foto mit der Kamera auf (JPEG).",
    "fin.time": "{r} Arbeit bei {e} geschätzt",
    "fin.leftOne": "{n} Möbel noch nicht als montiert abgehakt",
    "fin.leftMany": "{n} Möbel noch nicht als montiert abgehakt",
    "fin.allOk": "Alle Möbel sind als montiert abgehakt",
    "fin.chkLeftOne": "{n} Artikel ohne Montage noch nicht geprüft",
    "fin.chkLeftMany": "{n} Artikel ohne Montage noch nicht geprüft",
    "fin.chkAllOk": "Alle Artikel ohne Montage sind geprüft",
    "fin.probsNone": "Kein Problem gemeldet",
    "fin.probsOne": "{n} Problem gemeldet, in der Zusammenfassung enthalten",
    "fin.probsMany": "{n} Probleme gemeldet, in der Zusammenfassung enthalten",
    "fin.media": "📷 {c} zur Kontrolle",
    "fin.noMedia": "⚠️ Kein Foto und kein Video: Fotografieren oder filmen Sie die Montage vor dem Abschliessen",
    "fin.photosOne": "{n} Foto",
    "fin.photosMany": "{n} Fotos",
    "fin.videosOne": "{n} Video",
    "fin.videosMany": "{n} Videos",
    "fin.and": "und",
    "fin.pack": "Ich habe jedes Möbel mit MYOTAKU-Decken und Stretchfolie verpackt",
    "fin.needPack": "Bestätigen Sie die Verpackung (MYOTAKU-Decken + Stretchfolie) vor dem Abschliessen.",
    "fin.all": "Alles als montiert abhaken",
    "fin.chkAll": "Alles als geprüft abhaken",
    "fin.note": "Bemerkungen für den Verkäufer (optional)",
    "fin.notePh": "Z. B. Sideboard in der MYOTAKU-Decke verpackt, bereit im Kundenwagen.",
    "fin.send": "Zusammenfassung an {n} senden",
    "fin.go": "Abschliessen und Zusammenfassung senden",
    "fin.goNo": "Abschliessen ohne zu senden",
    "fin.done": "Montage abgeschlossen",
    "fin.doneSent": "Montage abgeschlossen, Zusammenfassung an {n} gesendet",
    "recap.none": "Zusammenfassung nicht gesendet (Entscheid des Monteurs)",
    "mediaJob.itemAdd": "📷 Hinzufügen",
    "mediaJob.itemTitle": "Fotos und Videos des Möbels",
    "mediaJob.close": "Schliessen",
    "thx.title": "Danke {names}!",
    "thx.titleTeam": "Danke an das ganze Team!",
    "thx.lead": "Die Montage für {c} ist abgeschlossen.",
    "thx.msg0": "Dank Ihrer sorgfältigen Arbeit erhält der Kunde gut montierte, geprüfte und geschützte Möbel: Sie tragen direkt zu seiner Zufriedenheit bei.",
    "thx.msg1": "Gute Arbeit! Jedes sorgfältig montierte Möbel bedeutet einen zufriedenen Kunden am Liefertag. Danke für Ihren Einsatz.",
    "thx.msg2": "Ihre Präzision und Sorgfalt machen beim Kunden den Unterschied. Danke, dass Sie zu seiner Zufriedenheit beigetragen haben!",
    "thx.asmOne": "Möbel montiert",
    "thx.asmMany": "Möbel montiert",
    "thx.chkOne": "Artikel geprüft",
    "thx.chkMany": "Artikel geprüft",
    "thx.time": "Arbeit",
    "thx.sent": "Die Zusammenfassung wurde an {n} gesendet.",
    "thx.close": "Schliessen",
    "links.title": "Links",
    "links.order": "Delivery calendar Auftrag #{id}",
    "links.event": "Montagetermin in der Planung",
    "links.contract": "Vertrag #{id}",
    "rst.title": "Verantwortlicher",
    "rst.p": "Setzt diese Montage zurück: Zähler montiert und geprüft, Stoppuhr, aufgewendete Zeit, Monteure und Abschlussnachricht. Gemeldete Probleme, Fotos und Videos bleiben erhalten.",
    "rst.b": "↺ Diese Montage zurücksetzen",
    "rst.ok": "Bestätigen: alles zurücksetzen",
    "rst.no": "Abbrechen",
    "rst.run": "Die Montage läuft: Sie wird gestoppt.",
    "rst.done": "Montage zurückgesetzt",
    "rst.undo": "↩️ Zurücksetzen rückgängig machen",
    "rst.undone": "Zurücksetzen rückgängig gemacht",
    "rst.last": "Zurückgesetzt am {d}",
    "media.del": "Löschen",
    "media.delOk": "Löschen bestätigen",
    "hist.kpiJobs": "Abgeschlossene Montagen",
    "hist.kpiActual": "Tatsächliche Zeit",
    "hist.ofEst": "von {e} geschätzt",
    "hist.kpiGap": "Gesamtabweichung",
    "hist.kpiOk": "Innerhalb der Schätzung",
    "hist.perJob": "Pro Montage",
    "hist.statsNote": "Tatsächliche Zeit = Summe der Zeiten jedes Monteurs an der Montage. Schätzung = Zeit aus der Planung (Montage + Handling).",
    "hist.colNo": "#",
    "hist.colClient": "Kunde",
    "hist.colWh": "Lager",
    "hist.colFitters": "Monteure",
    "hist.gap": "Abweichung",
    "hist.perFitter": "Pro Monteur",
    "hist.perFitterNote": "Zeit jedes Monteurs auf abgeschlossenen Montagen, verglichen mit seinem Anteil an der Schätzung. Nur für Administratoren sichtbar.",
    "hist.colJobs": "Montagen",
    "hist.colVs": "Ist / Soll",
    "sup.note": "Produktivität Minute für Minute: geplante Zeit der bestätigten Möbel, plus das Möbel in Arbeit bis zu seiner geplanten Zeit, verglichen mit der Zeit der Monteure. Alarm nach 30 Min. Montage, wenn die Leistung unter 70 % fällt oder seit 1,5 × der geplanten Zeit des grössten verbleibenden Möbels (mindestens 30 Min.) nichts bestätigt wurde. Slack an Ludo und Emerson, einmal pro Montage und Alarm (stündliche Prüfung, 7 bis 18 Uhr). Monteure sehen diesen Tab nicht.",
    "sup.sentAt": "Slack gesendet um {t}",
    "sup.sentWait": "Slack bei der nächsten Prüfung (stündlich, 7 bis 18 Uhr)",
    "sup.open": "Auftrag öffnen",
    "sup.lastAgo": "letzte Bestätigung: vor {t}",
    "sup.never": "letzte Bestätigung: keine Bestätigung",
    "sup.dayRate": "Leistung heute",
    "sup.per": "Pro Monteur",
    "sup.perN": "Montagezeit, geplante Zeit der validierten Möbel und Leistung (gewonnen ÷ aufgewendet). Ein Möbel, das mehrere zusammen montiert haben, wird unter ihnen aufgeteilt.",
    "sup.perNone": "Keine Montagezeit in diesem Zeitraum.",
    "sup.period.today": "Heute",
    "sup.period.7d": "7 Tage",
    "sup.period.30d": "30 Tage",
    "sup.col.fitter": "Monteur",
    "sup.col.minutes": "Montagezeit",
    "sup.col.earned": "Gewonnene Zeit",
    "sup.col.rate": "Leistung",
    "sup.col.items": "Validierte Möbel",
    "sup.col.alerts": "Warnungen",
    "sup.spkSpent": "aufgewendete Zeit (Personenminuten)",
    "sup.spkEarned": "gewonnene Zeit (validierte Möbel)",
    "sup.tl": "Der Tag Minute für Minute",
    "sup.tlN": "Eine Zeile pro Monteur: Jeder Block ist eine Montage (eine Farbe pro Kunde), die weissen Striche sind validierte Möbel. Für Details mit der Maus über einen Block fahren.",
    "sup.noTl": "Heute keine Montage.",
    "jobs.readAt": "Planung gelesen am {d} um {t}",
    "job.readOnly": "Nur-Lese-Zugriff: Bitte Ludo um Mitwirkenden-Zugriff, um Zeitmessungen zu starten und Probleme zu melden.",
    "stock.readOnly": "Sie haben Nur-Lese-Zugriff: Bitten Sie Ludo um Mitwirkenden-Zugriff, um Möbel zu erfassen.",
    "cartx.readOnly": "Dein Zugriff ist nur lesend: Bitte Ludo um Bearbeitungszugriff, um Wagen zuzuordnen.",
    "access.readOnly": "nur lesen",
    "access.adminCol": "Admin",
    "access.setPassword": "Neues Passwort",
    "access.savePassword": "Speichern",
    "access.cancel": "Abbrechen",
    "access.passwordChanged": "Passwort geändert.",
    "access.ownLocked": "Bei Ihrem eigenen Konto nicht möglich.",
    "nav.home": "Start",
    "home.choose": "Wähle deinen Bereich",
    "home.desc.stock": "Erfassung der Kundenrückgaben, Etiketten, Inventar",
    "home.desc.preparation": "Montagen des Tages, Zeitmessung, Eingang der Bestellungen",
    "home.desc.carts": "QR-Code eines Wagens scannen: zu welchem Kunden er gehört",
    "home.desc.supervisor": "Laufende Montagen, Leistung, Ist gegen Soll",
    "home.desc.commercial": "Möbel aus dem Lager für einen Kunden reservieren",
    "home.desc.production": "Kit-Produktion, Takt und Artikelbestand",
    "home.desc.receiving": "Lieferscheine der Lieferanten lesen",
    "home.desc.access": "Wer welche Module nutzt",
    "prod.status.queued": "Wartend",
    "prod.status.running": "In Arbeit",
    "prod.status.paused": "Pausiert",
    "prod.status.stopped": "Gestoppt",
    "prod.status.finished": "Fertig",
    "prod.status.cancelled": "Storniert",
    "prod.meta.total": "{t} insgesamt",
    "prod.meta.startedBy": "gestartet von {n}",
    "prod.meta.finishedBy": "beendet von {n}",
    "prod.stoppedAt": "⛔ Gestoppt um {t} von {n} —",
    "prod.time.running": "In Arbeit seit {a} (vorgegebene Zeit {g}) — {d}",
    "prod.time.paused": "⏸ Pausiert seit {at} — geleistete Zeit {a} (vorgegebene Zeit {g}) — {d}",
    "prod.time.done": "Erledigt in {a} (vorgegebene Zeit {g}) — {d}",
    "prod.time.exact": "genau in der vorgegebenen Zeit",
    "prod.time.more": "+{d} mehr",
    "prod.time.less": "−{d} weniger",
    "prod.pauseBanner": "⏸ Vorbereitung pausiert",
    "prod.stopBanner": "⛔ Vorbereitung gestoppt — der Verantwortliche wurde benachrichtigt",
    "prod.pauseSince": "seit {t}{by} — die Zeitmessung ist gestoppt. Bereits erledigte Einheiten: {d} / {q}.",
    "prod.pauseBy": " (von {n})",
    "prod.resume": "▶️ Vorbereitung fortsetzen",
    "prod.reportProblem": "⛔ Problem melden (Material, Kartons, Bestand)",
    "prod.bravoMany": "🎉 Bravo! Die {q} verlangten Einheiten sind fertig. Klicke auf „Als erledigt markieren“, um diesen Auftrag abzuschliessen.",
    "prod.bravoOne": "🎉 Bravo! Die verlangte Einheit ist fertig. Klicke auf „Als erledigt markieren“, um diesen Auftrag abzuschliessen.",
    "prod.newOrderHint": "Wähle das Kit und die Menge: Der Auftrag erscheint sofort am Platz des Vorbereiters.",
    "prod.chip.min": "{n} Min / Einheit",
    "prod.chip.composed": "Aus {n} Unterkits",
    "prod.chip.inStock": "{n} am Lager",
    "prod.chip.dispo": "verfügbar {n} (reserviert {r})",
    "prod.chip.short": "fehlen {n} (reserviert {r})",
    "prod.contains": "Dieses Kit enthält",
    "prod.pickKit": "Wähle ein Kit",
    "prod.shortage": "⚠ Artikelbestand reicht nicht für {q} × {k} — es fehlen: {list}{more}.",
    "prod.shortageMore": " + {n} weitere",
    "prod.notePh": "z. B. Kunde X zuerst, vor 14 Uhr zu liefern…",
    "prod.sendOrder": "Produktionsauftrag senden",
    "prod.sending": "Wird gesendet…",
    "prod.sendFailed": "Fehlgeschlagen — Auftrag NICHT gesendet, erneut versuchen",
    "prod.qtyPreview": "{q} × {m} Min =",
    "prod.chip.minShort": "{n} Min",
    "prod.sync.ok": "Synchronisiert",
    "prod.sync.off": "Nicht synchronisiert",
    "prod.sync.failed": "⚠️ Speichern fehlgeschlagen",
    "prod.prep.none": "Heute keine Vorbereitungstätigkeit erfasst.",
    "prod.prep.who": "Vorbereiter",
    "prod.prep.kits": "Validierte Kits",
    "prod.prep.worked": "Gearbeitete Zeit",
    "prod.prep.given": "Vorgegebene Zeit",
    "prod.prep.gap": "Abweichung",
    "prod.prep.avg": "Ø / Kit",
    "prod.prep.pauses": "Pausen",
    "prod.prep.now": "Gerade jetzt",
    "prod.prep.noName": "(ohne Namen)",
    "prod.prep.nowRunning": "{k} ×{q} — in Arbeit ({d}/{q})",
    "prod.prep.nowPaused": "⏸ {k} ×{q} — pausiert seit {t}",
    "audit.title": "Konsistenzprüfung des Kit-Bestands",
    "audit.checkedAt": "geprüft um {t}",
    "audit.checking": "Prüfung läuft…",
    "audit.ok": "✓ Alles stimmt überein: Bestand, Verlauf und Lieferausgänge passen zusammen.",
    "audit.errs": "✗ {n} Fehler",
    "audit.warns": "⚠ {n} Punkt(e) zu prüfen",
    "audit.checks": "Laufend geprüft: jeder Bestand = sein Verlauf, jede Lieferung einmal ausgebucht, keine vergangene Lieferung vergessen, automatische Prüfung aktuell.",
    "audit.contract": "Vertrag #{n}",
    "audit.untraced": "{warehouse} — {kit}: nicht erfasste Bewegung vor dem {at}",
    "audit.untracedD": "Eine Bestandsänderung wurde nicht im Verlauf erfasst: dieses Kit neu zählen.",
    "audit.mismatch": "{warehouse} — {kit}: Bestand {stock} ≠ Verlauf {history}",
    "audit.mismatchD": "Der angezeigte Bestand entspricht nicht der letzten erfassten Bewegung. Eine Änderung ohne Verlauf oder ein fehlgeschlagener Eintrag: dieses Kit neu zählen.",
    "audit.no_history": "{warehouse} — {kit}: Bestand {stock} ganz ohne Verlauf",
    "audit.no_historyD": "Dieser Bestand wurde nie über die App erfasst: mit einer Zählung prüfen.",
    "audit.delivered_no_exit": "Lieferung {label} als geliefert markiert ohne Lagerausgang für: {kits}",
    "audit.delivered_no_exitD": "Diese Kits wurden nicht ausgebucht: neu zählen.",
    "audit.reserved_but_exited": "Lieferung {label}: bereits ausgebucht für {kits}, obwohl noch „reserviert“",
    "audit.reserved_but_exitedD": "Vor der nächsten Prüfung mit einer Zählung kontrollieren.",
    "audit.past_reserved": "Lieferung {label}: Datum vorbei und Kits noch reserviert (Ausbuchung verpasst)",
    "audit.past_reservedD": "Die nächste automatische Prüfung sollte sie ausbuchen. Bleibt diese Meldung nach 9 oder 15 Uhr, funktioniert die Prüfung nicht.",
    "audit.short_exit": "{warehouse} — {kit}: Lieferung vom {at} bei zu geringem Bestand ausgebucht ({had} für {need})",
    "audit.short_exitD": "Der Bestand war schon zu niedrig: mit einer Zählung oder einer fehlenden Sendung prüfen.",
    "audit.orphan_exit": "Ausgang {kit} vom {at} ohne passende Lieferung",
    "audit.orphan_exitD": "Die Lieferung wurde nach dem Ausgang aus der Verfolgung genommen: prüfen, ob das Kit wirklich weg ist.",
    "audit.no_run": "Keine automatische Prüfung erfasst",
    "audit.no_runD": "Die Aufgabe um 9 / 15 Uhr hat noch nie einen Durchlauf erfasst.",
    "audit.run_failed": "Letzte automatische Prüfung fehlgeschlagen ({at})",
    "audit.run_failedD": "{message} — bei diesem Durchlauf wurde nichts ausgebucht.",
    "audit.run_stale": "Seit {hours} h keine automatische Prüfung",
    "audit.run_staleD": "Die heutigen Ausbuchungen sind nicht gemacht: die automatische Aufgabe läuft nicht mehr.",
    "audit.no_client": "{n} reservierte Lieferung(en) ohne Kundennamen in der Planung",
    "audit.no_clientD": "{contracts}",
    "alloc.incomplete": "⚠️ Prüfung unvollständig",
    "alloc.stale": "⚠️ seit über 26 h keine Prüfung",
    "prod.stockTotalTab": "Total",
    "prod.stockTotal": "Total der 3 Lager",
    "prod.stockAt": "Am Lager in {w}",
    "prod.kitsOne": "{n} Kit",
    "prod.kitsMany": "{n} Kits",
    "prod.pill.allocated": "zugeteilt {n}",
    "prod.pill.allocatedT": "Reserviert für kommende bestätigte Kundenlieferungen",
    "prod.pill.toSend": "zu senden {n}",
    "prod.pill.toSendT": "Fehlt in Dielsdorf / Berlin, mit einer Sendung aus Penthalaz zu decken",
    "prod.pill.dispo": "verfügbar {n}",
    "prod.pill.short": "fehlen {n}",
    "prod.short.wh": "⚠ {w} — {k}: fehlen {n} (zugeteilt {a}, am Lager {s})",
    "prod.short.pz": "⚠ Penthalaz — {k}: fehlen {n} (zugeteilt {a}{send}, am Lager {s}{prod})",
    "prod.short.send": " + zu senden {n}",
    "prod.short.inProd": ", bereits in Vorbereitung {n}",
    "prod.short.makeOrder": "Auftrag vorbereiten ×{n}",
    "prod.alloc.empty": "Derzeit keine bestätigte Lieferung mit Kits.",
    "prod.alloc.past": " — vergangene Lieferung, wartet auf den Status „Geliefert“",
    "prod.alloc.artPz": "Einzelartikel — am Liefertag aus dem Artikelbestand ausgebucht",
    "prod.alloc.artElse": "Einzelartikel — in {w} wird kein Artikelbestand geführt",
    "prod.alloc.type.CT": "Kurzfristig",
    "prod.alloc.type.LT": "Langfristig",
    "prod.alloc.type.COMPL": "Ergänzung",
    "prod.alloc.type.VENTE": "Verkauf",
    "prod.alloc.type.HS": "Home Staging",
    "prod.alloc.type.MAINT": "Wartung",
    "prod.alloc.type.AUTRE": "Andere",
    "prod.alloc.type.BTR": "BTR",
    "prod.alloc.type.EVT": "Vertrag als Event",
    "prod.short.makeTransfer": "Sendung vorbereiten ×{n}",
    "prod.send.title": "Kits an ein Lager senden",
    "prod.send.hint": "Wähle das Lager, gib die aus Penthalaz gesendeten Mengen ein, dann „Senden“. Der Bestand in Penthalaz sinkt sofort; die Kits sind unterwegs, bis das Ziellager sie annimmt (Reiter Wareneingang).",
    "prod.send.to": "Nach {w}",
    "prod.send.qtyFor": "Gesendete Menge — {k}",
    "prod.send.notePh": "z. B. Lastwagen vom 26.09, Lieferschein-Nr.…",
    "prod.send.total": "Total: {n} Kit(s) von Penthalaz nach {w}.",
    "prod.send.none": "Mindestens eine Menge eingeben.",
    "prod.send.over": "Bestand in Penthalaz reicht nicht — {list}.",
    "prod.send.button": "An {w} senden ({n} Kit(s))",
    "prod.send.buttonEmpty": "An {w} senden",
    "prod.send.ask": "{n} Kit(s) von Penthalaz nach {w} senden?",
    "prod.send.yes": "Ja, senden",
    "prod.send.done": "✓ Sendung erfasst: {n} Kit(s) nach {w}. Der Bestand in Penthalaz ist aktualisiert; {w} bucht sie beim Wareneingang ein.",
    "prod.send.log": "Letzte Sendungen",
    "prod.send.logEmpty": "Noch keine Sendung erfasst.",
    "prod.send.inTransit": "unterwegs",
    "prod.view.resp": "Verantwortlicher",
    "prod.view.prep": "Vorbereiter",
    "prod.orders": "Produktionsaufträge",
    "prod.ordersHint": "Neueste zuerst. Der Fortschritt wird vom Vorbereiter laufend aktualisiert.",
    "prod.ordersEmpty": "Noch kein Auftrag gesendet.",
    "prod.queueHint": "Die vom Verantwortlichen gesendeten Aufträge kommen hier an, älteste zuerst.",
    "prod.youAre": "Du bist:",
    "prod.youArePh": "Dein Vorname (nötig zum Starten)",
    "prod.nameFirst": "✋ Gib zuerst deinen Vornamen bei „Du bist“ ein.",
    "prod.stockInPlace": "Bereits vorhandener Bestand",
    "demand.ofMonth": "(Monatstotal {n})",
    "demand.segment": "{k} · {m}: {n} Kit(s) (Monatstotal {total})",
    "prod.entry.label": "Eingabe:",
    "prod.entry.add": "➕ Menge hinzufügen",
    "prod.entry.set": "✏️ Total korrigieren",
    "prod.entry.addHelp": "Gib nur die erhaltene Menge ein (z. B. 30), dann „Hinzufügen“ oder Enter: Sie wird zum aktuellen Bestand addiert, ohne Rechnen. Eine negative Zahl (z. B. -2) entfernt.",
    "prod.entry.setHelp": "Gib das neue gezählte Total ein: Es ersetzt die aktuelle Menge.",
    "prod.entry.now": "Aktuelle Menge",
    "prod.entry.addPh": "+ Mge",
    "prod.entry.addFor": "Hinzuzufügende Menge — {k}",
    "prod.entry.totalFor": "Neues Total — {k}",
    "prod.entry.addBtn": "Hinzufügen",
    "prod.entry.askAdd": "Bist du sicher, dass du {n} zur Menge von {k} hinzufügen willst?",
    "prod.entry.askDown": "Bist du sicher, dass du die Menge von {k} um {n} verringern willst?",
    "prod.entry.yesAdd": "Ja, hinzufügen",
    "prod.entry.yesDown": "Ja, verringern",
    "prod.entry.saved": "✅ Danke, die neue Menge ist im Bestand gespeichert.",
    "prod.entry.notSaved": "⚠️ Die Menge konnte NICHT gespeichert werden (Verbindung?). Erneut versuchen.",
    "prod.entry.tapClose": "Zum Schliessen tippen",
    "prod.history.open": "Klicken, um den Verlauf zu sehen",
    "prod.history.title": "Verlauf — {k} ({w})",
    "prod.history.close": "Schliessen",
    "prod.history.current": "Aktueller Bestand: {n}",
    "prod.history.none": "Keine Bewegung erfasst.",
    "inv.src.correction": "Korrektur",
    "inv.src.plus_minus": "Taste ±",
    "prod.delete": "Löschen",
    "prod.deleteYes": "Löschen bestätigen",
    "prod.unitSummary": "Einheit Nr. {n} in Montage — {c} / {m} Artikel abgehakt",
    "inv.lastAt": "Letzte Inventur in {w}: {when} von {who} — {n} Kit(s) gezählt, {d} Abweichung(en) korrigiert.",
    "inv.noneAt": "Keine Inventur für {w} erfasst.",
    "inv.kit": "Kit",
    "inv.app": "App",
    "inv.gap": "Abweichung",
    "inv.summary": "{n} Kit(s) gezählt — {d} Abweichung(en) zu korrigieren",
    "inv.start": "Gib die gezählten Mengen ein, um zu beginnen.",
    "inv.ask": "Inventur von {w} bestätigen?",
    "inv.askDetail": "{n} Kit(s) gezählt — {d} Korrektur(en)",
    "inv.yes": "Ja, Inventur bestätigen",
    "inv.done": "Inventur {w}: {n} Kit(s) gezählt, {d} Korrektur(en)",
    "inv.historyHint": "Jede Mengenänderung — von Hand, mit den +/−-Tasten oder die automatische Gutschrift jedes montierten Kits (sobald die Einheit validiert ist) — wird hier mit Datum und Uhrzeit erfasst.",
    "inv.countedCol": "Gezählt",
    "art.hint": "Menge im Regal für jeden Artikel, gleiche Referenz wie auf den Etiketten. Sie sinkt von selbst, wenn der Vorbereiter einen Artikel in einer Checkliste abhakt; bei einer Lieferung Modus „➕ Hinzufügen“: nur die erhaltene Menge eingeben, die App rechnet; nach einer Zählung Modus „✏️ Total korrigieren“. Auf einen Artikel klicken öffnet sein Datenblatt.",
    "art.total": "{q} Einheiten am Lager über {n} Referenzen",
    "art.noMatch": "Kein Artikel entspricht der Suche.",
    "art.linenShared": "Wäsche — Artikel für mehrere Grössen",
    "art.refCount": "{n} Ref.",
    "art.sharedBy": "Gemeinsame Referenz für: {list}",
    "art.chip.sku": "Syst. {s}",
    "art.chip.ref": "Lief.-Ref. {s}",
    "art.chip.confirm": "zu bestätigen",
    "art.chip.open": "Datenblatt ›",
    "sheet.hist.date": "Datum",
    "sheet.hist.move": "Bewegung",
    "sheet.hist.stock": "Bestand",
    "sheet.hist.loading": "Verlauf wird geladen…",
    "sheet.src.manual": "Manuell hinzugefügt",
    "sheet.src.correction": "Gesamtmenge korrigiert (Zählung)",
    "sheet.src.plus_minus": "Schaltfläche + / −",
    "sheet.src.production": "Für ein Kit entnommen (Checkliste)",
    "sheet.src.restock": "Zurück ins Lager (Einheit entfernt)",
    "sheet.src.receipt": "Lieferanteneingang",
    "sheet.src.reception_check": "Korrektur bei der Prüfung eines Wareneingangs",
    "sheet.src.delivery": "Kundenlieferung (Einzelartikel)",
    "sheet.src.deliveryTo": "Kundenlieferung — {c}",
    "sheet.src.inventory": "Inventur (physische Zählung)",
    "sheet.src.transfer": "Versand zwischen Lagern",
    "sheet.det.order": "Auftrag {k} ×{n}",
    "sheet.det.unticked": "(Haken entfernt)",
    "sheet.det.supplier": "Lieferant {s}",
    "sheet.det.receipt": "Eingang {n}",
    "sheet.det.contract": "Vertrag #{n}",
    "sheet.det.by": "von {w}",
    "sheet.det.short": "{n} fehlen im Regal",
    "inv.src.restock": "zurück ins Lager",
    "artrec.supplier": "Lieferant (optional)",
    "artrec.hintPaste": "Am Computer können Sie auch einen Screenshot einfügen (Ctrl+V) oder eine Datei auf diese Karte ziehen. Steht die erhaltene Menge nicht auf dem Dokument, schreiben Sie sie in die Notiz.",
    "note.notAdded": "⚠ Nicht hinzugefügt: {list}",
    "note.othersReady": " — die anderen Dateien sind bereit.",
    "note.maxImages": "{f} (höchstens {n} Bilder)",
    "note.badFormat": "{f} — Format nicht unterstützt",
    "artrec.photoN": "Foto {n}",
    "prod.printSheets": "{n} Kartonblätter drucken",
    "prod.printSheet": "1 Kartonblatt drucken",
    "ident.phase.type": "Möbeltyp wird erkannt…",
    "ident.phase.retour": "Foto wird mit der Rückgabe verglichen…",
    "ident.phase.catalogue": "Suche im Myotaku-Katalog…",
    "ident.cancel": "Abbrechen",
    "ident.recognize": "Möbel erkennen",
    "ident.noAi": "Die Erkennung ist hier nicht verfügbar: Wählen Sie das Möbel von Hand.",
    "ident.aiLimit": "Im Moment zu viele Anfragen. Versuchen Sie es gleich noch einmal oder suchen Sie von Hand.",
    "ident.hintGrid": "Fotografieren Sie das ganze Möbel vor dem Einpacken und vergleichen Sie es dann mit dem Katalogfoto.",
    "stock.qtyAuto": "{q} in dieser Rückgabe erwartet, {k} bereits erfasst: Menge automatisch eingestellt.",
    "stock.qtyOver": "Diese Rückgabe erwartete {q}, und {k} sind bereits erfasst: dieses Möbel ist zusätzlich.",
    "stock.qtyHint": "Gleiche Referenz, gleicher Zustand, gleiche Palette: {n} Datensätze, {n} Lagernummern und {n} Etiketten, eines pro Möbel. Ist eines in einem anderen Zustand, erfassen Sie es separat.",
    "stock.qtyNums": "Vergebene Nummern: {list}.",
    "stock.unitOf": "Möbel {i}/{n}",
    "compo.dialogTitle": "Zusammensetzung des Möbels",
    "compo.dialogHint": "In wie vielen Teilen wird dieses Möbel gelagert? Z. B. Hudson = Sofa + Récamiere (2 Teile). Jedes Teil erhält sein eigenes Etikett « Teil 1/2 », « Teil 2/2 ». Die Einstellung bleibt für dieses Produkt gespeichert.",
    "compo.sofa": "Sofa",
    "compo.chaise": "Récamiere",
    "compo.saved": "Zusammensetzung gespeichert",
    "compo.toConfirm": "zu bestätigen",
    "compo.pieceOf": "Teil {i}/{n}",
    "compo.cancel": "Abbrechen",
    "ident.yourPhoto": "Ihr Foto",
    "ident.catPhoto": "Katalogfoto",
    "ident.refMyo": "Myotaku-Ref.",
    "ident.refSup": "Lieferanten-Ref.",
    "ident.generic": "generischer Artikel ohne Lieferanten-Ref.",
    "ident.supplier": "Lieferant",
    "ident.type": "Typ",
    "ident.size": "Masse",
    "ident.notInCat": "nicht im Online-Katalog",
    "rec.upcoming": "Kommende Lieferungen",
    "rec.past": "Vergangene Lieferungen",
    "rec.toSchedule": "zu planen",
    "rec.runDone": "Montage abgeschlossen",
    "rec.runGoing": "Montage läuft",
    "rec.assembledN": "montiert {k}/{n}",
    "rec.po": "Best. #{n}",
    "stock.orderHint": "Bestellung",
    "note.stop": "Stoppen",
    "note.stopped": "Lesen gestoppt",
    "cam.title": "PC-Kamera",
    "cam.hint": "Halten Sie den Gegenstand oder die Seite vor die Kamera und machen Sie das Foto. Sie können mehrere nacheinander aufnehmen.",
    "cam.shot": "📸 Foto aufnehmen",
    "cam.file": "Stattdessen eine Datei wählen",
    "cam.close": "Schliessen",
    "cam.which": "Kamera",
    "cam.starting": "Kamera wird gestartet…",
    "cam.added": "Foto hinzugefügt ✓",
    "cam.e.denied": "Kamerazugriff verweigert. Erlauben Sie die Kamera für diese Seite (Kamera- oder Schloss-Symbol in der Adressleiste) und öffnen Sie erneut. Sonst wählen Sie eine Datei.",
    "cam.e.none": "Keine Kamera an diesem PC gefunden: Schliessen Sie die USB-Kamera an und öffnen Sie erneut.",
    "cam.e.busy": "Die Kamera wird bereits von einer anderen App verwendet (Teams, Windows-Kamera…): Schliessen Sie diese und öffnen Sie erneut.",
    "cam.e.blocked": "Diese Seite darf die Kamera in diesem Browser nicht verwenden. Verwenden Sie „Datei wählen“.",
    "cam.e.other": "Die Kamera konnte nicht starten. Wählen Sie stattdessen eine Datei.",
    "cam.tab": "📱 Tablet verwenden (Fotostation)",
    "cam.waitT": "Foto mit dem Tablet",
    "cam.wait": "Anfrage an das Tablet gesendet: „{l}“. Öffnen Sie auf dem Tablet „📱 Fotostation“ (oben am Bildschirm) und tippen Sie auf „Foto aufnehmen“. Die Fotos kommen sofort hier an.",
    "cam.waitRec": "Anfrage an das Tablet gesendet. Öffnen Sie auf dem Tablet „📱 Fotostation“: Es wechselt zum Tab Wareneingang, um die Seiten zu fotografieren, dann „Lesen“. Das Ergebnis erscheint hier.",
    "cam.got0": "Warten auf das Tablet…",
    "cam.fin": "Fertig",
    "st.btn": "📱 Fotostation",
    "st.title": "Fotostation",
    "st.hint": "Dieses Tablet ist die Kamera des PCs: Jede Anfrage vom PC erscheint hier. Lassen Sie diese Seite offen.",
    "st.none": "Noch keine Anfrage. Klicken Sie am PC auf 📷 Foto und dann auf „Tablet verwenden“.",
    "st.shot": "📸 Foto aufnehmen",
    "st.rec": "📄 Lieferschein fotografieren (Tab Wareneingang)",
    "st.quit": "Fotostation verlassen",
    "st.gen": "Montagefotos",
    "st.recL": "Lieferschein (Wareneingang)",
    "st.agoNow": "gerade eben",
    "st.ago": "vor {m} Min.",
    "st.up": "Wird hochgeladen…",
    "sdocs.photo": "📷 Foto des Lieferscheins oder der Kartons",
    "sdocs.photosT": "Vor Ort aufgenommene Fotos",
    "st.docs": "Lieferantendokumente",
    "com.noticeFailed": "Slack-Nachricht an die Logistik nicht zugestellt: {error}",
    "stock.supplierOrder": "Lieferantenbestellung",
    "cartons.unknownTitle": "Möbel ohne Kartonreferenzen",
    "cartons.unknownHint": "Zusammengesetzte Möbel in mehreren Kartons, deren Referenzen noch kein PDF-Lieferschein ihrer Bestellungen geliefert hat. Ein hier fotografierter Lieferschein trägt sie ein.",
    "cartons.nCartons": "{n} Kartons",
    "cartons.checked": "Geprüft am {when}",
    "cartons.allKnown": "Alle Möbel in mehreren Kartons haben ihre Referenzen.",
    "prod.devBanner1": "Diese Anwendung wurde von {name} entwickelt. Wenn Sie bei der Nutzung der Anwendung ein Problem haben, wenden Sie sich bitte an den Entwickler {name}.",
    "prod.devBanner2": "Bitte nutzen Sie die WhatsApp-Gruppe « MYOTAKU - Computeur & Mobile » nicht, um ein Problem zu melden.",
    "guide.title": "🎬 Video für die Monteure: deine Rolle, Schritt für Schritt (6 min)",
    "guide.sub": "ohne Ton, mit Texten auf dem Bildschirm",
    "guide.hide": "Video ausblenden",
    "guide.show": "Video anzeigen",
    "job.productPage": "Produktseite",
    "maybe.titleOne": "{n} Artikel unter einem ähnlichen Namen bestellt: zu prüfen",
    "maybe.titleMany": "{n} Artikel unter einem ähnlichen Namen bestellt: zu prüfen",
    "maybe.hint": "Im Delivery calendar gefunden, aber nicht sicher diesem Kunden zugeordnet (nicht in seiner Kundenliste oder Name einer anderen Wohnung). Vor dem Montieren oder Liefern mit dem Verkäufer klären: Es zählt nicht zur Montagezeit.",
    "stock.tlIn": "im Lager",
    "stock.tlOut": "ausgelagert",
    "maybe.unseenOne": "{n} Artikel für diesen Kunden bestellt, vielleicht schon geliefert",
    "maybe.unseenMany": "{n} Artikel für diesen Kunden bestellt, vielleicht schon geliefert",
    "maybe.unseenHint": "Vor dem {date} eingetroffen: Die App sieht die Lieferungen dieses Kunden vor diesem Datum nicht. Kläre mit dem Verkäufer, ob sie schon geliefert wurden, bevor du sie montierst oder lieferst: Sie zählen nicht zur Montagezeit.",
    "arch.t": "Archivierte Kunden",
    "arch.done": "Kunde archiviert: er ist im Tab Archiviert.",
    "arch.undone": "{c} ist wieder in den Listen.",
    "jobs.asm": "Montage",
    "jobs.deliv": "Lieferung",
    "jobs.est": "Geschätzt",
    "jobs.progressT": "Fortschritt {p} %: erledigte Arbeit, gewichtet nach der geschätzten Zeit jedes Artikels · Strich: {t} % der geschätzten Zeit bereits verbraucht",
    "job.salesRep": "Verkäufer",
    "job.noRep": "nicht gefunden",
    "job.noItems": "Keine Möbelliste aus der Bestellung gelesen: siehe die Delivery-calendar-Links unten.",
    "issue.reported": "Gemeldete Probleme",
    "issue.report": "Problem melden",
    "issue.sendFailed": "Slack-Versand fehlgeschlagen: die Nachricht ist nicht rausgegangen.",
    "issue.sendPending": "Slack-Versand ausstehend",
    "issue.qOfN": "{q} von {n}",
    "item.probOne": "⚠️ {n} Problem gemeldet",
    "item.probMany": "⚠️ {n} Probleme gemeldet",
    "recap.failed": "Zusammenfassung nicht gesendet",
    "recap.unclear": "Zusammenfassung wird gesendet",
    "maybe.ref": "Referenz",
    "st.sentOne": "{n} Foto gesendet",
    "st.sentMany": "{n} Fotos gesendet",
    "cam.gotOne": "✓ {n} Foto vom Tablet erhalten",
    "cam.gotMany": "✓ {n} Fotos vom Tablet erhalten",
    "cam.doneOne": "Fertig und lesen ({n} Seite)",
    "cam.doneMany": "Fertig und lesen ({n} Seiten)",
    "mediaJob.saved": "Gespeichert",
    "mediaJob.tooBig": "Datei zu groß ({mb} MB, maximal {max} MB).",
    "mediaJob.videoBig": "Video zu groß ({mb} MB, maximal {max} MB): filme einen kürzeren Clip.",
    "mediaJob.videoType": "Videoformat nicht akzeptiert: filme mit der Kamera (MP4).",
    "mediaJob.fileType": "Dateiformat nicht akzeptiert: wähle ein Foto oder ein Video.",
    "docs.unread": "Die Dokumente dieser Bestellung konnten gerade nicht gelesen werden: später erneut versuchen.",
    "com.readOnly": "Nur-Lese-Zugriff: Bitte Ludo um Mitwirkenden-Zugriff, um Möbel zu reservieren.",
    "prod.readOnly": "Nur-Lese-Zugriff: Bitte Ludo um Mitwirkenden-Zugriff, um Kits zu produzieren und Artikel anzunehmen.",
    "sup.kAlerts": "Aktuelle Warnungen",
    "sup.alT": "Warnungen",
    "sup.noAl": "Im Moment keine Warnung.",
    "sup.live": "Laufende Montagen",
    "sup.stRun": "läuft",
    "sup.stPau": "pausiert",
    "sup.nm": "nicht messbar: kein Möbel im Auftrag",
    "sup.readOnly": "Nur-Lese-Zugriff: Bitte Ludo um Mitwirkenden-Zugriff, um die feste Mannschaft zu markieren.",
    "note.modeNow": "Fotos und PDFs werden sofort gelesen.",
    "note.readOne": "Lesen und mit dem Delivery calendar abgleichen ({n} Seite)",
    "note.readMany": "Lesen und mit dem Delivery calendar abgleichen ({n} Seiten)",
    "artrec.sendOne": "Wareneingang senden ({n} Seite)",
    "artrec.sendMany": "Wareneingang senden ({n} Seiten)",
    "note.pagesOne": "{n} Seite",
    "note.pagesMany": "{n} Seiten",
    "note.linesOne": "{n} Zeile gelesen",
    "note.linesMany": "{n} Zeilen gelesen",
    "note.clientsOne": "{n} Kunde",
    "note.clientsMany": "{n} Kunden",
    "note.colliOne": "{n} Packstück",
    "note.colliMany": "{n} Packstücke",
    "note.asmOn": "Montage {d}",
    "note.err.rate_limited": "Zu viele Lesevorgänge auf einmal oder Nutzungslimit erreicht: später erneut versuchen.",
    "note.err.unavailable": "Das Lesen ist für diese App im Moment nicht erlaubt: später erneut versuchen.",
    "note.err.photo": "Foto abgelehnt (Format oder Grösse): neu aufnehmen.",
    "note.err.failed": "Lesen unterbrochen: erneut versuchen.",
    "note.notMatchedRetry": "Gelesen, aber noch nicht mit dem Delivery calendar abgeglichen: erneut abgleichen.",
    "brand.z": "Z",
    "brand.furniture": "Furniture",
    "jobs.titleMode": "montage",
    "issue.sending": "wird gesendet",
    "issue.notSent": "Nachricht nicht gesendet an {n}",
    "note.shortOne": "{n} fehlt",
    "note.shortMany": "{n} fehlen",
    "recv.unmatchedOne": "{n} nicht zugeordnet",
    "recv.unmatchedMany": "{n} nicht zugeordnet",
    "recv.completeOne": "{n} vollständig",
    "recv.completeMany": "{n} vollständig",
    "recv.missingOne": "{n} fehlt",
    "recv.missingMany": "{n} fehlen",
    "job.start": "Starten",
    "job.writeFailed": "Speichern nicht möglich: Prüfe die Verbindung und versuche es erneut.",
    "media.mb": "MB",
    "recv.readOnly": "Nur-Lese-Zugriff auf den Wareneingang: Bitte Ludo um Mitwirkenden-Zugriff.",
    "issue.sendingNow": "Wird gesendet…",
    "issue.netFailed": "Senden nicht möglich: Prüfe die Verbindung und versuche es erneut.",
    "crew.pickWarehouse": "Oben ein Lager wählen: Feste Mitarbeitende werden je Lager festgelegt.",
    "media.failVideo": "Das Video konnte nicht gespeichert werden: Prüfe die Verbindung und versuche es erneut.",
    "media.failPhoto": "Das Foto konnte nicht gespeichert werden: Prüfe die Verbindung und versuche es erneut.",
    "error.loadFailed": "Laden nicht möglich: Prüfe die Verbindung und versuche es erneut.",
  },
  it: {
    "recv.boxesPh": "Numeri dei cartoni (se più d'uno), separati da virgole",
    "job.dmToTick": "Da spuntare nel Delivery Manager: {n}",
    "art.title": "Stock articoli",
    "art.other": "Altri",
    "art.refs": "riferimenti",
    "art.save": "Salva il conteggio ({n} riferimento/i)",
    "alloc.sync": "Sincronizza con il planning",
    "alloc.result": "{q} consegne lette · {c} create · {u} aggiornate · {r} liberate · {d} uscite dal magazzino",
    "alloc.unknown": "Non riconosciuto",
    "docs.title": "Documenti e checklist",
    "docs.hint": "Checklist da stampare per ogni tipo di kit — anche senza ordine di produzione in corso — e i documenti di riferimento PDF.",
    "docs.refTitle": "Documenti di riferimento (PDF)",
    "docs.refHint": "Gli stessi documenti già stampati e messi nei cartoni / esposti a scaffale.",
    "docs.open": "Apri",
    "docs.selectAll": "Seleziona tutto",
    "docs.selectNone": "Nessuno",
    "docs.print": "Stampa la selezione ({n})",
    "docs.select": "Stampa questa checklist",
    "alloc.schedule": "Controllo automatico dal lunedì al venerdì alle 9:00 e alle 15:00, e aggiornamento alle 8:35, dalle 10:35 alle 14:35, alle 16:35 e alle 17:35",
    "alloc.points": "Punti da verificare (ultimo controllo)",
    "alloc.point.partial": "{date} · {wh} · contratto #{contract} · {client}: consegna spuntata in parte ({n}/{total}): tutti i kit scaricati, da verificare",
    "alloc.point.notTicked": "⚠ consegna del {date} · {wh} · contratto #{contract} non ancora spuntata dalla squadra: stock non scalato",
    "alloc.point.stillReserved": "❌ consegna del {date} · {wh} · contratto #{contract} ancora riservata (non scaricata: {reason})",
    "alloc.point.reason.missing": "evento non trovato nel planning",
    "alloc.point.reason.unapproved": "validazione ritirata",
    "alloc.point.reason.feed": "il planning non ha risposto",
    "alloc.point.postponed": "consegna del {date} · {wh} · contratto #{contract} rinviata al {newDate} dopo lo scarico dei kit: rimettere i kit in stock a mano se serve",
    "alloc.point.short": "⚠ {wh} — {code}: stock insufficiente allo scarico ({before} per {qty}): conteggio o spedizione da verificare · contratto #{contract}",
    "alloc.point.shortArticle": "⚠ stock articoli insufficiente: {code} {before} per {qty} · contratto #{contract}",
    "alloc.lastRun": "Ultimo controllo",
    "alloc.auto": "automatico",
    "alloc.never": "Nessun controllo registrato per ora.",
    "alloc.next": "Prossimo",
    "alloc.failed": "fallito",
    "crew.title": "Squadra",
    "crew.none": "Secondo Myotaku nessuno ha lavorato qui nelle ultime 3 settimane.",
    "crew.lastWorked": "ultimo giorno",
    "crew.permanent": "Fisso",
    "crew.hint": "I fissi sono proposti per primi all'avvio di un montaggio.",
    "compo.title": "Composizione",
    "compo.single": "1 pezzo",
    "compo.edit": "Modifica",
    "compo.hint": "In quanti pezzi viene stoccato? Un'etichetta per pezzo. Salvato per questo prodotto.",
    "compo.save": "Salva",
    "stock.palletPh": "es. 12",
    "stock.rackPh": "es. R3-2",
    "stock.recent": "Recenti",
    "stock.upcoming": "In arrivo",
    "stock.src.return": "reso cliente",
    "stock.src.reception": "ricezione fornitore",
    "stock.by.person": "a mano",
    "stock.by.ai": "da Claude",
    "stock.leaveSure": "Sì, togli dal magazzino",
    "com.cancelSure": "Sì, annulla",
    "com.done": "{n} mobile/i riservato/i per {client} — riferimento {ref}.",
    "inv.title": "📋 Inventario dei kit",
    "inv.last": "Ultimo inventario il {when} da {who} — {n} differenza/e",
    "inv.counted": "contato",
    "inv.save": "Conferma l'inventario",
    "inv.hint": "Dopo un conteggio fisico: scegli il magazzino, digita il numero contato per ogni kit (lascia vuoto ciò che non hai contato), verifica gli scarti, poi «Conferma l'inventario». Lo stock dell'app è sostituito dai tuoi numeri e ogni correzione resta nello storico.",
    "inv.history": "Storico dello stock dei kit",
    "inv.noHistory": "Nessuna modifica registrata per ora.",
    "inv.src.production": "produzione",
    "inv.src.transfer": "trasferimento",
    "inv.src.delivery": "consegna",
    "inv.src.inventory": "inventario",
    "inv.src.receipt": "ricezione",
    "inv.src.reception_check": "verifica ricevimento",
    "inv.src.manual": "aggiunta manuale",
    "nav.carts": "Carrelli",
    "nav.commercial": "Commerciale",
    "carts.sub": "Un QR su ogni carrello: dal cliente lo si associa al reso, in magazzino lo si scansiona per sapere di chi è.",
    "carts.number": "N. carrello",
    "carts.numberPh": "es. 42",
    "carts.open": "Apri",
    "carts.back": "← Tutti i carrelli",
    "carts.missing": "Resi passati senza carrello scansionato",
    "carts.missingHint": "Probabilmente il carrello non è stato scansionato dal cliente: scansionalo all'arrivo e associalo qui.",
    "carts.linked": "Carrelli in circolazione",
    "carts.linkedNone": "Nessun carrello associato.",
    "carts.grid": "I {n} carrelli",
    "carts.gridHint": "Tocca un numero per aprire il carrello.",
    "carts.linkT": "Associa questo carrello al reso del cliente",
    "carts.search": "Cerca un cliente, un nome o un n. evento",
    "carts.linkedBy": "Associato da {who} il {when}",
    "carts.arrivedAt": "arrivato il {when}",
    "carts.arrive": "Arrivato in magazzino",
    "carts.toStock": "Metti i mobili in magazzino",
    "carts.free": "Carrello svuotato — liberalo",
    "carts.freeConfirm": "Conferma: libera il carrello",
    "carts.st.free": "Libero",
    "carts.st.on_the_road": "Dal cliente o in viaggio",
    "carts.st.at_warehouse": "In magazzino · da svuotare",
    "carts.st.lost": "Nessuna notizia da 14 giorni",
    "com.s1": "Contratto",
    "com.s2": "Mobili dal magazzino",
    "com.s3": "Verifica e invia",
    "com.change": "Cambia",
    "com.searchContract": "Nome del cliente o n. di contratto",
    "com.reaches": "Consegne del planning fino al {d} (limite della fonte Myotaku).",
    "com.noStock": "Nessun mobile disponibile in questo magazzino.",
    "com.available": "disponibili",
    "com.prepare": "Preparazione in magazzino",
    "com.prepareHint": "Proposto: il giorno lavorativo prima della consegna.",
    "com.note": "Nota per la logistica",
    "com.notePh": "Es. togliere i piedi del divano, consegnare con il tavolo dello stesso cliente",
    "com.send": "Riserva {n} mobile/i",
    "com.sendHint": "I mobili sono riservati subito. L'evento «PRÉPARATION CLIENT» si crea ancora a mano nel planning; annota il suo numero nella prenotazione.",
    "com.reservations": "Prenotazioni",
    "com.none": "Nessuna prenotazione.",
    "com.st.sent": "inviata",
    "com.st.prepared": "preparata",
    "com.st.cancelled": "annullata",
    "com.prepareShort": "prep.",
    "com.deliverShort": "cons.",
    "com.prepared": "Preparata — fuori magazzino",
    "com.cancel": "Annulla",
    "com.eventPh": "N. evento",
    "com.saveEvent": "Salva",
    "nav.stock": "Magazzino",
    "stock.tab.intake": "Ingresso",
    "stock.tab.inventory": "Inventario",
    "stock.tab.returns": "Resi",
    "stock.tab.labelling": "Etichettatura",
    "ret.title": "Resi della pianificazione",
    "ret.hint": "Articoli previsti per reso e mobili già entrati in magazzino.",
    "ret.empty": "I resi (breve termine, lungo termine, integrazioni) delle ultime 2 settimane e della settimana prossima appaiono qui.",
    "ret.entered": "entrati",
    "ret.noItems": "Nessuna lista di mobili per questo reso.",
    "rec.title": "Etichettatura delle ricezioni",
    "rec.hint": "Mobili ordinati per i clienti (programma delle consegne). Un tocco su «Etichetta» = etichetta pronta, nient'altro.",
    "rec.itemsHint": "Etichetta ogni mobile dopo montaggio e imballaggio, prima di metterlo nel carrello del cliente.",
    "rec.labelOne": "Etichetta",
    "rec.labelAll": "Etichetta i {n} rimanenti",
    "rec.labeling": "Etichettatura…",
    "rec.empty": "Nessuna consegna con mobili da etichettare.",
    "rec.labelled": "etichettati",
    "rec.assembled": "montato",
    "rec.for": "Per",
    "rec.delivery": "consegna",
    "rec.unit": "pezzo",
    "stock.tagline": "Mobili dei clienti che rientrano in magazzino",
    "stock.pickWarehouse": "Scegli un magazzino in alto: ogni mobile entra in un magazzino preciso.",
    "stock.s1": "Reso cliente",
    "stock.s1hint": "Scegli il reso del planning con cui è tornato il mobile.",
    "stock.s2": "Mobile",
    "stock.s2hint": "Tocca il mobile che hai davanti. Non è nella lista? Cerca in tutto il catalogo.",
    "stock.s2hintNone": "Senza reso pianificato: cerca il mobile nel catalogo.",
    "stock.chosen": "Mobile scelto",
    "stock.nothingChosen": "Nessun mobile scelto.",
    "stock.s3": "Stato del mobile",
    "stock.cond.new": "Nuovo",
    "stock.cond.good": "Buono",
    "stock.cond.good.hint": "Pronto da noleggiare",
    "stock.cond.light_marks": "Medio",
    "stock.cond.light_marks.hint": "Segni leggeri",
    "stock.cond.damaged": "Danneggiato",
    "stock.cond.damaged.hint": "Da riparare",
    "stock.damagePh": "Cosa è danneggiato — es. graffio di 10 cm sul piano, gamba sinistra spaccata",
    "stock.s4": "Imballaggio e posizione",
    "stock.blanket": "Coperta Myotaku",
    "stock.film": "Pellicola",
    "stock.unwrapped": "Non imballato",
    "stock.pallet": "N. pallet",
    "stock.palletShort": "Pallet",
    "stock.rack": "N. scaffale",
    "stock.qty": "Mobili identici",
    "stock.placeHint": "Pallet e scaffale restano per il mobile successivo. Più mobili identici: una scheda, un numero e un'etichetta ciascuno.",
    "stock.save": "Salva {n} mobile/i e prepara le etichette",
    "stock.noReturns": "Nessun reso per questo magazzino questa settimana o la prossima.",
    "stock.today": "Oggi",
    "stock.contract": "contratto",
    "stock.entered": "entrato/i",
    "stock.noReturn": "Senza reso pianificato",
    "stock.noItems": "Questo reso non contiene mobili del catalogo: cercalo qui sotto.",
    "stock.searchPh": "Nome, rif. Myotaku, rif. fornitore…",
    "stock.search": "Cerca in tutto il catalogo",
    "stock.searchUnavailable": "La ricerca nel catalogo non è ancora disponibile su questo server Myotaku.",
    "stock.searchNone": "Nessun prodotto corrisponde.",
    "stock.filterPh": "N. di magazzino, nome, pallet…",
    "stock.status.held": "In magazzino e riservati",
    "stock.status.in_stock": "In magazzino",
    "stock.status.reserved": "Riservato",
    "stock.status.left": "Uscito",
    "stock.pieces": "mobili",
    "stock.emptyInventory": "Ancora nessun mobile qui.",
    "stock.days": "g",
    "stock.fromReturn": "reso",
    "stock.move": "Sposta",
    "stock.reprint": "Ristampa l'etichetta",
    "media.title": "Foto e video",
    "media.add": "Aggiungi foto o video",
    "media.uploading": "Caricamento…",
    "media.empty": "Nessuna foto per ora.",
    "media.video": "Video",
    "stock.leave": "Togli dal magazzino",
    "stock.ev.received": "entrato",
    "stock.ev.identified": "identificato",
    "stock.ev.labelled": "etichettato",
    "stock.ev.moved": "spostato",
    "stock.ev.graded": "stato cambiato",
    "stock.ev.reserved": "riservato",
    "stock.ev.released": "liberato",
    "stock.ev.left": "uscito",
    "stock.labelsReady": "{n} etichetta/e pronta/e",
    "stock.print": "Stampa",
    "stock.close": "Chiudi",
    "stock.part": "Parte",
    "hist.live": "In corso",
    "hist.past": "Reale vs previsto",
    "hist.none": "Nessun montaggio concluso.",
    "hist.when": "Concluso",
    "hist.estimate": "Previsto",
    "hist.actual": "Reale",
    "demand.title": "Kit richiesti per mese",
    "demand.hint": "Ultimi 6 mesi, ordini non annullati. Passa su un colore per il dettaglio.",
    "demand.chart": "Grafico",
    "demand.table": "Tabella",
    "demand.empty": "Nessun ordine di produzione negli ultimi 6 mesi.",
    "demand.kits": "kit",
    "demand.kit": "Kit",
    "demand.total": "Totale",
    "demand.never": "Nessuna richiesta in 6 mesi",
    "nav.supervisor": "Responsabile",
    "sup.running": "Montaggi in corso",
    "sup.people": "Montatori al lavoro",
    "sup.none": "Nessun montaggio in corso.",
    "sup.yield": "rendimento",
    "sup.earned": "guadagnato",
    "sup.spent": "impiegato",
    "sup.alertYield": "rendimento {p} % (soglia {l} %)",
    "sup.alertIdle": "niente convalidato da {m} (previsto: al massimo {l})",
    "nav.receiving": "Ricezione",
    "recv.title": "Bolle fornitore",
    "recv.new": "Nuova bolla di consegna",
    "recv.supplier": "Fornitore",
    "recv.docRef": "N. bolla",
    "recv.order": "Ordine",
    "recv.lines": "Righe",
    "recv.sku": "Riferimento",
    "recv.received": "Ricevuto",
    "recv.ordered": "Ordinato",
    "recv.boxes": "Scatole",
    "recv.addLine": "Aggiungi una riga",
    "recv.save": "Salva",
    "recv.reconcile": "Leggi e verifica",
    "recv.complete": "completo",
    "recv.missing": "mancante",
    "recv.extra": "in pi\u00f9",
    "recv.unmatched": "non abbinato",
    "recv.none": "Nessuna ricezione.",
    "recv.help": "Fotografa la bolla prima di aprire, poi confrontala con l'ordine.",
    "ident.title": "Foto per il riconoscimento",
    "ident.hint": "Fotografate il mobile intero prima di imballarlo. L'app cerca prima tra gli articoli del ritiro.",
    "ident.take": "📷 Scatta la foto",
    "ident.retake": "Rifai",
    "ident.sending": "Invio della foto…",
    "ident.analyzing": "Riconoscimento: il tipo di mobile, poi il confronto con il ritiro (e il catalogo se serve)…",
    "ident.pickThis": "È questo mobile",
    "ident.other": "Altre possibilità",
    "ident.searchCatalogue": "Cerca in tutto il catalogo",
    "ident.seen": "Visto nella foto",
    "ident.sure": "Sicuro",
    "ident.likely": "Probabile",
    "ident.check": "Da verificare",
    "ident.noMatch": "Nessun articolo del ritiro corrisponde.",
    "ident.noMatchAny": "Nessun prodotto identico trovato, né nel ritiro né nel catalogo. Cercate a mano o rifate la foto (mobile intero, di fronte).",
    "ident.notInRetour": "Non trovato tra gli articoli del ritiro: ecco la corrispondenza migliore nel catalogo. Verificate il ritiro scelto.",
    "ident.failed": "Il riconoscimento non è riuscito. Riprovate o cercate a mano.",
    "ident.fromReturn": "dal ritiro",
    "ident.fromCatalogue": "catalogo",
    "sheet.open": "Apri la scheda articolo (riferimenti, condizioni d'acquisto, storico)",
    "sheet.stockNow": "Stock attuale:",
    "sheet.kitsPossible": "cioè {n} kit possibili",
    "sheet.references": "Riferimenti",
    "sheet.refZ": "Rif. Myotaku (Z)",
    "sheet.refSystem": "Rif. sistema Myotaku",
    "sheet.systemName": "Nome nel sistema",
    "sheet.supplier": "Fornitore",
    "sheet.supplierRef": "Rif. fornitore",
    "sheet.supplierDesignation": "Designazione fornitore",
    "sheet.onNotes": "Rif. rilevati sulle bolle",
    "sheet.purchase": "Condizioni d'acquisto",
    "sheet.price": "Prezzo unitario",
    "sheet.noPrice": "non indicato",
    "sheet.moq": "Minimo per articolo",
    "sheet.rules": "Condizioni del fornitore",
    "sheet.usualQty": "Quantità ordinate di solito",
    "sheet.toConfirm": "Da confermare",
    "sheet.remark": "Nota",
    "sheet.notInCatalogue": "Nessun dato fornitore per questo articolo nel catalogo (lista di Charles Vila del 07.10.2026).",
    "sheet.reserved": "Condizioni d'acquisto riservate ai responsabili.",
    "sheet.notImported": "Le condizioni d'acquisto (lista di Charles Vila del 07.10.2026) non sono ancora importate nell'app.",
    "sheet.history": "Storico dello stock",
    "sheet.noHistory": "Nessun movimento registrato.",
    "nav.access": "Accessi",
    "access.title": "Accesso per modulo",
    "access.hint": "Ogni persona usa i moduli spuntati e apre l'app sul suo modulo iniziale. Un admin usa tutti i moduli. L'app rifiuta ogni azione fuori dai moduli di una persona.",
    "access.person": "Persona",
    "access.home": "Pagina iniziale",
    "access.active": "Attivo",
    "access.admin": "admin",
    "access.new": "Nuova persona",
    "access.name": "Nome",
    "access.password": "Password (min. 6 caratteri)",
    "access.homeFirst": "Il primo modulo spuntato diventa la pagina iniziale (modificabile dopo).",
    "access.create": "Crea l'accesso",
    "access.noModule": "Il vostro account non ha ancora nessun modulo. Chiedete a un admin di darvene in «Accessi».",
    "unit.title": "Unità n° {n} / {q}",
    "unit.count": "{c} / {m} articoli",
    "unit.inStock": "{n} in magazzino",
    "unit.validate": "Convalida questa unità ({d} → {n} / {q})",
    "unit.tickAll": "Spuntate tutti gli articoli per convalidare ({c}/{m})",
    "unit.correct": "Errore? Togli 1 unità già convalidata ({d} → {p})",
    "unit.finishNow": "Termina ora",
    "unit.markDone": "Segna come terminato",
    "unit.finishConfirm": "Terminare ora con {d} / {q} unità fatte? Gli articoli già spuntati dell'unità in corso tornano in stock.",
    "unit.cancel": "Annulla l'ordine",
    "unit.cancelConfirm": "Annullare questo ordine ({d} / {q} unità fatte)? I kit già fatti restano in stock; gli articoli spuntati dell'unità in corso tornano in stock.",
    "unit.yes": "Sì",
    "unit.no": "No",
    "artrec.title": "📷 Ricezione fornitore — foto, screenshot o PDF",
    "artrec.hint": "Inviate tutto ciò che mostra cosa è arrivato: foto della bolla (una foto per pagina), screenshot (conferma d'ordine, e-mail, WhatsApp, sito del fornitore), fattura, PDF, foto delle etichette dei cartoni… Ogni articolo riconosciuto entra nello stock articoli; il resto è da verificare.",
    "artrec.send": "Invia la ricezione ({n} pagina/e)",
    "artrec.typeAny": "Tipo di documento (facoltativo)",
    "artrec.type.bulletin": "Bolla di consegna",
    "artrec.type.capture": "Screenshot (ordine, e-mail, WhatsApp, sito)",
    "artrec.type.facture": "Fattura / conferma d'ordine",
    "artrec.type.etiquettes": "Foto dei cartoni / etichette",
    "artrec.type.autre": "Altro",
    "artrec.note": "Nota (facoltativa)",
    "artrec.noteShown": "Nota:",
    "artrec.latest": "Ultime ricezioni",
    "artrec.none": "Nessuna ricezione fotografata finora.",
    "artrec.updated": "Stock aggiornato:",
    "artrec.total": "totale",
    "artrec.toVerify": "Da verificare:",
    "artrec.clientReserved": "Riservato ai clienti (non messo in stock):",
    "artrec.forClient": "per",
    "artrec.summary": "Letto:",
    "artrec.st.verified": "Verificata e corretta",
    "artrec.verifiedBy": "Verificata e corretta il {at} da {by}:",
    "artrec.corrections": "Correzioni di stock:",
    "artrec.check.title": "Verifica di questo ricevimento",
    "artrec.check.hint": "Correggere lo stock se necessario (articolo e quantità, più o meno), scrivere cosa è stato verificato, poi «Verificato e corretto».",
    "artrec.check.pick": "Scegliere l'articolo",
    "artrec.check.qty": "+/− quantità",
    "artrec.check.remove": "Togliere questa correzione",
    "artrec.check.add": "Correzione di stock",
    "artrec.check.note": "Nota (obbligatoria): cosa è stato verificato e corretto",
    "artrec.check.button": "Verificato e corretto",
    "artrec.disputed.title": "Riferimenti fornitore contestati",
    "artrec.disputed.hint": "Finché un riferimento è contestato, la lettura dei ricevimenti lo ignora: scegliere l'articolo giusto, poi «Convalidare».",
    "artrec.disputed.now": "attualmente {code}",
    "artrec.disputed.validate": "Convalidare",
    "artrec.cal.noCatalogue": "Il catalogo fornitori di Z Production non è ancora importato: un ordine i cui codici fornitore sono noti solo lì non viene controllato qui.",
    "artrec.cal.held": "I messaggi Slack di questo controllo sono sospesi: il magazzino fotografa ancora i suoi ricevimenti in Z Production, che avvisa già il team.",
    "artrec.cal.missing": "Arrivato secondo il Delivery calendar ma non ancora fotografato nell'applicazione:",
    "artrec.cal.missingAsk": "Fotografare qui questa bolla.",
    "artrec.cal.last": "Ultimo controllo con il Delivery calendar: {at}",
    "artrec.cal.error": "errore",
    "artrec.cal.otherWarehouses": "Questa applicazione non vede ancora verso quale magazzino va ogni riga di un ordine: una riga di un ordine di Penthalaz consegnata altrove è ancora contata qui.",
    "artrec.cal.st.ok": "✅ Controllo Delivery calendar: conforme",
    "artrec.cal.st.ecart": "⚠️ Controllo Delivery calendar: differenza",
    "artrec.cal.st.absent_calendar": "ℹ️ Nessun ordine corrispondente nel Delivery calendar",
    "artrec.cal.st.non_livree": "ℹ️ Ordine trovato nel Delivery calendar ma non ancora segnato come consegnato",
    "artrec.cal.orders": "ordine {ids}",
    "artrec.cal.diff": "{name} ({code}): ordinato {ordered}, foto {photo}",
    "artrec.cal.extra": "{name} ({code}): foto {photo}, assente dall'ordine",
    "artrec.cal.notDeliveredNote": "ordine non ancora segnato come consegnato nel Delivery calendar (stato {statuses})",
    "artrec.cal.absentNote": "nessun ordine di Penthalaz con questi articoli dal {since}",
    "artrec.st.waiting": "In attesa di elaborazione",
    "artrec.st.running": "Elaborazione in corso",
    "artrec.st.done": "Elaborata — stock aggiornato",
    "artrec.st.verify": "Da verificare",
    "art.modeCount": "✏️ Correggi il totale",
    "art.modeAdd": "➕ Aggiungi",
    "art.addSave": "Aggiungi allo stock ({n} riferimento/i)",
    "art.search": "Cerca un riferimento, un articolo, un fornitore o un rif. fornitore…",
    "jobs.onlyAssembly": "Solo montaggi (lista di Ludovic)",
    "jobs.today": "Oggi",
    "jobs.upcoming": "In arrivo",
    "jobs.done": "Terminati",
    "jobs.archived": "Archiviati",
    "jobs.noDelivery": "consegna da pianificare",
    "stock.otherCat": "Altri",
    "stock.inStockN": "In magazzino",
    "stock.outN": "Usciti",
    "stock.avgAge": "Età media",
    "stock.searchAll": "Cerca n° magazzino, nome, rif., cliente…",
    "stock.allCats": "Tutte le categorie",
    "stock.allPallets": "Tutti i pallet",
    "stock.palletN": "Pallet {p}",
    "stock.all": "Tutti",
    "stock.allSources": "Tutte le origini",
    "stock.srcReturns": "Resi clienti",
    "stock.srcReceptions": "Ricevimenti",
    "stock.allWarehouses": "Tutti i magazzini",
    "stock.oldestFirst": "Più vecchi prima",
    "stock.exportCsv": "Esporta CSV",
    "stock.csvDone": "Esportazione pronta",
    "stock.emptyInvTitle": "Nessun mobile in magazzino qui",
    "stock.noResults": "Nessun risultato per questi filtri.",
    "stock.forClient": "Per",
    "stock.enteredOn": "entrato il",
    "stock.openCatalogue": "Apri la scheda catalogo ↗",
    "stock.changeCond": "Cambia stato",
    "stock.backIn": "Rimetti in magazzino",
    "stock.markOut": "Segna come uscito",
    "stock.delete": "Rimuovi",
    "stock.damageNotePh": "Difetto (obbligatorio per «Danneggiato»)",
    "stock.reason": "Motivo",
    "stock.out.redeliv": "Riconsegnato a un cliente",
    "stock.out.repair": "In riparazione",
    "stock.out.sale": "Venduto",
    "stock.out.scrap": "Smaltito",
    "stock.out.transfer": "Trasferimento magazzino",
    "stock.deleteConfirm": "Eliminare definitivamente questa scheda? Le foto restano salvate.",
    "stock.deleteYes": "Sì, elimina",
    "stock.ev.back": "rimesso in magazzino",
    "prod.capacitySaved": "Capacità salvata: {p} persona(e) × {h} h / giorno",
    "prod.objective": "Obiettivo",
    "prod.timeRequired": "Tempo totale richiesto",
    "prod.capacityDay": "Capacità del giorno",
    "prod.marginLeft": "Margine disponibile",
    "prod.overrun": "Sforamento",
    "prod.assignTo": "Assegna a un preparatore (facoltativo)",
    "prod.assignPh": "Es. Emerson — vuoto = chiunque",
    "prod.note": "Nota (facoltativa)",
    "prod.teamCapacity": "Capacità del team",
    "prod.people": "Persone",
    "prod.hoursPerDay": "Ore / giorno",
    "prod.doneShort": "fatti",
    "prod.alertFailed": "Preparazione fermata, ma l'avviso non è stato inviato: avvisa direttamente il responsabile.",
    "prod.resolve": "Segna come risolto",
    "prod.for": "Per",
    "prod.takeOver": "Prendi il cambio",
    "prod.changeAssign": "Cambia assegnazione",
    "prod.assign": "Assegna",
    "prod.split": "Dividi l'ordine",
    "prod.lockTitle": "In preparazione",
    "prod.lockBody": "da {o}{paused} — {d} / {q} fatti. La checklist è riservata a {o}.",
    "prod.pausedTag": "(in pausa)",
    "prod.takeOverAsk": "Prendere il cambio su {k}?",
    "prod.takeOverYes": "Sì, prendo il cambio",
    "prod.assignedAsk": "Questo ordine è assegnato a {n}. Avviarlo comunque?",
    "prod.assignedYes": "Sì, avvialo",
    "prod.assignName": "Nome del preparatore (vuoto = chiunque)",
    "prod.save": "Salva",
    "prod.splitQty": "Kit da mettere nel nuovo ordine (da 1 a {n})",
    "prod.splitTo": "Assegna il nuovo ordine a (facoltativo)",
    "prod.splitYes": "Dividi",
    "prod.reopen": "Riapri questo ordine",
    "prod.reopenPartial": "Riapri questo ordine ({d}/{q} fatti)",
    "jobs.overdue": "in ritardo",
    "jobs.issue": "Problema segnalato",
    "jobs.issues": "{n} problemi segnalati",
    "jobs.gone": "Rimosso dalla pianificazione",
    "team.title": "Montatori su questo lavoro",
    "team.nobody": "Ancora nessuno",
    "team.add": "+ Montatore",
    "team.remove": "Togli",
    "team.left": "ha lasciato il lavoro",
    "issue.howMany": "Quanti sono interessati?",
    "issue.ofN": "su {n}",
    "issue.sentTo": "inviato a {n} il {t}",
    "job.fromOrders": "Solo i mobili degli ordini fornitore (lista di Ludovic)",
    "job.otherList": "Questo lavoro si spunta nell'altra lista: questa è di sola lettura.",
    "job.toAssemble": "Mobili da montare",
    "job.toCheck": "Articoli da controllare (senza montaggio)",
    "job.toCheckHint": "Apri ogni imballo e controlla l'articolo: rotture, graffi, parti o accessori mancanti. Richiudilo o proteggilo, poi spunta ✓ la riga (o conta con + se ne hai fatto solo una parte). Se è danneggiato: 📷 foto, poi « ⚠️ Problema » sulla sua riga.",
    "job.order": "ordine",
    "job.arrived": "arrivato il {d}",
    "job.expected": "arrivo previsto il {d}",
    "job.noArrival": "senza data di arrivo",
    "job.cartons": "📦 {n} cartoni",
    "job.cartonsPer": "📦 {n} cartoni per mobile · {t} in tutto",
    "job.ref": "rif.",
    "job.unreadable": "Ordine citato ma non leggibile: {ids}",
    "job.noOrders": "Il commento del planning non cita alcun ordine fornitore.",
    "arch.hint": "Un cliente viene archiviato automaticamente il giorno dopo la data di consegna nel planning (consegna effettuata), tranne durante un montaggio in corso. Archiviare o ripristinare a mano vale finché le date di montaggio e di consegna nel planning non cambiano.",
    "arch.q": "Cerca un cliente o un n°",
    "arch.none": "Nessun cliente archiviato finora.",
    "arch.noHit": "Nessun cliente archiviato corrisponde alla ricerca.",
    "arch.un": "Ripristina",
    "arch.ar": "Archivia",
    "arch.by": "da",
    "arch.boxOn": "📦 Cliente archiviato",
    "arch.boxOff": "Archiviazione",
    "arch.pAuto": "Consegna effettuata il {d}: il cliente è negli Archiviati.",
    "arch.pMan": "Archiviato a mano il {d}{w}.",
    "arch.pWill": "Archiviato automaticamente il giorno dopo la consegna ({d}).",
    "arch.pNone": "Nessuna data di consegna nel planning: archivialo a mano quando è finito (consegna annullata o fatta senza data).",
    "arch.pBack": "Ripristinato a mano: resta negli elenchi finché le date del planning non cambiano.",
    "arch.run": "Montaggio in corso: mettilo in pausa o terminalo prima di archiviare.",
    "note.title": "Ricezione documenti fornitore",
    "note.hint": "Fotografa ogni pagina della bolla di consegna (o di un altro documento del fornitore). L'app la fa leggere e la confronta con il Delivery calendar: cosa è arrivato, cosa manca o è in più per ogni cliente.",
    "note.photo": "📷 Fotografa una pagina",
    "note.files": "📁 Scegli foto o PDF",
    "note.drop": "…o trascina qui foto o PDF dal computer",
    "note.page": "Pagina {n}",
    "note.del": "Togli",
    "note.read": "Leggi e confronta con il Delivery calendar ({n} pagina/e)",
    "note.uploading": "Invio delle pagine…",
    "note.reading": "Lettura, poi confronto con il Delivery calendar… (da 10 a 60 s per pagina, e circa 1 minuto per il confronto)",
    "note.failed": "La lettura non è riuscita:",
    "note.retry": "Rileggi",
    "note.rematch": "Riconfronta con il Delivery calendar",
    "note.type": "File non supportato: foto (JPEG, PNG, HEIC) o PDF.",
    "note.doc.delivery_note": "Bolla di consegna",
    "note.doc.order_confirmation": "Conferma d'ordine",
    "note.doc.invoice": "Fattura",
    "note.doc.labels": "Etichette",
    "note.doc.other": "Documento",
    "note.lines": "{n} riga/e letta/e",
    "note.pages": "{n} pagina/e",
    "note.readAt": "letto il {when}",
    "note.clients": "{n} cliente/i",
    "note.ord": "ordinato",
    "note.got": "ricevuto",
    "note.conf": "confermato",
    "note.ok": "completo",
    "note.extra": "{n} in più",
    "note.miss": "non su questa bolla",
    "note.confSt": "non ancora una ricezione",
    "note.colli": "{n} colli",
    "note.cartons": "{n} cartoni",
    "note.cw": "cartoni incompleti: controllare",
    "note.un": "Non trovato nel Delivery calendar (nessun montaggio previsto per questi articoli)",
    "note.many": "Più clienti possibili: assegnare a mano",
    "note.unread": "Numero illeggibile",
    "note.unclear": "Da verificare sulla carta",
    "note.open": "Apri la scheda",
    "note.sec": "Ricezione (documenti fornitore letti)",
    "note.ocW": "Questo documento è una conferma d'ordine (o una fattura), non una bolla di consegna: non prova che la merce sia arrivata. Non cercare il mobile finché non è stato ricevuto.",
    "note.deliveryOn": "consegna {when}",
    "note.hist": "Ultimi documenti letti",
    "note.none": "Nessun documento letto finora.",
    "note.byHand": "Inserisci una bolla a mano",
    "note.matched": "{n} assegnata/e",
    "note.pillOk": "📄 ricevuto",
    "note.pillWarn": "⚠️ ricezione",
    "note.pillOc": "📝 ordine confermato",
    "tr.title": "Trasferimenti tra sedi",
    "tr.from": "Da",
    "tr.to": "A",
    "tr.send": "Invia",
    "tr.receive": "Ricevi",
    "tr.pending": "in attesa",
    "tr.inTransit": "in transito",
    "tr.received": "ricevuto",
    "tr.new": "Nuovo trasferimento",
    "tr.none": "Nessun trasferimento.",
    "tr.arrived": "Arrivati",
    "tr.arrivalHint": "Contate ci\u00f2 che \u00e8 arrivato: solo questi kit entrano nello stock di questa sede.",
    "tr.lossNote": "Cosa \u00e8 successo ai mancanti (obbligatorio)",
    "tr.confirmArrival": "Conferma l'arrivo",
    "tr.receivedOf": "{n} su {m} arrivati",
    "tr.inTransitNote": "In transito: la merce non appartiene a nessuna delle due sedi.",
    "nav.production": "Produzione",
    "prod.progress": "Avanzamento di oggi",
    "prod.target": "Obiettivo",
    "prod.done": "Fatti",
    "prod.variance": "Scarto",
    "prod.ahead": "in anticipo",
    "prod.behind": "in ritardo",
    "prod.byPreparer": "Per preparatore",
    "prod.kitsMade": "kit fatti",
    "prod.perKit": "min / kit",
    "prod.queue": "Coda di produzione",
    "prod.finishedToday": "Completati oggi",
    "prod.stock": "Giacenza",
    "prod.onHand": "In magazzino",
    "prod.reserved": "Riservato",
    "prod.available": "Disponibile",
    "prod.newOrder": "Nuovo ordine di produzione",
    "prod.kit": "Kit",
    "prod.qty": "Quantit\u00e0",
    "prod.raise": "Invia",
    "prod.noQueue": "Nessun ordine in attesa — la coda è vuota.",
    "prod.allocations": "Kit riservati per le prossime consegne",
    "prod.neededOn": "Per il",
    "prod.unitMinutes": "min / kit",
    "prod.reservedNote": "Una prenotazione non riduce mai la giacenza reale.",
    "wh.all": "Tutti",
    "job.assembly": "Montaggio",
    "job.delivery": "Consegna",
    "job.estimate": "Stima",
    "job.assembled": "montati",
    "job.checked": "verificati",
    "job.inProgress": "In corso",
    "job.todo": "Da fare",
    "job.finished": "Completato",
    "job.issueReported": "Problema segnalato",
    "job.packing": "Imballaggio obbligatorio",
    "job.packingBody": "Ogni mobile montato viene avvolto in coperte MYOTAKU e sigillato con pellicola, prima di entrare nel carrello del cliente.",
    "job.furniture": "Mobili da montare",
    "job.pickOne": "Scegli una consegna.",
    "start.title": "Chi monta?",
    "start.help": "Tocca ogni montatore che lavora con te. Un montatore gi\u00e0 avviato su un altro cliente ne viene rimosso automaticamente.",
    "start.extras": "Extra",
    "start.permanent": "Personale fisso",
    "start.addName": "Aggiungi un nome",
    "start.add": "Aggiungi",
    "start.atLeastOne": "Scegli almeno un montatore",
    "issue.title": "Segnala un problema",
    "issue.kind": "Problema",
    "issue.item": "Articolo interessato",
    "issue.none": "Nessuno in particolare",
    "issue.what": "Cosa vedi",
    "issue.by": "Segnalato da",
    "issue.blocks": "Montaggio bloccato (mette in pausa il timer)",
    "issue.send": "Segnala",
    "issue.damaged_furniture": "Mobile danneggiato",
    "issue.missing_part": "Pezzo o ferramenta mancante",
    "issue.missing_furniture": "Mobile mancante",
    "issue.wrong_model_or_colour": "Modello o colore sbagliato",
    "issue.missing_instructions": "Istruzioni di montaggio mancanti",
    "issue.other": "Altro problema",
    "finish.title": "Concludi il lavoro",
    "finish.send": "Concludi",
    "finish.reopen": "Riapri",
    "finish.recapSent": "Riepilogo inviato su Slack a",
    "finish.resend": "Invia il riepilogo",
    "cancel": "Annulla",
    "prod.stop": "⛔ Ferma",
    "prod.stopTitle": "Fermare la preparazione e avvisare il responsabile",
    "prod.stopSend": "⛔ Ferma e invia l'avviso",
    "prod.stopDetail": "Precisa (quale articolo, quanti ne mancano…)",
    "prod.stockShort": "Stock insufficiente",
    "prod.stoppedTitle": "Preparazioni fermate",
    "prod.stoppedBecause": "Fermata:",
    "prod.reason0": "Manca materiale / articolo",
    "prod.reason1": "Niente più cartoni",
    "prod.reason2": "Niente più stock",
    "prod.reason3": "Altro problema",
    "item.issue": "Problema",
    "item.blocked": "Bloccato",
    "app.title": "Myotaku Magazzino",
    "nav.preparation": "Montaggio",
    "nav.signOut": "Esci",
    "days.title": "Consegne in arrivo",
    "days.none": "Nessuna consegna quel giorno.",
    "days.outsideWindow": "Fuori dalla finestra del feed",
    "event.pieces": "pezzi",
    "event.lines": "righe",
    "event.packs": "stanze",
    "event.awaitingSales": "In attesa delle vendite",
    "event.awaitingLogistics": "In attesa della logistica",
    "event.approved": "Approvato",
    "run.start": "Avvia preparazione",
    "run.resume": "Riprendi",
    "run.pause": "Pausa",
    "run.running": "In corso",
    "run.paused": "In pausa",
    "run.wall": "Trascorso",
    "run.worked": "Lavorato",
    "run.minutes": "min",
    "run.noRun": "Non iniziato",
    "item.done": "Montato",
    "item.todo": "Da montare",
    "item.noAssembly": "Senza montaggio",
    "item.estimate": "Stima",
    "pack.standardKit": "Kit standard",
    "error.title": "Problema",
    "loading": "Caricamento…",
    "lang.label": "Lingua",
    "login.title": "Myotaku Magazzino",
    "login.intro": "Accedi per continuare.",
    "login.email": "E-mail",
    "login.password": "Password",
    "login.submit": "Accedi",
    "login.busy": "Accesso in corso…",
    "login.noSignup": "Gli account vengono creati da un amministratore. Non è prevista la registrazione.",
    "login.failed": "Accesso non riuscito.",
    "login.failedStatus": "Accesso non riuscito ({status}).",
    "login.wrong": "E-mail e password non corrispondono.",
    "login.noToken": "Accesso effettuato, ma non è stato emesso alcun token. Avvisa un amministratore.",
    "login.sessionCheck": "Impossibile verificare la sessione ({status}).",
    "error.request": "Richiesta non riuscita ({status}).",
    "error.upload": "L'archivio ha rifiutato il caricamento ({status}).",
    "status.intro": "Questa pagina verifica solo che l'intera catena funzioni.",
    "status.signedInAs": "Accesso come",
    "status.frontEnd": "Browser",
    "status.server": "Server",
    "status.browser": "Browser",
    "status.mounted": "il codice è caricato e React è avviato",
    "status.asking": "richiesta in corso…",
    "status.unreachable": "NON RAGGIUNGIBILE",
    "status.answeredAt": "Il server ha risposto alle {when}",
    "docs.ref.checklists.title": "Checklist A4 (cartone)",
    "docs.ref.checklists.desc": "Un foglio per kit da inserire in ogni cartone – stessa impaginazione di quella qui sotto.",
    "docs.ref.shelf.title": "Etichette per scaffali",
    "docs.ref.shelf.desc": "Etichette colorate per gli scaffali, 8 per pagina A4.",
    "docs.ref.inventory.title": "Inventario generale",
    "docs.ref.inventory.desc": "Tutti i codici, da compilare durante un conteggio fisico.",
    "chk.title": "Checklist del cartone",
    "chk.phase1": "Fase 1 — Kit installato",
    "chk.phase2": "Fase 2 — Kit di riserva",
    "chk.phase1Short": "Kit installato",
    "chk.phase2Short": "Kit di riserva",
    "chk.complete": "Cartone completo e verificato",
    "chk.preparedBy": "Preparato da",
    "chk.checkedBy": "Verificato da",
    "chk.date": "Data",
    "chk.signature": "Firma",
    "chk.article": "{n} articolo",
    "chk.articles": "{n} articoli",
    "csv.col.stock_no": "n_stock",
    "csv.col.entrepot": "magazzino",
    "csv.col.categorie": "categoria",
    "csv.col.statut": "stato",
    "csv.col.etat": "condizione",
    "csv.col.origine": "origine",
    "csv.col.nom": "nome",
    "csv.col.ref_myotaku": "rif_myotaku",
    "csv.col.ref_fournisseur": "rif_fornitore",
    "csv.col.fournisseur": "fornitore",
    "csv.col.commande_fournisseur": "ordine_fornitore",
    "csv.col.dimensions": "dimensioni",
    "csv.col.emplacement": "ubicazione",
    "csv.col.palette": "pallet",
    "csv.col.rack": "scaffale",
    "csv.col.numero": "numero",
    "csv.col.jours_au_depot": "giorni_in_magazzino",
    "csv.col.retour": "reso",
    "csv.col.client": "cliente",
    "csv.col.reserve_pour": "riservato_per",
    "csv.col.livraison": "consegna",
    "csv.col.entree_le": "entrata_il",
    "csv.col.degats": "danni",
    "csv.col.catalogue_url": "url_catalogo",
    "csv.in": "in magazzino",
    "csv.out": "uscito",
    "csv.reception": "ricezione fornitore",
    "csv.return": "reso cliente",
    "csv.cond.new": "nuovo",
    "csv.cond.good": "buono",
    "csv.cond.light_marks": "discreto",
    "csv.cond.damaged": "danneggiato",
    "csv.pallet": "Pallet {p}",
    "csv.rack": "Scaffale {r}",
    "csv.file": "magazzino-mobili",
    "csv.fileAll": "tutti",
    "label.stockLabel": "MAGAZZINO MOBILI",
    "label.piece": "Pezzo",
    "label.unit": "Mobile",
    "label.withOthers": "con {n} altro/i pezzo/i",
    "label.refMyo": "Rif. Myotaku",
    "label.refSup": "Rif. fornitore",
    "label.entry": "Entrata",
    "label.qrCaption": "Scheda catalogo",
    "label.qrCaptionBig": "Scansiona: scheda catalogo Myotaku",
    "label.for": "Per",
    "label.deliv": "cons.",
    "label.ret": "Reso",
    "label.sample": "Esempio",
    "label.cond.neuf": "Nuovo",
    "label.cond.bon": "Buono",
    "label.cond.moyen": "Discreto",
    "label.cond.abime": "Danneggiato",
    "label.fmt.a4": "A4 orizzontale · 1 etichetta per foglio (leggibile da lontano)",
    "label.fmt.a4x2": "A4 · 2 etichette per foglio (A5, da tagliare a metà)",
    "label.fmt.ql62": "Brother QL · 62 × 100 mm",
    "label.fmt.dymo": "Dymo LabelWriter · 54 × 101 mm",
    "label.fmt.zebra": "Zebra / termica · 100 × 150 mm",
    "label.format": "Formato etichetta",
    "label.preview": "Anteprima etichetta",
    "label.dlOne": "Scarica l'etichetta",
    "label.dlAll": "Scarica le {n} etichette",
    "label.file1": "etichetta-{id}.pdf",
    "label.fileN": "etichette-{wh}-{day}.pdf",
    "stock.todayLabels": "Etichette di oggi",
    "rec.reprint": "Ristampa",
    "art.perKit": "×{n}/kit",
    "label.printA4": "Apri il PDF e stampa su A4, scala 100 %.",
    "label.printRoll": "Stampa con l'app della stampante (PDF).",
    "label.s5": "Etichetta",
    "label.count": "{n} etichette (una per pezzo)",
    "label.previewFirst": "anteprima del pezzo 1",
    "cartLabel.cart": "Carrello",
    "cartLabel.atClient": "Dal cliente: scansiona e scegli il reso.",
    "cartLabel.atWarehouse": "In magazzino: scansiona per vedere il cliente.",
    "cartLabel.scanBoth": "Scansiona al carico dal cliente e allo scarico.",
    "cartLabel.keep": "non rimuovere",
    "cartLabel.fmt.zebra": "Zebra / termica · 150 × 100 mm (orizzontale)",
    "cartLabel.fmt.ql62": "Brother QL · 62 × 100 mm",
    "cartLabel.fmt.a4x4": "A4 orizzontale · 4 etichette per foglio, da ritagliare",
    "cartLabel.open": "🖨 Stampa etichette",
    "cartLabel.title": "Stampa etichette dei carrelli",
    "cartLabel.hint": "Il modulo prepara un PDF: aprilo e stampalo sulla stampante di etichette (dimensioni reali, senza ridimensionamento). Stampa prima una pagina di prova.",
    "cartLabel.format": "Formato",
    "cartLabel.from": "Dal carrello n.",
    "cartLabel.to": "al n.",
    "cartLabel.copies": "Etichette per carrello",
    "cartLabel.print1": "🖨 Stampa {n} etichetta (PDF)",
    "cartLabel.printN": "🖨 Stampa {n} etichette (PDF)",
    "cartLabel.busy": "Preparazione del PDF…",
    "cartLabel.ok": "PDF pronto: aprilo per stamparlo.",
    "cartLabel.err": "Non è stato possibile preparare il PDF: riprova.",
    "cartLabel.range": "Inserisci numeri da 1 a 999, dal più piccolo al più grande (al massimo 400 etichette per PDF).",
    "cartLabel.reprint": "🖨 Ristampa l'etichetta di questo carrello",
    "cartLabel.file1": "etichetta-{id}-{fmt}.pdf",
    "cartLabel.fileN": "etichette-carrelli-{from}-{to}-{fmt}.pdf",
    "cartLabel.copiesN": "× {n}",
    "cartLabel.close": "Chiudi",
    "note.notMatched": "Letto, ma non ancora abbinato al Delivery calendar:",
    "carton.title": "Etichette dei cartoni",
    "carton.sub": "in bianco e nero, stessa impaginazione delle etichette degli scaffali.",
    "carton.printOn": "Stampa su:",
    "carton.fmt.a4": "Foglio A4 — 8 etichette 10 × 7 cm (stampante normale)",
    "carton.fmt.ql102x70": "Etichettatrice Brother QL — 102 × 70 mm (rotolo continuo 102 mm, DK-22243)",
    "carton.fmt.ql102x152": "Etichettatrice Brother QL — 102 × 152 mm (etichette pretagliate DK-11241)",
    "carton.fmt.ql62x100": "Etichettatrice Brother QL — 62 × 100 mm (etichette pretagliate DK-11202)",
    "carton.helpQl": "Un'etichetta per cartone. Nella finestra di stampa: scegli la stampante «Brother QL-1100», Margini «Nessuno» e lo stesso formato carta ({w} × {h} mm) in «Altre impostazioni».",
    "carton.helpQlPage": "Un'etichetta per pagina. Nella finestra di stampa: stampante «Brother QL-1100», Margini «Nessuno», formato {w} × {h} mm.",
    "carton.helpA4": "8 etichette identiche da 10 × 7 cm per foglio A4.",
    "carton.helpA4Page": "Foglio A4: 8 etichette identiche da 10 × 7 cm per foglio.",
    "carton.editHint": "La quantità ricevuta e il riferimento fornitore vengono stampati sull'etichetta — modificabili qui sotto (lascia vuoto per non stamparli).",
    "carton.ref": "Rif.",
    "carton.article": "Articolo",
    "carton.supRefCol": "Rif. fornitore",
    "carton.qtyCol": "Qtà ricevuta",
    "carton.qtyShort": "Qtà",
    "carton.sheets": "Fogli",
    "carton.labels": "Etichette",
    "carton.allRefs": "Tutti i riferimenti:",
    "carton.apply": "Applica",
    "carton.addRef": "Aggiungi un rif.:",
    "carton.addPh": "es. 9KUC",
    "carton.add": "Aggiungi",
    "carton.unknown": "Riferimento «{ref}» sconosciuto.",
    "carton.noneRead": "Nessun articolo riconosciuto in questa ricezione — aggiungi un riferimento qui sotto.",
    "carton.print": "Stampa",
    "carton.nLabel": "{n} etichetta {w} × {h} mm",
    "carton.nLabels": "{n} etichette {w} × {h} mm",
    "carton.nSheet": "{n} foglio · {labels} etichette",
    "carton.nSheets": "{n} fogli · {labels} etichette",
    "carton.nLabelShort": "{n} etichetta",
    "carton.nLabelsShort": "{n} etichette",
    "carton.libTotal": "{refs} rif. · {what}",
    "carton.noneSelected": "Nessuna etichetta selezionata",
    "carton.footer": "Ricezione del {date}",
    "carton.footerSupplier": "Ricezione del {date} · {supplier}",
    "carton.fileReception": "Etichette_cartoni_{supplier}_{date}",
    "carton.fileLibrary": "Etichette_Myotaku",
    "carton.fileOne": "Etichetta_{code}",
    "carton.nothing": "Niente da stampare.",
    "carton.blocked": "Stampa impossibile: il browser ha bloccato la nuova finestra. Consenti i pop-up per questo sito e riprova.",
    "carton.qtyReceived": "Qtà ricevuta",
    "carton.supRef": "Rif. fornitore:",
    "carton.catLinen": "Kit biancheria",
    "carton.libTitle": "Archivio etichette",
    "carton.libSub": "Tutti i riferimenti Myotaku — 🖨️ sulla riga = UNA sola etichetta (su A4: in alto a sinistra del foglio); per stamparne diverse, scegli il numero e poi «Stampa la selezione». Il rif. fornitore e la quantità inseriti vengono memorizzati per la prossima volta.",
    "carton.searchPh": "Cerca: codice, articolo, rif. fornitore…",
    "carton.allKits": "Tutti i kit",
    "carton.footerField": "Testo in fondo all'etichetta (facoltativo):",
    "carton.footerPh": "es. Consegna Rotel del 05.10",
    "carton.noMatch": "Nessun riferimento corrisponde alla ricerca.",
    "carton.reset": "Riporta tutto a 0",
    "carton.printSelection": "Stampa la selezione",
    "carton.printOne": "Stampa questa etichetta",
    "carton.remembered": "memorizzato",
    "carton.saveFailed": "Memorizzazione impossibile: {reason}",
    "carton.printCartons": "Stampa le etichette dei cartoni",
    "cartx.today": "Oggi",
    "cartx.yesterday": "Ieri",
    "cartx.tomorrow": "Domani",
    "cartx.waiting1": "{n} salvataggio in attesa di rete su questo telefono",
    "cartx.waitingN": "{n} salvataggi in attesa di rete su questo telefono",
    "cartx.queued": "Nessuna rete: salvato su questo telefono, inviato automaticamente appena torna la rete.",
    "cartx.unknownNumber": "Numero di carrello sconosciuto.",
    "cartx.missingNone": "Ogni reso passato degli ultimi 3 giorni ha un carrello.",
    "cartx.notYet": "{n} non ancora scansionato/i",
    "cartx.cart": "Carrello",
    "cartx.lastClient": "Ultimo cliente: {client} · liberato il {date}",
    "cartx.never": "Non ancora usato con il modulo.",
    "cartx.linkedOk": "{cart} associato a {client}",
    "cartx.client": "Cliente",
    "cartx.event": "Evento",
    "cartx.byHand": "inserito a mano",
    "cartx.linkedBy": "Associato",
    "cartx.arrived": "Arrivato",
    "cartx.warehouse": "Magazzino",
    "cartx.photo": "Foto del carrello carico",
    "cartx.optional": "facoltativo",
    "cartx.photoUp": "Invio della foto…",
    "cartx.photoErr": "Foto non inviata: rifalla o continua senza.",
    "cartx.photoDel": "Rimuovi",
    "cartx.others": "Carrelli di questo reso",
    "cartx.iOfN": "carrello {i} di {n}",
    "cartx.items": "Mobili previsti per questo reso ({n})",
    "cartx.noItems": "Nessun elenco di mobili per questo reso (inserito a mano o fuori dalla pianificazione).",
    "cartx.arrivedOk": "{cart} arrivato in magazzino",
    "cartx.toStock": "Entrata in magazzino di questo reso",
    "cartx.free": "Carrello svuotato: libera",
    "cartx.freeConfirm": "Conferma: libera {cart}",
    "cartx.freedOk": "{cart} è libero",
    "cartx.relink": "Cambia reso",
    "cartx.relinkWarn": "Questo carrello è associato a {client}. La nuova associazione sostituirà la precedente.",
    "cartx.history": "Cronologia",
    "cartx.h.link": "Associato",
    "cartx.h.arrive": "Arrivato in magazzino",
    "cartx.h.free": "Liberato",
    "cartx.h.stock": "Entrata in magazzino",
    "cartx.cartsOk1": "{n} carrello ✓",
    "cartx.cartsOkN": "{n} carrelli ✓",
    "cartx.noHit": "Nessun reso corrispondente.",
    "cartx.noEv": "Nessun reso nella pianificazione per questo magazzino intorno a oggi.",
    "cartx.manual": "Il reso non è nell'elenco?",
    "cartx.manEv": "Numero di evento della pianificazione",
    "cartx.manErr": "Inserisci il numero di evento (cifre) e il cliente.",
    "cartx.pickFirst": "Scegli il reso del cliente.",
    "cartx.nBad": "Il numero del carrello supera il numero di carrelli.",
    "cartx.unitNo": "Carrello n.",
    "cartx.of": "di",
    "cartx.forClient": "per questo cliente",
    "cartx.doLink": "Associa {cart}",
    "stock.stillNeeded": "Da completare:",
    "stock.mProduct": "mobile",
    "stock.mPhoto": "foto",
    "stock.mCond": "condizione",
    "stock.mDamage": "foto dei danni",
    "stock.mWrap": "coperta + pellicola",
    "stock.mPallet": "n. pallet",
    "stock.mRack": "n. scaffale",
    "stock.damagePhotos": "Foto dei danni",
    "stock.required": "obbligatorio",
    "stock.optional": "facoltativo",
    "stock.addPhoto": "+ Foto",
    "stock.damageNote": "Descrizione dei danni",
    "stock.wrapEach": "Mobile in {n} pezzi: imballa ogni pezzo separatamente e attacca un'etichetta su ciascuno ({n} etichette).",
    "stock.wrapPhoto": "Foto del mobile imballato",
    "stock.saved": "Entrato in magazzino",
    "stock.savedN": "{n} mobili entrati in magazzino",
    "stock.next": "Mobile successivo",
    "stock.nextSame": "Mobile successivo dello stesso reso",
    "stock.photosFailed": "{n} foto non inviata/e: aggiungile dalla scheda del mobile.",
    "ret.emptyTitle": "Nessun reso per questo magazzino",
    "ret.group.today": "Oggi",
    "ret.group.upcoming": "In arrivo",
    "ret.group.recent": "Recenti",
    "ret.tapToEnter": "Tocca un mobile per avviarne l'entrata in magazzino.",
    "stock.nParts": "{n} pezzi",
    "stock.outPill": "Uscito",
    "stock.catPhoto": "Foto catalogo",
    "stock.typeSize": "Tipo",
    "stock.composition": "Composizione",
    "stock.itemNo": "Numero",
    "stock.ofReturn": "del reso",
    "stock.ofIntake": "di questa entrata",
    "stock.place": "Collocazione",
    "stock.day1": "{n} giorno",
    "stock.daysN": "{n} giorni",
    "stock.stIn": "In magazzino da",
    "stock.stInSince": "entrato il {date}",
    "stock.stBackSince": "rientrato il {date}",
    "stock.stOut": "Uscito il",
    "stock.stOutAfter": "dopo {n} in magazzino",
    "stock.stTotal": "Totale in magazzino",
    "stock.stTotalH": "tutti i periodi insieme",
    "stock.stCycles": "Entrate · uscite",
    "stock.stCyclesV": "{i} · {o}",
    "stock.stLast": "Ultimo movimento",
    "stock.stPlace": "In questa posizione",
    "stock.stPlaceSince": "dal {date}",
    "stock.stRef": "Stesso riferimento",
    "stock.stRefV": "{n} in magazzino",
    "stock.stRefH": "qui · {all} tutti i magazzini · {out} usciti",
    "stock.stAvg": "Durata media",
    "stock.stAvgH": "in magazzino per questo riferimento ({n} schede)",
    "stock.stRet": "Resi clienti",
    "stock.stRetH": "di questo riferimento registrati",
    "media.role.identification": "Identificazione",
    "media.role.damage": "Danno",
    "media.role.wrapped": "Imballato",
    "stock.factReturn": "Reso",
    "stock.factWrapping": "Imballaggio",
    "stock.factAdded": "Entrato il",
    "com.tab.new": "Nuova prenotazione",
    "com.tab.mine": "Le mie prenotazioni",
    "com.tab.all": "Tutte le prenotazioni",
    "com.step1": "Contratto",
    "com.step2": "Mobili",
    "com.step3": "Invia",
    "com.whichClient": "Per quale cliente?",
    "com.continueWith": "Continua con «{q}»",
    "com.notFoundManual": "Non trovato nelle consegne in arrivo: inserimento a mano",
    "com.contractN": "contratto {n}",
    "com.delivOn": "consegna {date}",
    "com.delivToPlan": "consegna da pianificare",
    "com.noUpcoming": "Ancora nessun contratto con una consegna in arrivo. Usa «Contratto non nell'elenco».",
    "com.notInList": "Contratto non nell'elenco?",
    "com.clientPh": "Famiglia Rossi",
    "com.contractNo": "N. di contratto",
    "com.ifYouHave": "se ce l'hai",
    "com.delivDate": "Data di consegna",
    "com.ifKnown": "se nota",
    "com.continueClient": "Continua con questo cliente",
    "com.manClientReq": "Inserisci il cliente.",
    "com.manCidBad": "Il n. di contratto ha da 3 a 7 cifre.",
    "com.manDelivPast": "La data di consegna è già passata.",
    "com.searchFurniture": "Cerca: divano, tavolo, nome…",
    "com.transferWarn": "Questo cliente viene servito da {from}: un mobile di {other} richiederà un trasferimento tra magazzini.",
    "com.allCats": "Tutto",
    "com.loadingStock": "Caricamento del magazzino…",
    "com.noneFilter": "Nessun mobile disponibile con questo filtro",
    "com.noneAt": "Nessun mobile disponibile a {wh}",
    "com.noneHint": "Qui appaiono i mobili in magazzino, non prenotati e in buono stato.",
    "com.seePhotos": "Vedi le foto di {name}",
    "com.defectPhotos": "foto dei difetti",
    "com.nAvailable": "{n} disponibili",
    "com.add": "Aggiungi",
    "com.removeOne": "Togline uno",
    "com.addOne1": "Aggiungine uno",
    "com.selected1": "mobile selezionato",
    "com.selectedN": "mobili selezionati",
    "com.continue": "Continua",
    "com.addOne": "Aggiungi almeno un mobile.",
    "com.nthTaken": "{n}° preso",
    "com.noDefectNoted": "nessun difetto annotato",
    "com.addOthers": "Aggiungi altri mobili",
    "com.prepareHintDay": "Proposto: il giorno lavorativo prima della consegna del {date}.",
    "com.prepareNoDeliv": "Nessuna consegna in pianificazione: scegli il giorno di preparazione.",
    "com.sending": "Prenotazione in corso…",
    "com.send1": "Prenota {n} mobile e avvisa la logistica",
    "com.sendN": "Prenota {n} mobili e avvisa la logistica",
    "com.justTaken": "Un mobile è appena stato prenotato da qualcun altro: l'elenco è aggiornato, verifica la selezione.",
    "com.doneTitle": "Mobili prenotati per {client}",
    "com.doneText1": "{n} mobile è prenotato a tuo nome. Emerson e Ludo sono avvisati; preparazione prevista il {prep}.",
    "com.doneTextN": "{n} mobili sono prenotati a tuo nome. Emerson e Ludo sono avvisati; preparazione prevista il {prep}.",
    "com.reference": "Riferimento {ref}",
    "com.seeMine": "Vedi le mie prenotazioni",
    "com.filter.sent": "Da preparare",
    "com.filter.prepared": "Preparate",
    "com.filter.cancelled": "Annullate",
    "com.filter.all": "Tutte",
    "com.noneYet": "Ancora nessuna prenotazione",
    "com.noneYetHint": "Le tue prenotazioni appaiono qui con il loro stato: inviata, in pianificazione, preparata.",
    "com.status.cancelled": "Annullata",
    "com.status.prepared": "Preparata",
    "com.status.planned": "In pianificazione · #{ev}",
    "com.status.sent": "Inviata · logistica avvisata",
    "com.prepOn": "preparazione {date}",
    "com.reservedAt": "prenotato il {when}",
    "com.cancelWarn": "I mobili tornano disponibili.",
    "com.cancelWarnPlanning": "Emerson e Ludo saranno avvisati di togliere la preparazione dalla pianificazione.",
    "com.markPrepared": "Segna come preparata",
    "com.cancelResa": "Annulla la prenotazione",
    "job.backToList": "Elenco",
    "job.timesheetNo": "Numero di evento per il tuo foglio ore",
    "job.copyNo": "Copia",
    "job.copied": "#{n} copiato",
    "job.emptyToday": "Nessun montaggio previsto oggi",
    "job.emptyTodayHint": "I prossimi lavori sono nella scheda «In arrivo».",
    "job.emptyUpcoming": "Nessun montaggio in arrivo",
    "job.emptyUpcomingHint": "Gli eventi «Assemblage meubles pour …» vengono creati automaticamente non appena i mobili di un cliente hanno una data di arrivo.",
    "job.emptyDone": "Nessun lavoro ancora concluso",
    "job.emptyDoneHint": "Quando i montatori premono «Concludi», il tempo reale compare qui e in «Reale vs previsto».",
    "jobs.notArrivedOne": "⏳ {n} non ancora arrivato",
    "jobs.notArrivedMany": "⏳ {n} non ancora arrivati",
    "jobs.receivedPct": "📦 ricevuto {p}%",
    "jobs.receivedOfOne": "{g} mobile ricevuto su {n}",
    "jobs.receivedOfMany": "{g} mobili ricevuti su {n}",
    "jobs.rxWarn": "⚠️ ricezione",
    "jobs.rxOk": "📄 ricevuto",
    "jobs.rxConfirmed": "📝 ordine confermato",
    "jobs.progress": "Avanzamento {p}%: lavoro svolto, ponderato in base al tempo stimato di ogni articolo",
    "timer.notStarted": "Non ancora iniziato",
    "timer.ofEst": "{t} su {e} stimate",
    "timer.teamTime": "{n} montatori · {t} di lavoro complessivo",
    "timer.left": "restano {t} sulla stima",
    "timer.over": "{t} oltre la stima",
    "timer.doneSum": "Concluso in {r} di lavoro per {e} stimate",
    "arch.nOne": "{n} cliente archiviato",
    "arch.nMany": "{n} clienti archiviati",
    "arch.more": "… e altri {n}: affina la ricerca.",
    "arch.auto": "consegnato il {d}",
    "arch.man": "archiviato a mano il {d}",
    "start.added": "Aggiunti",
    "start.addTitle": "Aggiungi montatori",
    "start.busyOn": "su {c}",
    "start.goOne": "Inizia con {n} montatore",
    "start.goMany": "Inizia con {n} montatori",
    "start.addGoOne": "Aggiungi {n} montatore",
    "start.addGoMany": "Aggiungi {n} montatori",
    "start.moved": "{n} tolto da {c}",
    "start.noStaff": "Ancora nessun montatore trovato per questo magazzino: aggiungi i nomi qui sotto.",
    "furn.one.wardrobes": "armadio",
    "furn.many.wardrobes": "armadi",
    "furn.one.beds": "letto",
    "furn.many.beds": "letti",
    "furn.one.bed-components": "elemento del letto",
    "furn.many.bed-components": "elementi del letto",
    "furn.one.chest-drawers": "cassettiera",
    "furn.many.chest-drawers": "cassettiere",
    "furn.one.sideboard": "credenza",
    "furn.many.sideboard": "credenze",
    "furn.one.bookcase-display-cabinet": "libreria",
    "furn.many.bookcase-display-cabinet": "librerie",
    "furn.one.tv-tables": "mobile TV",
    "furn.many.tv-tables": "mobili TV",
    "furn.one.desks": "scrivania",
    "furn.many.desks": "scrivanie",
    "furn.one.dining-tables": "tavolo da pranzo",
    "furn.many.dining-tables": "tavoli da pranzo",
    "furn.one.high-table": "tavolo alto",
    "furn.many.high-table": "tavoli alti",
    "furn.one.console": "consolle",
    "furn.many.console": "consolle",
    "furn.one.coffee-tables": "tavolino",
    "furn.many.coffee-tables": "tavolini",
    "furn.one.bedside-tables": "comodino",
    "furn.many.bedside-tables": "comodini",
    "furn.one.shoe-coat-rack": "appendiabiti",
    "furn.many.shoe-coat-rack": "appendiabiti",
    "furn.one.sofas": "divano",
    "furn.many.sofas": "divani",
    "furn.one.outdoor-furniture": "mobile da esterno",
    "furn.many.outdoor-furniture": "mobili da esterno",
    "furn.one.side-table": "tavolino d'appoggio",
    "furn.many.side-table": "tavolini d'appoggio",
    "furn.one.desk-chair": "sedia da ufficio",
    "furn.many.desk-chair": "sedie da ufficio",
    "furn.one.chairs": "sedia",
    "furn.many.chairs": "sedie",
    "furn.one.high-chair": "sgabello da bar",
    "furn.many.high-chair": "sgabelli da bar",
    "furn.one.armchairs": "poltrona",
    "furn.many.armchairs": "poltrone",
    "furn.one.pouf-stool-bench": "pouf",
    "furn.many.pouf-stool-bench": "pouf",
    "furn.one.floor-lamp": "lampada da terra",
    "furn.many.floor-lamp": "lampade da terra",
    "furn.one.mirror": "specchio",
    "furn.many.mirror": "specchi",
    "furn.one.table-lamp": "lampada da tavolo",
    "furn.many.table-lamp": "lampade da tavolo",
    "furn.one.ceiling-light": "lampada a soffitto",
    "furn.many.ceiling-light": "lampade a soffitto",
    "furn.one.wall-light": "applique",
    "furn.many.wall-light": "applique",
    "furn.one.decorative-items": "oggetto decorativo",
    "furn.many.decorative-items": "oggetti decorativi",
    "furn.one.decoration": "decorazione",
    "furn.many.decoration": "decorazioni",
    "furn.one.cushion-and-throws": "cuscino o plaid",
    "furn.many.cushion-and-throws": "cuscini e plaid",
    "furn.one.carpet": "tappeto",
    "furn.many.carpet": "tappeti",
    "furn.one.artificial-plant": "pianta artificiale",
    "furn.many.artificial-plant": "piante artificiali",
    "furn.one.electric-appliances": "elettrodomestico",
    "furn.many.electric-appliances": "elettrodomestici",
    "furn.one.internet": "articolo online",
    "furn.many.internet": "articoli online",
    "furn.one.hangers": "gruccia",
    "furn.many.hangers": "grucce",
    "furn.one.kits": "kit",
    "furn.many.kits": "kit",
    "furn.one.others": "altro articolo",
    "furn.many.others": "altri articoli",
    "job.notArrived": "non ancora arrivato",
    "job.naExpected": "previsto il {d}",
    "job.rxScanned": "✅ ricevuto (bolla di consegna scansionata)",
    "job.boxesUnknown": "riferimenti dei cartoni non ancora noti",
    "docs.kindDn": "🚚 Bolla di consegna",
    "docs.kindOc": "📝 Conferma d'ordine",
    "docs.kindInv": "🧾 Fattura",
    "docs.kindOth": "📄 Documento d'ordine",
    "docs.notRx": "non è una ricezione",
    "docs.arrOk": "✅ arrivato il {d}",
    "docs.arrNo": "⏳ non ancora ricevuto",
    "docs.ocWarn": "Conferma d'ordine ≠ ricezione: questo ordine non è ancora arrivato al magazzino. Inutile cercare il mobile.",
    "docs.others": "Altri documenti dell'ordine ({n})",
    "docs.none": "Ancora nessun documento in questo ordine.",
    "docs.exp": "Link forse scaduti: apri l'ordine nel planning.",
    "docs.img": "foto",
    "docs.pdf": "PDF",
    "docs.noOrder": "Nessun ordine fornitore collegato.",
    "sdocs.title": "Documenti del fornitore",
    "sdocs.hint": "Documenti caricati nell'ordine fornitore del planning (prima quelli del cliente). Attenzione: una conferma d'ordine o una fattura non significa che il mobile sia arrivato. Solo «arrivato il …» o una bolla di consegna lo conferma.",
    "issue.placeholder": "Es. piano graffiato per 20 cm, viti del piede mancanti, cartone aperto all'arrivo…",
    "issue.needText": "Descrivi il problema in poche parole.",
    "issue.needBy": "Indica chi segnala il problema.",
    "issue.to": "A:",
    "issue.cc": "Copia:",
    "issue.unsales": "Nessun commerciale nel planning: il messaggio va a Ludo.",
    "issue.sendTo": "Invia a {n}",
    "mediaJob.title": "Foto e video del montaggio",
    "mediaJob.hint": "Fotografa o filma il montaggio: cassetti e ante aperti e chiusi, stabilità, fissaggi, poi l'imballaggio. Ogni mobile ha anche il suo pulsante 📷.",
    "mediaJob.empty": "Ancora nessuna foto né video.",
    "mediaJob.photo": "📷 Scatta una foto",
    "mediaJob.video": "🎥 Filma",
    "mediaJob.file": "📁 Scegli un file",
    "mediaJob.prep": "Preparazione della foto…",
    "mediaJob.photoType": "Formato della foto non accettato: scatta la foto con la fotocamera (JPEG).",
    "fin.time": "{r} di lavoro per {e} stimate",
    "fin.leftOne": "{n} mobile non ancora spuntato come montato",
    "fin.leftMany": "{n} mobili non ancora spuntati come montati",
    "fin.allOk": "Tutti i mobili sono spuntati come montati",
    "fin.chkLeftOne": "{n} articolo senza montaggio non ancora controllato",
    "fin.chkLeftMany": "{n} articoli senza montaggio non ancora controllati",
    "fin.chkAllOk": "Tutti gli articoli senza montaggio sono controllati",
    "fin.probsNone": "Nessun problema segnalato",
    "fin.probsOne": "{n} problema segnalato, incluso nel riepilogo",
    "fin.probsMany": "{n} problemi segnalati, inclusi nel riepilogo",
    "fin.media": "📷 {c} di controllo",
    "fin.noMedia": "⚠️ Nessuna foto né video: fotografa o filma il montaggio prima di concludere",
    "fin.photosOne": "{n} foto",
    "fin.photosMany": "{n} foto",
    "fin.videosOne": "{n} video",
    "fin.videosMany": "{n} video",
    "fin.and": "e",
    "fin.pack": "Ho imballato ogni mobile con le coperte MYOTAKU e la pellicola",
    "fin.needPack": "Conferma l'imballaggio (coperte MYOTAKU + pellicola) prima di concludere.",
    "fin.all": "Spunta tutto come montato",
    "fin.chkAll": "Spunta tutto come controllato",
    "fin.note": "Note per il commerciale (facoltativo)",
    "fin.notePh": "Es. credenza imballata nella coperta MYOTAKU, pronta nel carrello del cliente.",
    "fin.send": "Invia il riepilogo a {n}",
    "fin.go": "Concludi e invia il riepilogo",
    "fin.goNo": "Concludi senza inviare",
    "fin.done": "Montaggio concluso",
    "fin.doneSent": "Montaggio concluso, riepilogo inviato a {n}",
    "recap.none": "Riepilogo non inviato (scelta del montatore)",
    "mediaJob.itemAdd": "📷 Aggiungi",
    "mediaJob.itemTitle": "Foto e video del mobile",
    "mediaJob.close": "Chiudi",
    "thx.title": "Grazie {names}!",
    "thx.titleTeam": "Grazie a tutta la squadra!",
    "thx.lead": "Il montaggio per {c} è concluso.",
    "thx.msg0": "Grazie al vostro lavoro accurato, il cliente riceverà mobili ben montati, controllati e protetti: contribuite direttamente alla sua soddisfazione.",
    "thx.msg1": "Ottimo lavoro! Ogni mobile montato con cura è un cliente soddisfatto il giorno della consegna. Grazie per il vostro impegno.",
    "thx.msg2": "La vostra precisione e la vostra cura fanno la differenza per il cliente. Grazie per aver contribuito alla sua soddisfazione!",
    "thx.asmOne": "mobile montato",
    "thx.asmMany": "mobili montati",
    "thx.chkOne": "articolo controllato",
    "thx.chkMany": "articoli controllati",
    "thx.time": "di lavoro",
    "thx.sent": "Il riepilogo è stato inviato a {n}.",
    "thx.close": "Chiudi",
    "links.title": "Link",
    "links.order": "Delivery calendar ordine #{id}",
    "links.event": "Evento di montaggio nel planning",
    "links.contract": "Contratto #{id}",
    "rst.title": "Responsabile",
    "rst.p": "Azzera questo montaggio: contatori montati e controllati, cronometro, tempo impiegato, montatori e messaggio finale. Problemi segnalati, foto e video vengono conservati.",
    "rst.b": "↺ Azzera questo montaggio",
    "rst.ok": "Conferma: azzera tutto",
    "rst.no": "Annulla",
    "rst.run": "Il montaggio è in corso: verrà fermato.",
    "rst.done": "Montaggio azzerato",
    "rst.undo": "↩️ Annulla l'azzeramento",
    "rst.undone": "Azzeramento annullato",
    "rst.last": "Azzerato il {d}",
    "media.del": "Elimina",
    "media.delOk": "Conferma eliminazione",
    "hist.kpiJobs": "Montaggi conclusi",
    "hist.kpiActual": "Tempo reale",
    "hist.ofEst": "su {e} stimate",
    "hist.kpiGap": "Scostamento globale",
    "hist.kpiOk": "Entro la stima",
    "hist.perJob": "Per montaggio",
    "hist.statsNote": "Tempo reale = somma dei tempi di ogni montatore sul montaggio. Stima = tempo del planning (montaggio + movimentazione).",
    "hist.colNo": "#",
    "hist.colClient": "Cliente",
    "hist.colWh": "Magazzino",
    "hist.colFitters": "Montatori",
    "hist.gap": "Scostamento",
    "hist.perFitter": "Per montatore",
    "hist.perFitterNote": "Tempo di ogni montatore sui montaggi conclusi, confrontato con la sua quota della stima. Visibile solo agli amministratori.",
    "hist.colJobs": "Montaggi",
    "hist.colVs": "Reale / stimato",
    "sup.note": "Produttività minuto per minuto: tempo previsto dei mobili convalidati, più il mobile in corso fino al suo tempo previsto, confrontato con il tempo impiegato dai montatori. Allerta dopo 30 min di montaggio se il rendimento scende sotto il 70 %, o se nulla è stato convalidato da 1,5 × il tempo previsto del mobile restante più grande (almeno 30 min). Slack a Ludo ed Emerson, una volta per montaggio e per allerta (controllo ogni ora, dalle 7 alle 18). I montatori non vedono questa scheda.",
    "sup.sentAt": "Slack inviato alle {t}",
    "sup.sentWait": "Slack al prossimo controllo (ogni ora, dalle 7 alle 18)",
    "sup.open": "Apri il lavoro",
    "sup.lastAgo": "ultima convalida: {t} fa",
    "sup.never": "ultima convalida: nessuna convalida",
    "sup.dayRate": "Rendimento di oggi",
    "sup.per": "Per montatore",
    "sup.perN": "Tempo di montaggio, tempo previsto dei mobili convalidati e rendimento (guadagnato ÷ impiegato). Un mobile montato da più montatori è diviso tra loro.",
    "sup.perNone": "Nessun tempo di montaggio in questo periodo.",
    "sup.period.today": "Oggi",
    "sup.period.7d": "7 giorni",
    "sup.period.30d": "30 giorni",
    "sup.col.fitter": "Montatore",
    "sup.col.minutes": "Tempo di montaggio",
    "sup.col.earned": "Tempo guadagnato",
    "sup.col.rate": "Rendimento",
    "sup.col.items": "Mobili convalidati",
    "sup.col.alerts": "Avvisi",
    "sup.spkSpent": "tempo impiegato (minuti-persona)",
    "sup.spkEarned": "tempo guadagnato (mobili convalidati)",
    "sup.tl": "La giornata minuto per minuto",
    "sup.tlN": "Una riga per montatore: ogni blocco è un montaggio (un colore per cliente), i trattini bianchi sono i mobili convalidati. Passa sopra un blocco per i dettagli.",
    "sup.noTl": "Nessun montaggio oggi.",
    "jobs.readAt": "Pianificazione letta il {d} alle {t}",
    "job.readOnly": "Accesso in sola lettura: chiedi a Ludo l'accesso Collaboratore per avviare i cronometri e segnalare un problema.",
    "stock.readOnly": "Hai un accesso in sola lettura: chiedi a Ludo un accesso Collaboratore per registrare mobili.",
    "cartx.readOnly": "Il tuo accesso è in sola lettura: chiedi a Ludo un accesso in modifica per associare i carrelli.",
    "access.readOnly": "sola lettura",
    "access.adminCol": "Admin",
    "access.setPassword": "Nuova password",
    "access.savePassword": "Salva",
    "access.cancel": "Annulla",
    "access.passwordChanged": "Password cambiata.",
    "access.ownLocked": "Non possibile sul vostro account.",
    "nav.home": "Home",
    "home.choose": "Scegli la tua sezione",
    "home.desc.stock": "Entrata dei resi clienti, etichette, inventario",
    "home.desc.preparation": "Montaggi del giorno, cronometro, ricezione degli ordini",
    "home.desc.carts": "Scansiona il QR di un carrello: a quale cliente appartiene",
    "home.desc.supervisor": "Montaggi in corso, rendimento, reale vs previsto",
    "home.desc.commercial": "Riservare mobili del magazzino per un cliente",
    "home.desc.production": "Produzione dei kit, ritmo e stock articoli",
    "home.desc.receiving": "Leggere le bolle di consegna dei fornitori",
    "home.desc.access": "Chi usa quali moduli",
    "prod.status.queued": "In attesa",
    "prod.status.running": "In corso",
    "prod.status.paused": "In pausa",
    "prod.status.stopped": "Fermato",
    "prod.status.finished": "Terminato",
    "prod.status.cancelled": "Annullato",
    "prod.meta.total": "{t} in totale",
    "prod.meta.startedBy": "avviato da {n}",
    "prod.meta.finishedBy": "terminato da {n}",
    "prod.stoppedAt": "⛔ Fermato alle {t} da {n} —",
    "prod.time.running": "In corso da {a} (tempo dato {g}) — {d}",
    "prod.time.paused": "⏸ In pausa dalle {at} — tempo svolto {a} (tempo dato {g}) — {d}",
    "prod.time.done": "Fatto in {a} (tempo dato {g}) — {d}",
    "prod.time.exact": "esattamente nel tempo dato",
    "prod.time.more": "+{d} in più",
    "prod.time.less": "−{d} in meno",
    "prod.pauseBanner": "⏸ Preparazione in pausa",
    "prod.stopBanner": "⛔ Preparazione fermata — il responsabile è stato avvisato",
    "prod.pauseSince": "dalle {t}{by} — il cronometro è fermo. Unità già fatte: {d} / {q}.",
    "prod.pauseBy": " (da {n})",
    "prod.resume": "▶️ Riprendi la preparazione",
    "prod.reportProblem": "⛔ Segnala un problema (materiale, cartoni, stock)",
    "prod.bravoMany": "🎉 Bravo! Le {q} unità richieste sono pronte. Clicca su «Segna come terminato» per chiudere questo ordine.",
    "prod.bravoOne": "🎉 Bravo! L'unità richiesta è pronta. Clicca su «Segna come terminato» per chiudere questo ordine.",
    "prod.newOrderHint": "Scegli il kit e la quantità: l'ordine appare subito alla postazione del preparatore.",
    "prod.chip.min": "{n} min / unità",
    "prod.chip.composed": "Composto da {n} sotto-kit",
    "prod.chip.inStock": "{n} in stock",
    "prod.chip.dispo": "disponibili {n} (riservati {r})",
    "prod.chip.short": "mancano {n} (riservati {r})",
    "prod.contains": "Questo kit comprende",
    "prod.pickKit": "Seleziona un kit",
    "prod.shortage": "⚠ Stock articoli insufficiente per {q} × {k} — mancano: {list}{more}.",
    "prod.shortageMore": " + {n} altro/i",
    "prod.notePh": "Es. priorità cliente X, da consegnare prima delle 14…",
    "prod.sendOrder": "Invia l'ordine di produzione",
    "prod.sending": "Invio…",
    "prod.sendFailed": "Errore — ordine NON inviato, riprova",
    "prod.qtyPreview": "{q} × {m} min =",
    "prod.chip.minShort": "{n} min",
    "prod.sync.ok": "Sincronizzato",
    "prod.sync.off": "Non sincronizzato",
    "prod.sync.failed": "⚠️ Salvataggio non riuscito",
    "prod.prep.none": "Nessuna attività di preparazione registrata oggi.",
    "prod.prep.who": "Preparatore",
    "prod.prep.kits": "Kit convalidati",
    "prod.prep.worked": "Tempo lavorato",
    "prod.prep.given": "Tempo dato",
    "prod.prep.gap": "Scarto",
    "prod.prep.avg": "Media / kit",
    "prod.prep.pauses": "Pause",
    "prod.prep.now": "In questo momento",
    "prod.prep.noName": "(senza nome)",
    "prod.prep.nowRunning": "{k} ×{q} — in corso ({d}/{q})",
    "prod.prep.nowPaused": "⏸ {k} ×{q} — in pausa dalle {t}",
    "audit.title": "Controllo di coerenza dello stock dei kit",
    "audit.checkedAt": "verificato alle {t}",
    "audit.checking": "Verifica in corso…",
    "audit.ok": "✓ Tutto è coerente: stock, storico e uscite delle consegne concordano.",
    "audit.errs": "✗ {n} errore/i",
    "audit.warns": "⚠ {n} punto/i da verificare",
    "audit.checks": "Verificato di continuo: ogni stock = il suo storico, ogni consegna scaricata una sola volta, nessuna consegna passata dimenticata, controllo automatico aggiornato.",
    "audit.contract": "contratto #{n}",
    "audit.untraced": "{warehouse} — {kit}: movimento non tracciato prima del {at}",
    "audit.untracedD": "Una variazione di stock non è stata registrata nello storico: ricontare questo kit.",
    "audit.mismatch": "{warehouse} — {kit}: stock {stock} ≠ storico {history}",
    "audit.mismatchD": "Lo stock mostrato non corrisponde all'ultimo movimento registrato. Una modifica senza storico, o una scrittura fallita: ricontare questo kit.",
    "audit.no_history": "{warehouse} — {kit}: stock {stock} senza alcuno storico",
    "audit.no_historyD": "Questo stock non è mai stato inserito tramite l'app: verificarlo con un conteggio.",
    "audit.delivered_no_exit": "Consegna {label} segnata consegnata senza uscita di stock per: {kits}",
    "audit.delivered_no_exitD": "Questi kit non sono usciti dallo stock: ricontarli.",
    "audit.reserved_but_exited": "Consegna {label}: uscita già fatta per {kits} mentre è ancora «riservata»",
    "audit.reserved_but_exitedD": "Verificare con un conteggio prima del prossimo controllo.",
    "audit.past_reserved": "Consegna {label}: data passata e kit ancora riservati (uscita di stock mancata)",
    "audit.past_reservedD": "Il prossimo controllo automatico deve scaricarla. Se questo messaggio resta dopo le 9 o le 15, il controllo non funziona.",
    "audit.short_exit": "{warehouse} — {kit}: consegna del {at} scaricata con stock insufficiente ({had} per {need})",
    "audit.short_exitD": "Lo stock era già troppo basso: verificare con un conteggio o un invio mancante.",
    "audit.orphan_exit": "Uscita {kit} del {at} senza consegna corrispondente",
    "audit.orphan_exitD": "La consegna è stata tolta dal monitoraggio dopo l'uscita: verificare se il kit è davvero partito.",
    "audit.no_run": "Nessun controllo automatico registrato",
    "audit.no_runD": "Il compito delle 9 / 15 non ha ancora mai registrato un passaggio.",
    "audit.run_failed": "Ultimo controllo automatico non riuscito ({at})",
    "audit.run_failedD": "{message} — in quel passaggio non è stata fatta alcuna uscita di stock.",
    "audit.run_stale": "Nessun controllo automatico da {hours} h",
    "audit.run_staleD": "Le uscite di stock di oggi non sono fatte: il compito automatico non gira più.",
    "audit.no_client": "{n} consegna/e riservata/e senza nome del cliente nella pianificazione",
    "audit.no_clientD": "{contracts}",
    "alloc.incomplete": "⚠️ controllo incompleto",
    "alloc.stale": "⚠️ più di 26 h senza controllo",
    "prod.stockTotalTab": "Totale",
    "prod.stockTotal": "Totale dei 3 magazzini",
    "prod.stockAt": "In stock a {w}",
    "prod.kitsOne": "{n} kit",
    "prod.kitsMany": "{n} kit",
    "prod.pill.allocated": "assegnati {n}",
    "prod.pill.allocatedT": "Riservati per consegne clienti confermate in arrivo",
    "prod.pill.toSend": "da inviare {n}",
    "prod.pill.toSendT": "Mancano a Dielsdorf / Berlino, da coprire con un invio da Penthalaz",
    "prod.pill.dispo": "disponibili {n}",
    "prod.pill.short": "mancano {n}",
    "prod.short.wh": "⚠ {w} — {k}: mancano {n} (assegnati {a}, in stock {s})",
    "prod.short.pz": "⚠ Penthalaz — {k}: mancano {n} (assegnati {a}{send}, in stock {s}{prod})",
    "prod.short.send": " + da inviare {n}",
    "prod.short.inProd": ", già in preparazione {n}",
    "prod.short.makeOrder": "Prepara un ordine ×{n}",
    "prod.alloc.empty": "Nessuna consegna confermata con kit per ora.",
    "prod.alloc.past": " — consegna passata, in attesa dello stato «Consegnato»",
    "prod.alloc.artPz": "Articolo singolo — tolto dallo stock articoli il giorno della consegna",
    "prod.alloc.artElse": "Articolo singolo — nessuno stock articoli tenuto a {w}",
    "prod.alloc.type.CT": "Breve termine",
    "prod.alloc.type.LT": "Lungo termine",
    "prod.alloc.type.COMPL": "Complemento",
    "prod.alloc.type.VENTE": "Vendita",
    "prod.alloc.type.HS": "Home staging",
    "prod.alloc.type.MAINT": "Manutenzione",
    "prod.alloc.type.AUTRE": "Altro",
    "prod.alloc.type.BTR": "BTR",
    "prod.alloc.type.EVT": "Contratto come evento",
    "prod.short.makeTransfer": "Prepara un invio ×{n}",
    "prod.send.title": "Invio di kit a un magazzino",
    "prod.send.hint": "Scegli il magazzino, inserisci le quantità inviate da Penthalaz, poi «Invia». Lo stock di Penthalaz scende subito; i kit sono in viaggio finché il magazzino di destinazione non li riceve (scheda Ricezione).",
    "prod.send.to": "Verso {w}",
    "prod.send.qtyFor": "Quantità inviata — {k}",
    "prod.send.notePh": "Es. camion del 26.09, n° di bolla…",
    "prod.send.total": "Totale: {n} kit da Penthalaz verso {w}.",
    "prod.send.none": "Inserisci almeno una quantità.",
    "prod.send.over": "Stock di Penthalaz insufficiente — {list}.",
    "prod.send.button": "Invia a {w} ({n} kit)",
    "prod.send.buttonEmpty": "Invia a {w}",
    "prod.send.ask": "Inviare {n} kit da Penthalaz verso {w}?",
    "prod.send.yes": "Sì, invia",
    "prod.send.done": "✓ Invio registrato: {n} kit verso {w}. Lo stock di Penthalaz è aggiornato; {w} li aggiunge alla ricezione.",
    "prod.send.log": "Ultimi invii",
    "prod.send.logEmpty": "Nessun invio registrato per ora.",
    "prod.send.inTransit": "in viaggio",
    "prod.view.resp": "Responsabile",
    "prod.view.prep": "Preparatore",
    "prod.orders": "Ordini di produzione",
    "prod.ordersHint": "Dal più recente al più vecchio. L'avanzamento è aggiornato dal preparatore in tempo reale.",
    "prod.ordersEmpty": "Nessun ordine inviato per ora.",
    "prod.queueHint": "Gli ordini inviati dal responsabile arrivano qui, dal più vecchio al più recente.",
    "prod.youAre": "Tu sei:",
    "prod.youArePh": "Il tuo nome (necessario per iniziare)",
    "prod.nameFirst": "✋ Indica prima il tuo nome in «Tu sei».",
    "prod.stockInPlace": "Stock già in posto",
    "demand.ofMonth": "(totale del mese {n})",
    "demand.segment": "{k} · {m}: {n} kit (totale del mese {total})",
    "prod.entry.label": "Inserimento:",
    "prod.entry.add": "➕ Aggiungi una quantità",
    "prod.entry.set": "✏️ Correggi il totale",
    "prod.entry.addHelp": "Digita solo la quantità ricevuta (es. 30), poi «Aggiungi» o Invio: si somma allo stock attuale, senza calcoli. Un numero negativo (es. -2) toglie.",
    "prod.entry.setHelp": "Digita il nuovo totale contato: sostituisce la quantità attuale.",
    "prod.entry.now": "Quantità attuale",
    "prod.entry.addPh": "+ qtà",
    "prod.entry.addFor": "Quantità da aggiungere — {k}",
    "prod.entry.totalFor": "Nuovo totale — {k}",
    "prod.entry.addBtn": "Aggiungi",
    "prod.entry.askAdd": "Sei sicuro di voler aggiungere {n} alla quantità di {k}?",
    "prod.entry.askDown": "Sei sicuro di voler diminuire di {n} la quantità di {k}?",
    "prod.entry.yesAdd": "Sì, aggiungi",
    "prod.entry.yesDown": "Sì, diminuisci",
    "prod.entry.saved": "✅ Grazie, la nuova quantità è registrata nello stock.",
    "prod.entry.notSaved": "⚠️ La quantità NON è stata registrata (connessione?). Riprova.",
    "prod.entry.tapClose": "Tocca per chiudere",
    "prod.history.open": "Clicca per vedere lo storico",
    "prod.history.title": "Storico — {k} ({w})",
    "prod.history.close": "Chiudi",
    "prod.history.current": "Stock attuale: {n}",
    "prod.history.none": "Nessun movimento registrato.",
    "inv.src.correction": "correzione",
    "inv.src.plus_minus": "pulsante ±",
    "prod.delete": "Elimina",
    "prod.deleteYes": "Conferma l'eliminazione",
    "prod.unitSummary": "Unità n. {n} in assemblaggio — {c} / {m} articoli spuntati",
    "inv.lastAt": "Ultimo inventario a {w}: {when} da {who} — {n} kit contati, {d} scarto/i corretto/i.",
    "inv.noneAt": "Nessun inventario registrato per {w}.",
    "inv.kit": "Kit",
    "inv.app": "App",
    "inv.gap": "Scarto",
    "inv.summary": "{n} kit contati — {d} scarto/i da correggere",
    "inv.start": "Digita le quantità contate per iniziare.",
    "inv.ask": "Confermare l'inventario di {w}?",
    "inv.askDetail": "{n} kit contati — {d} correzione/i",
    "inv.yes": "Sì, conferma l'inventario",
    "inv.done": "Inventario {w}: {n} kit contati, {d} correzione/i",
    "inv.historyHint": "Ogni variazione di quantità — inserita a mano, con i pulsanti +/−, o l'accredito automatico di ogni kit assemblato (appena l'unità è convalidata) — è registrata qui con data e ora.",
    "inv.countedCol": "Contato",
    "art.hint": "Quantità a scaffale per ogni articolo, stesso riferimento delle etichette. Scende da sola quando il preparatore spunta un articolo in una checklist; all'arrivo di una consegna, modalità «➕ Aggiungi»: digita solo la quantità ricevuta, l'app fa il calcolo; dopo un conteggio, modalità «✏️ Correggi il totale». Clicca su un articolo per aprirne la scheda.",
    "art.total": "{q} unità in stock su {n} riferimenti",
    "art.noMatch": "Nessun articolo corrisponde alla ricerca.",
    "art.linenShared": "Biancheria — articoli comuni a più misure",
    "art.refCount": "{n} rif.",
    "art.sharedBy": "Riferimento comune a: {list}",
    "art.chip.sku": "Sist. {s}",
    "art.chip.ref": "Rif. forn. {s}",
    "art.chip.confirm": "da confermare",
    "art.chip.open": "Scheda ›",
    "sheet.hist.date": "Data",
    "sheet.hist.move": "Movimento",
    "sheet.hist.stock": "Stock",
    "sheet.hist.loading": "Caricamento dello storico…",
    "sheet.src.manual": "Aggiunta manuale",
    "sheet.src.correction": "Correzione del totale (conteggio)",
    "sheet.src.plus_minus": "Pulsante + / −",
    "sheet.src.production": "Prelevato per un kit (checklist)",
    "sheet.src.restock": "Rimesso in stock (unità rimossa)",
    "sheet.src.receipt": "Ricevimento fornitore",
    "sheet.src.reception_check": "Correzione alla verifica di un ricevimento",
    "sheet.src.delivery": "Consegna cliente (articolo singolo)",
    "sheet.src.deliveryTo": "Consegna cliente — {c}",
    "sheet.src.inventory": "Inventario (conteggio fisico)",
    "sheet.src.transfer": "Invio tra magazzini",
    "sheet.det.order": "ordine {k} ×{n}",
    "sheet.det.unticked": "(deselezionato)",
    "sheet.det.supplier": "fornitore {s}",
    "sheet.det.receipt": "ricevimento {n}",
    "sheet.det.contract": "contratto #{n}",
    "sheet.det.by": "da {w}",
    "sheet.det.short": "{n} mancanti sullo scaffale",
    "inv.src.restock": "rimesso in stock",
    "artrec.supplier": "Fornitore (facoltativo)",
    "artrec.hintPaste": "Sul computer potete anche incollare uno screenshot (Ctrl+V) o trascinare un file su questa scheda. Se la quantità ricevuta non compare sul documento, scrivetela nella nota.",
    "note.notAdded": "⚠ Non aggiunto: {list}",
    "note.othersReady": " — gli altri file sono pronti.",
    "note.maxImages": "{f} (massimo {n} immagini)",
    "note.badFormat": "{f} — formato non supportato",
    "artrec.photoN": "Foto {n}",
    "prod.printSheets": "Stampa {n} schede cartone",
    "prod.printSheet": "Stampa 1 scheda cartone",
    "ident.phase.type": "Identificazione del tipo di mobile…",
    "ident.phase.retour": "Confronto della foto con il reso…",
    "ident.phase.catalogue": "Ricerca nel catalogo Myotaku…",
    "ident.cancel": "Annulla",
    "ident.recognize": "Riconoscere il mobile",
    "ident.noAi": "Il riconoscimento non è disponibile qui: scegliete il mobile a mano.",
    "ident.aiLimit": "Troppe richieste al momento. Riprovate tra poco o cercate a mano.",
    "ident.hintGrid": "Fotografate il mobile intero prima di imballarlo, poi confrontatelo con la foto del catalogo.",
    "stock.qtyAuto": "{q} attesi in questo reso, {k} già inseriti: quantità impostata automaticamente.",
    "stock.qtyOver": "Questo reso ne attendeva {q} e {k} sono già inseriti: questo mobile è in più.",
    "stock.qtyHint": "Stesso riferimento, stesso stato e stesso pallet: {n} schede, {n} numeri di stock e {n} etichette, una per mobile. Se uno è in un altro stato, inseritelo a parte.",
    "stock.qtyNums": "Numeri assegnati: {list}.",
    "stock.unitOf": "Mobile {i}/{n}",
    "compo.dialogTitle": "Composizione del mobile",
    "compo.dialogHint": "In quanti pezzi è stoccato questo mobile? Es. Hudson = divano + chaise longue (2 pezzi). Ogni pezzo riceve la propria etichetta « Pezzo 1/2 », « Pezzo 2/2 ». L'impostazione resta per questo prodotto.",
    "compo.sofa": "Divano",
    "compo.chaise": "Chaise longue",
    "compo.saved": "Composizione salvata",
    "compo.toConfirm": "da confermare",
    "compo.pieceOf": "Pezzo {i}/{n}",
    "compo.cancel": "Annulla",
    "ident.yourPhoto": "La vostra foto",
    "ident.catPhoto": "Foto del catalogo",
    "ident.refMyo": "Rif. Myotaku",
    "ident.refSup": "Rif. fornitore",
    "ident.generic": "articolo generico senza rif. fornitore",
    "ident.supplier": "Fornitore",
    "ident.type": "Tipo",
    "ident.size": "Dimensioni",
    "ident.notInCat": "non nel catalogo online",
    "rec.upcoming": "Consegne in arrivo",
    "rec.past": "Consegne passate",
    "rec.toSchedule": "da pianificare",
    "rec.runDone": "Montaggio terminato",
    "rec.runGoing": "Montaggio in corso",
    "rec.assembledN": "montato {k}/{n}",
    "rec.po": "Ord. #{n}",
    "stock.orderHint": "Ordine",
    "note.stop": "Interrompi",
    "note.stopped": "Lettura interrotta",
    "cam.title": "Fotocamera del PC",
    "cam.hint": "Mettete l'oggetto o la pagina davanti alla fotocamera, poi scattate la foto. Potete scattarne diverse di seguito.",
    "cam.shot": "📸 Scatta la foto",
    "cam.file": "Scegli invece un file",
    "cam.close": "Chiudi",
    "cam.which": "Fotocamera",
    "cam.starting": "Avvio della fotocamera…",
    "cam.added": "Foto aggiunta ✓",
    "cam.e.denied": "Accesso alla fotocamera negato. Consentite la fotocamera per questo sito (icona fotocamera o lucchetto nella barra degli indirizzi), poi riaprite. Altrimenti scegliete un file.",
    "cam.e.none": "Nessuna fotocamera trovata su questo PC: collegate la fotocamera USB e riaprite.",
    "cam.e.busy": "La fotocamera è già usata da un'altra app (Teams, Fotocamera di Windows…): chiudetela e riaprite.",
    "cam.e.blocked": "Questa pagina non può usare la fotocamera in questo browser. Usate « Scegli un file ».",
    "cam.e.other": "La fotocamera non è partita. Scegliete invece un file.",
    "cam.tab": "📱 Usa il tablet (postazione foto)",
    "cam.waitT": "Foto con il tablet",
    "cam.wait": "Richiesta inviata al tablet: « {l} ». Sul tablet aprite « 📱 Postazione foto » (in alto) e toccate « Scatta la foto ». Le foto arrivano qui subito.",
    "cam.waitRec": "Richiesta inviata al tablet. Sul tablet aprite « 📱 Postazione foto »: passa alla scheda Ricezione per fotografare le pagine, poi « Leggi ». Il risultato apparirà qui.",
    "cam.got0": "In attesa del tablet…",
    "cam.fin": "Fatto",
    "st.btn": "📱 Postazione foto",
    "st.title": "Postazione foto",
    "st.hint": "Questo tablet è la fotocamera del PC: ogni richiesta fatta sul PC appare qui. Tenete aperta questa pagina.",
    "st.none": "Nessuna richiesta per ora. Sul PC cliccate su 📷 Foto poi « Usa il tablet ».",
    "st.shot": "📸 Scatta la foto",
    "st.rec": "📄 Fotografa la bolla (scheda Ricezione)",
    "st.quit": "Esci dalla postazione foto",
    "st.gen": "Foto del montaggio",
    "st.recL": "Bolla di consegna (Ricezione)",
    "st.agoNow": "proprio ora",
    "st.ago": "{m} min fa",
    "st.up": "Invio…",
    "sdocs.photo": "📷 Foto della bolla o dei cartoni",
    "sdocs.photosT": "Foto scattate sul posto",
    "st.docs": "Documenti fornitore",
    "com.noticeFailed": "Messaggio Slack alla logistica non consegnato: {error}",
    "stock.supplierOrder": "Ordine fornitore",
    "cartons.unknownTitle": "Mobili senza riferimenti cartone",
    "cartons.unknownHint": "Mobili composti consegnati in più cartoni i cui riferimenti nessuna bolla PDF dei loro ordini ha ancora fornito. Fotografare qui la loro bolla li registra.",
    "cartons.nCartons": "{n} cartoni",
    "cartons.checked": "Controllato il {when}",
    "cartons.allKnown": "Tutti i mobili in più cartoni hanno i loro riferimenti.",
    "prod.devBanner1": "Questa applicazione è stata sviluppata da {name}. Se hai un qualsiasi problema nell'uso dell'applicazione, rivolgiti allo sviluppatore {name}.",
    "prod.devBanner2": "Per favore non usare il gruppo WhatsApp « MYOTAKU - Computeur & Mobile » per segnalare un problema.",
    "guide.title": "🎬 Video per i montatori: il tuo ruolo, passo dopo passo (6 min)",
    "guide.sub": "senza voce, con testi sullo schermo",
    "guide.hide": "Nascondi il video",
    "guide.show": "Mostra il video",
    "job.productPage": "Scheda prodotto",
    "maybe.titleOne": "{n} articolo ordinato con un nome simile: da verificare",
    "maybe.titleMany": "{n} articoli ordinati con un nome simile: da verificare",
    "maybe.hint": "Trovato nel Delivery calendar, ma non collegato con certezza a questo cliente (assente dalla sua lista cliente, o nome di un altro appartamento). Verifica con il commerciale prima di montare o consegnare: non è contato nel tempo di montaggio.",
    "stock.tlIn": "in magazzino",
    "stock.tlOut": "uscito",
    "maybe.unseenOne": "{n} articolo ordinato per questo cliente, forse già consegnato",
    "maybe.unseenMany": "{n} articoli ordinati per questo cliente, forse già consegnati",
    "maybe.unseenHint": "Arrivati prima del {date}: l'app non vede le consegne di questo cliente prima di questa data. Verifica con il commerciale se sono già stati consegnati prima di montarli o consegnarli: non sono contati nel tempo di montaggio.",
    "arch.t": "Clienti archiviati",
    "arch.done": "Cliente archiviato: è nella scheda Archiviati.",
    "arch.undone": "{c} è di nuovo nelle liste.",
    "jobs.asm": "Montaggio",
    "jobs.deliv": "Consegna",
    "jobs.est": "Stima",
    "jobs.progressT": "Avanzamento {p}%: lavoro svolto, ponderato in base al tempo stimato di ogni articolo · tacca: {t}% del tempo stimato già trascorso",
    "job.salesRep": "Commerciale",
    "job.noRep": "non trovato",
    "job.noItems": "Nessun elenco di mobili letto dall'ordine: vedi i link del Delivery calendar qui sotto.",
    "issue.reported": "Problemi segnalati",
    "issue.report": "Segnala un problema",
    "issue.sendFailed": "Invio Slack non riuscito: il messaggio non è partito.",
    "issue.sendPending": "In attesa di invio su Slack",
    "issue.qOfN": "{q} di {n}",
    "item.probOne": "⚠️ {n} problema segnalato",
    "item.probMany": "⚠️ {n} problemi segnalati",
    "recap.failed": "Riepilogo non inviato",
    "recap.unclear": "Invio del riepilogo in corso",
    "maybe.ref": "riferimento",
    "st.sentOne": "{n} foto inviata",
    "st.sentMany": "{n} foto inviate",
    "cam.gotOne": "✓ {n} foto ricevuta dal tablet",
    "cam.gotMany": "✓ {n} foto ricevute dal tablet",
    "cam.doneOne": "Termina e leggi ({n} pagina)",
    "cam.doneMany": "Termina e leggi ({n} pagine)",
    "mediaJob.saved": "Salvato",
    "mediaJob.tooBig": "File troppo pesante ({mb} MB, massimo {max} MB).",
    "mediaJob.videoBig": "Video troppo pesante ({mb} MB, massimo {max} MB): gira un video più breve.",
    "mediaJob.videoType": "Formato video non accettato: filma con la fotocamera (MP4).",
    "mediaJob.fileType": "Formato di file non accettato: scegli una foto o un video.",
    "docs.unread": "Non è stato possibile leggere ora i documenti di questo ordine: riprova più tardi.",
    "com.readOnly": "Accesso in sola lettura: chiedi a Ludo l'accesso Collaboratore per prenotare mobili.",
    "prod.readOnly": "Accesso in sola lettura: chiedi a Ludo l'accesso Collaboratore per produrre kit e ricevere articoli.",
    "sup.kAlerts": "Avvisi in corso",
    "sup.alT": "Avvisi",
    "sup.noAl": "Nessun avviso al momento.",
    "sup.live": "Montaggi in corso",
    "sup.stRun": "in corso",
    "sup.stPau": "in pausa",
    "sup.nm": "non misurabile: nessun mobile nel lavoro",
    "sup.readOnly": "Accesso in sola lettura: chiedi a Ludo l'accesso Collaboratore per segnare il personale fisso.",
    "note.modeNow": "Foto e PDF vengono letti subito.",
    "note.readOne": "Leggi e confronta con il Delivery calendar ({n} pagina)",
    "note.readMany": "Leggi e confronta con il Delivery calendar ({n} pagine)",
    "artrec.sendOne": "Invia la ricezione ({n} pagina)",
    "artrec.sendMany": "Invia la ricezione ({n} pagine)",
    "note.pagesOne": "{n} pagina",
    "note.pagesMany": "{n} pagine",
    "note.linesOne": "{n} riga letta",
    "note.linesMany": "{n} righe lette",
    "note.clientsOne": "{n} cliente",
    "note.clientsMany": "{n} clienti",
    "note.colliOne": "{n} collo",
    "note.colliMany": "{n} colli",
    "note.asmOn": "montaggio {d}",
    "note.err.rate_limited": "Troppe letture in una volta o limite di utilizzo raggiunto: riprova più tardi.",
    "note.err.unavailable": "La lettura non è consentita per questa app al momento: riprova più tardi.",
    "note.err.photo": "Foto rifiutata (formato o dimensione): rifalla.",
    "note.err.failed": "Lettura interrotta: riprova.",
    "note.notMatchedRetry": "Letto, ma non ancora confrontato con il Delivery calendar: riconfronta.",
    "brand.z": "Z",
    "brand.furniture": "Furniture",
    "jobs.titleMode": "montage",
    "issue.sending": "invio in corso",
    "issue.notSent": "messaggio non inviato a {n}",
    "note.shortOne": "manca {n}",
    "note.shortMany": "mancano {n}",
    "recv.unmatchedOne": "{n} non abbinato",
    "recv.unmatchedMany": "{n} non abbinati",
    "recv.completeOne": "{n} completo",
    "recv.completeMany": "{n} completi",
    "recv.missingOne": "{n} mancante",
    "recv.missingMany": "{n} mancanti",
    "job.start": "Avvia",
    "job.writeFailed": "Impossibile salvare: controlla la connessione e riprova.",
    "media.mb": "MB",
    "recv.readOnly": "Accesso in sola lettura alla Ricezione: chiedi a Ludo l'accesso Collaboratore.",
    "issue.sendingNow": "Invio in corso…",
    "issue.netFailed": "Impossibile inviare: controlla la connessione e riprova.",
    "crew.pickWarehouse": "Scegli un magazzino in alto: i fissi si scelgono magazzino per magazzino.",
    "media.failVideo": "Impossibile salvare il video: controlla la connessione e riprova.",
    "media.failPhoto": "Impossibile salvare la foto: controlla la connessione e riprova.",
    "error.loadFailed": "Impossibile caricare: controlla la connessione e riprova.",
  },
};

const KEY = "myotaku-warehouse:locale";

const isLocale = (v: string | null | undefined): v is Locale => !!v && (LOCALES as readonly string[]).includes(v);

// The person's own choice first; before they have made one, the browser's language when it is one
// of the four (a Berlin browser opens in German, Hackbridge in English); else French.
export function readLocale(): Locale {
  try {
    const stored = localStorage.getItem(KEY);
    if (isLocale(stored)) return stored;
  } catch {
    /* private window, blocked storage — fall through to the browser's language */
  }
  if (typeof navigator !== "undefined") {
    for (const tag of navigator.languages ?? [navigator.language]) {
      const short = tag?.slice(0, 2).toLowerCase();
      if (isLocale(short)) return short;
    }
  }
  return "fr";
}

export function writeLocale(locale: Locale) {
  try {
    localStorage.setItem(KEY, locale);
  } catch {
    /* the choice lasts for this tab only; the page still works */
  }
}

export const LocaleContext = createContext<{ locale: Locale; setLocale: (l: Locale) => void }>({
  locale: "fr",
  setLocale: () => {},
});

export type Vars = Record<string, string | number | null | undefined>;
export type T = (key: string, vars?: Vars) => string;

// ⛔ Falls back to the KEY, never to English. A missing French string should look obviously
// missing on the floor, not quietly become English and go unreported for months.
// `{name}` placeholders are filled from `vars`.
function lookup(locale: Locale, key: string, vars?: Vars) {
  const s = STRINGS[locale][key] ?? key;
  return vars ? s.replace(/\{(\w+)\}/g, (m, k: string) => (vars[k] == null ? m : String(vars[k]))) : s;
}

export function useT() {
  const { locale, setLocale } = useContext(LocaleContext);
  const t: T = (key, vars) => lookup(locale, key, vars);
  return { t, locale, setLocale };
}

// One string in a given language, whatever the reader chose (his sheets print English under the
// reader's own language).
export const translateTo = (locale: Locale, key: string, vars?: Vars) => lookup(locale, key, vars);

// For code outside a component (services, error messages): the same strings, in the language the
// person has chosen.
export const translate: T = (key, vars) => lookup(readLocale(), key, vars);

// Sent with every request, so the server answers (errors, documents) in the same language.
export const localeHeaders = (): Record<string, string> => ({ "Accept-Language": readLocale() });
